// src/components/atoms/Button.tsx
import React from "react";
import { cva } from "class-variance-authority";

// src/components/atoms/Icon.tsx
import {
  AlertCircle,
  Archive,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  ArrowUpLeft,
  BarChart,
  Bell,
  BookOpen,
  Bookmark,
  Briefcase,
  ClipboardList,
  Building,
  Building2,
  Calendar,
  Check,
  CheckSquare,
  ChevronsLeftRight,
  ChevronsRightLeft,
  Circle,
  Copy,
  Crown,
  Edit,
  Eye,
  FileBarChart,
  FileText,
  FlaskConical,
  Folder,
  Heart,
  Home,
  CheckCircle,
  LogOut,
  SquarePen,
  House,
  PanelRightOpen,
  PanelRightClose,
  RefreshCcw,
  RotateCcw,
  RotateCw,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  CircleAlert,
  CircleCheck,
  CircleX,
  Clock,
  CloudUpload,
  Download,
  EllipsisVertical,
  Plus,
  Filter,
  Globe,
  Image,
  ImageUp,
  Inbox,
  Info,
  LayoutDashboard,
  ListFilter,
  Loader,
  Lock,
  Mail,
  MessageSquare,
  MessageSquareText,
  Minus,
  MoreVertical,
  OctagonXIcon,
  Pencil,
  Search,
  Send,
  Settings,
  Square,
  Star,
  Tag,
  Trash2,
  TrendingUp,
  Triangle,
  TriangleAlert,
  User,
  UserCheck,
  Users,
  X,
  Upload,
  Menu,
  MessageCircleMore,
  MessageSquareCheck,
  PenLine,
  Zap,
  Presentation,
  Sparkles
} from "lucide-react";
import { jsx } from "react/jsx-runtime";
var iconRegistry = {
  AlertCircle,
  Archive,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  ArrowUpLeft,
  BarChart,
  Bell,
  BookOpen,
  Bookmark,
  Briefcase,
  ClipboardList,
  Building,
  Building2,
  Calendar,
  Check,
  CheckSquare,
  ChevronsLeftRight,
  ChevronsRightLeft,
  Circle,
  Copy,
  Crown,
  Edit,
  Eye,
  FileBarChart,
  FileText,
  FlaskConical,
  Folder,
  Heart,
  Home,
  ImageUp,
  CheckCircle,
  House,
  PanelRightOpen,
  PanelRightClose,
  RefreshCcw,
  RotateCcw,
  RotateCw,
  ChevronDown,
  ChevronRight,
  LogOut,
  SquarePen,
  ChevronsLeft,
  ChevronsRight,
  CircleAlert,
  CircleCheck,
  CircleX,
  Clock,
  CloudUpload,
  Download,
  EllipsisVertical,
  Filter,
  Globe,
  Image,
  Inbox,
  Info,
  LayoutDashboard,
  ListFilter,
  Loader,
  Lock,
  Mail,
  MessageSquare,
  MessageSquareText,
  Minus,
  MoreVertical,
  OctagonXIcon,
  Pencil,
  Search,
  Send,
  Settings,
  Square,
  Star,
  Trash2,
  TrendingUp,
  Triangle,
  TriangleAlert,
  Tag,
  User,
  UserCheck,
  Users,
  X,
  Plus,
  Upload,
  Menu,
  MessageCircleMore,
  MessageSquareCheck,
  PenLine,
  Zap,
  Presentation,
  Sparkles
};
var Icon = ({ name, testID, ...props }) => {
  const LucideIcon = iconRegistry[name];
  if (!LucideIcon) {
    console.info(
      `\u{1F6A8} [Missing Icon] ${name} is not registered in iconRegistry \u2014 add it to Icon.tsx`
    );
    return /* @__PURE__ */ jsx(
      OctagonXIcon,
      {
        "data-testid": testID,
        className: "text-red-500",
        strokeWidth: 1,
        absoluteStrokeWidth: false,
        style: { color: "red" }
      }
    );
  }
  return /* @__PURE__ */ jsx(
    LucideIcon,
    {
      "data-testid": testID,
      strokeWidth: 1,
      absoluteStrokeWidth: true,
      ...props
    }
  );
};

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/atoms/Spinner.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var Spinner = ({
  className,
  testID
}) => /* @__PURE__ */ jsxs(
  "svg",
  {
    className: cn("animate-spin h-4 w-4", className),
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 24 24",
    "data-testid": testID,
    children: [
      /* @__PURE__ */ jsx2(
        "circle",
        {
          className: "opacity-25",
          cx: "12",
          cy: "12",
          r: "10",
          stroke: "currentColor",
          strokeWidth: "4"
        }
      ),
      /* @__PURE__ */ jsx2(
        "path",
        {
          className: "opacity-75",
          fill: "currentColor",
          d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        }
      )
    ]
  }
);

// src/components/atoms/Button.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var brandButtonVariants = cva(
  "inline-flex items-center justify-between whitespace-nowrap font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none relative overflow-hidden cursor-pointer [gap:var(--spacing-xl,16px)]",
  {
    variants: {
      variant: {
        primary: [
          "bg-background text-fg-primary rounded-full",
          "hover:bg-bg-primary-hover",
          "focus-visible:ring-ring",
          "disabled:bg-button-disabled disabled:text-disabled"
        ],
        secondary: [
          "bg-bg-tertiary text-fg-primary rounded-full",
          "hover:bg-bg-secondary-hover",
          "focus-visible:ring-ring",
          "disabled:bg-button-disabled disabled:text-disabled"
        ],
        tertiary: [
          "group text-fg-primary border-2 border-transparent p-1",
          "focus-visible:ring-ring",
          "disabled:text-disabled"
        ],
        quaternary: [
          "text-fg-brand-primary border-2 border-transparent",
          "hover:text-quaternary-on-brand",
          "focus-visible:ring-ring",
          "disabled:text-disabled"
        ]
      },
      size: {
        sm: "text-xs h-9",
        md: "text-sm h-11",
        lg: "text-lg h-12"
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "md"
    }
  }
);
var iconWrapperSizeClass = {
  sm: "w-6 h-6",
  md: "w-7 h-7",
  lg: "w-8 h-8"
};
var iconSizeMap = { sm: 10, md: 14, lg: 16 };
var getIconColor = (disabled, variant) => {
  if (disabled) return "text-disabled";
  if (variant === "tertiary")
    return "text-fg-primary group-hover:text-fg-white";
  if (variant === "quaternary") return "text-fg-primary";
  return "text-fg-white";
};
var getDefaultIcon = (iconPosition) => iconPosition === "leading" ? "ArrowLeft" : "ArrowRight";
var DIRECTIONAL_ARROWS = /* @__PURE__ */ new Set(["ArrowLeft", "ArrowRight"]);
var IconWrapper = React.memo(
  ({
    iconName,
    disabled = false,
    variant,
    iconClassName,
    size = "md",
    flipInRtl = false
  }) => {
    const resolvedSize = size ?? "md";
    return /* @__PURE__ */ jsx3(
      "span",
      {
        className: cn(
          "inline-flex items-center justify-center rounded-full p-0",
          iconWrapperSizeClass[resolvedSize],
          flipInRtl && "icon-rtl-flip",
          disabled ? "bg-button-disabled-subtle" : variant === "tertiary" ? "bg-bg-tertiary group-hover:bg-fg-brand-green" : variant === "quaternary" ? "bg-bg-tertiary" : "bg-fg-brand-green"
        ),
        children: iconName && /* @__PURE__ */ jsx3(
          Icon,
          {
            name: iconName,
            size: iconSizeMap[resolvedSize],
            className: iconClassName ?? getIconColor(disabled, variant)
          }
        )
      }
    );
  }
);
IconWrapper.displayName = "IconWrapper";
var Button = React.memo(
  ({
    className,
    variant,
    size,
    iconPosition = "trailing",
    iconName,
    disabled,
    label,
    testID,
    loading = false,
    children,
    iconClassName,
    labelClassName,
    ref,
    ...props
  }) => {
    if (children) {
      return /* @__PURE__ */ jsx3(
        "button",
        {
          ref,
          className: cn(brandButtonVariants({ variant, size }), className),
          disabled: disabled || loading,
          "data-testid": testID,
          ...props,
          children
        }
      );
    }
    const isQuaternary = variant === "quaternary";
    const isIconOnly = !label;
    const shouldShowIcon = !isQuaternary;
    const showLeadingIcon = shouldShowIcon && !isIconOnly && iconPosition === "leading" && !loading;
    const showTrailingIcon = shouldShowIcon && !isIconOnly && iconPosition === "trailing" && !loading;
    const resolvedSize = size ?? "md";
    const buttonClasses = cn(
      brandButtonVariants({ variant, size }),
      isIconOnly && "justify-center",
      !isIconOnly && showLeadingIcon && "pe-2xl ps-md py-md",
      !isIconOnly && (showTrailingIcon || loading) && "ps-2xl pe-md py-md",
      className
    );
    const labelClasses = cn(
      "inline-flex items-center",
      (variant === "primary" || variant === "secondary") && "uppercase",
      labelClassName
    );
    if (isIconOnly) {
      return /* @__PURE__ */ jsx3(
        "button",
        {
          ref,
          className: buttonClasses,
          disabled: disabled || loading,
          "data-testid": testID,
          ...props,
          children: loading ? /* @__PURE__ */ jsx3(Spinner, {}) : /* @__PURE__ */ jsx3(
            IconWrapper,
            {
              iconName,
              disabled: disabled ?? false,
              variant,
              iconClassName,
              size,
              flipInRtl: !!iconName && DIRECTIONAL_ARROWS.has(iconName)
            }
          )
        }
      );
    }
    return /* @__PURE__ */ jsxs2(
      "button",
      {
        ref,
        className: buttonClasses,
        disabled: disabled || loading,
        "data-testid": testID,
        ...props,
        children: [
          showLeadingIcon && /* @__PURE__ */ jsx3(
            IconWrapper,
            {
              iconName: iconName || getDefaultIcon(iconPosition),
              disabled,
              variant,
              size,
              flipInRtl: DIRECTIONAL_ARROWS.has(
                iconName || getDefaultIcon(iconPosition)
              )
            }
          ),
          /* @__PURE__ */ jsx3("span", { className: labelClasses, children: label }),
          loading && /* @__PURE__ */ jsx3(
            "span",
            {
              className: cn(
                "rounded-full bg-fg-brand-green flex items-center justify-center",
                iconWrapperSizeClass[resolvedSize]
              ),
              children: /* @__PURE__ */ jsx3(Spinner, { className: "text-white" })
            }
          ),
          showTrailingIcon && /* @__PURE__ */ jsx3(
            IconWrapper,
            {
              iconName: iconName || getDefaultIcon(iconPosition),
              disabled,
              variant,
              size,
              flipInRtl: DIRECTIONAL_ARROWS.has(
                iconName || getDefaultIcon(iconPosition)
              )
            }
          )
        ]
      }
    );
  }
);
Button.displayName = "Button";

// src/components/atoms/Badge.tsx
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var badgeVariants = cva2(
  "inline-flex items-center justify-center whitespace-nowrap font-normal transition-all duration-200 rounded-full min-w-4xl",
  {
    variants: {
      variant: {
        brand: [
          "bg-fg-brand-primary text-fg-white min-h-3xl",
          "px-md py-xxs gap-0.5"
        ],
        white: [
          "bg-background text-fg-brand-primary min-h-3xl",
          "px-md py-xxs gap-0.5"
        ],
        gray: [
          "bg-bg-tertiary text-fg-secondary min-h-3xl",
          "px-md py-xxs gap-0.5"
        ]
      },
      size: {
        sm: "text-sm leading-tight"
      }
    },
    defaultVariants: {
      variant: "brand",
      size: "sm"
    }
  }
);
var Badge = ({
  className,
  variant,
  size,
  icon,
  children,
  testID,
  ...props
}) => {
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      className: cn(badgeVariants({ variant, size }), className),
      "data-testid": testID,
      ...props,
      children: [
        icon && /* @__PURE__ */ jsx4(Icon, { name: icon, size: 12, className: "flex-shrink-0" }),
        /* @__PURE__ */ jsx4("span", { className: "inline-flex items-center min-w-0 overflow-hidden", children })
      ]
    }
  );
};
Badge.displayName = "Badge";

// src/components/atoms/StatusBadge.tsx
import { cva as cva3 } from "class-variance-authority";
import { useTranslation } from "@tasama/i18n";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var statusBadgeVariants = cva3(
  "inline-flex items-center justify-center whitespace-nowrap font-normal transition-all duration-200 rounded-full text-xs leading-normal",
  {
    variants: {
      status: {
        // Task statuses
        INCOMPLETE: ["bg-warning-50 text-warning-600", "px-2 py-0.5 gap-1"],
        MORE_INFO_REQUIRED: [
          "bg-warning-50 text-warning-600",
          "px-2 py-0.5 gap-1"
        ],
        ON_HOLD: ["bg-warning-50 text-warning-600", "px-2 py-0.5 gap-1"],
        CONFIRM_COMPLETION: [
          "bg-warning-50 text-warning-600",
          "px-2 py-0.5 gap-1"
        ],
        REJECTED: ["bg-error-50 text-error-700", "px-2 py-0.5 gap-1"],
        NOT_STARTED: ["bg-error-50 text-error-700", "px-2 py-0.5 gap-1"],
        WAITING_YOUR_CONFIRMATION: [
          "bg-success-50 text-success-600",
          "px-2 py-0.5 gap-1"
        ],
        IN_PROGRESS: ["bg-success-50 text-success-600", "px-2 py-0.5 gap-1"],
        // Service statuses
        REQUESTED: [
          "bg-status-light-blue text-status-blue-600",
          "px-2 py-0.5 gap-1"
        ],
        COMPLETED: ["bg-success-50 text-success-600", "px-2 py-0.5 gap-1"],
        COMPLETE: ["bg-success-50 text-success-600", "px-2 py-0.5 gap-1"],
        Complete: ["bg-success-50 text-success-600", "px-2 py-0.5 gap-1"],
        Completed: ["bg-success-50 text-success-600", "px-2 py-0.5 gap-1"],
        // Team member statuses
        ACTIVE: ["bg-success-50 text-success-600", "px-2 py-0.5 gap-1"],
        INACTIVE: ["bg-error-50 text-error-700", "px-2 py-0.5 gap-1"],
        PENDING: ["bg-warning-50 text-warning-600", "px-2 py-0.5 gap-1"],
        // Activity Request (SNOW) statuses
        OPEN: [
          "bg-status-light-blue text-status-blue-600",
          "px-2 py-0.5 gap-1"
        ],
        WORK_IN_PROGRESS: [
          "bg-success-50 text-success-600",
          "px-2 py-0.5 gap-1"
        ],
        AWAITING_CUSTOMER: [
          "bg-warning-50 text-warning-600",
          "px-2 py-0.5 gap-1"
        ],
        AWAITING_VENDOR: [
          "bg-warning-50 text-warning-600",
          "px-2 py-0.5 gap-1"
        ],
        AWAITING_CANDIDATE: [
          "bg-warning-50 text-warning-600",
          "px-2 py-0.5 gap-1"
        ],
        CLOSED_COMPLETE: [
          "bg-success-50 text-success-600",
          "px-2 py-0.5 gap-1"
        ],
        CLOSED_INCOMPLETE: ["bg-error-50 text-error-700", "px-2 py-0.5 gap-1"],
        // Support Ticket (SNOW) statuses — TDF-1394
        New: ["bg-status-light-blue text-status-blue-600", "px-2 py-0.5 gap-1"],
        Open: [
          "bg-status-light-blue text-status-blue-600",
          "px-2 py-0.5 gap-1"
        ],
        "Awaiting Customer": [
          "bg-warning-50 text-warning-600",
          "px-2 py-0.5 gap-1"
        ],
        Resolved: ["bg-success-50 text-success-600", "px-2 py-0.5 gap-1"],
        "On Hold": ["bg-warning-50 text-warning-600", "px-2 py-0.5 gap-1"],
        "Awaiting 3rd party info": [
          "bg-warning-50 text-warning-600",
          "px-2 py-0.5 gap-1"
        ],
        Closed: ["bg-bg-tertiary text-fg-tertiary", "px-2 py-0.5 gap-1"],
        Canceled: ["bg-error-50 text-error-700", "px-2 py-0.5 gap-1"],
        // Legacy lowercase support
        incomplete: ["bg-warning-50 text-warning-600", "px-2 py-0.5 gap-1"],
        "not-started": ["bg-error-50 text-error-700", "px-2 py-0.5 gap-1"],
        requested: [
          "bg-status-light-blue text-status-blue-600",
          "px-2 py-0.5 gap-1"
        ],
        "Not Started": ["bg-error-50 text-error-700", "px-2 py-0.5 gap-1"],
        "In Progress": ["bg-success-50 text-success-600", "px-2 py-0.5 gap-1"],
        "More Info Required": [
          "bg-warning-50 text-warning-600",
          "px-2 py-0.5 gap-1"
        ],
        "in-progress": ["bg-success-50 text-success-600", "px-2 py-0.5 gap-1"],
        active: ["bg-success-50 text-success-600", "px-2 py-0.5 gap-1"],
        inactive: ["bg-error-50 text-error-700", "px-2 py-0.5 gap-1"],
        pending: ["bg-warning-50 text-warning-600", "px-2 py-0.5 gap-1"],
        complete: ["bg-success-50 text-success-600", "px-2 py-0.5 gap-1"]
      }
    },
    defaultVariants: {
      status: "INCOMPLETE"
    }
  }
);
var statusIconMap = {
  // Task statuses — Figma design system ref
  INCOMPLETE: "Clock",
  MORE_INFO_REQUIRED: "Clock",
  "More Info Required": "Clock",
  ON_HOLD: "Clock",
  CONFIRM_COMPLETION: "CircleCheck",
  REJECTED: "CircleAlert",
  NOT_STARTED: "CircleAlert",
  WAITING_YOUR_CONFIRMATION: "CircleCheck",
  "Not Started": "CircleAlert",
  IN_PROGRESS: "Check",
  "In Progress": "Check",
  // Service statuses
  REQUESTED: "Send",
  COMPLETED: "CircleCheck",
  COMPLETE: "CircleCheck",
  Completed: "CircleCheck",
  Complete: "CircleCheck",
  // Team member statuses
  ACTIVE: "CircleCheck",
  INACTIVE: "CircleAlert",
  PENDING: "Clock",
  // Activity Request (SNOW) statuses
  OPEN: "Send",
  WORK_IN_PROGRESS: "Check",
  AWAITING_CUSTOMER: "Clock",
  AWAITING_VENDOR: "Clock",
  AWAITING_CANDIDATE: "Clock",
  CLOSED_COMPLETE: "CircleCheck",
  CLOSED_INCOMPLETE: "CircleAlert",
  // Support Ticket (SNOW) statuses — TDF-1394
  New: "Plus",
  Open: "Send",
  "Awaiting Customer": "Clock",
  Resolved: "CircleCheck",
  "On Hold": "Clock",
  "Awaiting 3rd party info": "Clock",
  Closed: "CircleCheck",
  Canceled: "CircleAlert",
  // Legacy lowercase support
  incomplete: "Clock",
  "not-started": "CircleAlert",
  requested: "Send",
  "in-progress": "Check",
  active: "CircleCheck",
  inactive: "CircleAlert",
  pending: "Clock",
  complete: "CircleCheck"
};
var getStatusTextMap = (t) => ({
  // Task statuses
  INCOMPLETE: t("status.incomplete"),
  MORE_INFO_REQUIRED: t("status.moreInfoRequired"),
  ON_HOLD: t("status.onHold"),
  CONFIRM_COMPLETION: t("status.confirmCompletion"),
  REJECTED: t("status.rejected"),
  NOT_STARTED: t("status.notStarted"),
  WAITING_YOUR_CONFIRMATION: t("status.waitingYourConfirmation"),
  IN_PROGRESS: t("status.inProgress"),
  "In Progress": t("status.inProgress"),
  "Not Started": t("status.notStarted"),
  "More Info Required": t("status.moreInfoRequired"),
  // Service statuses
  REQUESTED: t("status.requested"),
  COMPLETED: t("status.completed"),
  COMPLETE: t("status.completed"),
  Complete: t("status.completed"),
  Completed: t("status.completed"),
  // Team member statuses
  ACTIVE: t("status.active"),
  INACTIVE: t("status.inactive"),
  PENDING: t("status.pending"),
  // Activity Request (SNOW) statuses
  OPEN: t("status.requested"),
  WORK_IN_PROGRESS: t("status.inProgress"),
  AWAITING_CUSTOMER: t("status.awaitingCustomer"),
  AWAITING_VENDOR: t("status.awaitingVendor"),
  AWAITING_CANDIDATE: t("status.awaitingCandidate"),
  CLOSED_COMPLETE: t("status.completed"),
  CLOSED_INCOMPLETE: t("status.closedIncomplete"),
  // Support Ticket (SNOW) statuses — TDF-1394 AC8: rendered in ServiceNow's own
  // casing, not the app's usual sentence case, since these ARE the ServiceNow
  // values (not translated into a different label set).
  New: t("status.new"),
  Open: t("status.ticketOpen"),
  "Awaiting Customer": t("status.awaitingCustomerTicket"),
  Resolved: t("status.resolved"),
  "On Hold": t("status.onHoldTicket"),
  "Awaiting 3rd party info": t("status.awaitingThirdPartyInfo"),
  Closed: t("status.closedTicket"),
  Canceled: t("status.canceledTicket"),
  // Legacy lowercase support
  incomplete: t("status.incomplete"),
  "not-started": t("status.notStarted"),
  requested: t("status.requested"),
  "in-progress": t("status.inProgress"),
  active: t("status.active"),
  inactive: t("status.inactive"),
  pending: t("status.pending"),
  complete: "complete"
});
function getStatusBadgeText(status, t) {
  return getStatusTextMap(t)[status] ?? status;
}
var StatusBadge = ({
  className,
  status,
  testID,
  showIcon = false,
  customText,
  ...props
}) => {
  const { t } = useTranslation("common");
  const iconName = statusIconMap[status];
  const text = customText || getStatusTextMap(t)[status];
  return /* @__PURE__ */ jsxs4(
    "div",
    {
      className: cn(statusBadgeVariants({ status }), className),
      "data-testid": testID,
      ...props,
      children: [
        showIcon && iconName && /* @__PURE__ */ jsx5(Icon, { name: iconName, size: 12, className: "flex-shrink-0" }),
        /* @__PURE__ */ jsx5("span", { className: "inline-flex items-center", children: text })
      ]
    }
  );
};
StatusBadge.displayName = "StatusBadge";

// src/components/atoms/Chip.tsx
import React2 from "react";
import { useTranslation as useTranslation2 } from "@tasama/i18n";
import { cva as cva4 } from "class-variance-authority";
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
var chipVariants = cva4(
  "inline-flex items-center justify-center font-normal transition-all duration-200",
  {
    variants: {
      size: {
        s: "h-6 px-2 py-1 gap-1 text-xs leading-tight",
        m: "h-8 px-3 py-1.5 gap-1.5 text-sm leading-normal",
        l: "h-10 px-4 py-2 gap-2 text-base leading-normal"
      },
      variant: {
        default: [
          "bg-gray-100 text-gray-700",
          "border border-gray-300",
          "hover:bg-gray-200"
        ],
        primary: [
          "bg-brand-blue-50 text-brand-blue-700",
          "border border-brand-blue-200",
          "hover:bg-brand-blue-100"
        ],
        success: [
          "bg-success-50 text-success-700",
          "border border-success-200",
          "hover:bg-success-100"
        ],
        warning: [
          "bg-warning-50 text-warning-700",
          "border border-warning-200",
          "hover:bg-warning-100"
        ],
        error: [
          "bg-error-50 text-error-700",
          "border border-error-200",
          "hover:bg-error-100"
        ]
      }
    },
    defaultVariants: {
      size: "m",
      variant: "default"
    }
  }
);
var Chip = React2.forwardRef(
  ({
    className,
    size,
    variant,
    children,
    icon,
    iconPosition = "left",
    onIconClick,
    rtl = false,
    testID,
    ...props
  }, ref) => {
    const { t } = useTranslation2("common");
    const iconSize = size === "s" ? 12 : size === "m" ? 14 : 16;
    const shouldRenderIconFirst = rtl ? iconPosition === "right" : iconPosition === "left";
    const renderIcon = () => {
      if (!icon) return null;
      const iconElement = /* @__PURE__ */ jsx6(
        Icon,
        {
          name: icon,
          size: iconSize,
          strokeWidth: 1.5,
          className: "flex-shrink-0"
        }
      );
      if (onIconClick) {
        return /* @__PURE__ */ jsx6(
          "button",
          {
            type: "button",
            onClick: onIconClick,
            className: "inline-flex items-center justify-center hover:opacity-70 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-brand-blue-500 rounded-full",
            "aria-label": t("chip.iconAction"),
            "data-testid": testID ? `${testID}-icon-button` : void 0,
            children: iconElement
          }
        );
      }
      return iconElement;
    };
    return /* @__PURE__ */ jsxs5(
      "div",
      {
        ref,
        className: cn(
          chipVariants({ size, variant }),
          "rounded-sm min-w-0 overflow-hidden",
          className
        ),
        dir: rtl ? "rtl" : void 0,
        "data-testid": testID,
        ...props,
        children: [
          shouldRenderIconFirst && renderIcon(),
          /* @__PURE__ */ jsx6("span", { className: "truncate min-w-0", children }),
          !shouldRenderIconFirst && renderIcon()
        ]
      }
    );
  }
);
Chip.displayName = "Chip";

// src/components/atoms/ServiceBadge.tsx
import { cva as cva5 } from "class-variance-authority";
import { jsx as jsx7 } from "react/jsx-runtime";
var serviceBadgeVariants = cva5(
  "inline-flex items-center justify-center whitespace-nowrap font-normal rounded-full leading-normal",
  {
    variants: {
      variant: {
        // Figma fills are semi-transparent (40% alpha) over the header photo, not solid.
        service: "bg-brand-green-500/40 text-white",
        bundle: "bg-badge-bundle-base/40 text-white",
        "add-on": "bg-brand-gold-500/40 text-white",
        neutral: "bg-white text-brand-blue-500"
      },
      size: {
        s: "px-2 py-0.5 text-xs",
        m: "px-3 py-1 text-sm"
      },
      // 'outline' — colored border + colored text, transparent fill. Used on
      // plain white-card contexts (Home's "My services" widget) where the
      // solid variants' translucent fill (designed to sit over the dark
      // header photo elsewhere) would render illegibly pale.
      appearance: {
        solid: "",
        outline: "bg-transparent border"
      }
    },
    compoundVariants: [
      // Outline is a distinct, more muted palette per Figma — not just the
      // solid variants' colors at full opacity — so each gets its own token
      // (border at /50 alpha, text at full opacity, per the design).
      {
        variant: "service",
        appearance: "outline",
        class: "border-brand-green-800/50 text-brand-green-800"
      },
      {
        variant: "bundle",
        appearance: "outline",
        class: "border-badge-bundle-outline/50 text-badge-bundle-outline"
      },
      {
        variant: "add-on",
        appearance: "outline",
        class: "border-badge-addon-outline/50 text-badge-addon-outline"
      },
      {
        variant: "neutral",
        appearance: "outline",
        class: "border-brand-blue-500 text-brand-blue-500"
      }
    ],
    defaultVariants: {
      variant: "neutral",
      size: "s",
      appearance: "solid"
    }
  }
);
var ServiceBadge = ({
  className,
  variant,
  size,
  appearance,
  text,
  testID,
  ...props
}) => /* @__PURE__ */ jsx7(
  "div",
  {
    className: cn(
      serviceBadgeVariants({ variant, size, appearance }),
      className
    ),
    "data-testid": testID,
    ...props,
    children: /* @__PURE__ */ jsx7("span", { className: "inline-flex items-center", children: text })
  }
);
ServiceBadge.displayName = "ServiceBadge";

// src/components/atoms/Checkbox.tsx
import * as React3 from "react";
import { jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
var CheckIcon = (props) => /* @__PURE__ */ jsx8(Icon, { name: "Check", strokeWidth: 4, ...props });
var DashIcon = (props) => /* @__PURE__ */ jsx8(Icon, { name: "Minus", strokeWidth: 4, ...props });
var sizeClasses = {
  sm: "w-xl h-xl",
  lg: "w-2xl h-2xl"
};
var radiusClasses = {
  sm: "rounded-xs",
  lg: "rounded-sm"
};
var iconSizeClasses = {
  sm: "w-lg h-lg",
  lg: "w-xl h-xl"
};
function Checkbox({
  uiSize = "sm",
  indeterminate = false,
  className,
  style,
  testID,
  ref,
  ...props
}) {
  const innerRef = React3.useRef(null);
  React3.useEffect(() => {
    if (innerRef.current) {
      innerRef.current.indeterminate = !!indeterminate;
    }
  }, [indeterminate]);
  React3.useEffect(() => {
    if (ref) {
      if (typeof ref === "function") {
        ref(innerRef.current);
      } else {
        ref.current = innerRef.current;
      }
    }
  }, [ref]);
  const IconComponent = indeterminate ? DashIcon : CheckIcon;
  return /* @__PURE__ */ jsxs6(
    "span",
    {
      className: cn(
        "group relative align-middle shrink-0 flex items-center justify-center outline-none",
        sizeClasses[uiSize],
        className
      ),
      "data-testid": testID,
      style,
      children: [
        /* @__PURE__ */ jsx8(
          "input",
          {
            ref: innerRef,
            type: "checkbox",
            "aria-checked": indeterminate ? "mixed" : void 0,
            className: "peer absolute inset-0 z-10 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed focus-visible:outline-none focus-visible:[box-shadow:none]",
            ...props
          }
        ),
        /* @__PURE__ */ jsx8(
          "div",
          {
            "aria-hidden": "true",
            className: cn(
              "absolute inset-0 border transition-all duration-200 ease-out",
              // Focus ring — driven by the hidden peer input receiving keyboard focus
              "peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2",
              // Default (Unchecked) State - using design tokens from Figma
              "bg-white border-brand-tint-500",
              // Hover State (Unchecked only)
              "peer-[&:not(:checked):not(:disabled):hover]:border-brand-tint-600",
              // Checked State: filled background and border
              "peer-checked:bg-brand-blue-500 peer-checked:border-brand-blue-500",
              // Indeterminate State: filled background and border
              "peer-aria-[checked=mixed]:bg-brand-blue-500 peer-aria-[checked=mixed]:border-brand-blue-500",
              // Disabled State (unchecked)
              "peer-disabled:border-fg-disabled-subtle peer-disabled:bg-bg-disabled peer-disabled:opacity-70",
              // Disabled + Checked: gray fill instead of brand blue
              "peer-[&:disabled:checked]:bg-button-disabled peer-[&:disabled:checked]:border-fg-disabled-subtle",
              // Disabled + Indeterminate: same gray treatment
              "peer-[&:disabled[aria-checked=mixed]]:bg-button-disabled peer-[&:disabled[aria-checked=mixed]]:border-fg-disabled-subtle",
              radiusClasses[uiSize]
            )
          }
        ),
        /* @__PURE__ */ jsx8(
          IconComponent,
          {
            className: cn(
              "absolute text-white pointer-events-none opacity-0 scale-90 transition-all duration-200 ease-out",
              // Show the icon when checked or indeterminate via peer state
              "peer-checked:opacity-100 peer-checked:scale-100",
              "peer-aria-[checked=mixed]:opacity-100 peer-aria-[checked=mixed]:scale-100",
              iconSizeClasses[uiSize]
            )
          }
        )
      ]
    }
  );
}

// src/components/atoms/DateInput.tsx
import * as React4 from "react";
import ReactDOM from "react-dom";
import { useTranslation as useTranslation3 } from "@tasama/i18n";
import { ar } from "date-fns/locale";
import {
  DayPicker
} from "react-day-picker";

// src/hooks/useBackToTop.ts
import { useCallback, useEffect as useEffect2, useState } from "react";
var useBackToTop = ({
  containerRef,
  threshold = 400
} = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect2(() => {
    const scrollTarget = containerRef?.current ?? window;
    const handleScroll = () => {
      const scrollTop = scrollTarget instanceof Window ? window.scrollY : scrollTarget.scrollTop;
      setIsVisible(scrollTop > threshold);
    };
    handleScroll();
    scrollTarget.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scrollTarget.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef, threshold]);
  const scrollToTop = useCallback(() => {
    const scrollTarget = containerRef?.current ?? window;
    scrollTarget.scrollTo({ top: 0, behavior: "smooth" });
  }, [containerRef]);
  return { isVisible, scrollToTop };
};

// src/hooks/useDir.ts
import { useEffect as useEffect3, useState as useState2 } from "react";
var useDir = () => {
  const [dir, setDir] = useState2(
    () => document.documentElement.dir || "ltr"
  );
  useEffect3(() => {
    const observer = new MutationObserver(() => {
      setDir(document.documentElement.dir || "ltr");
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["dir"]
    });
    return () => observer.disconnect();
  }, []);
  return dir;
};

// src/hooks/useInfiniteScrollWithQuery.ts
import { useEffect as useEffect4, useCallback as useCallback2, useRef as useRef2 } from "react";
var useInfiniteScrollWithQuery = ({
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  threshold = 10,
  root = null,
  enabled = true
}) => {
  const observerRef = useRef2(null);
  const lastTriggerTimeRef = useRef2(0);
  const fetchNextPageRef = useRef2(fetchNextPage);
  const hasNextPageRef = useRef2(hasNextPage);
  const isFetchingNextPageRef = useRef2(isFetchingNextPage);
  useEffect4(() => {
    fetchNextPageRef.current = fetchNextPage;
    hasNextPageRef.current = hasNextPage;
    isFetchingNextPageRef.current = isFetchingNextPage;
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  const handleIntersection = useCallback2(
    (entries) => {
      const [entry] = entries;
      const now = Date.now();
      const shouldTrigger = enabled && entry && (entry.isIntersecting || entry.intersectionRatio > 0) && hasNextPageRef.current && !isFetchingNextPageRef.current && now - lastTriggerTimeRef.current > 500;
      if (shouldTrigger) {
        lastTriggerTimeRef.current = now;
        fetchNextPageRef.current();
      }
    },
    [enabled]
    // Include enabled in dependency array
  );
  const sentinelRef = useCallback2(
    (node) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node || !enabled) {
        return;
      }
      observerRef.current = new IntersectionObserver(handleIntersection, {
        root,
        rootMargin: `${threshold}px`,
        threshold: [0, 0.1, 0.5, 1]
      });
      observerRef.current.observe(node);
    },
    [handleIntersection, threshold, root, enabled]
  );
  return {
    sentinelRef
  };
};

// src/hooks/usePageLoader.ts
import { useCallback as useCallback3 } from "react";

// src/stores/usePageLoaderStore.ts
import { createBaseStore } from "@tasama/utils";
var usePageLoaderStore = createBaseStore(
  {
    isVisible: false,
    label: ""
  },
  (set) => ({
    // Actions
    show: (label) => set({
      isVisible: true,
      label: label ?? ""
    }),
    hide: () => set({
      isVisible: false
    }),
    updateLabel: (label) => set({
      label
    })
  })
);

// src/hooks/usePageLoader.ts
var usePageLoader = () => {
  const { show, hide, updateLabel } = usePageLoaderStore();
  const showLoader = useCallback3(
    (label) => {
      show(label);
    },
    [show]
  );
  const hideLoader = useCallback3(() => {
    hide();
  }, [hide]);
  const updateLoaderLabel = useCallback3(
    (label) => {
      updateLabel(label);
    },
    [updateLabel]
  );
  return {
    show: showLoader,
    hide: hideLoader,
    updateLabel: updateLoaderLabel
  };
};

// src/hooks/useMediaQuery.ts
import { useSyncExternalStore } from "react";
var breakpoints = {
  xs: "(min-width: 390px)",
  sm: "(min-width: 576px)",
  md: "(min-width: 768px)",
  lg: "(min-width: 900px)",
  xl: "(min-width: 1024px)",
  "2xl": "(min-width: 1280px)"
};
function useMediaQuery(query) {
  return useSyncExternalStore(
    (onChange) => {
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    () => window.matchMedia(query).matches,
    () => false
  );
}
function useBreakpoint(bp) {
  return useMediaQuery(breakpoints[bp]);
}
var useIsMobile = () => !useMediaQuery(breakpoints.md);
var useIsTablet = () => {
  const isMd = useMediaQuery(breakpoints.md);
  const isXl = useMediaQuery(breakpoints.xl);
  return isMd && !isXl;
};
var useIsDesktop = () => useMediaQuery(breakpoints.xl);

// src/hooks/use-auto-resize-textarea.ts
import { useEffect as useEffect5, useRef as useRef3, useCallback as useCallback4 } from "react";
function useAutoResizeTextarea(value, options = {}) {
  const {
    minHeight = 48,
    // Default to xs size (--spacing-6xl)
    maxHeight = 360,
    // Updated default max height to 360px
    enabled = true,
    heightOffset = 0
  } = options;
  const textareaRef = useRef3(null);
  const isResizingRef = useRef3(false);
  const resize = useCallback4(() => {
    const textarea = textareaRef.current;
    if (!textarea || !enabled || isResizingRef.current) return;
    isResizingRef.current = true;
    requestAnimationFrame(() => {
      try {
        const scrollTop = textarea.scrollTop;
        if (!textarea.value.trim()) {
          textarea.style.height = `${minHeight}px`;
          textarea.style.overflowY = "hidden";
          isResizingRef.current = false;
          return;
        }
        textarea.style.height = `${minHeight}px`;
        const isOverflowing = textarea.scrollHeight > textarea.clientHeight;
        if (!isOverflowing) {
          textarea.style.overflowY = "hidden";
          isResizingRef.current = false;
          return;
        }
        textarea.style.height = "auto";
        const contentHeight = textarea.scrollHeight;
        const newHeight = Math.min(
          Math.max(contentHeight, minHeight),
          maxHeight
        );
        textarea.style.height = `${newHeight}px`;
        if (contentHeight > maxHeight) {
          textarea.style.overflowY = "auto";
          textarea.scrollTop = scrollTop;
        } else {
          textarea.style.overflowY = "hidden";
        }
      } catch (error) {
        if (textarea) {
          textarea.style.height = `${minHeight}px`;
        }
        console.warn("Auto-resize textarea error:", error);
      } finally {
        isResizingRef.current = false;
      }
    });
  }, [enabled, minHeight, maxHeight, heightOffset]);
  useEffect5(() => {
    if (enabled) {
      resize();
    }
  }, [value, resize, enabled]);
  useEffect5(() => {
    if (enabled) {
      resize();
    } else {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = `${minHeight}px`;
      }
    }
  }, [enabled, resize, minHeight]);
  useEffect5(() => {
    if (!enabled) return;
    const handleResize = () => {
      setTimeout(resize, 100);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [resize, enabled]);
  return {
    textareaRef,
    resize
  };
}

// src/hooks/usePopoverPosition.ts
import { useEffect as useEffect6, useLayoutEffect, useState as useState3 } from "react";
var usePopoverPosition = ({
  open,
  containerRef,
  popupRef,
  onClose,
  recomputeKey
}) => {
  const dir = useDir();
  const [popupStyle, setPopupStyle] = useState3({});
  useLayoutEffect(() => {
    if (!open) return;
    setPopupStyle({ opacity: 0, pointerEvents: "none" });
  }, [open]);
  useEffect6(() => {
    if (!open || !containerRef.current) return;
    const locked = [];
    let el = containerRef.current.parentElement;
    while (el && el !== document.body) {
      const { overflow, overflowY } = getComputedStyle(el);
      if (/auto|scroll/.test(overflow + overflowY)) {
        locked.push({ el, overflow: el.style.overflow });
        el.style.overflow = "hidden";
      }
      el = el.parentElement;
    }
    const prevBodyOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      locked.forEach(({ el: lockedEl, overflow }) => {
        lockedEl.style.overflow = overflow;
      });
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [open, containerRef]);
  useEffect6(() => {
    if (!open || !popupRef.current || !containerRef.current) return;
    const inputRect = containerRef.current.getBoundingClientRect();
    const popupHeight = popupRef.current.scrollHeight;
    const popupWidth = popupRef.current.scrollWidth;
    const spaceBelow = window.innerHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;
    const openUpward = spaceBelow < popupHeight + 4 && spaceAbove > spaceBelow;
    const isRtl = dir === "rtl";
    const margin = 8;
    const startAligned = inputRect.left;
    const endAligned = inputRect.right - popupWidth;
    const fitsStart = startAligned + popupWidth <= window.innerWidth - margin;
    const fitsEnd = endAligned >= margin;
    let leftPos;
    if (isRtl) {
      leftPos = fitsEnd ? endAligned : fitsStart ? startAligned : endAligned;
    } else {
      leftPos = fitsStart ? startAligned : fitsEnd ? endAligned : startAligned;
    }
    leftPos = Math.min(
      Math.max(leftPos, margin),
      window.innerWidth - popupWidth - margin
    );
    if (openUpward) {
      setPopupStyle({
        bottom: window.innerHeight - inputRect.top + 4,
        top: "auto",
        left: leftPos,
        opacity: 1,
        pointerEvents: "auto"
      });
    } else {
      setPopupStyle({
        top: inputRect.bottom + 4,
        bottom: "auto",
        left: leftPos,
        opacity: 1,
        pointerEvents: "auto"
      });
    }
  }, [open, dir, recomputeKey]);
  useEffect6(() => {
    if (!open) return;
    const handler = (e) => {
      const target = e.target;
      if (!containerRef.current?.contains(target) && !popupRef.current?.contains(target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose, containerRef, popupRef]);
  useEffect6(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);
  return { popupStyle };
};

// src/components/atoms/DateInput.tsx
import { jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
var parseDate = (value) => {
  if (!value) return void 0;
  const m1 = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m1) {
    const d = new Date(Number(m1[3]), Number(m1[2]) - 1, Number(m1[1]));
    return isNaN(d.getTime()) ? void 0 : d;
  }
  const m2 = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m2) {
    const d = new Date(Number(m2[1]), Number(m2[2]) - 1, Number(m2[3]));
    return isNaN(d.getTime()) ? void 0 : d;
  }
  const m3 = value.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (m3) {
    const d = new Date(Number(m3[1]), Number(m3[2]) - 1, Number(m3[3]));
    return isNaN(d.getTime()) ? void 0 : d;
  }
  return void 0;
};
var formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()}`;
};
var buildDatePickerClassNames = (dateLocale, showDropdown, extra) => ({
  root: "select-none",
  months: "",
  month: "flex flex-col gap-3 relative",
  month_caption: "flex justify-center items-center h-8 px-10",
  caption_label: showDropdown ? "hidden" : "text-sm font-light text-fg-primary",
  dropdowns: "flex items-center gap-2",
  dropdown: "text-sm font-light text-fg-primary bg-background border border-border rounded-md px-2 py-1 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue-500",
  months_dropdown: "",
  years_dropdown: "",
  nav: "",
  button_previous: "absolute start-0 top-0 h-8 w-8 flex items-center justify-center rounded-md text-quaternary hover:text-fg-primary hover:bg-bg-brand-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer disabled:default",
  button_next: "absolute end-0 top-0 h-8 w-8 flex items-center justify-center rounded-md text-quaternary hover:text-fg-primary hover:bg-bg-brand-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer disabled:cursor-default",
  chevron: "w-4 h-4",
  month_grid: "w-full border-collapse",
  // gap-0.5 (2px, per Figma) instead of justify-between — justify-between
  // distributes the *table's* full width as leftover space between the
  // fixed-width day cells, so a wide popup (e.g. DateRangePicker's two-month
  // layout) stretched the gaps way past the 2px the design calls for. A
  // fixed gap keeps cells tightly packed regardless of container width.
  weekdays: "flex gap-0.5",
  weekday: cn(
    "h-9 flex items-center justify-center text-xs font-light text-quaternary",
    dateLocale ? "flex-1" : "w-9"
  ),
  weeks: "",
  week: "flex gap-0.5 mt-1",
  day: cn(
    "h-9 p-0 text-center relative flex items-center justify-center",
    dateLocale ? "flex-1" : "w-9"
  ),
  day_button: "w-8 h-8 text-sm rounded-lg font-light text-fg-primary hover:bg-bg-brand-primary transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer disabled:cursor-default",
  focused: "[&>button]:ring-2 [&>button]:ring-ring",
  selected: "[&>button]:bg-fg-brand-primary [&>button]:text-white [&>button]:hover:bg-fg-brand-primary",
  today: "[&>button]:font-semibold [&>button]:text-fg-brand-primary",
  outside: "[&>button]:text-quaternary [&>button]:opacity-40",
  disabled: "[&>button]:opacity-30 [&>button]:cursor-not-allowed [&>button]:pointer-events-none",
  hidden: "invisible",
  ...extra
});
var DatePickerPanel = ({
  selected,
  onSelect,
  defaultMonth,
  startMonth,
  endMonth,
  showDropdown = true,
  disabledDays,
  modifiers,
  modifiersClassNames,
  dateLocale,
  autoFocus = true
}) => /* @__PURE__ */ jsx9(
  DayPicker,
  {
    mode: "single",
    navLayout: "around",
    captionLayout: showDropdown ? "dropdown" : "label",
    ...showDropdown && startMonth && endMonth ? { startMonth, endMonth } : {},
    selected,
    onSelect,
    defaultMonth: selected ?? defaultMonth ?? /* @__PURE__ */ new Date(),
    dir: document.documentElement.dir || "ltr",
    locale: dateLocale,
    autoFocus,
    disabled: disabledDays,
    ...modifiers !== void 0 && { modifiers },
    ...modifiersClassNames !== void 0 && { modifiersClassNames },
    classNames: buildDatePickerClassNames(dateLocale, showDropdown)
  }
);
var DateInput = ({
  label,
  id,
  hintText,
  error = false,
  errorMessage,
  disabled = false,
  required = false,
  testID,
  className,
  value = "",
  onChange,
  onBlur,
  placeholder,
  disabledDays,
  modifiers,
  modifiersClassNames,
  defaultMonth,
  startMonth,
  endMonth,
  showDropdown = true,
  yearRange = 10
}) => {
  const now = /* @__PURE__ */ new Date();
  const defaultStartMonth = new Date(now.getFullYear() - yearRange, 0, 1);
  const defaultEndMonth = new Date(now.getFullYear() + yearRange, 11, 31);
  const { t, i18n: i18n2 } = useTranslation3("common");
  const defaultPlaceholder = t("dateInput.placeholder");
  const dateLocale = i18n2.language === "ar" ? ar : void 0;
  const [open, setOpen] = React4.useState(false);
  const containerRef = React4.useRef(null);
  const popupRef = React4.useRef(null);
  const inputRef = React4.useRef(null);
  const selectedDate = parseDate(value);
  const displayValue = selectedDate ? formatDate(selectedDate) : "";
  const closeCalendar = React4.useCallback(() => {
    setOpen(false);
    inputRef.current?.focus();
  }, []);
  const { popupStyle } = usePopoverPosition({
    open,
    containerRef,
    popupRef,
    onClose: closeCalendar
  });
  const toggleCalendar = React4.useCallback(() => {
    if (disabled) return;
    setOpen((prev) => !prev);
  }, [disabled]);
  const handleDaySelect = (date) => {
    if (!date) return;
    onChange?.({
      target: { value: formatDate(date) }
    });
    setOpen(false);
    inputRef.current?.focus();
  };
  const handleInputBlur = (e) => {
    if (containerRef.current?.contains(e.relatedTarget)) return;
    if (popupRef.current?.contains(e.relatedTarget)) return;
    setOpen(false);
    onBlur?.(e);
  };
  const describedByIds = [];
  if (hintText) describedByIds.push(`${id}-hint`);
  if (error && errorMessage) describedByIds.push(`${id}-error`);
  const ariaDescribedBy = describedByIds.length ? describedByIds.join(" ") : void 0;
  const inputClasses = cn(
    "flex h-12 w-full rounded-lg border border-solid transition-colors motion-safe:duration-150",
    "focus-visible:outline-none cursor-pointer select-none",
    "ps-10 pe-12 py-[14px]",
    "text-sm font-light",
    "bg-background",
    disabled ? "cursor-not-allowed pointer-events-none border-border text-disabled" : displayValue ? "text-fg-primary" : "text-quaternary",
    error ? "border-error focus:shadow-[0_0_0_4px_var(--color-bg-error-secondary)]" : "border-tertiary focus:shadow-[0_0_0_4px_var(--color-brand-tint-200)]",
    className
  );
  const calendarPopup = open ? ReactDOM.createPortal(
    /* @__PURE__ */ jsx9(
      "div",
      {
        ref: popupRef,
        role: "dialog",
        "aria-label": t("dateInput.chooseDate"),
        tabIndex: -1,
        style: popupStyle,
        className: cn(
          "fixed z-popover bg-background border border-border rounded-xl shadow-lg p-3 focus:outline-none",
          dateLocale ? "min-w-[320px]" : "min-w-[280px]"
        ),
        children: /* @__PURE__ */ jsx9(
          DatePickerPanel,
          {
            selected: selectedDate,
            onSelect: handleDaySelect,
            defaultMonth,
            startMonth: startMonth ?? defaultStartMonth,
            endMonth: endMonth ?? defaultEndMonth,
            showDropdown,
            disabledDays,
            modifiers,
            modifiersClassNames,
            dateLocale
          }
        )
      }
    ),
    document.body
  ) : null;
  return /* @__PURE__ */ jsxs7("div", { className: "space-y-sm", "data-testid": testID, children: [
    /* @__PURE__ */ jsxs7(
      Label,
      {
        htmlFor: id,
        className: cn(
          "block",
          "font-normal text-sm",
          /**responsive spacing to prevent misalignment of fields */
          "input-label-spacing",
          disabled ? "text-disabled" : "text-fg-secondary"
        ),
        children: [
          label,
          !required && /* @__PURE__ */ jsx9("span", { className: "ms-1", children: t("actions.optional") })
        ]
      }
    ),
    /* @__PURE__ */ jsxs7("div", { className: "relative", ref: containerRef, children: [
      /* @__PURE__ */ jsx9(
        "div",
        {
          className: cn(
            "absolute start-3 top-1/2 -translate-y-1/2 z-10",
            disabled ? "pointer-events-none" : "cursor-pointer"
          ),
          onClick: toggleCalendar,
          children: /* @__PURE__ */ jsx9(
            Icon,
            {
              name: "Calendar",
              size: 20,
              className: cn(disabled ? "text-disabled" : "text-quaternary"),
              "aria-hidden": true
            }
          )
        }
      ),
      /* @__PURE__ */ jsx9(
        "input",
        {
          ref: inputRef,
          id,
          readOnly: true,
          value: displayValue,
          placeholder: placeholder || defaultPlaceholder,
          className: inputClasses,
          "aria-invalid": error || void 0,
          "aria-describedby": ariaDescribedBy,
          "aria-haspopup": "dialog",
          disabled,
          "data-testid": testID ? `${testID}-input` : void 0,
          onClick: toggleCalendar,
          onKeyDown: (e) => {
            if (e.key === " " || e.key === "Enter") {
              e.preventDefault();
              toggleCalendar();
            }
          },
          onBlur: handleInputBlur
        }
      ),
      error && /* @__PURE__ */ jsx9("div", { className: "pointer-events-none absolute end-3 top-1/2 -translate-y-1/2", children: /* @__PURE__ */ jsx9(
        Icon,
        {
          name: "CircleAlert",
          size: 16,
          "aria-hidden": true,
          className: "text-error-primary"
        }
      ) })
    ] }),
    calendarPopup,
    !error && hintText && /* @__PURE__ */ jsx9(
      "p",
      {
        id: `${id}-hint`,
        "data-testid": `${testID}-hint`,
        className: cn(
          "text-xs font-normal font-sans",
          disabled ? "text-disabled" : "text-quaternary"
        ),
        children: hintText
      }
    ),
    error && errorMessage && /* @__PURE__ */ jsx9(
      "p",
      {
        id: `${id}-error`,
        "data-testid": `${testID}-error`,
        className: "text-error-primary text-xs font-normal font-sans",
        children: errorMessage
      }
    )
  ] });
};

// src/components/atoms/TimeInput.tsx
import * as React5 from "react";
import ReactDOM2 from "react-dom";
import { useTranslation as useTranslation4 } from "@tasama/i18n";
import { Fragment, jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
var parseTime = (value) => {
  const match = value.match(/^(\d{2}):(\d{2})$/);
  if (!match) return void 0;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return void 0;
  return { hour, minute };
};
var formatTime = ({ hour, minute }) => `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
var to12Hour = (hour) => {
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return { hour12, period };
};
var from12Hour = (hour12, period) => period === "AM" ? hour12 === 12 ? 0 : hour12 : hour12 === 12 ? 12 : hour12 + 12;
var formatDisplay = (parsed, format2) => {
  if (format2 === "24h") return formatTime(parsed);
  const { hour12, period } = to12Hour(parsed.hour);
  return `${String(hour12).padStart(2, "0")}:${String(parsed.minute).padStart(2, "0")} ${period}`;
};
var columnButtonClasses = (isSelected) => cn(
  "w-full rounded-md px-3 py-1.5 text-sm font-light text-center transition-colors",
  "hover:bg-bg-brand-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer",
  isSelected ? "bg-fg-brand-primary text-white hover:bg-fg-brand-primary" : "text-fg-primary"
);
var TimePickerPanel = ({
  draft,
  onDraftChange,
  onConfirm,
  minuteStep = 5,
  format: format2 = "12h",
  showColumnLabels = false,
  testID
}) => {
  const { t } = useTranslation4("common");
  const hours = React5.useMemo(
    () => format2 === "24h" ? Array.from({ length: 24 }, (_, i) => i) : Array.from({ length: 12 }, (_, i) => i + 1),
    [format2]
  );
  const minutes = React5.useMemo(
    () => Array.from({ length: 60 / minuteStep }, (_, i) => i * minuteStep),
    [minuteStep]
  );
  const { hour12: draftHour12, period: draftPeriod } = to12Hour(draft.hour);
  return /* @__PURE__ */ jsxs8(Fragment, { children: [
    showColumnLabels && /* @__PURE__ */ jsxs8("div", { className: "flex gap-2 mb-2", children: [
      /* @__PURE__ */ jsx10("p", { className: "flex-1 text-center text-sm font-normal text-fg-primary", children: t("timeInput.hoursLabel") }),
      /* @__PURE__ */ jsx10("p", { className: "flex-1 text-center text-sm font-normal text-fg-primary", children: t("timeInput.minutesLabel") }),
      format2 === "12h" && /* @__PURE__ */ jsx10("div", { className: "flex-1" })
    ] }),
    /* @__PURE__ */ jsxs8("div", { className: "flex gap-2", children: [
      /* @__PURE__ */ jsx10("div", { className: "flex-1 max-h-48 overflow-y-auto flex flex-col gap-1", children: hours.map((hour) => /* @__PURE__ */ jsx10(
        "button",
        {
          type: "button",
          "data-testid": testID ? `${testID}-hour-${hour}` : void 0,
          className: columnButtonClasses(
            format2 === "24h" ? draft.hour === hour : draftHour12 === hour
          ),
          onClick: () => onDraftChange({
            ...draft,
            hour: format2 === "24h" ? hour : from12Hour(hour, draftPeriod)
          }),
          children: String(hour).padStart(2, "0")
        },
        hour
      )) }),
      /* @__PURE__ */ jsx10("div", { className: "flex-1 max-h-48 overflow-y-auto flex flex-col gap-1", children: minutes.map((minute) => /* @__PURE__ */ jsx10(
        "button",
        {
          type: "button",
          "data-testid": testID ? `${testID}-minute-${minute}` : void 0,
          className: columnButtonClasses(draft.minute === minute),
          onClick: () => onDraftChange({ ...draft, minute }),
          children: String(minute).padStart(2, "0")
        },
        minute
      )) }),
      format2 === "12h" && /* @__PURE__ */ jsx10("div", { className: "flex-1 flex flex-col gap-1", children: ["AM", "PM"].map((period) => /* @__PURE__ */ jsx10(
        "button",
        {
          type: "button",
          "data-testid": testID ? `${testID}-period-${period}` : void 0,
          className: columnButtonClasses(draftPeriod === period),
          onClick: () => onDraftChange({
            ...draft,
            hour: from12Hour(draftHour12, period)
          }),
          children: period
        },
        period
      )) })
    ] }),
    /* @__PURE__ */ jsx10(
      "button",
      {
        type: "button",
        "data-testid": testID ? `${testID}-confirm` : void 0,
        className: "mt-3 w-full rounded-md bg-fg-brand-primary text-white text-sm font-normal py-1.5 cursor-pointer hover:opacity-90 transition-opacity",
        onClick: onConfirm,
        children: t("timeInput.done")
      }
    )
  ] });
};
var TimeInput = ({
  label,
  id,
  hintText,
  error = false,
  errorMessage,
  disabled = false,
  required = false,
  testID,
  className,
  value = "",
  onChange,
  onBlur,
  placeholder,
  minuteStep = 5,
  format: format2 = "12h",
  showColumnLabels = false
}) => {
  const { t } = useTranslation4("common");
  const defaultPlaceholder = t("timeInput.placeholder");
  const [open, setOpen] = React5.useState(false);
  const containerRef = React5.useRef(null);
  const popupRef = React5.useRef(null);
  const inputRef = React5.useRef(null);
  const defaultTime = { hour: 0, minute: 0 };
  const selectedTime = parseTime(value);
  const displayValue = selectedTime ? formatDisplay(selectedTime, format2) : "";
  const [draft, setDraft] = React5.useState(
    selectedTime ?? defaultTime
  );
  const closePopup = React5.useCallback(() => {
    setOpen(false);
    inputRef.current?.focus();
  }, []);
  const { popupStyle } = usePopoverPosition({
    open,
    containerRef,
    popupRef,
    onClose: closePopup
  });
  const openPopup = React5.useCallback(() => {
    setDraft(selectedTime ?? defaultTime);
    setOpen(true);
  }, [selectedTime]);
  const togglePopup = React5.useCallback(() => {
    if (disabled) return;
    if (open) {
      setOpen(false);
    } else {
      openPopup();
    }
  }, [disabled, open, openPopup]);
  const handleConfirm = () => {
    onChange?.({
      target: { value: formatTime(draft) }
    });
    setOpen(false);
    inputRef.current?.focus();
  };
  const handleInputBlur = (e) => {
    if (containerRef.current?.contains(e.relatedTarget)) return;
    if (popupRef.current?.contains(e.relatedTarget)) return;
    setOpen(false);
    onBlur?.(e);
  };
  const describedByIds = [];
  if (hintText) describedByIds.push(`${id}-hint`);
  if (error && errorMessage) describedByIds.push(`${id}-error`);
  const ariaDescribedBy = describedByIds.length ? describedByIds.join(" ") : void 0;
  const inputClasses = cn(
    "flex h-12 w-full rounded-lg border border-solid transition-colors motion-safe:duration-150",
    "focus-visible:outline-none cursor-pointer select-none",
    "ps-10 pe-12 py-[14px]",
    "text-sm font-light",
    "bg-background",
    disabled ? "cursor-not-allowed pointer-events-none border-border text-disabled" : displayValue ? "text-fg-primary" : "text-quaternary",
    error ? "border-error focus:shadow-[0_0_0_4px_var(--color-bg-error-secondary)]" : "border-tertiary focus:shadow-[0_0_0_4px_var(--color-brand-tint-200)]",
    className
  );
  const timePopup = open ? ReactDOM2.createPortal(
    /* @__PURE__ */ jsx10(
      "div",
      {
        ref: popupRef,
        role: "dialog",
        "aria-label": t("timeInput.chooseTime"),
        tabIndex: -1,
        style: popupStyle,
        className: "fixed z-popover bg-background border border-border rounded-xl shadow-lg p-3 min-w-[220px]",
        children: /* @__PURE__ */ jsx10(
          TimePickerPanel,
          {
            draft,
            onDraftChange: setDraft,
            onConfirm: handleConfirm,
            minuteStep,
            format: format2,
            showColumnLabels,
            testID
          }
        )
      }
    ),
    document.body
  ) : null;
  return /* @__PURE__ */ jsxs8("div", { className: "space-y-sm", "data-testid": testID, children: [
    /* @__PURE__ */ jsxs8(
      Label,
      {
        htmlFor: id,
        className: cn(
          "block",
          "font-normal text-sm",
          "input-label-spacing",
          disabled ? "text-disabled" : "text-fg-secondary"
        ),
        children: [
          label,
          !required && /* @__PURE__ */ jsx10("span", { className: "ms-1", children: t("actions.optional") })
        ]
      }
    ),
    /* @__PURE__ */ jsxs8("div", { className: "relative", ref: containerRef, children: [
      /* @__PURE__ */ jsx10(
        "div",
        {
          className: cn(
            "absolute start-3 top-1/2 -translate-y-1/2 z-10",
            disabled ? "pointer-events-none" : "cursor-pointer"
          ),
          onClick: togglePopup,
          children: /* @__PURE__ */ jsx10(
            Icon,
            {
              name: "Clock",
              size: 20,
              className: cn(disabled ? "text-disabled" : "text-quaternary"),
              "aria-hidden": true
            }
          )
        }
      ),
      /* @__PURE__ */ jsx10(
        "input",
        {
          ref: inputRef,
          id,
          readOnly: true,
          value: displayValue,
          placeholder: placeholder || defaultPlaceholder,
          className: inputClasses,
          "aria-invalid": error || void 0,
          "aria-describedby": ariaDescribedBy,
          "aria-haspopup": "dialog",
          disabled,
          "data-testid": testID ? `${testID}-input` : void 0,
          onClick: togglePopup,
          onKeyDown: (e) => {
            if (e.key === " " || e.key === "Enter") {
              e.preventDefault();
              togglePopup();
            }
          },
          onBlur: handleInputBlur
        }
      ),
      error && /* @__PURE__ */ jsx10("div", { className: "pointer-events-none absolute end-3 top-1/2 -translate-y-1/2", children: /* @__PURE__ */ jsx10(
        Icon,
        {
          name: "CircleAlert",
          size: 16,
          "aria-hidden": true,
          className: "text-error-primary"
        }
      ) })
    ] }),
    timePopup,
    !error && hintText && /* @__PURE__ */ jsx10(
      "p",
      {
        id: `${id}-hint`,
        "data-testid": `${testID}-hint`,
        className: cn(
          "text-xs font-normal font-sans",
          disabled ? "text-disabled" : "text-quaternary"
        ),
        children: hintText
      }
    ),
    error && errorMessage && /* @__PURE__ */ jsx10(
      "p",
      {
        id: `${id}-error`,
        "data-testid": `${testID}-error`,
        className: "text-error-primary text-xs font-normal font-sans",
        children: errorMessage
      }
    )
  ] });
};

// src/components/atoms/Input.tsx
import * as React6 from "react";
function Input({ className, type, testID, ref, ...props }) {
  return React6.createElement("input", {
    ref,
    type,
    "data-testid": testID,
    className: cn(
      "flex h-10 w-full rounded-md border px-3 py-2 text-sm",
      "file:border-0 file:bg-transparent file:text-sm file:font-medium",
      "focus-visible:outline-none",
      "disabled:cursor-not-allowed",
      "border-tertiary bg-background text-fg-primary",
      "placeholder:text-placeholder disabled:placeholder:text-disabled",
      // Give the placeholder (and any pre-filled value) a real ellipsis
      // when the input is squeezed below its intrinsic content width.
      // Without this the browser silently slices characters at the box
      // edge — see e.g. the mobile-phone field on the onboarding "Your
      // details" page where "Enter your mobile phone" became "Enter your
      // mobile pho". For this to actually fire the input must also be
      // allowed to shrink — flex parents need `min-w-0` (see
      // MobileInputField).
      "truncate",
      className
    ),
    ...props
  });
}
Input.displayName = "Input";

// src/components/atoms/Label.tsx
import { cva as cva6 } from "class-variance-authority";
import { jsx as jsx11 } from "react/jsx-runtime";
var labelVariants = cva6(
  "text-sm font-normal font-sans peer-disabled:cursor-not-allowed peer-disabled:text-disabled"
);
function Label({ className, testID, ...props }) {
  return /* @__PURE__ */ jsx11(
    "label",
    {
      className: cn(labelVariants(), className),
      "data-testid": testID,
      ...props
    }
  );
}
Label.displayName = "Label";

// src/components/atoms/RadioButton.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
function RadioButton({
  uiSize = "sm",
  checked,
  className,
  style,
  testID,
  ref,
  ...props
}) {
  const sizeClasses2 = {
    sm: "w-xl h-xl",
    md: "w-[20px] h-[20px]",
    lg: "w-2xl h-2xl"
  };
  const checkedBorderClasses = {
    sm: "checked:border-5",
    md: "checked:border-[6px]",
    lg: "checked:border-6"
  };
  return /* @__PURE__ */ jsx12(
    "input",
    {
      ref,
      "data-testid": testID,
      type: "radio",
      className: cn(
        "align-middle shrink-0 rounded-full appearance-none outline-none !cursor-pointer transition-colors duration-200 ease-out motion-reduce:transition-none",
        sizeClasses2[uiSize],
        // --- Default (Unchecked) State (Outline/Empty) ---
        // Set a normal border for the unchecked state.
        // You might want a background color too, like 'bg-white' or 'bg-gray-100'.
        // The border should be relatively thin (e.g., border-2) and a light color.
        "border border-brand-tint-500",
        // Focus Ring
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        // hover state
        "not-checked:not-disabled:hover:border-selection",
        // Disabled styles
        "disabled:cursor-not-allowed disabled:border-fg-disabled disabled:opacity-70",
        // --- Checked State (The Fix: Apply Blue Color) ---
        // When checked, increase border width for the inner fill effect,
        // and apply the dark blue color to both the background and border.
        // This creates the solid blue circle appearance.
        checkedBorderClasses[uiSize],
        "checked:border-brand-blue-500 checked:bg-brand-blue-500 accent-brand-blue-500",
        className
      ),
      style: {
        background: "white",
        ...style
      },
      checked,
      ...props
    }
  );
}

// src/components/atoms/Textarea.tsx
import * as React7 from "react";
import { jsx as jsx13 } from "react/jsx-runtime";
function Textarea({
  uiSize = "md",
  className,
  testID,
  ref,
  autoResize = false,
  autoResizeOptions,
  hasCustomBorderRadius = false,
  value,
  ...props
}) {
  const { textareaRef } = useAutoResizeTextarea(value || "", {
    minHeight: uiSize === "xs" ? 48 : uiSize === "sm" ? 80 : 96,
    maxHeight: 200,
    enabled: autoResize,
    heightOffset: 0,
    ...autoResizeOptions
  });
  const combinedRef = React7.useCallback(
    (node) => {
      if (textareaRef.current !== node) {
        textareaRef.current = node;
      }
      if (ref) {
        if (typeof ref === "function") {
          ref(node);
        } else {
          ref.current = node;
        }
      }
    },
    [ref, textareaRef]
  );
  const sizeClasses2 = uiSize === "xs" ? "min-h-6xl  text-sm py-3 px-3" : uiSize === "sm" ? "min-h-8xl  text-sm py-2 px-3" : "min-h-9xl  py-[10px] px-[14px] text-sm";
  const autoResizeHeightClasses = uiSize === "xs" ? "h-6xl" : uiSize === "sm" ? "h-8xl" : "h-9xl";
  return /* @__PURE__ */ jsx13(
    "textarea",
    {
      ref: combinedRef,
      "data-testid": testID,
      value,
      className: cn(
        // Base styles
        "flex w-full transition-colors motion-safe:duration-150 appearance-none",
        // Resize behavior - disable manual resize when auto-resize is enabled
        autoResize ? "resize-none overflow-hidden" : "resize-y",
        // Focus styles
        "focus-visible:outline-none",
        // Disabled styles
        "disabled:cursor-not-allowed disabled:border-secondary",
        // Border radius - only apply default if no custom border radius is provided
        !hasCustomBorderRadius && "rounded-lg",
        // Border styles
        "border border-solid border-tertiary",
        // Background
        "bg-background",
        // Text color
        "text-fg-primary disabled:text-disabled",
        // Typography
        "text-sm font-light",
        // Font family
        "font-sans",
        // Focus styles with box shadow
        "focus:shadow-[0_0_0_4px_var(--color-brand-tint-200)]",
        // Size classes - only apply min-height when auto-resize is disabled
        !autoResize && sizeClasses2,
        // Auto-resize height classes - only apply when auto-resize is enabled
        autoResize && autoResizeHeightClasses,
        className
      ),
      ...props
    }
  );
}

// src/components/atoms/Heading.tsx
import * as React8 from "react";
function Heading({
  className,
  level = 1,
  size = "xl",
  weight = "medium",
  color = "default",
  testID,
  children,
  ref,
  ...props
}) {
  const sizeClasses2 = {
    xs: "text-xs",
    sm: "text-sm",
    base: "text-base",
    lg: "text-lg",
    xl: "text-xl",
    "2xl": "text-2xl",
    "3xl": "text-3xl",
    "4xl": "text-4xl",
    "5xl": "text-5xl",
    "6xl": "text-6xl"
  };
  const weightClasses = {
    light: "font-light",
    normal: "font-normal",
    medium: "font-medium",
    semibold: "font-semibold"
  };
  const colorClasses = {
    default: "text-foreground",
    muted: "text-muted-foreground",
    primary: "text-fg-primary",
    secondary: "text-secondary-foreground"
  };
  const commonProps = {
    ref,
    "data-testid": testID,
    className: cn(
      "tracking-tight",
      sizeClasses2[size],
      weightClasses[weight],
      colorClasses[color],
      className
    ),
    ...props
  };
  switch (level) {
    case 1:
      return React8.createElement("h1", commonProps, children);
    case 2:
      return React8.createElement("h2", commonProps, children);
    case 3:
      return React8.createElement("h3", commonProps, children);
    case 4:
      return React8.createElement("h4", commonProps, children);
    case 5:
      return React8.createElement("h5", commonProps, children);
    case 6:
      return React8.createElement("h6", commonProps, children);
    default:
      return React8.createElement("h1", commonProps, children);
  }
}
Heading.displayName = "Heading";

// src/components/atoms/Text.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
function Text({
  className,
  size = "base",
  weight = "light",
  color = "default",
  leading = "normal",
  as = "p",
  testID,
  children,
  ref,
  ...props
}) {
  const Component = as;
  const sizeClasses2 = {
    "2xs": "text-2xs",
    xs: "text-xs",
    sm: "text-sm",
    base: "text-base",
    lg: "text-lg",
    xl: "text-xl"
  };
  const weightClasses = {
    light: "font-light",
    normal: "font-normal",
    medium: "font-medium",
    semibold: "font-semibold"
  };
  const colorClasses = {
    default: "text-foreground",
    muted: "text-muted-foreground",
    primary: "text-fg-primary",
    secondary: "text-secondary-foreground"
  };
  const leadingClasses = {
    tight: "leading-tight",
    snug: "leading-snug",
    normal: "leading-normal",
    relaxed: "leading-relaxed",
    loose: "leading-loose"
  };
  return /* @__PURE__ */ jsx14(
    Component,
    {
      ref,
      "data-testid": testID,
      className: cn(
        sizeClasses2[size],
        weightClasses[weight],
        colorClasses[color],
        leadingClasses[leading],
        className
      ),
      ...props,
      children
    }
  );
}
Text.displayName = "Text";

// src/components/atoms/Container.tsx
import { jsx as jsx15 } from "react/jsx-runtime";
function Container({
  className,
  maxWidth = "full",
  center = true,
  testID,
  children,
  ref,
  ...props
}) {
  const maxWidthClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
    full: "max-w-full"
  };
  return /* @__PURE__ */ jsx15(
    "main",
    {
      ref,
      "data-testid": testID,
      className: cn(
        "w-full px-4 sm:px-6 lg:px-8",
        maxWidthClasses[maxWidth],
        center && "mx-auto",
        className
      ),
      ...props,
      children
    }
  );
}
Container.displayName = "Container";

// src/components/atoms/PageBackground.tsx
import { jsx as jsx16, jsxs as jsxs9 } from "react/jsx-runtime";
var DefaultPageBackground = () => {
  return /* @__PURE__ */ jsxs9(
    "svg",
    {
      width: "642",
      height: "644",
      viewBox: "0 0 322 323",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M173.5 299.98L121.22 307.52L148.04 322.28L200.38 314.72L173.5 299.98Z",
            stroke: "var(--color-fg-disabled-subtle)",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M293.66 0.5L293.6 57.05L320.64 71.46L320.72 14.83L293.66 0.5Z",
            stroke: "var(--color-fg-disabled-subtle)",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M320.72 14.83L320.64 71.45L200.47 88.44L200.37 314.71L148.03 322.27C148.03 293.06 148.03 263.84 148.03 234.63C148.03 216.53 148.03 198.44 148.03 180.34C148.03 171.37 147.79 162.35 148.59 153.41C150.92 127.48 160.32 102.43 175.14 81.06C189.69 60.08 209.67 42.51 233.04 32.02C243.53 27.31 254.55 24.18 265.92 22.52C282.53 20.1 299.17 17.84 315.79 15.51C317.43 15.28 319.07 15.05 320.71 14.82L320.72 14.83Z",
            stroke: "var(--color-fg-disabled-subtle)",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M293.6 57.05L293.67 0.5L246.51 7.12C235.41 8.68 224.59 10.55 214.01 14.43C190.16 23.18 169.35 39.4 153.69 59.27C137.74 79.51 126.98 103.78 122.93 129.24C121.42 138.71 121.18 148.19 121.18 157.75C121.18 173.28 121.18 188.82 121.19 204.35C121.19 238.08 121.2 271.82 121.21 305.55V307.53L173.5 299.99L173.55 74C208.59 69.05 243.63 64.11 278.68 59.16C283.65 58.46 288.63 57.76 293.6 57.05Z",
            stroke: "var(--color-fg-disabled-subtle)",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M173.55 74L200.47 88.45",
            stroke: "var(--color-fg-disabled-subtle)",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M148.02 39.14V95.88L27.31 112.95L27.26 56.13C62.45 51.18 97.65 46.23 132.84 41.28C137.9 40.57 142.96 39.86 148.01 39.15L148.02 39.14Z",
            stroke: "var(--color-fg-disabled-subtle)",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M121.15 81.4V24.74L0.5 41.68L0.57 98.43C35.71 93.47 70.86 88.51 106 83.55C111.05 82.84 116.1 82.12 121.15 81.41V81.4Z",
            stroke: "var(--color-fg-disabled-subtle)",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M121.14 24.73L148.02 39.14",
            stroke: "var(--color-fg-disabled-subtle)",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M121.15 81.4L148.02 95.87",
            stroke: "var(--color-fg-disabled-subtle)",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M0.380127 41.72L27.2601 56.12",
            stroke: "var(--color-fg-disabled-subtle)",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M0.390137 98.38L27.2601 112.86",
            stroke: "var(--color-fg-disabled-subtle)",
            strokeLinejoin: "round"
          }
        )
      ]
    }
  );
};
var PageBackgroundInner = ({
  className,
  children,
  testID,
  decorationOffsetX = -66,
  decorationOffsetY = -311,
  decorationTestID,
  ref,
  ...rest
}) => {
  return /* @__PURE__ */ jsxs9(
    "div",
    {
      ref,
      "data-testid": testID,
      className: cn(
        "relative isolate h-lvh overflow-hidden flex flex-col",
        className
      ),
      ...rest,
      children: [
        /* @__PURE__ */ jsx16(
          "div",
          {
            "aria-hidden": "true",
            className: cn(
              "pointer-events-none absolute inset-0 -z-10 bg-page-gradient"
            )
          }
        ),
        /* @__PURE__ */ jsx16(
          "img",
          {
            src: "/assets/images/page-background-pattern-mobile.svg",
            alt: "",
            width: 276,
            height: 190,
            "aria-hidden": "true",
            className: "pointer-events-none absolute bottom-0 end-0 -z-10 md:hidden"
          }
        ),
        /* @__PURE__ */ jsx16(
          "div",
          {
            "aria-hidden": "true",
            "data-testid": decorationTestID,
            className: cn("pointer-events-none absolute -z-10 hidden md:block"),
            style: { insetInlineEnd: decorationOffsetX, bottom: decorationOffsetY },
            children: /* @__PURE__ */ jsx16(DefaultPageBackground, {})
          }
        ),
        /* @__PURE__ */ jsx16("div", { className: "overflow-y-auto [scrollbar-gutter:stable] h-full w-full", children })
      ]
    }
  );
};
var PageBackground = (props) => /* @__PURE__ */ jsx16(PageBackgroundInner, { ...props });

// src/components/atoms/AccordionContent.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
var AccordionContent = ({
  children,
  className,
  isExpanded = false,
  testID,
  innerClassName,
  ref,
  ...props
}) => {
  return /* @__PURE__ */ jsx17(
    "div",
    {
      ref,
      className: cn(
        // Base styles using design tokens
        "overflow-hidden transition-all duration-300 ease-in-out",
        "bg-white border-b border-brand-tint-300",
        // Conditional height based on expansion state
        isExpanded ? "max-h-screen opacity-100" : "max-h-0 opacity-0",
        className
      ),
      "data-testid": testID,
      ...props,
      children: /* @__PURE__ */ jsx17("div", { className: cn("px-5 py-4", innerClassName), children: /* @__PURE__ */ jsx17("div", { className: "text-sm text-gray-600 leading-relaxed", children }) })
    }
  );
};

// src/components/atoms/AccordionTrigger.tsx
import { jsx as jsx18, jsxs as jsxs10 } from "react/jsx-runtime";
var AccordionTrigger = ({
  children,
  className,
  isExpanded = false,
  description,
  testID,
  icon,
  hideIcon = false,
  ref,
  ...props
}) => {
  return /* @__PURE__ */ jsxs10(
    Button,
    {
      ref,
      className: cn(
        // Base styles using design tokens
        "flex w-full items-center justify-between py-4 px-5",
        "text-start font-medium transition-all duration-200",
        "bg-white border-b border-brand-tint-300",
        "hover:bg-gray-50",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        className
      ),
      "aria-expanded": isExpanded,
      testID,
      ...props,
      children: [
        /* @__PURE__ */ jsxs10("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx18("div", { className: "text-sm font-medium text-gray-900", children }),
          description && /* @__PURE__ */ jsx18("div", { className: "text-xs text-gray-500 mt-1 truncate", children: description })
        ] }),
        !hideIcon && /* @__PURE__ */ jsx18(
          "span",
          {
            className: cn(
              "ms-3 flex-shrink-0 transition-transform duration-200",
              isExpanded ? "rotate-180" : "rotate-0"
            ),
            "aria-hidden": "true",
            children: icon || /* @__PURE__ */ jsx18(Icon, { name: "ChevronDown", className: "h-4 w-4 text-gray-500" })
          }
        )
      ]
    }
  );
};

// src/components/atoms/CountryFlag.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var CountryFlag = ({
  countryCode,
  className
}) => /* @__PURE__ */ jsx19(
  "span",
  {
    className: cn(
      "fi",
      `fi-${countryCode.toLowerCase()}`,
      "rounded-xxs",
      className
    ),
    "aria-hidden": "true"
  }
);
CountryFlag.displayName = "CountryFlag";

// src/components/atoms/FeaturedIcon.tsx
import { cva as cva7 } from "class-variance-authority";
import { jsx as jsx20 } from "react/jsx-runtime";
var featuredIconVariants = cva7(
  // Base styles for the circular container
  "inline-flex items-center justify-center rounded-full transition-all duration-200",
  {
    variants: {
      // Color variants matching Figma design
      variant: {
        success: [
          "bg-[var(--color-success-100)]"
          // #DCFAE6 from Figma
        ],
        error: ["bg-[var(--color-error-100)]"],
        warning: ["bg-[var(--color-warning-100)]"],
        brand: ["bg-[var(--color-brand-blue-100)]"],
        gray: ["bg-[var(--color-gray-100)]"]
      },
      // Size variants
      size: {
        sm: "w-8 h-8",
        md: "w-10 h-10",
        lg: "w-12 h-12",
        xl: "w-14 h-14"
      }
    },
    defaultVariants: {
      variant: "gray",
      size: "md"
    }
  }
);
var FeaturedIcon = ({
  className,
  variant = "gray",
  size = "md",
  icon,
  testID,
  "aria-label": ariaLabel,
  ...props
}) => {
  return /* @__PURE__ */ jsx20(
    "div",
    {
      className: cn(featuredIconVariants({ variant, size }), className),
      "data-testid": testID,
      role: "img",
      "aria-label": ariaLabel,
      ...props,
      children: icon
    }
  );
};
FeaturedIcon.displayName = "FeaturedIcon";

// src/components/atoms/FileUploadItem.tsx
import { useTranslation as useTranslation5 } from "@tasama/i18n";

// src/components/atoms/ProgressBar.tsx
import { jsx as jsx21, jsxs as jsxs11 } from "react/jsx-runtime";
var ProgressBar = ({
  progress,
  size = "md",
  variant = "primary",
  showLabel = true,
  label,
  animated = true,
  className,
  testID
}) => {
  const clampedProgress = Math.min(Math.max(progress, 0), 100);
  const sizeClasses2 = {
    sm: "h-1",
    md: "h-2",
    lg: "h-3"
  };
  const variantClasses = {
    primary: "bg-brand-blue-500",
    success: "bg-brand-green-500",
    warning: "bg-warning-500",
    error: "bg-error-500"
  };
  const containerClasses = cn("flex items-center gap-3", className);
  const trackClasses = cn(
    "flex-1 bg-gray-200 rounded-full overflow-hidden",
    sizeClasses2[size]
  );
  const fillClasses = cn(
    "h-full rounded-full transition-all duration-300 ease-out",
    variantClasses[variant],
    !animated && "transition-none"
  );
  const displayLabel = label || `${Math.round(clampedProgress)}%`;
  return /* @__PURE__ */ jsxs11("div", { className: containerClasses, "data-testid": testID, children: [
    /* @__PURE__ */ jsx21("div", { className: trackClasses, children: /* @__PURE__ */ jsx21(
      "div",
      {
        className: fillClasses,
        style: {
          width: `${clampedProgress}%`,
          minWidth: clampedProgress > 0 ? "2px" : "0px"
          // Ensure visibility for small values
        },
        role: "progressbar",
        "aria-valuenow": clampedProgress,
        "aria-valuemin": 0,
        "aria-valuemax": 100,
        "aria-label": `Progress: ${displayLabel}`,
        "data-testid": testID ? `${testID}-fill` : void 0
      }
    ) }),
    showLabel && /* @__PURE__ */ jsx21(
      Text,
      {
        size: "sm",
        weight: "medium",
        className: "text-gray-700 text-[14px] leading-[20px] min-w-[3ch] shrink-0",
        testID: testID ? `${testID}-label` : "",
        children: displayLabel
      }
    )
  ] });
};

// src/components/atoms/FileUploadItem.tsx
import { jsx as jsx22, jsxs as jsxs12 } from "react/jsx-runtime";
var FileUploadItem = ({
  fileName,
  fileSize,
  progress,
  status,
  onRemove,
  testID,
  className = "",
  showProgress = true,
  showStatus = true,
  disableRemove = false
}) => {
  const { t } = useTranslation5("common");
  const getStatusIcon = () => {
    switch (status) {
      case "complete":
        return /* @__PURE__ */ jsx22(
          Icon,
          {
            name: "CircleCheck",
            className: "text-fg-success-primary",
            size: 16
          }
        );
      case "error":
        return /* @__PURE__ */ jsx22(Icon, { name: "CircleX", className: "text-fg-error-primary", size: 16 });
      default:
        return /* @__PURE__ */ jsx22(Icon, { name: "CloudUpload", className: "text-fg-quaternary", size: 16 });
    }
  };
  const getStatusText = () => {
    switch (status) {
      case "complete":
        return t("fileUploadItem.statusComplete");
      case "error":
        return t("fileUploadItem.statusFailed");
      case "uploading":
        return t("fileUploadItem.statusUploading");
      default:
        return "";
    }
  };
  const getStatusColor = () => {
    switch (status) {
      case "complete":
        return "text-fg-success-primary";
      case "error":
        return "text-fg-error-primary";
      case "uploading":
        return "text-fg-tertiary";
      default:
        return "text-fg-tertiary";
    }
  };
  const getFileIconMeta = (name) => {
    const ext = name?.split(".").pop()?.toLowerCase() ?? "";
    switch (ext) {
      case "pdf":
        return { text: "PDF", color: "var(--fg-error-primary)" };
      case "png":
      case "jpg":
      case "jpeg":
      case "svg":
      case "gif":
        return {
          text: ext.toUpperCase(),
          color: "var(--fg-brand-primary)"
        };
      case "doc":
      case "docx":
        return { text: "DOC", color: "var(--fg-brand-primary)" };
      case "xls":
      case "xlsx":
        return { text: "XLS", color: "var(--fg-success-primary)" };
      case "ppt":
      case "pptx":
        return { text: "PPT", color: "var(--fg-brand-primary)" };
      case "txt":
        return { text: "TXT", color: "var(--fg-tertiary)" };
      default: {
        const t2 = ext ? ext.slice(0, 3).toUpperCase() : "FILE";
        return { text: t2, color: "var(--fg-tertiary)" };
      }
    }
  };
  const { text: fileTypeText, color: fileTypeColor } = getFileIconMeta(fileName);
  return /* @__PURE__ */ jsxs12(
    "div",
    {
      className: `relative flex items-start gap-3 p-4 bg-white rounded-lg border border-brand-tint-500 ${className}`,
      "data-testid": testID,
      "data-status": status,
      children: [
        /* @__PURE__ */ jsx22("div", { className: "flex-shrink-0", children: /* @__PURE__ */ jsx22(
          FileIcon_default,
          {
            variant: "solid",
            text: fileTypeText,
            color: fileTypeColor,
            testID: testID ? `${testID}-file-icon` : void 0
          }
        ) }),
        /* @__PURE__ */ jsx22("div", { className: "flex-1 min-w-0", children: /* @__PURE__ */ jsxs12("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsxs12("div", { className: "flex flex-row items-start justify-between flex-1 space-x-1", children: [
            /* @__PURE__ */ jsxs12("div", { className: "space-y-1 min-w-0 flex-1", children: [
              /* @__PURE__ */ jsx22(
                Text,
                {
                  size: "sm",
                  weight: "normal",
                  className: "text-gray-700 text-[14px] leading-[20px] truncate block",
                  title: fileName,
                  children: fileName
                }
              ),
              /* @__PURE__ */ jsxs12("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsx22(
                  Text,
                  {
                    size: "sm",
                    weight: "light",
                    className: "text-gray-600 text-[14px] leading-[20px]",
                    dir: "ltr",
                    children: fileSize
                  }
                ),
                showStatus && /* @__PURE__ */ jsx22("div", { className: "w-px h-3 bg-brand-tint-100" }),
                showStatus && /* @__PURE__ */ jsxs12("div", { className: "flex items-center gap-1", children: [
                  getStatusIcon(),
                  /* @__PURE__ */ jsx22(
                    Text,
                    {
                      size: "sm",
                      weight: "normal",
                      className: `text-[14px] leading-[20px] ${getStatusColor()}`,
                      children: getStatusText()
                    }
                  )
                ] })
              ] })
            ] }),
            onRemove && /* @__PURE__ */ jsx22(
              Button,
              {
                onClick: onRemove,
                disabled: disableRemove,
                variant: "quaternary",
                size: "sm",
                className: "p-0",
                iconPosition: "none",
                "aria-label": t("fileUploadItem.removeAriaLabel"),
                testID: testID ? `${testID}-remove` : void 0,
                children: /* @__PURE__ */ jsx22(
                  Icon,
                  {
                    size: 20,
                    absoluteStrokeWidth: true,
                    name: "Trash2",
                    className: "text-fg-quaternary hover:text-fg-error-primary"
                  }
                )
              }
            )
          ] }),
          showProgress && (status === "uploading" || status === "complete") && /* @__PURE__ */ jsx22("div", { className: "mt-2", children: /* @__PURE__ */ jsx22(
            ProgressBar,
            {
              progress: status === "complete" ? 100 : progress,
              variant: status === "complete" ? "success" : "primary",
              size: "md",
              showLabel: true,
              animated: status === "uploading",
              testID: testID ? `${testID}-progress` : void 0
            }
          ) })
        ] }) })
      ]
    }
  );
};

// src/logos/TasamaLogo.tsx
import { useTranslation as useTranslation6 } from "@tasama/i18n";
import { jsx as jsx23, jsxs as jsxs13 } from "react/jsx-runtime";
function TasamaLogo({
  color = "#FFF",
  backgroundColor = "#10069F",
  className,
  testID,
  title,
  ...props
}) {
  const { t } = useTranslation6("common");
  const logoTitle = title || t("logos.tasama");
  return /* @__PURE__ */ jsxs13(
    "svg",
    {
      width: "100",
      height: "100",
      viewBox: "0 0 100 100",
      fill: "none",
      className: cn("flex-shrink-0", className),
      "data-testid": testID,
      "aria-label": logoTitle,
      xmlns: "http://www.w3.org/2000/svg",
      ...props,
      children: [
        /* @__PURE__ */ jsx23("rect", { width: "100", height: "100", rx: "0", fill: backgroundColor }),
        /* @__PURE__ */ jsxs13("g", { transform: "translate(25, 27.5)", children: [
          /* @__PURE__ */ jsx23("path", { d: "M20.5296 0H0V8.94506H20.5296V0Z", fill: color }),
          /* @__PURE__ */ jsx23(
            "path",
            {
              d: "M20.5254 19.2342C20.5254 8.60316 29.1203 0.00817871 39.7473 0.00817871H50V8.94117H29.4583V44.7093H20.5254V19.2302V19.2342Z",
              fill: color
            }
          )
        ] })
      ]
    }
  );
}
TasamaLogo.displayName = "TasamaLogo";

// src/logos/PIFLogo.tsx
import { useTranslation as useTranslation7 } from "@tasama/i18n";
import { jsx as jsx24, jsxs as jsxs14 } from "react/jsx-runtime";
var TasamaLogo2 = ({
  primaryColor = "#035E4E",
  secondaryColor = "#C3984E",
  className,
  testID,
  title,
  ref,
  ...props
}) => {
  const { t } = useTranslation7("common");
  const logoTitle = title || t("logos.tasama");
  return /* @__PURE__ */ jsxs14(
    "svg",
    {
      ref,
      width: "176",
      height: "32",
      viewBox: "0 0 176 32",
      fill: "none",
      className: cn("flex-shrink-0", className),
      "data-testid": testID,
      "aria-label": logoTitle,
      xmlns: "http://www.w3.org/2000/svg",
      ...props,
      children: [
        /* @__PURE__ */ jsxs14("g", { clipPath: "url(#clip0_16572_220597)", children: [
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M41.5045 17.2768L43.5552 11.7216H43.6231L45.6739 17.2768H41.4909H41.5045ZM41.2872 9.7793V9.86079C41.2872 9.86079 41.9391 10.3362 41.4773 11.6129L37.2943 22.2343H39.6574L40.839 19.0425H46.3258L47.5074 22.2343H49.8977L44.9948 9.79288H41.26L41.2872 9.7793Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M64.321 12.2784C64.1716 11.7895 63.9136 11.3684 63.5469 11.0017C63.1802 10.635 62.6641 10.3361 62.0258 10.1188C61.3739 9.90151 60.559 9.79285 59.5676 9.79285H54.2301V9.87434C54.2301 9.87434 55.0042 10.4176 55.0042 11.8438V22.2343H57.2044V11.64H59.5268C60.0836 11.64 60.5318 11.6944 60.8985 11.7895C61.2652 11.8845 61.5504 12.0339 61.7677 12.2105C61.985 12.4007 62.1344 12.618 62.2159 12.9032C62.2974 13.1748 62.3517 13.5008 62.3517 13.8675C62.3517 14.2343 62.311 14.5467 62.2295 14.8319C62.148 15.1171 62.0122 15.3616 61.7949 15.5653C61.5912 15.7691 61.306 15.9185 60.9393 16.0407C60.5726 16.1494 60.1108 16.2037 59.5404 16.2037H58.7119L57.9378 18.0645H59.5947C60.5726 18.0645 61.3739 17.9558 62.0122 17.7249C62.6505 17.494 63.153 17.1952 63.5333 16.8013C63.9136 16.4074 64.1716 15.9592 64.321 15.4431C64.4704 14.927 64.5383 14.3837 64.5383 13.8132C64.5383 13.2835 64.4704 12.781 64.321 12.292",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M67.0373 9.7793H66.2631V9.86079C66.2631 9.86079 67.0373 10.4041 67.0373 11.8302V22.2207H69.2646V9.7793H67.0373Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M80.2926 11.708L81.0667 9.79285H71.8722V9.87434C71.8722 9.87434 72.6463 10.4176 72.6463 11.8438V22.2343H74.8737V17.3854H79.2468L80.021 15.4703H74.8737V11.7215H80.279L80.2926 11.708Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M98.5323 18.1053C98.3693 18.8659 98.0434 19.5722 97.5545 20.2377C97.0655 20.9032 96.4136 21.4465 95.5988 21.8676C94.7839 22.2886 93.8196 22.4924 92.7331 22.4924C91.4972 22.4924 90.3971 22.2207 89.4193 21.7046C88.4414 21.1749 87.6809 20.4143 87.124 19.4364C86.5672 18.4584 86.282 17.3039 86.282 15.9729C86.282 14.6418 86.5672 13.5145 87.124 12.5365C87.6809 11.5586 88.4414 10.8116 89.4193 10.2819C90.3971 9.75214 91.4972 9.49408 92.7331 9.49408C93.806 9.49408 94.7431 9.69781 95.5716 10.0917C96.3865 10.4992 97.0384 11.0017 97.5273 11.6129C98.0162 12.2241 98.3286 12.8625 98.4644 13.5009L96.305 13.8676C96.1148 13.1341 95.721 12.5229 95.0962 12.034C94.4715 11.545 93.6838 11.3005 92.7331 11.3005C91.9318 11.3005 91.212 11.4907 90.5737 11.8438C89.9354 12.2105 89.4328 12.7538 89.0662 13.4601C88.6995 14.1664 88.5229 15.0085 88.5229 15.9729C88.5229 16.9372 88.713 17.7929 89.0662 18.4992C89.4328 19.2055 89.9354 19.7487 90.5737 20.1291C91.212 20.4958 91.9318 20.6859 92.7331 20.6859C93.7381 20.6859 94.5394 20.4143 95.137 19.871C95.7482 19.3277 96.142 18.6486 96.3457 17.8065L98.5459 18.1053H98.5323Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M170.214 15.4975L173.555 9.7793H176L171.301 17.3718V22.2207H169.101V17.3854L164.388 9.7793H166.873L170.214 15.4975Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M108.419 20.1291C109.071 19.7623 109.574 19.219 109.941 18.5128C110.307 17.8065 110.484 16.9644 110.484 16C110.484 15.0357 110.294 14.18 109.941 13.4873C109.574 12.781 109.071 12.2513 108.419 11.871C107.781 11.5043 107.048 11.3141 106.233 11.3141C105.418 11.3141 104.671 11.5043 104.033 11.871C103.394 12.2377 102.878 12.781 102.525 13.4873C102.158 14.1936 101.982 15.0357 101.982 16C101.982 16.9644 102.158 17.8201 102.525 18.5128C102.892 19.219 103.394 19.7487 104.033 20.1291C104.685 20.4958 105.418 20.6859 106.233 20.6859C107.048 20.6859 107.781 20.4958 108.419 20.1291ZM109.574 10.2819C110.565 10.8116 111.339 11.5586 111.896 12.5365C112.453 13.5145 112.738 14.669 112.738 16C112.738 17.3311 112.467 18.4856 111.896 19.4635C111.339 20.4414 110.565 21.2021 109.574 21.7182C108.582 22.2343 107.469 22.506 106.233 22.506C104.997 22.506 103.87 22.2479 102.892 21.7182C101.9 21.1885 101.126 20.4414 100.569 19.4635C100.013 18.4856 99.7274 17.3311 99.7274 16C99.7274 14.669 100.013 13.528 100.569 12.5365C101.126 11.5586 101.9 10.798 102.892 10.2819C103.883 9.75214 104.997 9.49408 106.233 9.49408C107.469 9.49408 108.596 9.75214 109.574 10.2819Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M143.052 17.2768L145.103 11.7216H145.17L147.221 17.2768H143.038H143.052ZM142.834 9.7793V9.86079C142.834 9.86079 143.486 10.3362 143.025 11.6129L138.842 22.2343H141.205L142.386 19.0425H147.873L149.055 22.2343H151.445L146.542 9.79288H142.807L142.834 9.7793Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M114.585 22.2207H116.623V12.7267H116.691L119.991 22.2207H121.702L125.002 12.7267H125.07V22.2207H127.162V9.7793H123.97L120.914 18.9881H120.846L117.791 9.7793H113.811V9.86079C113.811 9.86079 114.585 10.3905 114.585 11.8166V22.2207Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M152.409 22.2207H154.446V12.8082H154.514L160.857 22.2207H163.247V9.7793H161.21V19.0289H161.142L154.881 9.7793H151.635V9.86079C151.635 9.86079 152.409 10.3905 152.409 11.8166V22.2207Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M129.905 22.2207H132.105V11.6265H134.428C134.984 11.6265 135.446 11.6808 135.799 11.7759C136.166 11.8709 136.451 12.0204 136.669 12.1969C136.886 12.3871 137.035 12.6044 137.117 12.8896C137.198 13.1748 137.239 13.4872 137.239 13.8675C137.239 14.2479 137.198 14.5467 137.117 14.8319C137.035 15.1171 136.899 15.3616 136.682 15.5653C136.478 15.7691 136.193 15.9321 135.827 16.0407C135.46 16.1494 134.998 16.2037 134.428 16.2037H133.572L132.811 18.0645H134.468C135.446 18.0645 136.248 17.9558 136.886 17.7249C137.524 17.494 138.04 17.1952 138.407 16.8013C138.787 16.4074 139.045 15.9592 139.195 15.4431C139.344 14.927 139.412 14.3837 139.412 13.8132C139.412 13.2835 139.344 12.781 139.195 12.292C139.045 11.803 138.787 11.382 138.421 11.0017C138.054 10.635 137.579 10.3361 136.927 10.1188C136.275 9.90151 135.433 9.79285 134.428 9.79285H129.09V9.87434C129.09 9.87434 129.864 10.4041 129.864 11.8302V22.2343L129.905 22.2207Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M27.9639 13.8812L28.0318 14.2615L29.7159 13.9491L29.6615 13.5552L27.9639 13.8812Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M27.9232 18.3226L29.6344 18.6078L29.6887 18.2139L27.9911 17.9287L27.9232 18.3226Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M29.2541 17.3311C29.4307 17.3447 29.5665 17.2768 29.5801 17.1274C29.5936 16.9915 29.5122 16.8829 29.3084 16.8693C29.1319 16.8557 29.0097 16.9372 28.9961 17.0595L28.9689 17.3039L29.2405 17.3311H29.2541ZM28.059 17.6027L28.0997 17.2224L28.7244 17.2904L28.7652 16.9508C28.7924 16.6656 28.9961 16.4482 29.3492 16.489C29.6751 16.5297 29.906 16.7606 29.8653 17.1817C29.8109 17.6435 29.5122 17.7657 29.159 17.7386L28.0725 17.6163L28.059 17.6027Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M29.2405 14.8455L29.2541 15.1308L29.8517 15.09L29.8652 15.4839L29.2812 15.5111L29.2948 15.7963L29.8652 15.7555L29.8924 16.1494L29.0639 16.2038L28.9825 14.8591L28.0997 14.8999L28.0725 14.5195L29.7973 14.4109L29.8245 14.8048L29.2405 14.8455Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M3.92501 14.4652L2.28168 14.2207L2.336 13.8404L3.39534 14.0034L3.43609 13.7046L3.72129 13.7453V13.7861C3.70771 13.8676 3.72129 13.9491 3.76204 14.017C3.81636 14.0849 3.87069 14.1256 3.95218 14.1528L3.89785 14.4652H3.92501Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M3.1373 15.1172C3.1373 15.1172 3.05581 15.2122 3.05581 15.2666C3.05581 15.3345 3.06939 15.3888 3.12372 15.4295C3.16446 15.4839 3.23237 15.4975 3.31386 15.511C3.39534 15.511 3.46325 15.4975 3.51757 15.4567C3.5719 15.416 3.59906 15.3616 3.59906 15.2937C3.59906 15.2258 3.58548 15.1715 3.53116 15.1307C3.49041 15.09 3.42251 15.0628 3.32744 15.0492C3.24595 15.0492 3.17804 15.0628 3.12372 15.1036M2.85209 14.927C2.89283 14.8591 2.96074 14.7912 3.05581 14.7504C3.1373 14.7097 3.23237 14.6825 3.3546 14.6961C3.49041 14.6961 3.59906 14.7368 3.68055 14.8048C3.76204 14.8727 3.82994 14.9406 3.85711 15.0357C3.89785 15.1307 3.91143 15.2258 3.89785 15.3209C3.89785 15.5382 3.80278 15.7012 3.65339 15.7827C3.50399 15.8778 3.28669 15.9049 3.00149 15.8913C2.68912 15.8778 2.47181 15.8099 2.34958 15.6876C2.22735 15.5654 2.17303 15.416 2.18661 15.2258C2.18661 15.0764 2.24093 14.9677 2.32242 14.8727C2.40391 14.7776 2.51256 14.7233 2.64837 14.6961L2.67553 15.0628C2.5533 15.0764 2.4854 15.1443 2.47181 15.2394C2.47181 15.3345 2.49898 15.4024 2.58047 15.4431C2.66195 15.4839 2.75702 15.511 2.89284 15.511C2.89284 15.511 2.82493 15.4024 2.79777 15.3481C2.7706 15.2937 2.7706 15.2258 2.7706 15.1715C2.7706 15.09 2.79777 15.0085 2.85209 14.9406",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M3.82995 16.0679L3.89786 17.2768L3.53116 17.2903L2.2002 16.6927L2.17303 16.2309L3.504 16.8557L3.46326 16.0951L3.82995 16.0679Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M4.02005 18.1325L2.37671 18.3769L2.32239 17.9966L3.38173 17.8336L3.34098 17.5348L3.62619 17.4941V17.5348C3.63977 17.6299 3.68052 17.6842 3.74842 17.7386C3.81633 17.7793 3.88424 17.8065 3.96572 17.8065L4.02005 18.1189V18.1325Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M27.3935 8.43466C27.4885 8.36675 27.5021 8.2445 27.4342 8.16301C27.3663 8.06793 27.2441 8.05435 27.1626 8.10868C27.0675 8.17659 27.0539 8.29883 27.1218 8.39391C27.1898 8.48899 27.312 8.50257 27.3935 8.43466Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M27.5293 10.8387L27.3527 10.9338C27.2034 10.6078 27.0404 10.2818 26.8638 9.96945L27.2441 9.73855L27.5972 10.472C27.6787 10.635 27.6515 10.7844 27.5157 10.8523M27.8688 10.3498L27.5293 9.64347L27.3256 9.67064L27.57 9.52123L27.3527 9.16809L26.6465 9.58915C26.565 9.43974 26.47 9.30392 26.3749 9.15451L27.054 8.67913L26.8095 8.32599L26.1304 8.80137C26.0354 8.66555 25.9403 8.54331 25.8452 8.40748L26.0489 8.23091C26.3613 7.97285 26.3885 7.68762 26.0897 7.33448L25.8995 7.10358L25.6822 7.29373L25.7909 7.42955C25.9539 7.61971 25.9403 7.74195 25.7909 7.86419L25.5464 8.06792C25.397 7.89135 25.2476 7.71478 25.0983 7.55179L24.8945 7.74195C25.2476 8.12225 25.5736 8.52972 25.8724 8.95078C25.9539 9.07302 26.0489 9.12735 26.1712 9.14093L26.1168 9.30392C26.5514 9.96945 26.9181 10.6621 27.2169 11.4092L27.6108 11.2054C28.0046 11.0017 27.9775 10.635 27.8145 10.3226",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M24.3105 5.26995C24.229 5.21563 24.2154 5.10697 24.2697 5.02547C24.3241 4.94398 24.4463 4.9304 24.5142 4.98473C24.5957 5.03905 24.6093 5.1613 24.555 5.22921C24.5006 5.3107 24.392 5.32428 24.3105 5.25637M23.9438 4.99831C23.8623 4.94398 23.8487 4.83532 23.9031 4.75383C23.9574 4.67233 24.0796 4.65875 24.1475 4.71308C24.229 4.76741 24.2426 4.88965 24.1883 4.95756C24.1339 5.03905 24.0117 5.05264 23.9438 4.99831ZM23.7808 5.5416C23.6586 5.44652 23.482 5.46011 23.3734 5.60951C23.2647 5.75892 23.2919 5.92191 23.4277 6.01698L23.5771 6.13922C23.5771 6.13922 23.5907 6.11206 23.6043 6.11206L23.9438 5.66384L23.7944 5.5416H23.7808ZM24.4463 5.69101L23.9031 6.41087C23.5635 6.85909 23.061 6.76401 22.6536 6.4652C22.2597 6.16639 22.0153 5.70459 22.3412 5.26995L22.477 5.09338L22.8165 5.35145L22.6536 5.56877C22.4634 5.81325 22.6264 6.09848 22.8437 6.26147C23.0067 6.38371 23.1968 6.43804 23.3598 6.35654L23.1697 6.20714C22.898 6.0034 22.8165 5.66384 23.0203 5.3922C23.2647 5.06622 23.6586 5.12055 23.9438 5.33787L24.4327 5.70459L24.4463 5.69101Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M24.7315 7.14429L25.139 6.70966L25.0032 6.57383C24.8945 6.46518 24.718 6.45159 24.5957 6.58742C24.4735 6.72324 24.4735 6.88623 24.5957 7.00847L24.7315 7.14429ZM25.6551 6.81832L24.9624 7.53818C24.5821 7.93207 24.2562 7.85058 23.9302 7.53818L23.7673 7.37519L23.9574 7.18504L24.0796 7.2937C24.2562 7.45669 24.392 7.49743 24.555 7.36161L24.3513 7.15788C24.1068 6.91339 24.0661 6.57383 24.3105 6.32935C24.5957 6.03054 24.976 6.1392 25.2341 6.39726L25.6822 6.81832H25.6551Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M10.1996 4.22413C10.2946 4.18339 10.3218 4.07473 10.2811 3.97965C10.2403 3.88458 10.1317 3.85741 10.0366 3.89816C9.94152 3.9389 9.91436 4.04756 9.9551 4.14264C9.99585 4.22413 10.1045 4.26488 10.186 4.22413",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M9.79212 4.42787C9.88719 4.38712 9.91435 4.27846 9.8736 4.18339C9.83286 4.08831 9.72421 4.06115 9.62914 4.10189C9.54765 4.14264 9.50691 4.2513 9.56123 4.34638C9.60198 4.42787 9.71063 4.46862 9.79212 4.42787Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M15.7 2.33618C15.7 2.43125 15.7815 2.51275 15.8765 2.51275C15.9716 2.51275 16.0531 2.43125 16.0531 2.33618C16.0531 2.2411 15.9716 2.15961 15.863 2.15961C15.7679 2.15961 15.6864 2.2411 15.6864 2.33618",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M15.4283 2.51275C15.5234 2.51275 15.6049 2.43125 15.6049 2.33618C15.6049 2.2411 15.5234 2.15961 15.4283 2.15961C15.3333 2.15961 15.2518 2.2411 15.2518 2.33618C15.2518 2.43125 15.3333 2.51275 15.4283 2.51275Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M15.6457 2.11883C15.7407 2.11883 15.8222 2.03733 15.8222 1.94226C15.8222 1.84718 15.7407 1.76569 15.6457 1.76569C15.5506 1.76569 15.4691 1.84718 15.4691 1.94226C15.4691 2.03733 15.5506 2.11883 15.6457 2.11883Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M16.9087 2.53991C17.0038 2.53991 17.0988 2.472 17.0988 2.37692C17.0988 2.28184 17.0309 2.18677 16.9359 2.18677C16.8408 2.18677 16.7593 2.25468 16.7457 2.34976C16.7457 2.44483 16.8136 2.52633 16.9087 2.53991Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M16.4605 2.51275C16.5556 2.51275 16.6507 2.44484 16.6507 2.34976C16.6507 2.25468 16.5828 2.15961 16.4877 2.15961C16.3926 2.15961 16.3111 2.22752 16.2975 2.3226C16.2975 2.41767 16.3655 2.49917 16.4605 2.51275Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M11.4354 4.72666L11.0144 3.6944L10.6885 3.83022L11.0144 4.618C10.5798 4.80815 10.1316 5.05263 9.90076 5.17487C9.7242 5.26995 9.64271 5.24278 9.54764 5.06621L9.42541 4.83531L9.05872 5.03905L9.1402 5.18845C9.3575 5.58234 9.62913 5.60951 9.99583 5.43294C10.4576 5.16129 10.9465 4.93039 11.449 4.72666",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M14.1517 3.69441C14.1925 3.61291 14.2196 3.51784 14.2468 3.40918L14.2875 3.0832H14.3962C14.5456 3.05604 14.695 3.13753 14.7221 3.32768L14.7629 3.61291C14.5592 3.64008 14.369 3.65366 14.1653 3.68083M18.294 4.06113C18.4298 4.0883 18.5656 4.06113 18.6471 3.97964L18.7286 4.15621C18.9731 4.21054 19.204 4.26487 19.4349 4.33278L19.7201 3.25977L19.3126 3.15111L19.1089 3.95247C18.9867 3.92531 18.878 3.89814 18.7558 3.87098C18.7829 3.83023 18.7965 3.78948 18.7965 3.73515L18.9188 3.1647L18.5113 3.0832L18.3619 3.78948C18.2261 3.76232 18.0767 3.73515 17.9409 3.72157L18.036 3.02887L17.615 2.97454L17.5063 3.66724C17.3569 3.65366 17.1532 3.6265 17.0038 3.61291L17.0717 2.78439L16.6507 2.75723L16.5828 3.59933C16.4062 3.59933 16.2297 3.58575 16.0531 3.58575V2.75723H15.6321V3.59933C15.4691 3.59933 15.3197 3.61291 15.1567 3.6265L15.116 3.34127C15.0617 2.9338 14.695 2.77081 14.3419 2.82514L13.8529 2.90663L13.8122 3.46351C13.785 3.68083 13.7171 3.81665 13.5406 3.84381L13.3368 3.88456L12.9566 2.3905L12.5491 2.48558L12.8887 3.84381C12.943 4.0883 13.1467 4.21054 13.3912 4.15621C13.4591 4.14263 13.5406 4.12904 13.6085 4.11546C13.8529 4.06113 14.0295 3.96605 14.1246 3.78948L14.3283 3.99322C14.7629 3.93889 15.2111 3.89814 15.6592 3.88456C15.7951 3.88456 15.8901 3.83023 15.9716 3.73515L16.0939 3.88456C16.2568 3.88456 16.4198 3.88456 16.5828 3.89814C16.7322 3.89814 16.8408 3.8574 16.9223 3.7759L17.0174 3.92531C17.452 3.96605 17.8866 4.02038 18.3076 4.10188",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M21.7301 3.25977L21.3362 3.09678L20.7522 4.50935C20.63 4.45502 20.5078 4.41427 20.3855 4.35994L20.793 3.17828L20.3991 3.02887L19.9917 4.21053C19.8966 4.18337 19.7879 4.14262 19.6929 4.11546L19.6114 4.3871C20.1003 4.53651 20.5757 4.72666 21.0374 4.9304L21.7301 3.25977Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M11.4219 2.79797L12.0738 4.48218L11.6663 4.63159L11.028 2.9338L11.4219 2.79797Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M12.7528 4.23767C12.8615 4.60439 12.7528 4.87604 12.359 5.05261L12.196 4.83529C12.359 4.74022 12.4405 4.64514 12.3861 4.45499L12.0602 3.34124L12.4676 3.21899L12.7664 4.23767H12.7528Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M22.4635 3.61288L21.6757 5.24276L21.2955 5.05261L22.0968 3.42273L22.4635 3.61288Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M3.51755 10.2682C3.42248 10.2275 3.31383 10.2682 3.28667 10.3633C3.24592 10.4584 3.28667 10.5535 3.38174 10.5942C3.47681 10.635 3.57187 10.5942 3.61262 10.4991C3.65336 10.4041 3.61262 10.309 3.53113 10.2682",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M3.70767 9.86077C3.6126 9.82002 3.50395 9.86076 3.47679 9.95584C3.43605 10.0509 3.47679 10.146 3.57186 10.1867C3.66693 10.2275 3.762 10.1867 3.80274 10.0917C3.84349 9.99659 3.80274 9.90151 3.72126 9.86077",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M5.39176 9.53481L5.3646 9.56198C5.32385 9.48048 5.24237 9.37182 5.18804 9.30391C5.13372 9.236 4.95716 9.04585 4.95716 9.04585L5.01148 8.95077C5.09297 8.81495 5.25595 8.7742 5.41892 8.86928L5.69055 9.04585C5.5819 9.20883 5.48683 9.35824 5.37818 9.52123M4.68553 10.8523C4.61763 10.9881 4.48181 11.0425 4.346 10.9745C4.21019 10.9066 4.15586 10.7708 4.22377 10.635L4.33242 10.4177L4.64479 10.5807C4.64479 10.5807 4.73986 10.6214 4.7806 10.635L4.67195 10.8523H4.68553ZM6.16589 8.84211C6.16589 8.84211 5.96217 8.71987 5.66339 8.52972C5.32385 8.3124 4.95716 8.48897 4.76702 8.80136L4.52256 9.18167L4.90283 9.6163C5.05223 9.77929 5.13372 9.91512 5.05223 10.0781L5.02507 10.1324L4.95716 10.2683L4.10154 9.83362L3.9114 10.2139L4.06079 10.2818L3.9114 10.5807C3.762 10.8659 3.84349 11.1919 4.1287 11.3277C4.41391 11.4771 4.73986 11.3548 4.87567 11.0696V11.0289C4.87567 11.0289 4.93 10.9338 4.95716 10.893L4.9979 10.798L4.90283 10.6214C5.01148 10.6078 5.10655 10.5263 5.17446 10.4177C5.20162 10.3633 5.29669 10.1868 5.29669 10.1868C5.41892 9.96945 5.4325 9.80646 5.3646 9.6163L5.6498 9.56198C5.7992 9.31749 5.96217 9.07301 6.13873 8.84211",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M6.45114 7.19865C6.49188 7.10357 6.55979 7.0085 6.61411 6.95417C6.74992 6.81835 6.89932 6.79118 7.02155 6.91342C7.10304 6.99492 7.14378 7.11716 7.08946 7.26656C6.92648 7.28014 6.50546 7.22582 6.45114 7.21223M6.98081 7.83702C7.10304 7.7012 7.14378 7.57896 7.19811 7.45672C7.29318 7.42955 7.41541 7.3888 7.52406 7.26656C7.79568 6.98133 8.09447 6.73685 8.39326 6.49237L7.26601 5.06622L6.94006 5.33787L7.90433 6.5467C7.72778 6.6961 7.55122 6.85909 7.38824 7.0085C7.41541 6.80476 7.3475 6.61461 7.23885 6.51953C7.06229 6.35655 6.76351 6.35655 6.46472 6.66894C6.31532 6.83193 6.19309 7.03566 6.1116 7.21223C6.1116 7.21223 6.15235 7.41597 6.16593 7.42955C6.16593 7.45672 6.22025 7.48388 6.261 7.48388C6.30174 7.48388 6.6956 7.56537 6.83141 7.57896C6.65486 7.78269 6.4783 7.98643 6.30174 8.19016L5.09301 7.22582L4.83496 7.56537L5.90788 8.43464C6.09802 8.59763 6.34249 8.57047 6.49188 8.36673C6.64127 8.17658 6.80425 8.00001 6.95364 7.82344",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M7.90429 4.61798L8.95005 6.08487L8.61052 6.34294L7.56476 4.86246L7.90429 4.61798Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M4.22377 21.365C4.22377 21.365 4.29168 21.4737 4.346 21.4873C4.40033 21.5009 4.45465 21.5009 4.52256 21.4737C4.59047 21.4465 4.63121 21.4058 4.64479 21.3515C4.65837 21.2971 4.65837 21.2428 4.63121 21.1749L4.50898 20.9032L4.10154 21.0934L4.22377 21.365ZM5.01149 21.528C4.95716 21.6503 4.84851 21.7589 4.68554 21.8268C4.52256 21.8947 4.37317 21.9083 4.25093 21.8676C4.11512 21.8268 4.02005 21.7182 3.95215 21.5688L3.80275 21.2428L3.30024 21.4737L3.13727 21.1206L4.68554 20.4279L4.99791 21.107C5.06581 21.2564 5.07939 21.4058 5.02507 21.528",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M4.7127 23.5654C4.60404 23.5654 4.49539 23.5382 4.40033 23.4839C4.30526 23.4295 4.22377 23.3345 4.15586 23.2258C4.04721 23.0492 4.02005 22.8862 4.04721 22.7233C4.07437 22.5603 4.18302 22.4244 4.37316 22.3158L5.28311 21.7589L5.48683 22.0849L4.61763 22.6146C4.41391 22.7368 4.37316 22.8727 4.46823 23.0356C4.5633 23.1986 4.71269 23.2258 4.91641 23.1036L5.7992 22.5739L6.00292 22.8998L5.09297 23.4567C4.97074 23.5246 4.84851 23.5654 4.73986 23.5789",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M6.05725 24.1087C6.05725 24.1087 6.12515 24.1766 6.17948 24.1766C6.22022 24.1766 6.26097 24.1766 6.30171 24.1358C6.34246 24.0951 6.36962 24.0679 6.36962 24.0136C6.36962 23.9728 6.36962 23.9321 6.32887 23.8914L6.09799 23.6061L5.82637 23.8234L6.05725 24.1087ZM5.55474 24.5433C5.55474 24.5433 5.63623 24.6112 5.67697 24.6248C5.71772 24.6248 5.77204 24.6248 5.81278 24.5705C5.85353 24.5297 5.88069 24.489 5.89427 24.4482C5.89427 24.4075 5.89427 24.3532 5.83995 24.3124L5.59548 24.0136L5.2967 24.2445L5.54116 24.5433H5.55474ZM6.76348 24.0815C6.76348 24.2037 6.69557 24.2988 6.58692 24.3803C6.51901 24.4346 6.46469 24.4618 6.39678 24.4754C6.32887 24.4754 6.27455 24.4754 6.20664 24.4482C6.22022 24.5297 6.22022 24.6112 6.20664 24.6791C6.17948 24.747 6.13874 24.815 6.05725 24.8829C5.93502 24.9779 5.81278 25.0323 5.69055 25.0187C5.56832 25.0187 5.45967 24.9372 5.3646 24.8285L4.82135 24.163L6.13874 23.1036L6.65483 23.7419C6.74989 23.8506 6.79064 23.9728 6.77706 24.0815",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M5.935 25.494L7.11657 24.2852L7.3882 24.5569L6.46467 25.5076L7.00792 26.0373L6.74988 26.309L5.935 25.494Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M8.05794 25.1881L6.96625 26.4883L7.25747 26.7328L8.34916 25.4326L8.05794 25.1881Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M8.81426 27.5179C8.70561 27.5586 8.59695 27.5722 8.47472 27.5722C8.35249 27.5722 8.24384 27.5179 8.13519 27.4499C8.01296 27.3684 7.91789 27.2598 7.86357 27.1376C7.80924 27.0153 7.78208 26.8795 7.80924 26.7301C7.8364 26.5807 7.89073 26.4313 7.99938 26.2819C8.10803 26.1325 8.21668 26.0238 8.35249 25.9423C8.4883 25.8744 8.61054 25.8472 8.74635 25.8472C8.88216 25.8472 9.01798 25.9016 9.14021 25.9831C9.24886 26.051 9.33034 26.146 9.38467 26.2547C9.439 26.3634 9.46616 26.472 9.46616 26.5671C9.46616 26.6622 9.439 26.7844 9.39825 26.8795L9.04514 26.7029C9.08588 26.6214 9.08588 26.5399 9.0723 26.4584C9.05872 26.3769 9.01798 26.309 8.93649 26.2683C8.88216 26.2275 8.81426 26.214 8.74635 26.214C8.67844 26.214 8.59696 26.2275 8.52905 26.2819C8.46114 26.3226 8.37965 26.3905 8.31175 26.4856C8.24384 26.5807 8.2031 26.6758 8.18952 26.7572C8.17594 26.8387 8.18952 26.9202 8.2031 26.9881C8.23026 27.0561 8.271 27.0968 8.32533 27.1376C8.39324 27.1919 8.47472 27.2055 8.55621 27.1919C8.6377 27.1783 8.70561 27.124 8.78709 27.0561L9.08588 27.3141C9.00439 27.4092 8.90933 27.4771 8.80067 27.5179",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M10.3167 26.7182L9.54175 28.2288L9.88009 28.4024L10.655 26.8918L10.3167 26.7182Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M11.5034 29.107L11.1095 28.944L10.9873 27.708L10.5799 28.7267L10.2539 28.6044L10.8786 27.0289L11.2861 27.1919L11.4083 28.4007L11.8158 27.4092L12.1281 27.5314L11.5034 29.107Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M13.7442 27.9525L12.8207 29.4737L12.359 29.3515L12.2775 27.5858L12.6849 27.6944L12.6985 28.9712H12.7121L13.3368 27.8574L13.7442 27.9525Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M14.206 28.6723L14.9123 28.7674L14.8715 29.0934L14.1653 29.0119L14.1245 29.3514L14.8715 29.4465L14.8172 29.7996L13.6899 29.6502L13.9072 27.9796L15.0345 28.1154L14.9937 28.455L14.2468 28.3599L14.206 28.6723Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M15.9716 28.4957C15.9716 28.4957 15.863 28.4414 15.7951 28.4414C15.7272 28.4414 15.6728 28.4414 15.6321 28.4822C15.5913 28.5093 15.5778 28.5501 15.5778 28.6044C15.5778 28.6451 15.5913 28.6723 15.6185 28.6995C15.6457 28.7266 15.6864 28.7402 15.7543 28.7538L15.9852 28.8217C16.0938 28.8489 16.1753 28.8896 16.2297 28.9168C16.2976 28.944 16.3519 28.9983 16.3926 29.0662C16.4334 29.1341 16.4605 29.2292 16.447 29.3514C16.447 29.528 16.3791 29.6638 16.2568 29.7453C16.1346 29.8268 15.9852 29.8676 15.7951 29.8676C15.6049 29.8676 15.4555 29.8132 15.3333 29.7181C15.2111 29.6231 15.1432 29.4737 15.1296 29.2971L15.5099 29.2564C15.5099 29.3514 15.5506 29.4193 15.6049 29.4601C15.6592 29.5144 15.7272 29.528 15.8086 29.528C15.8765 29.528 15.9445 29.5144 15.9852 29.4872C16.0259 29.4601 16.0531 29.4193 16.0531 29.365C16.0531 29.3107 16.0395 29.2699 16.0124 29.2428C15.9852 29.2156 15.9173 29.1884 15.8358 29.1749L15.6185 29.1069C15.5234 29.0798 15.4555 29.0526 15.3876 29.0119C15.3333 28.9847 15.279 28.9304 15.2382 28.8625C15.1975 28.7945 15.1839 28.7131 15.1839 28.6044C15.1839 28.4414 15.2518 28.3056 15.3605 28.2241C15.4827 28.129 15.6185 28.0883 15.8086 28.1019C15.9716 28.1019 16.1074 28.1562 16.2161 28.2377C16.3247 28.3328 16.3926 28.455 16.4062 28.6316L16.0395 28.6587C16.0395 28.5908 16.0124 28.5365 15.958 28.4957",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M17.8594 28.4007L17.3976 28.4414L17.5198 29.7589L17.1396 29.7861L17.0173 28.4822L16.542 28.5229L16.5148 28.1426L17.8186 28.0204L17.8594 28.4007Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M19.5435 27.6401L19.9238 29.2971L19.5843 29.3786L19.3126 28.2241H19.299L19.2719 29.4465L19.0138 29.5009L18.457 28.4143H18.4434L18.715 29.5824L18.3755 29.6503L17.9952 28.0068L18.5385 27.8846L19.0003 28.8217V27.7759L19.5435 27.6401Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M20.3855 28.034L21.051 27.7759L21.1733 28.0883L20.5078 28.3464L20.63 28.6588L21.3362 28.4007L21.4585 28.7267L20.3991 29.1341L19.788 27.5586L20.8609 27.1511L20.9831 27.4635L20.2769 27.7352L20.3855 28.034Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M23.0746 27.9117L22.7079 28.1155L21.6893 27.4092H21.6757L22.1918 28.3871L21.8795 28.5501L21.0917 27.056L21.472 26.8523L22.477 27.5314H22.4906L21.9745 26.5807L22.2869 26.4177L23.0746 27.9117Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M23.7265 25.8743L23.3462 26.1324L24.1068 27.219L23.7944 27.4363L23.0339 26.3633L22.6536 26.6214L22.4363 26.3226L23.5092 25.562L23.7265 25.8743Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M24.5143 25.1409L24.7723 25.399L25.2884 24.91L25.5193 25.1545L25.0032 25.6434L25.4514 26.1188L25.1662 26.3769L23.9982 25.1545L24.8266 24.3667L25.0575 24.6112L24.5143 25.1409Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M26.8638 24.163C26.9045 24.2716 26.9045 24.3803 26.8909 24.489C26.8638 24.5976 26.8095 24.7063 26.728 24.8014C26.6057 24.9508 26.4563 25.0458 26.2934 25.073C26.1304 25.1002 25.9674 25.0323 25.7909 24.8964L24.9624 24.2173L25.2069 23.9185L25.9946 24.5705C26.1711 24.7199 26.3205 24.7199 26.4428 24.5705C26.565 24.4211 26.5378 24.2716 26.3613 24.1358L25.5736 23.4839L25.818 23.1851L26.6465 23.8642C26.7551 23.9593 26.8366 24.0543 26.8774 24.163",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M28.1133 22.7368L27.8824 23.1036L26.6465 23.0085L27.57 23.5925L27.3935 23.8913L25.9539 22.9949L26.1847 22.6146L27.3935 22.7232L27.4071 22.7097L26.4835 22.1392L26.6737 21.8404L28.1133 22.7368Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M28.2626 21.6231C28.317 21.5009 28.3306 21.3786 28.2898 21.2836C28.2491 21.1885 28.154 21.107 27.9774 21.0255C27.8145 20.944 27.6787 20.9304 27.5836 20.9576C27.4885 20.9847 27.407 21.0662 27.3527 21.2021L27.2576 21.4058L28.1812 21.8133L28.2762 21.6095L28.2626 21.6231ZM27.4749 20.6044C27.6651 20.5365 27.8824 20.5637 28.1404 20.6859C28.3985 20.8082 28.5614 20.9576 28.6429 21.1341C28.7108 21.3107 28.6973 21.5145 28.6022 21.7182L28.3306 22.3294L26.7823 21.6367L27.0539 21.0255C27.149 20.8082 27.2848 20.6723 27.4749 20.6044Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M15.9988 1.19525C7.82283 1.19525 1.19515 7.82343 1.19515 16C1.19515 24.1766 7.82283 30.8048 15.9988 30.8048C24.1747 30.8048 30.8024 24.1766 30.8024 16C30.8024 7.82343 24.1747 1.19525 15.9988 1.19525ZM31.9975 16C31.9975 24.8421 24.8402 32 15.9988 32C7.15734 32 0 24.8285 0 16C0 7.17148 7.15734 0 15.9988 0C24.8402 0 31.9975 7.15789 31.9975 16Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M15.9988 26.6757C10.1045 26.6757 5.32386 21.8947 5.32386 16C5.32386 10.1052 10.1045 5.32426 15.9988 5.32426C21.893 5.32426 26.6737 10.1052 26.6737 16C26.6737 21.8947 21.893 26.6757 15.9988 26.6757ZM15.9988 4.84888C9.83285 4.84888 4.84851 9.8336 4.84851 16C4.84851 22.1664 9.84643 27.1511 15.9988 27.1511C22.1511 27.1511 27.149 22.1528 27.149 16C27.149 9.84718 22.1511 4.84888 15.9988 4.84888Z",
              fill: primaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M25.4649 16.1086V16.0815C21.9881 15.5925 18.8237 16.8013 15.9988 19.3412C13.1738 16.8013 10.0094 15.5925 6.53259 16.0815V16.1086C10.1588 16.2716 13.1874 18.146 15.6185 20.4142C16.0938 20.8625 16.8408 21.6095 17.2075 22.0577L17.0174 22.2207C16.8544 22.3565 16.8272 22.601 16.9766 22.764C17.1124 22.927 17.3569 22.9541 17.5199 22.8047L17.71 22.6553C17.8322 22.8047 17.9409 22.9406 17.9816 22.9949C17.9816 22.9949 18.3619 23.5653 18.7558 24.1222C19.72 25.4669 20.9559 24.9507 21.0646 24.2716C21.1868 23.5653 20.5485 23.2801 20.0324 23.3616C20.0324 23.3616 19.9238 23.3616 19.8151 23.3209C19.4484 23.1715 18.8508 22.4923 18.457 22.0034L18.8237 21.691C18.9866 21.5552 19.0138 21.3107 18.8644 21.1477C18.7286 20.9847 18.4841 20.9575 18.3212 21.1069L17.9816 21.3922C17.5878 20.9168 17.1803 20.4686 16.7593 20.0475C18.8508 18.1732 21.8387 16.2445 25.4649 16.0951",
              fill: secondaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M17.9002 9.18165L20.0596 9.38539C19.8559 8.57045 18.9052 8.24447 17.9681 8.35313C18.2533 8.0679 19.0003 7.86417 19.6793 7.90491C19.041 7.18505 18.2397 7.15789 17.2619 7.59252L18.1175 6.73683C17.4248 6.4516 16.7322 6.84549 16.4062 7.32087C16.4062 7.10356 16.2025 6.17996 16.0124 6.01697C15.8222 6.17996 15.6185 7.08997 15.6185 7.32087C15.2926 6.84549 14.5999 6.4516 13.9073 6.73683L14.7629 7.59252C13.7986 7.15789 12.9973 7.18505 12.3454 7.90491C13.0245 7.85058 13.7715 8.0679 14.0567 8.35313C13.106 8.23089 12.1689 8.57045 11.9651 9.38539L14.1246 9.18165C13.3233 9.42614 12.6578 9.64345 12.4269 11.0289L14.6271 9.90152C13.9616 10.3633 13.6628 11.7759 14.1517 12.3463C14.3147 11.9796 15.1432 10.5671 15.4691 10.2818C15.7272 12.3192 15.5506 15.6468 15.4012 16.326L15.2111 16.584C14.7901 16.584 14.0431 16.6248 13.6085 16.6927C13.8801 16.8421 14.1382 17.0051 14.3962 17.1681C15.2382 17.073 16.3383 17.0458 16.8408 17.2631C16.2433 17.2631 15.537 17.3854 14.9259 17.5348C15.279 17.7793 15.5506 17.9966 15.7 18.1188C15.7815 18.1867 15.8901 18.2139 15.9988 18.2139C16.1074 18.2139 16.2161 18.1867 16.2976 18.1188C16.61 17.8744 17.5063 17.1817 18.3891 16.7063C17.9681 16.6248 17.2075 16.5976 16.7865 16.584L16.5964 16.326C16.4606 15.6468 16.2704 12.3328 16.5285 10.2818C16.868 10.5671 17.6829 11.9796 17.8458 12.3463C18.3348 11.7759 18.036 10.3769 17.3705 9.90152L19.5843 11.0289C19.3534 9.65704 18.6879 9.42614 17.8866 9.18165",
              fill: secondaryColor
            }
          ),
          /* @__PURE__ */ jsx24(
            "path",
            {
              d: "M14.79 22.0577C14.9802 21.8133 15.2925 21.4873 15.6185 21.1613C15.4827 21.0255 15.3605 20.9168 15.2518 20.8082C15.1296 20.6859 14.9938 20.5637 14.8579 20.455C14.5727 20.7538 14.2875 21.0798 14.0159 21.4058L13.6764 21.107C13.5134 20.9711 13.2689 20.9847 13.1331 21.1477C12.9973 21.3107 13.0109 21.5552 13.1739 21.691L13.5406 22.0034C13.1331 22.4924 12.5491 23.1579 12.1824 23.3209C12.0738 23.3616 11.9651 23.3481 11.9651 23.3481C11.449 23.2666 10.8107 23.5654 10.933 24.2581C11.0416 24.9236 12.2775 25.4533 13.2418 24.1087C13.6356 23.5518 14.0159 22.9813 14.0159 22.9813C14.0702 22.927 14.1653 22.7912 14.2875 22.6418L14.4777 22.7912C14.6406 22.927 14.8851 22.9134 15.0209 22.7504C15.1567 22.5874 15.1432 22.343 14.9802 22.2071L14.79 22.0577Z",
              fill: secondaryColor
            }
          )
        ] }),
        /* @__PURE__ */ jsx24("defs", { children: /* @__PURE__ */ jsx24("clipPath", { id: "clip0_16572_220597", children: /* @__PURE__ */ jsx24("rect", { width: "176", height: "32", fill: "white" }) }) })
      ]
    }
  );
};
TasamaLogo2.displayName = "TasamaLogo";

// src/logos/TasamaTextLogo.tsx
import { useTranslation as useTranslation8 } from "@tasama/i18n";
import { jsx as jsx25 } from "react/jsx-runtime";
function TasamaTextLogo({
  color = "#10069F",
  className,
  testID,
  title,
  ...props
}) {
  const { t } = useTranslation8("common");
  const logoTitle = title || t("logos.tasama");
  return /* @__PURE__ */ jsx25(
    "svg",
    {
      width: "297",
      height: "32",
      viewBox: "0 0 297 32",
      fill: "none",
      className: cn("flex-shrink-0", className),
      "data-testid": testID,
      "aria-label": logoTitle,
      xmlns: "http://www.w3.org/2000/svg",
      ...props,
      children: /* @__PURE__ */ jsx25(
        "path",
        {
          d: "M67.5957 25.5986H48.4072L45.2031 32H38.0479L38.0449 31.9971C41.9683 24.1503 49.9852 19.1963 58.7588 19.1963H70.7988L67.5957 25.5986ZM163.995 25.5986H144.808L141.604 32H134.448L134.445 31.9971C138.369 24.1505 146.385 19.1964 155.158 19.1963H167.199L163.995 25.5986ZM273.196 0C277.643 0 281.512 2.51576 283.446 6.19434V6.19141L296.351 31.9941H289.195L282.797 19.1992L279.595 25.5986H260.407L257.203 32H250.048L250.045 31.9971C253.968 24.1505 261.984 19.1964 270.758 19.1963H282.796L282.459 18.5234L273.196 0ZM196.599 0.0849609C200.123 -0.415395 203.657 1.55389 204.993 5.02246L215.396 31.9971C211.269 31.9971 207.564 29.4586 206.079 25.6045L196.261 0.147461V31.9971H189.861V7.84082C189.861 4.69462 191.764 1.72681 194.876 0.532227C195.333 0.354094 195.795 0.226963 196.258 0.140625L196.599 0.0849609ZM225.802 5.02246C227.181 1.44184 230.903 -0.54147 234.538 0.140625L234.537 0.141602C234.999 0.227937 235.461 0.35436 235.917 0.532227C239.029 1.72682 240.932 4.69464 240.932 7.84082V31.9971H234.532V0.155273L224.72 25.6045C223.232 29.4586 219.527 31.997 215.399 31.9971L225.802 5.02246ZM61.1992 0C65.6464 1.51647e-06 69.5148 2.51575 71.4492 6.19434V6.19141L84.3535 31.9941H77.1982L70.4619 18.5234L61.1992 0ZM125.396 25.5918C125.309 26.0552 125.182 26.5189 125.004 26.9766C123.809 30.0882 120.841 31.9911 117.695 31.9912L93.7949 31.9941V25.5918H125.396ZM157.603 0C162.05 4.58965e-05 165.918 2.51576 167.853 6.19434V6.19141L180.757 31.9941H173.601L166.865 18.5234L157.603 0ZM35.7676 6.39648H21.0732V31.9834H14.6826V13.7598C14.6826 6.15476 20.8315 0.00585938 28.4336 0.00585938H35.7676V6.39648ZM120.516 16.8564C124.096 18.2352 126.081 21.9573 125.398 25.5928L98.2881 15.1348C94.7077 13.7559 92.7244 10.0346 93.4062 6.39941L120.516 16.8564ZM51.5996 19.2021H44.4434L50.7646 6.55469C52.6328 2.68022 56.6052 0 61.1992 0L51.5996 19.2021ZM148.003 19.2021H140.847L147.168 6.55469C149.036 2.68022 153.008 0 157.603 0L148.003 19.2021ZM263.597 19.2021H256.44L262.762 6.55469C264.63 2.68023 268.602 5.18003e-06 273.196 0L263.597 19.2021ZM14.6855 6.39844H0V0H14.6855V6.39844ZM125.007 6.39844H93.4062C93.4926 5.93524 93.6195 5.47209 93.7979 5.01465C94.9924 1.90301 97.9603 2.73718e-05 101.106 0H125.007V6.39844Z",
          fill: color
        }
      )
    }
  );
}
TasamaTextLogo.displayName = "TasamaTextLogo";

// src/logos/TasamaTextLogoWithTagline.tsx
import { useTranslation as useTranslation9 } from "@tasama/i18n";
import { jsx as jsx26, jsxs as jsxs15 } from "react/jsx-runtime";
var TasamaTextLogoWithTagline = ({
  color = "#10069F",
  taglineColor = "#B59E5F",
  width = 297,
  height = 63,
  className,
  testID,
  title,
  ...props
}) => {
  const { t } = useTranslation9("common");
  const logoTitle = title || t("logos.tasamaWithTagline");
  return /* @__PURE__ */ jsxs15(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 297 63",
      fill: "none",
      className: cn("flex-shrink-0", className),
      "data-testid": testID,
      "aria-label": logoTitle,
      xmlns: "http://www.w3.org/2000/svg",
      ...props,
      children: [
        /* @__PURE__ */ jsxs15("g", { clipPath: "url(#clip0_16572_220595)", children: [
          /* @__PURE__ */ jsx26(
            "path",
            {
              d: "M242.428 53.6172C242.989 53.6172 243.529 53.7993 244.051 54.165L244.276 54.3359C244.866 54.8137 245.335 55.476 245.689 56.3193C246.041 57.1627 246.205 58.1014 246.182 59.1318C245.62 59.3851 245.022 59.5776 244.38 59.71C243.738 59.8452 243.16 59.9121 242.639 59.9121C241.804 59.9121 240.983 59.7136 240.18 59.3164C239.797 59.7134 239.267 59.9121 238.585 59.9121H237.722C237.736 60.0415 237.745 60.148 237.745 60.2285C237.745 61.0316 237.373 61.6362 236.631 62.0479C235.888 62.4565 234.665 62.6611 233.082 62.6611C231.499 62.6611 230.296 62.4332 229.467 61.9785C228.641 61.5237 228.226 60.7409 228.226 59.6299C228.226 59.3852 228.243 59.0915 228.28 58.7461C228.318 58.4036 228.372 58.0755 228.444 57.7676L229.368 57.9746C229.311 58.2077 229.265 58.4812 229.228 58.7949C229.19 59.1056 229.173 59.3762 229.173 59.6035C229.173 60.133 229.3 60.5505 229.556 60.8643C229.812 61.178 230.221 61.4058 230.785 61.5469C231.349 61.6879 232.112 61.7598 233.079 61.7598C234.36 61.7598 235.283 61.6391 235.842 61.4004C236.403 61.1615 236.683 60.7928 236.683 60.2979C236.683 60.1597 236.659 60.0301 236.61 59.9092H236.49C235.598 59.9092 234.921 59.6844 234.458 59.2383L235.114 58.582C235.267 58.7288 235.451 58.8352 235.667 58.9043C235.883 58.9733 236.156 59.0078 236.49 59.0078H238.583C238.94 59.0077 239.225 58.9216 239.435 58.7461C239.645 58.5734 239.774 58.3143 239.823 57.9746L239.86 57.6582C239.927 57.0249 240.028 56.4285 240.166 55.8672C240.304 55.309 240.555 54.7941 240.92 54.3223C241.283 53.8532 241.786 53.6173 242.428 53.6172ZM148.289 58.5068C148.289 59.2351 148.144 59.9003 147.856 60.4961C147.569 61.0948 147.151 61.5638 146.607 61.9092C146.063 62.2546 145.424 62.4277 144.685 62.4277C143.945 62.4277 143.309 62.2516 142.768 61.9033C142.229 61.5522 141.815 61.0832 141.527 60.4961C141.24 59.9089 141.096 59.2527 141.096 58.5303C141.096 58.018 141.158 57.5084 141.279 56.9961L142.218 57.1426C142.106 57.6634 142.048 58.1327 142.048 58.5557C142.048 59.1311 142.157 59.6468 142.376 60.0957C142.595 60.5476 142.904 60.899 143.301 61.1494C143.698 61.3997 144.161 61.5263 144.688 61.5264C145.214 61.5264 145.678 61.4028 146.081 61.1553C146.481 60.9077 146.792 60.5563 147.011 60.1016C147.229 59.6469 147.339 59.129 147.339 58.5449V50.9834H148.289V58.5068ZM89.2725 51.2822C89.7645 51.2823 90.248 51.3512 90.7314 51.4951C91.2121 51.639 91.6324 51.8325 91.9922 52.0771L91.5752 52.9463C91.2068 52.7103 90.8119 52.5311 90.3916 52.4131C89.9687 52.2951 89.5773 52.2354 89.209 52.2354C88.6534 52.2354 88.2099 52.3447 87.8789 52.5605C87.5508 52.7764 87.3838 53.0671 87.3838 53.4355C87.3838 53.7636 87.479 54.0281 87.666 54.2324C87.8531 54.4368 88.086 54.5988 88.3652 54.7168C88.6444 54.8348 89.0215 54.9665 89.5049 55.1133C89.9474 55.2536 90.324 55.3873 90.6338 55.5146L90.9189 55.6436C91.2727 55.8076 91.5749 56.049 91.8252 56.3711C92.0756 56.6963 92.1992 57.1168 92.1992 57.6406C92.1992 58.1126 92.0696 58.5299 91.8135 58.8896C91.5544 59.2466 91.1918 59.5259 90.7227 59.7217C90.2535 59.9174 89.7126 60.0156 89.0996 60.0156C88.4865 60.0156 87.89 59.8951 87.3086 59.6533C86.7302 59.4145 86.2327 59.0914 85.8154 58.6914L86.2559 57.8447C86.6473 58.2304 87.0989 58.5272 87.6084 58.7402C88.1206 58.9532 88.6212 59.0595 89.1104 59.0596C89.7321 59.0596 90.2222 58.9391 90.582 58.6973C90.9417 58.4555 91.1201 58.1242 91.1201 57.6982C91.1201 57.3644 91.025 57.0909 90.8379 56.8779C90.648 56.6679 90.4149 56.5008 90.1387 56.3828C89.8624 56.2648 89.4735 56.1271 88.9756 55.9717C88.4028 55.8019 87.939 55.6348 87.585 55.4707C87.2281 55.3067 86.9291 55.0674 86.6787 54.748C86.4283 54.4315 86.3047 54.0141 86.3047 53.499C86.3047 53.0586 86.4282 52.6698 86.6729 52.3359C86.9175 52.002 87.2658 51.7428 87.7148 51.5586C88.1639 51.3744 88.6853 51.2822 89.2725 51.2822ZM41.251 57.2236C41.251 57.7962 41.4092 58.2451 41.7285 58.5703C42.0451 58.8955 42.4827 59.0595 43.0381 59.0596C43.7 59.0509 44.2186 58.827 44.5957 58.3867C44.9728 57.9463 45.1602 57.3702 45.1602 56.6592V53.502H46.1152V59.9463H45.1602V58.4873C44.7428 59.4659 43.9246 59.9642 42.707 59.9814C41.9561 59.9814 41.3663 59.7564 40.9375 59.3076C40.5086 58.8586 40.2949 58.245 40.2949 57.4678V53.499H41.251V57.2236ZM118.105 53.4609C118.621 53.461 119.09 53.545 119.51 53.7119C119.93 53.8788 120.293 54.1149 120.595 54.417L120.068 55.0791C119.547 54.5725 118.905 54.3193 118.146 54.3193C117.702 54.3194 117.311 54.4198 116.963 54.624C116.615 54.8284 116.344 55.1139 116.148 55.4766C115.953 55.8392 115.854 56.2477 115.854 56.7197C115.854 57.1918 115.953 57.6119 116.148 57.9746C116.344 58.3372 116.615 58.6228 116.963 58.8271C117.311 59.0313 117.723 59.1347 118.146 59.1348C118.516 59.1348 118.855 59.0799 119.162 58.9697L119.288 58.9219C119.628 58.778 119.913 58.5733 120.141 58.3027L120.693 58.8672C120.4 59.2183 120.031 59.4918 119.591 59.6875C119.15 59.8832 118.655 59.9814 118.108 59.9814C117.487 59.9814 116.934 59.8428 116.453 59.5693C115.97 59.2959 115.593 58.913 115.319 58.418C115.043 57.9229 114.907 57.3472 114.907 56.7197C114.907 56.0923 115.046 55.5312 115.319 55.0361C115.593 54.5439 115.973 54.1552 116.459 53.8789C116.943 53.5997 117.492 53.4609 118.105 53.4609ZM50.1602 53.4502C50.5341 53.4502 50.9022 53.502 51.2617 53.6084C51.6215 53.7149 51.9386 53.8586 52.2178 54.0371L51.8496 54.7705C51.5963 54.6065 51.3138 54.4799 51.0029 54.3906C50.6923 54.3015 50.3901 54.2559 50.0967 54.2559C49.714 54.2559 49.406 54.3279 49.1729 54.4717C48.9426 54.6156 48.8242 54.8343 48.8242 55.1279C48.8242 55.3294 48.8901 55.4994 49.0195 55.6289C49.149 55.7612 49.3132 55.8647 49.5088 55.9424C49.7045 56.0201 49.9758 56.1122 50.3184 56.2188C50.7673 56.3569 51.133 56.4926 51.415 56.6221C51.6969 56.7545 51.9362 56.941 52.1318 57.1855C52.3276 57.4302 52.4248 57.7532 52.4248 58.1533C52.4248 58.7518 52.1978 59.2038 51.7461 59.5146C51.2942 59.8255 50.7322 59.9805 50.0615 59.9805C49.5953 59.9804 49.1405 59.9005 48.7002 59.7422C48.2598 59.5839 47.8882 59.3592 47.5859 59.0742L47.9551 58.375C48.2256 58.6253 48.5539 58.8269 48.9424 58.9736C49.3309 59.1204 49.7169 59.1953 50.0996 59.1953C50.5081 59.1953 50.8416 59.1141 51.0977 58.9502C51.3538 58.7861 51.4834 58.5384 51.4834 58.2275C51.4833 57.9169 51.3567 57.6808 51.1035 57.5283C50.8502 57.3758 50.4672 57.2229 49.9521 57.0762C49.5207 56.9467 49.1699 56.817 48.8994 56.6904C48.6288 56.5638 48.3979 56.3854 48.2051 56.1465C48.0124 55.9076 47.917 55.5965 47.917 55.2139C47.9171 54.6326 48.1278 54.1948 48.5537 53.8955C48.9797 53.5992 49.5155 53.4502 50.1602 53.4502ZM74.2295 53.4502C74.6036 53.4502 74.9723 53.5019 75.332 53.6084C75.6916 53.7149 76.008 53.8587 76.2871 54.0371L75.9189 54.7705C75.6657 54.6065 75.3831 54.4798 75.0723 54.3906C74.7616 54.3015 74.4594 54.2559 74.166 54.2559C73.7804 54.2559 73.4724 54.3278 73.2393 54.4717C73.009 54.6156 72.8906 54.8343 72.8906 55.1279C72.8906 55.3294 72.9574 55.4994 73.0869 55.6289C73.2164 55.7612 73.3806 55.8647 73.5762 55.9424C73.7719 56.0201 74.0424 56.1123 74.3848 56.2188C74.7214 56.3223 75.0111 56.4243 75.2539 56.5234L75.4844 56.6221C75.7663 56.7544 76.0055 56.941 76.2012 57.1855C76.3969 57.4302 76.4941 57.7532 76.4941 58.1533C76.4941 58.7519 76.2672 59.2038 75.8154 59.5146C75.3636 59.8255 74.8023 59.9804 74.1318 59.9805C73.6655 59.9805 73.2099 59.9005 72.7695 59.7422C72.3293 59.5839 71.9584 59.3591 71.6562 59.0742L72.0244 58.375C72.295 58.6254 72.6232 58.8269 73.0117 58.9736C73.4003 59.1204 73.7833 59.1953 74.1689 59.1953C74.5776 59.1953 74.9118 59.1143 75.168 58.9502C75.4239 58.7861 75.5537 58.5383 75.5537 58.2275C75.5536 57.917 75.4269 57.6837 75.1738 57.5283C74.9206 57.3758 74.5376 57.2229 74.0225 57.0762C73.5907 56.9466 73.2393 56.8171 72.9688 56.6904C72.6982 56.5638 72.4673 56.3854 72.2744 56.1465C72.0818 55.9076 71.9873 55.5965 71.9873 55.2139C71.9874 54.6326 72.1971 54.1948 72.623 53.8955C73.049 53.5991 73.5848 53.4502 74.2295 53.4502ZM79.7822 53.4502C80.1562 53.4502 80.5242 53.502 80.8838 53.6084C81.2436 53.7149 81.5606 53.8586 81.8398 54.0371L81.4717 54.7705C81.2184 54.6064 80.9359 54.4799 80.625 54.3906C80.3143 54.3015 80.0122 54.2559 79.7188 54.2559C79.3332 54.2559 79.0251 54.3279 78.792 54.4717C78.5617 54.6156 78.4434 54.8343 78.4434 55.1279C78.4434 55.3294 78.5092 55.4965 78.6387 55.6289C78.7681 55.7612 78.9324 55.8647 79.1279 55.9424C79.3237 56.0201 79.595 56.1122 79.9375 56.2188C80.2741 56.3223 80.5639 56.4243 80.8066 56.5234L81.0371 56.6221C81.319 56.7545 81.5583 56.941 81.7539 57.1855C81.9496 57.4302 82.0469 57.7532 82.0469 58.1533C82.0468 58.7518 81.8199 59.2038 81.3682 59.5146C80.9162 59.8255 80.3543 59.9805 79.6836 59.9805C79.2174 59.9804 78.7626 59.9005 78.3223 59.7422C77.8819 59.5839 77.5102 59.3592 77.208 59.0742L77.5771 58.375C77.8477 58.6253 78.176 58.8269 78.5645 58.9736C78.9501 59.1204 79.3361 59.1953 79.7217 59.1953C80.1302 59.1953 80.4636 59.1141 80.7197 58.9502C80.9759 58.7861 81.1055 58.5384 81.1055 58.2275C81.1054 57.9169 80.9788 57.6808 80.7256 57.5283C80.4723 57.3758 80.0892 57.2229 79.5742 57.0762C79.1427 56.9467 78.7919 56.817 78.5215 56.6904C78.2509 56.5638 78.02 56.3854 77.8271 56.1465C77.6345 55.9076 77.5391 55.5965 77.5391 55.2139C77.5392 54.6326 77.7499 54.1948 78.1758 53.8955C78.6017 53.5992 79.1376 53.4502 79.7822 53.4502ZM131.05 53.4502C131.424 53.4502 131.793 53.5019 132.152 53.6084C132.512 53.7149 132.828 53.8587 133.107 54.0371L132.739 54.7705C132.486 54.6066 132.204 54.4798 131.894 54.3906C131.583 54.3014 131.28 54.2559 130.986 54.2559C130.604 54.2559 130.296 54.3278 130.062 54.4717C129.832 54.6156 129.714 54.8343 129.714 55.1279C129.714 55.3294 129.781 55.4994 129.91 55.6289C130.04 55.7612 130.204 55.8647 130.399 55.9424C130.595 56.0201 130.866 56.1123 131.208 56.2188C131.657 56.3569 132.023 56.4925 132.305 56.6221C132.587 56.7544 132.826 56.941 133.021 57.1855C133.217 57.4302 133.315 57.7532 133.315 58.1533C133.315 58.7519 133.088 59.2038 132.636 59.5146C132.184 59.8255 131.623 59.9804 130.952 59.9805C130.486 59.9805 130.03 59.9005 129.59 59.7422C129.15 59.5839 128.779 59.3591 128.477 59.0742L128.845 58.375C129.115 58.6254 129.443 58.8268 129.832 58.9736C130.221 59.1204 130.606 59.1953 130.989 59.1953C131.398 59.1953 131.732 59.1143 131.988 58.9502C132.244 58.7861 132.374 58.5383 132.374 58.2275C132.374 57.9169 132.247 57.6808 131.994 57.5283C131.741 57.3758 131.358 57.223 130.843 57.0762C130.411 56.9466 130.06 56.8171 129.789 56.6904C129.519 56.5638 129.289 56.3854 129.096 56.1465C128.903 55.9076 128.808 55.5967 128.808 55.2139C128.808 54.6326 129.017 54.1948 129.443 53.8955C129.869 53.5991 130.405 53.4502 131.05 53.4502ZM67.8594 53.4639C68.8783 53.4639 69.6499 53.787 70.168 54.4375C70.6858 55.085 70.9219 55.9655 70.873 57.0762H65.6025C65.686 57.7123 65.9392 58.2256 66.3623 58.6084C66.7853 58.9911 67.3122 59.1835 67.9424 59.1836C68.3338 59.1836 68.7025 59.1116 69.0449 58.9678C69.3875 58.8239 69.6871 58.6223 69.9404 58.3604L70.4668 58.9102C70.156 59.2555 69.7791 59.518 69.333 59.7021C68.887 59.8863 68.3976 59.9785 67.8682 59.9785C67.2378 59.9785 66.6818 59.8399 66.1953 59.5664C65.709 59.293 65.3321 58.9101 65.0615 58.415C64.791 57.9199 64.6553 57.3443 64.6553 56.7168C64.6553 56.0894 64.791 55.5282 65.0615 55.0332C65.3321 54.5411 65.709 54.1523 66.1953 53.876C66.6211 53.6316 67.0978 53.4951 67.627 53.4648L67.8594 53.4639ZM96.6289 53.4639C97.6479 53.4639 98.4194 53.787 98.9375 54.4375C99.4555 55.0851 99.6914 55.9654 99.6426 57.0762H94.373C94.4565 57.7123 94.7097 58.2256 95.1328 58.6084C95.5559 58.991 96.0827 59.1836 96.7129 59.1836C97.1043 59.1836 97.4729 59.1117 97.8154 58.9678C98.1578 58.8239 98.4567 58.6222 98.71 58.3604L99.2373 58.9102C98.9265 59.2555 98.5496 59.5179 98.1035 59.7021C97.6574 59.8864 97.1673 59.9785 96.6377 59.9785C96.0075 59.9785 95.4522 59.8398 94.9658 59.5664C94.4794 59.293 94.1016 58.9101 93.8311 58.415C93.5606 57.92 93.4258 57.3442 93.4258 56.7168C93.4258 56.0895 93.5606 55.5282 93.8311 55.0332C94.1016 54.541 94.4794 54.1523 94.9658 53.876C95.3914 53.6317 95.8676 53.4951 96.3965 53.4648L96.6289 53.4639ZM124.68 53.4639C125.699 53.4639 126.47 53.787 126.988 54.4375C127.506 55.085 127.742 55.9655 127.693 57.0762H122.423C122.506 57.7123 122.759 58.2256 123.183 58.6084C123.606 58.9912 124.136 59.1836 124.764 59.1836C125.155 59.1835 125.523 59.1115 125.865 58.9678C126.208 58.8239 126.507 58.6223 126.761 58.3604L127.287 58.9102C126.976 59.2555 126.599 59.518 126.153 59.7021C125.707 59.8863 125.218 59.9785 124.688 59.9785C124.058 59.9785 123.502 59.8399 123.016 59.5664C122.529 59.293 122.152 58.91 121.882 58.415C121.611 57.92 121.476 57.3443 121.476 56.7168C121.476 56.0894 121.611 55.5282 121.882 55.0332C122.152 54.5411 122.529 54.1523 123.016 53.876C123.441 53.6316 123.918 53.4951 124.447 53.4648L124.68 53.4639ZM35.4912 51.3662C36.3576 51.3662 37.0371 51.5556 37.5322 51.9355C38.0272 52.3154 38.2743 52.8393 38.2744 53.5098C38.2744 54.0076 38.1335 54.428 37.8516 54.7705C37.5695 55.113 37.1777 55.3378 36.6797 55.4443C37.2985 55.5336 37.7825 55.7696 38.125 56.1553C38.4675 56.541 38.6406 57.028 38.6406 57.6152C38.6405 58.3431 38.3668 58.9127 37.8203 59.3242C37.2734 59.7358 36.513 59.9434 35.54 59.9434H31.876V51.3662H35.4912ZM108.146 58.9736L110.192 53.4961H111.16L108.624 59.9434H107.631L105.069 53.4961H106.088L108.146 58.9736ZM54.9404 59.9404H53.9971V53.4961H54.9404V59.9404ZM60.7236 53.458C61.4921 53.458 62.0936 53.6799 62.5254 54.126C62.96 54.5721 63.1758 55.1887 63.1758 55.9717V59.9404H62.2207V56.2158C62.2207 55.6462 62.0594 55.1972 61.7373 54.8691C61.4149 54.5439 60.9654 54.3799 60.3955 54.3799C59.7653 54.3885 59.2618 54.5811 58.8818 54.9609C58.5019 55.3408 58.2832 55.8503 58.2256 56.4863V59.9404H57.2695V53.4961H58.2256V54.918C58.666 53.9595 59.4974 53.4753 60.7236 53.458ZM104.557 54.3877C103.886 54.3819 103.347 54.5663 102.938 54.9463C102.53 55.3262 102.297 55.8478 102.239 56.5098V59.9404H101.284V53.4961H102.239L102.242 54.9268C102.455 54.4518 102.758 54.0912 103.149 53.8408C103.541 53.5935 104.01 53.4637 104.557 53.4551V54.3877ZM113.232 59.9404H112.288V53.4961H113.232V59.9404ZM192.567 59.9092L186.042 59.9209C185.222 59.9208 184.589 59.702 184.143 59.2646C183.696 58.8272 183.472 58.2224 183.472 57.4512C183.472 56.9331 183.576 56.4804 183.783 56.0947C183.99 55.7092 184.292 55.3606 184.689 55.0527L185.455 55.5996C185.098 55.8817 184.836 56.1703 184.669 56.4639C184.502 56.7545 184.419 57.0798 184.419 57.4365C184.419 57.9258 184.563 58.3087 184.856 58.5879C185.15 58.8671 185.567 59.0078 186.111 59.0078H191.618V54.2012H192.567V59.9092ZM175.861 59.9092H169.849V59.0078H171.492V52.7012H172.441V59.0078H174.914V50.9863H175.861V59.9092ZM178.806 59.9092H177.856V50.9863H178.806V59.9092ZM195.51 59.0078H196.995C197.352 59.0078 197.637 58.9216 197.847 58.7461C198.057 58.5734 198.186 58.3143 198.235 57.9746L198.272 57.6582C198.339 57.0251 198.44 56.4293 198.578 55.8682C198.716 55.3098 198.967 54.7943 199.332 54.3223C199.695 53.8531 200.199 53.6172 200.841 53.6172C201.482 53.6173 202.096 53.8554 202.686 54.333C203.276 54.8108 203.745 55.4732 204.099 56.3164C204.45 57.1598 204.614 58.0984 204.591 59.1289C204.03 59.3822 203.431 59.5756 202.789 59.708C202.147 59.8433 201.569 59.9092 201.048 59.9092C200.213 59.9092 199.393 59.7106 198.59 59.3135C198.207 59.7106 197.677 59.9091 196.995 59.9092H194.56V50.9863H195.51V59.0078ZM248.715 59.0078H250.396C250.916 59.0078 251.279 58.9133 251.486 58.7178C251.694 58.5221 251.798 58.1907 251.798 57.7188V55.4531H252.747V57.7305C252.747 58.1274 252.701 58.4468 252.612 58.6914C252.883 58.9015 253.294 59.0078 253.807 59.0078C254.319 59.0078 254.675 58.9132 254.877 58.7178C255.081 58.5221 255.183 58.1907 255.183 57.7188V54.8457H256.133V57.7305C256.133 58.0872 256.089 58.4066 256 58.6914C256.259 58.9015 256.679 59.0078 257.191 59.0078C257.704 59.0078 258.061 58.9135 258.263 58.7178C258.467 58.5221 258.567 58.1907 258.567 57.7188V54.2354H259.518V57.7305C259.518 58.113 259.477 58.4294 259.396 58.6797C259.681 58.8984 260.047 59.0078 260.493 59.0078H262.609V54.2012H263.57V59.9092H260.721C259.998 59.9092 259.419 59.7279 258.99 59.3623C258.625 59.7276 258.055 59.9092 257.324 59.9092C256.593 59.9092 256.014 59.731 255.594 59.374C255.245 59.7306 254.67 59.9091 253.939 59.9092C253.208 59.9092 252.632 59.7309 252.209 59.374C251.852 59.7308 251.271 59.9092 250.468 59.9092H247.765V50.9863H248.715V59.0078ZM151.236 59.0049H152.722C153.078 59.0049 153.363 58.9186 153.573 58.7432C153.783 58.5705 153.913 58.3113 153.962 57.9717L154 57.6553C154.066 57.0222 154.167 56.4264 154.305 55.8652C154.443 55.3069 154.693 54.7913 155.059 54.3193C155.421 53.8501 155.925 53.6143 156.567 53.6143C157.157 53.6143 157.731 53.8179 158.277 54.2266C158.827 54.6353 159.282 55.2054 159.647 55.9307C160.013 56.656 160.226 57.4742 160.292 58.3838C160.591 58.7981 161.052 59.0049 161.668 59.0049H163.823C163.271 58.4293 162.995 57.7354 162.995 56.9238C162.995 56.3166 163.144 55.7667 163.443 55.2803C163.743 54.7938 164.152 54.4081 164.673 54.1289C165.191 53.8497 165.761 53.709 166.377 53.709C167.091 53.709 167.888 53.9076 168.763 54.3047L168.337 55.0967C167.589 54.7743 166.935 54.6094 166.377 54.6094C165.914 54.6094 165.496 54.7136 165.122 54.915C164.751 55.1194 164.46 55.3958 164.253 55.7441C164.046 56.0923 163.941 56.4834 163.941 56.9121C163.941 57.3453 164.05 57.7326 164.264 58.0732L164.368 58.2139C164.647 58.5878 165.021 58.8495 165.493 59.0049H168.999V59.9062H161.9C161.098 59.9062 160.476 59.6874 160.038 59.25C159.503 59.46 158.942 59.6241 158.357 59.7363C157.773 59.8486 157.244 59.9062 156.774 59.9062C155.94 59.9062 155.119 59.7077 154.316 59.3105C153.934 59.7077 153.404 59.9062 152.722 59.9062H150.286V50.9834H151.236V59.0049ZM223.759 59.9033H213.094C212.671 59.9033 212.322 59.8548 212.046 59.7627C211.77 59.6706 211.54 59.5289 211.353 59.3418C210.331 59.716 209.179 59.9033 207.896 59.9033C207.075 59.9033 206.304 59.8223 205.581 59.6582V58.7344C206.304 58.9128 207.078 59.002 207.907 59.002C208.987 59.0019 209.974 58.853 210.866 58.5537C210.791 58.3839 210.707 58.147 210.609 57.8477L209.038 53.2568L209.939 52.9775L211.496 57.5664C211.649 58.0208 211.778 58.3401 211.879 58.5215C211.983 58.7057 212.118 58.8293 212.288 58.8926C212.458 58.9588 212.725 58.9912 213.091 58.9912H213.822V59.002H219.069V56.8721C219.069 56.1583 218.845 55.5881 218.398 55.1621C217.949 54.7364 217.391 54.5235 216.62 54.5234C215.849 54.5234 215.163 54.7513 214.562 55.2061L214.124 54.3906C214.864 53.8811 215.696 53.625 216.62 53.625C217.285 53.625 217.872 53.7637 218.385 54.04C218.897 54.3164 219.297 54.7022 219.585 55.1914C219.873 55.6836 220.017 56.2448 220.017 56.8779V59.0078H222.806L222.809 59.002V50.9805H223.759V59.9033ZM32.8604 59.0342H35.4453C36.0596 59.0417 36.5527 58.9431 36.9229 58.7373L37.0742 58.6396C37.457 58.372 37.6504 57.969 37.6504 57.4883C37.6504 57.0076 37.4569 56.633 37.0684 56.3682C36.6798 56.1035 36.1387 55.9738 35.4453 55.9824H32.8604V59.0342ZM156.613 54.5146C156.207 54.5146 155.888 54.6878 155.657 55.0332C155.427 55.3784 155.266 55.7668 155.177 56.2012C155.088 56.6357 155.016 57.1282 154.958 57.6807C154.958 57.6568 154.941 57.8012 154.909 58.1074C154.886 58.2599 154.849 58.4068 154.8 58.5449C155.45 58.8529 156.113 59.0078 156.786 59.0078C157.08 59.0078 157.389 58.9858 157.718 58.9404L158.05 58.8848C158.505 58.8042 158.939 58.6857 159.354 58.5303C159.305 57.8251 159.144 57.1629 158.873 56.5469C158.602 55.9309 158.265 55.4358 157.862 55.0674C157.459 54.699 157.045 54.5147 156.613 54.5146ZM200.887 54.5146C200.481 54.5146 200.161 54.6878 199.931 55.0332C199.701 55.3784 199.539 55.7668 199.45 56.2012C199.361 56.6357 199.289 57.1282 199.231 57.6807C199.231 57.6569 199.214 57.8012 199.183 58.1074C199.16 58.2599 199.122 58.4068 199.073 58.5449C199.724 58.8528 200.386 59.0078 201.06 59.0078C201.351 59.0078 201.661 58.9857 201.99 58.9404L202.32 58.8848C202.775 58.8042 203.21 58.6857 203.624 58.5303C203.575 57.8251 203.414 57.1628 203.144 56.5469C202.873 55.9309 202.533 55.4358 202.133 55.0674C201.733 54.699 201.316 54.5147 200.887 54.5146ZM242.475 54.5146C242.069 54.5146 241.749 54.6878 241.519 55.0332C241.288 55.3785 241.127 55.7668 241.038 56.2012C240.949 56.6357 240.877 57.1282 240.819 57.6807C240.819 57.6568 240.802 57.8012 240.771 58.1074C240.747 58.2599 240.71 58.4068 240.661 58.5449C241.311 58.8527 241.973 59.0077 242.646 59.0078C242.938 59.0078 243.249 58.9857 243.578 58.9404L243.91 58.8838C244.365 58.8032 244.8 58.6857 245.215 58.5303C245.166 57.8251 245.007 57.1629 244.733 56.5469C244.463 55.931 244.127 55.4358 243.724 55.0674C243.321 54.6991 242.903 54.5148 242.475 54.5146ZM232.869 52.8076C233.448 53.9101 233.519 55.2119 233.067 56.3604L232.596 56.1982C232.811 55.4471 232.776 54.6383 232.497 53.9131C232.411 53.68 232.287 53.4295 232.169 53.2568L232.869 52.8076ZM67.8535 54.2529C67.2346 54.2529 66.7193 54.4402 66.3105 54.8115C65.902 55.1856 65.6659 55.6834 65.5996 56.3135H70.0098C69.978 55.6833 69.7674 55.1828 69.3789 54.8115C68.9903 54.4402 68.4724 54.2529 67.8535 54.2529ZM96.626 54.2529C96.0074 54.253 95.4926 54.4404 95.084 54.8115C94.6753 55.1856 94.4393 55.6833 94.373 56.3135H98.7822C98.7505 55.6834 98.5407 55.1827 98.1523 54.8115C97.7638 54.4402 97.2448 54.2529 96.626 54.2529ZM124.674 54.2529C124.055 54.253 123.54 54.4402 123.131 54.8115C122.722 55.1856 122.486 55.6834 122.42 56.3135H126.83C126.798 55.6833 126.588 55.1828 126.199 54.8115C125.811 54.4402 125.293 54.2529 124.674 54.2529ZM32.8574 55.0762H35.4424C36.0207 55.0762 36.4696 54.9524 36.7891 54.7021C37.1085 54.4546 37.2675 54.0946 37.2676 53.6543C37.2676 53.2139 37.1086 52.8736 36.7891 52.6318C36.4725 52.3903 36.0235 52.2695 35.4424 52.2695H32.8574V55.0762ZM263.884 52.1025C264.054 52.1025 264.192 52.1603 264.305 52.2754C264.417 52.3905 264.472 52.5345 264.472 52.7129C264.472 52.8913 264.417 53.0383 264.305 53.1562C264.192 53.2743 264.051 53.335 263.884 53.335C263.717 53.3349 263.579 53.2741 263.467 53.1562C263.355 53.0354 263.3 52.8914 263.3 52.7129C263.3 52.5344 263.355 52.3905 263.467 52.2754C263.576 52.1603 263.714 52.1026 263.884 52.1025ZM187.447 52.0996C187.617 52.0997 187.755 52.1574 187.867 52.2725C187.979 52.3876 188.034 52.5315 188.034 52.71C188.034 52.8884 187.977 53.0353 187.867 53.1533C187.755 53.2741 187.614 53.332 187.447 53.332C187.277 53.332 187.139 53.2713 187.026 53.1533C186.914 53.0325 186.859 52.8883 186.859 52.71C186.859 52.5316 186.914 52.3876 187.026 52.2725C187.139 52.1573 187.277 52.0996 187.447 52.0996ZM188.993 52.0996C189.163 52.0997 189.301 52.1574 189.413 52.2725C189.525 52.3876 189.58 52.5315 189.58 52.71C189.58 52.8884 189.522 53.0353 189.413 53.1533C189.301 53.2741 189.16 53.3319 188.993 53.332C188.823 53.332 188.685 53.2713 188.572 53.1533C188.46 53.0324 188.405 52.8884 188.405 52.71C188.405 52.5316 188.46 52.3876 188.572 52.2725C188.685 52.1573 188.823 52.0996 188.993 52.0996ZM262.341 52.0996C262.508 52.0996 262.647 52.1573 262.759 52.2725C262.871 52.3876 262.926 52.5316 262.926 52.71C262.926 52.8884 262.871 53.0353 262.759 53.1533C262.649 53.2742 262.508 53.332 262.341 53.332C262.171 53.3319 262.033 53.2712 261.921 53.1533C261.809 53.0324 261.754 52.8884 261.754 52.71C261.754 52.5315 261.809 52.3876 261.921 52.2725C262.033 52.1574 262.174 52.0997 262.341 52.0996ZM216.643 51.2646C216.821 51.2646 216.968 51.3285 217.086 51.4492C217.204 51.573 217.262 51.726 217.262 51.9131C217.262 52.1 217.204 52.2553 217.086 52.3818C216.971 52.5055 216.821 52.5693 216.643 52.5693C216.464 52.5692 216.318 52.5082 216.2 52.3818C216.082 52.2553 216.024 52.1 216.024 51.9131C216.024 51.726 216.082 51.5701 216.2 51.4492C216.315 51.3257 216.464 51.2648 216.643 51.2646ZM54.46 50.9863C54.6384 50.9863 54.7882 51.0492 54.9062 51.1758C55.0242 51.3023 55.0849 51.4577 55.085 51.6475C55.085 51.8374 55.0243 51.9905 54.9062 52.1143C54.7882 52.238 54.6384 52.2988 54.46 52.2988C54.2816 52.2988 54.1317 52.238 54.0137 52.1143C53.8957 51.9905 53.8359 51.8345 53.8359 51.6475C53.836 51.4606 53.8958 51.3023 54.0137 51.1758C54.1317 51.0492 54.2816 50.9864 54.46 50.9863ZM112.754 50.9863C112.932 50.9863 113.082 51.0492 113.2 51.1758C113.318 51.3023 113.379 51.4577 113.379 51.6475C113.379 51.8374 113.318 51.9905 113.2 52.1143C113.082 52.2379 112.932 52.2988 112.754 52.2988C112.576 52.2987 112.426 52.2378 112.309 52.1143C112.191 51.9905 112.13 51.8346 112.13 51.6475C112.13 51.4605 112.191 51.3023 112.309 51.1758C112.426 51.0494 112.576 50.9864 112.754 50.9863ZM172.128 49.9902C172.272 49.9902 172.422 50.0074 172.571 50.0391C172.721 50.0736 172.848 50.1163 172.954 50.1738L172.687 50.7207C172.629 50.6862 172.545 50.6608 172.436 50.6406C172.326 50.6205 172.226 50.6084 172.137 50.6084C171.958 50.6084 171.815 50.6461 171.711 50.7236C171.604 50.7984 171.553 50.8966 171.553 51.0088C171.553 51.1124 171.595 51.2076 171.682 51.2939C171.768 51.3802 171.872 51.4258 171.992 51.4346H173.052V52.0596H170.861V51.4375H171.106C171.017 51.285 170.971 51.121 170.971 50.9512C170.971 50.6664 171.077 50.4362 171.293 50.2578C171.509 50.0794 171.788 49.9902 172.128 49.9902Z",
              fill: taglineColor
            }
          ),
          /* @__PURE__ */ jsx26(
            "path",
            {
              d: "M67.5918 25.5977H48.4043L45.2002 31.9971H38.041C41.9643 24.1533 49.9843 19.1963 58.7549 19.1963H70.7949L67.5918 25.5977ZM125.394 25.5977C125.307 26.0611 125.178 26.5248 125.002 26.9824C123.807 30.094 120.842 31.9971 117.693 31.9971H93.7939V25.5977H125.394ZM163.99 25.5977H144.803L141.6 31.9971H134.44C138.364 24.1535 146.383 19.1965 155.153 19.1963H167.194L163.99 25.5977ZM196.258 0.140625C199.893 -0.541545 203.615 1.44188 204.994 5.02246L215.396 31.9971C211.269 31.997 207.567 29.4548 206.079 25.6035L196.264 0.155273V31.9971H189.862V7.84082C189.862 4.69476 191.764 1.72682 194.876 0.532227C195.217 0.398401 195.563 0.294278 195.909 0.213867L196.258 0.140625ZM225.8 5.02246C227.179 1.44212 230.9 -0.541291 234.535 0.143555C234.996 0.227015 235.459 0.356689 235.914 0.532227C239.026 1.72677 240.929 4.69183 240.929 7.84082V31.9971H234.529V0.155273L224.714 25.6035C223.229 29.4549 219.524 31.9971 215.396 31.9971L225.8 5.02246ZM273.608 0.00683594C277.883 0.157721 281.57 2.62786 283.446 6.19434L296.35 31.9971L296.347 31.9941H289.191L282.79 19.1934L279.586 25.5947H260.398L257.194 31.9941H250.039C253.962 24.1504 261.981 19.1934 270.752 19.1934H282.793L282.456 18.5225L273.193 0L273.608 0.00683594ZM61.1982 0C65.6453 0 69.5139 2.51291 71.4482 6.19141L84.3525 31.9941H77.1973L70.4609 18.5225L61.1982 0ZM158.016 0.00683594C162.288 0.157691 165.977 2.62776 167.851 6.19141L180.755 31.9941H173.599L166.863 18.5225L157.601 0L158.016 0.00683594ZM35.7676 6.39551H21.0732V31.9854H14.6826V13.7588C14.6827 6.15402 20.8317 0.00585938 28.4336 0.00585938H35.7676V6.39551ZM125.002 6.40137H93.4111L120.512 16.8564C124.092 18.2352 126.076 21.9565 125.394 25.5918L98.2842 15.1348C94.7035 13.753 92.7202 10.0338 93.4023 6.39844C93.4887 5.93523 93.6185 5.4721 93.7939 5.01465C94.9885 1.90305 97.9535 0 101.103 0H125.002V6.40137ZM51.5986 19.2021H44.4434L50.7646 6.55469C52.6327 2.68039 56.6044 0.000121121 61.1982 0L51.5986 19.2021ZM148.001 19.2021H140.845L147.166 6.55469C149.034 2.6803 153.007 0 157.601 0L148.001 19.2021ZM263.594 19.2021H256.438L262.759 6.55469C264.627 2.6803 268.599 0 273.193 0L263.594 19.2021ZM14.6855 6.39844H0V0H14.6855V6.39844Z",
              fill: color
            }
          )
        ] }),
        /* @__PURE__ */ jsx26("defs", { children: /* @__PURE__ */ jsx26("clipPath", { id: "clip0_16572_220595", children: /* @__PURE__ */ jsx26("rect", { width: "296.35", height: "62.6608", fill: "white" }) }) })
      ]
    }
  );
};
TasamaTextLogoWithTagline.displayName = "TasamaTextLogoWithTagline";

// src/components/atoms/ProfileLogo.tsx
import { jsx as jsx27 } from "react/jsx-runtime";
var ProfileLogo = ({
  className,
  testID = "profile-logo"
}) => {
  return /* @__PURE__ */ jsx27(
    TasamaLogo,
    {
      className: cn("w-full h-full", className),
      testID: `${testID}-fallback-logo`
    }
  );
};
ProfileLogo.displayName = "ProfileLogo";

// src/components/atoms/Loader.tsx
import * as React9 from "react";
import { useTranslation as useTranslation10 } from "@tasama/i18n";
import { jsx as jsx28, jsxs as jsxs16 } from "react/jsx-runtime";
var sizeMap = {
  sm: {
    containerSize: 24,
    dotSize: 3,
    gap: 16,
    label: { fontSize: 14, lineHeight: "1.4285714285714286em" }
  },
  md: {
    containerSize: 32,
    dotSize: 4,
    gap: 16,
    label: { fontSize: 14, lineHeight: "1.4285714285714286em" }
  },
  lg: {
    containerSize: 40,
    dotSize: 5,
    gap: 16,
    label: { fontSize: 18, lineHeight: "1.5555555555555556em" }
  },
  xl: {
    containerSize: 48,
    dotSize: 6,
    gap: 20,
    label: { fontSize: 18, lineHeight: "1.5555555555555556em" }
  }
};
var DOT_COUNT = 20;
var generateDotPositions = (containerSize, dotSize) => {
  const radius = (containerSize - dotSize) / 2;
  const center = containerSize / 2;
  const angleStep = 3 * Math.PI / DOT_COUNT;
  return Array.from({ length: DOT_COUNT }, (_, index) => {
    const angle = index * angleStep - Math.PI / 2;
    return {
      cx: center + radius * Math.cos(angle),
      cy: center + radius * Math.sin(angle)
    };
  });
};
var Loader2 = ({
  size = "md",
  showLabel = true,
  label,
  className,
  testID,
  labelClassname = "",
  color = "var(--color-brand-blue-500)",
  ...rest
}) => {
  const { t } = useTranslation10("common");
  const resolvedLabel = label ?? t("labels.loading");
  const { containerSize, dotSize, gap, label: labelStyle } = sizeMap[size];
  const dotPositions = React9.useMemo(
    () => generateDotPositions(containerSize, dotSize),
    [containerSize, dotSize]
  );
  const spinDuration = size === "xl" || size === "lg" ? "1200ms" : size === "md" ? "1000ms" : "900ms";
  return /* @__PURE__ */ jsxs16(
    "div",
    {
      "data-testid": testID,
      className: cn("flex flex-col items-center", className),
      style: { gap },
      ...rest,
      children: [
        /* @__PURE__ */ jsx28(
          "svg",
          {
            width: containerSize,
            height: containerSize,
            viewBox: `0 0 ${containerSize} ${containerSize}`,
            role: "img",
            "aria-label": resolvedLabel,
            className: "animate-spin",
            style: {
              animationDuration: spinDuration,
              animationTimingFunction: "linear"
            },
            children: dotPositions.map((pos, index) => /* @__PURE__ */ jsx28(
              "circle",
              {
                cx: pos.cx,
                cy: pos.cy,
                r: dotSize / 2,
                style: {
                  fill: color,
                  opacity: 1 - index * 0.1
                }
              },
              index
            ))
          }
        ),
        showLabel && /* @__PURE__ */ jsx28(
          "div",
          {
            "aria-live": "polite",
            className: `select-none text-[var(--color-gray-700)] font-display ${labelClassname}`,
            style: {
              fontWeight: 500,
              fontSize: labelStyle.fontSize,
              lineHeight: labelStyle.lineHeight,
              textAlign: "center"
            },
            children: resolvedLabel
          }
        )
      ]
    }
  );
};
Loader2.displayName = "Loader";

// src/components/atoms/Skeleton.tsx
import { jsx as jsx29 } from "react/jsx-runtime";
var Skeleton = ({ className, testID, ...props }) => /* @__PURE__ */ jsx29(
  "div",
  {
    "data-testid": testID,
    "aria-hidden": "true",
    className: cn("animate-pulse rounded-md bg-gray-300", className),
    ...props
  }
);
Skeleton.displayName = "Skeleton";

// src/components/atoms/custom-icons/AISparkleIcon.tsx
import { jsx as jsx30, jsxs as jsxs17 } from "react/jsx-runtime";
var AISparkleIcon = ({
  className,
  "aria-hidden": ariaHidden = true
}) => /* @__PURE__ */ jsxs17(
  "svg",
  {
    "aria-hidden": ariaHidden,
    className: cn("w-5 h-5", className),
    viewBox: "0 0 20 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx30(
        "path",
        {
          d: "M4.5 11L5.28446 12.5689C5.54995 13.0999 5.68269 13.3654 5.86003 13.5954C6.01739 13.7996 6.20041 13.9826 6.40455 14.14C6.63462 14.3173 6.90011 14.4501 7.43108 14.7155L9 15.5L7.43108 16.2845C6.90011 16.5499 6.63462 16.6827 6.40455 16.86C6.20041 17.0174 6.01739 17.2004 5.86003 17.4046C5.68269 17.6346 5.54995 17.9001 5.28446 18.4311L4.5 20L3.71554 18.4311C3.45005 17.9001 3.31731 17.6346 3.13997 17.4046C2.98261 17.2004 2.79959 17.0174 2.59545 16.86C2.36538 16.6827 2.0999 16.5499 1.56892 16.2845L0 15.5L1.56892 14.7155C2.0999 14.4501 2.36538 14.3173 2.59545 14.14C2.79959 13.9826 2.98261 13.7996 3.13997 13.5954C3.31731 13.3654 3.45005 13.0999 3.71554 12.5689L4.5 11Z",
          fill: "var(--color-brand-green-500)"
        }
      ),
      /* @__PURE__ */ jsx30(
        "path",
        {
          d: "M13 0L14.1786 3.06442C14.4606 3.79765 14.6016 4.16426 14.8209 4.47264C15.0153 4.74595 15.254 4.98475 15.5274 5.17909C15.8357 5.39836 16.2024 5.53937 16.9356 5.82138L20 7L16.9356 8.17862C16.2024 8.46063 15.8357 8.60164 15.5274 8.82091C15.254 9.01525 15.0153 9.25405 14.8209 9.52736C14.6016 9.83574 14.4606 10.2024 14.1786 10.9356L13 14L11.8214 10.9356C11.5394 10.2024 11.3984 9.83574 11.1791 9.52736C10.9847 9.25405 10.746 9.01525 10.4726 8.82091C10.1643 8.60164 9.79765 8.46063 9.06442 8.17862L6 7L9.06442 5.82138C9.79765 5.53937 10.1643 5.39836 10.4726 5.17909C10.746 4.98475 10.9847 4.74595 11.1791 4.47264C11.3984 4.16426 11.5394 3.79765 11.8214 3.06442L13 0Z",
          fill: "var(--color-brand-green-500)"
        }
      )
    ]
  }
);
AISparkleIcon.displayName = "AISparkleIcon";

// src/components/atoms/custom-icons/CheckIcon.tsx
import { cva as cva8 } from "class-variance-authority";
import { jsx as jsx31 } from "react/jsx-runtime";
var checkIconVariants = cva8(
  // Base styles for the circular container
  "inline-flex items-center justify-center rounded-full transition-all duration-200",
  {
    variants: {
      // State variants matching Figma design
      state: {
        completed: [
          "bg-[var(--color-brand-green-500)]"
          // #ADDC91 from Figma
        ],
        required: [
          "bg-[var(--color-gray-300)]"
          // #E9E9E9 from Figma
        ]
      },
      // Size variants
      size: {
        sm: "w-4 h-4",
        md: "w-6 h-6",
        lg: "w-8 h-8",
        xl: "w-10 h-10"
      }
    },
    defaultVariants: {
      state: "required",
      size: "md"
    }
  }
);
var CheckIconSVG = ({
  className,
  strokeColor
}) => /* @__PURE__ */ jsx31(Icon, { name: "Check", size: 14, color: strokeColor, className });
var CheckIcon2 = ({
  className,
  state = "required",
  size = "md",
  testID,
  "aria-label": ariaLabel,
  ref,
  ...props
}) => {
  const strokeColor = state === "completed" ? "var(--color-white)" : "var(--color-gray-500)";
  const iconSizeMap4 = {
    sm: "w-2.5 h-2.5",
    md: "w-3.5 h-3.5",
    lg: "w-5 h-5",
    xl: "w-6 h-6"
  };
  const iconSize = iconSizeMap4[size || "md"];
  const defaultAriaLabel = state === "completed" ? "Step is completed" : "Step required";
  return /* @__PURE__ */ jsx31(
    "div",
    {
      ref,
      className: cn(checkIconVariants({ state, size }), className),
      "data-testid": testID,
      role: "img",
      "aria-label": ariaLabel || defaultAriaLabel,
      ...props,
      children: /* @__PURE__ */ jsx31(CheckIconSVG, { className: iconSize, strokeColor })
    }
  );
};
CheckIcon2.displayName = "CheckIcon";

// src/components/atoms/custom-icons/FileIcon.tsx
import { Fragment as Fragment2, jsx as jsx32, jsxs as jsxs18 } from "react/jsx-runtime";
var SIZE = 40;
var FileIcon = ({
  variant = "default",
  color = "#FFFFFF",
  text,
  className = "",
  testID = "file-icon"
}) => {
  return /* @__PURE__ */ jsxs18(
    "div",
    {
      className: `inline-flex relative ${className}`,
      style: { width: SIZE, height: SIZE },
      "data-testid": testID,
      children: [
        variant === "default" && /* @__PURE__ */ jsxs18(Fragment2, { children: [
          /* @__PURE__ */ jsxs18(
            "svg",
            {
              width: "40",
              height: "40",
              viewBox: "0 0 40 40",
              fill: "none",
              xmlns: "http://www.w3.org/2000/svg",
              children: [
                /* @__PURE__ */ jsx32(
                  "path",
                  {
                    d: "M11 0.75H27C27.1212 0.75 27.2375 0.798088 27.3232 0.883789L38.1162 11.6768C38.2019 11.7625 38.25 11.8788 38.25 12V36C38.25 37.7949 36.7949 39.25 35 39.25H11C9.20507 39.25 7.75 37.7949 7.75 36V4C7.75 2.20507 9.20508 0.75 11 0.75Z",
                    stroke: "#F2F2FA",
                    strokeWidth: "1.5"
                  }
                ),
                /* @__PURE__ */ jsx32(
                  "path",
                  {
                    d: "M27 0.5V8C27 10.2091 28.7909 12 31 12H38.5",
                    stroke: "#F2F2FA",
                    strokeWidth: "1.5"
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx32(
            "div",
            {
              className: "h-[16px] min-w-[26px] flex justify-center items-center center absolute rounded-xs top-[18px] px-[3px] py-[2px]",
              style: { backgroundColor: color },
              children: /* @__PURE__ */ jsx32("span", { className: "text-white uppercase text-[10px]", children: text })
            }
          )
        ] }),
        variant === "gray" && /* @__PURE__ */ jsxs18(Fragment2, { children: [
          /* @__PURE__ */ jsxs18(
            "svg",
            {
              width: "40",
              height: "40",
              viewBox: "0 0 40 40",
              fill: "none",
              xmlns: "http://www.w3.org/2000/svg",
              children: [
                /* @__PURE__ */ jsx32(
                  "mask",
                  {
                    id: "mask0_17478_18401",
                    style: { maskType: "alpha" },
                    maskUnits: "userSpaceOnUse",
                    x: "4",
                    y: "0",
                    width: "32",
                    height: "40",
                    children: /* @__PURE__ */ jsx32(
                      "path",
                      {
                        d: "M4 4C4 1.79086 5.79086 0 8 0H24L36 12V36C36 38.2091 34.2091 40 32 40H8C5.79086 40 4 38.2091 4 36V4Z",
                        fill: "url(#paint0_linear_17478_18401)"
                      }
                    )
                  }
                ),
                /* @__PURE__ */ jsx32("g", { mask: "url(#mask0_17478_18401)", children: /* @__PURE__ */ jsx32(
                  "path",
                  {
                    d: "M4 4C4 1.79086 5.79086 0 8 0H24L36 12V36C36 38.2091 34.2091 40 32 40H8C5.79086 40 4 38.2091 4 36V4Z",
                    fill: "#F5F5F5"
                  }
                ) }),
                /* @__PURE__ */ jsx32(
                  "path",
                  {
                    d: "M24 0L36 12H28C25.7909 12 24 10.2091 24 8V0Z",
                    fill: "#F3F4F5"
                  }
                ),
                /* @__PURE__ */ jsx32("defs", { children: /* @__PURE__ */ jsxs18(
                  "linearGradient",
                  {
                    id: "paint0_linear_17478_18401",
                    x1: "20",
                    y1: "0",
                    x2: "20",
                    y2: "40",
                    gradientUnits: "userSpaceOnUse",
                    children: [
                      /* @__PURE__ */ jsx32("stop", { "stop-opacity": "0.4" }),
                      /* @__PURE__ */ jsx32("stop", { offset: "1" })
                    ]
                  }
                ) })
              ]
            }
          ),
          /* @__PURE__ */ jsx32("div", { className: "h-[16px] w-full flex justify-center items-center center absolute rounded-xs top-[18px] px-[3px] py-[2px]", children: /* @__PURE__ */ jsx32("span", { className: "text-fg-secondary uppercase text-[10px]", children: text }) })
        ] }),
        variant === "solid" && /* @__PURE__ */ jsxs18(Fragment2, { children: [
          /* @__PURE__ */ jsxs18(
            "svg",
            {
              width: "40",
              height: "40",
              viewBox: "0 0 40 40",
              fill: "none",
              xmlns: "http://www.w3.org/2000/svg",
              children: [
                /* @__PURE__ */ jsx32(
                  "path",
                  {
                    d: "M4 4C4 1.79086 5.79086 0 8 0H24L36 12V36C36 38.2091 34.2091 40 32 40H8C5.79086 40 4 38.2091 4 36V4Z",
                    fill: color
                  }
                ),
                /* @__PURE__ */ jsx32(
                  "path",
                  {
                    opacity: "0.3",
                    d: "M24 0L36 12H28C25.7909 12 24 10.2091 24 8V0Z",
                    fill: "white"
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx32("div", { className: "h-[16px] w-full flex justify-center items-center center absolute rounded-xs top-[18px] px-[3px] py-[2px]", children: /* @__PURE__ */ jsx32("span", { className: "text-white uppercase text-[10px]", children: text }) })
        ] })
      ]
    }
  );
};
var FileIcon_default = FileIcon;

// src/components/atoms/custom-icons/SARCurrencyIcon.tsx
import { jsx as jsx33 } from "react/jsx-runtime";
var SARCurrencyIcon = ({
  className = "",
  testID = "sar-currency-icon",
  size = 14,
  color = "currentColor"
}) => {
  return /* @__PURE__ */ jsx33(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: size,
      height: size,
      viewBox: "0 0 45 45",
      fill: "none",
      className: `inline-block ${className}`,
      "data-testid": testID,
      children: /* @__PURE__ */ jsx33(
        "path",
        {
          d: "M40.1787 36.6846C39.9132 38.8319 39.7963 39.7624 38.8047 41.8555L23.5781 44.999C23.9281 42.7371 24.3951 40.9929 25.1533 39.9463L40.1787 36.6846ZM18.9697 21.8779L23.5205 20.8896V6.5127C25.2158 4.60967 26.2574 3.75496 28.3037 2.6748V19.8516L40.1787 17.2734C39.9132 19.4208 39.7964 20.3512 38.8047 22.4443L28.3037 24.6641V29.4932L40.1787 26.9795C39.9132 29.1268 39.7963 30.0574 38.8047 32.1504L28.3037 34.3184V34.3643L23.5205 35.3525V25.6758L18.9697 26.6377V32.7363L18.8926 32.752C17.8461 34.5868 16.3681 36.791 14.9443 38.5508L0 41.3955C0.134008 39.4728 0.413722 38.3899 1.2832 36.4531L14.1865 33.6562V27.6484L2.22754 30.1777C2.36154 28.2549 2.64122 27.1722 3.51074 25.2354L14.1865 22.916V3.83789C15.8818 1.93488 16.9235 1.08014 18.9697 0V21.8779Z",
          fill: color
        }
      )
    }
  );
};
var SARCurrencyIcon_default = SARCurrencyIcon;

// src/components/atoms/custom-icons/InfoIcon/InfoIconWithBackground.tsx
import { memo } from "react";
import { jsx as jsx34 } from "react/jsx-runtime";
var paddingMap = {
  small: "8px",
  medium: "10px",
  large: "12px",
  xlarge: "14px"
};
var sizeMap2 = {
  small: "32px",
  medium: "40px",
  large: "48px",
  xlarge: "56px"
};
var iconSizeMap2 = {
  small: "16px",
  medium: "20px",
  large: "24px",
  xlarge: "28px"
};
var borderRadiusMap = {
  small: "10px",
  medium: "12px",
  large: "12px",
  xlarge: "16px"
};
var InfoIconWithBackground = ({
  testID,
  color = "var(--color-white)",
  bgColor = "var(--color-brand-green-500)",
  borderColor = "transparent",
  className,
  size = "xlarge",
  variant = "circular",
  iconName = "Info"
}) => {
  const padding = paddingMap[size];
  const containerSize = sizeMap2[size];
  const borderRadius = borderRadiusMap[size];
  const iconSize = iconSizeMap2[size];
  const getContainerStyle = () => {
    const baseStyle = {
      backgroundColor: bgColor,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding,
      width: containerSize,
      height: containerSize,
      border: "1px solid",
      borderColor
    };
    switch (variant) {
      case "square":
        return {
          ...baseStyle,
          borderRadius
        };
      case "squareWithBorder":
        return {
          ...baseStyle,
          borderRadius
        };
      case "circular":
      default:
        return {
          ...baseStyle,
          borderRadius: "50%"
        };
    }
  };
  return /* @__PURE__ */ jsx34(
    "div",
    {
      style: getContainerStyle(),
      "data-testid": `${testID}-container`,
      className,
      children: /* @__PURE__ */ jsx34(Icon, { name: iconName, size: iconSize, testID, color })
    }
  );
};
var InfoIconWithBackground_default = memo(InfoIconWithBackground);

// src/components/atoms/custom-icons/InfoIcon/InfoIconCircularWave.tsx
import { memo as memo2 } from "react";
import { jsx as jsx35, jsxs as jsxs19 } from "react/jsx-runtime";
var sizeMap3 = {
  small: "34px",
  medium: "38px",
  large: "42px",
  xlarge: "46px"
};
var iconSizeMap3 = {
  small: "16px",
  medium: "20px",
  large: "24px",
  xlarge: "28px"
};
var InfoIconCircularWave = ({
  testID,
  color = "#10069F",
  bgColor = "#10069F",
  className,
  size = "xlarge",
  iconName = "Info"
}) => {
  const containerSize = parseInt(sizeMap3[size]);
  const iconSize = iconSizeMap3[size];
  const containerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: `${containerSize}px`,
    height: `${containerSize}px`,
    position: "relative"
  };
  return /* @__PURE__ */ jsxs19(
    "div",
    {
      style: containerStyle,
      "data-testid": `${testID}-container`,
      className,
      children: [
        /* @__PURE__ */ jsx35(
          "div",
          {
            style: {
              position: "absolute",
              width: `${containerSize}px`,
              height: `${containerSize}px`,
              borderRadius: "50%",
              padding: "5px",
              border: `2px solid ${bgColor}`,
              opacity: 0.1
            },
            "data-testid": `${testID}-outer-circle`
          }
        ),
        /* @__PURE__ */ jsx35(
          "div",
          {
            style: {
              position: "absolute",
              width: `${containerSize - 10}px`,
              height: `${containerSize - 10}px`,
              borderRadius: "50%",
              padding: "4px",
              border: `2px solid ${bgColor}`,
              opacity: 0.3
            },
            "data-testid": `${testID}-inner-circle`
          }
        ),
        /* @__PURE__ */ jsx35(
          Icon,
          {
            name: iconName,
            size: iconSize,
            testID,
            className,
            color
          }
        )
      ]
    }
  );
};
var InfoIconCircularWave_default = memo2(InfoIconCircularWave);

// src/components/atoms/custom-icons/CheckCircleIcon.tsx
import { cva as cva9 } from "class-variance-authority";
import { jsx as jsx36, jsxs as jsxs20 } from "react/jsx-runtime";
var checkCircleIconVariants = cva9(
  // Base styles for the icon
  "inline-flex items-center justify-center transition-all duration-200",
  {
    variants: {
      // Size variants
      size: {
        sm: "w-4 h-4",
        md: "w-6 h-6",
        lg: "w-8 h-8",
        xl: "w-10 h-10"
      },
      // Color variants matching Figma design
      variant: {
        success: "text-[var(--color-success-600)]",
        // #079455 from Figma
        muted: "text-[var(--color-gray-400)]",
        brand: "text-[var(--color-brand-blue-500)]"
      }
    },
    defaultVariants: {
      size: "md",
      variant: "success"
    }
  }
);
var CheckCircleIconSVG = ({
  className
}) => /* @__PURE__ */ jsxs20(
  "svg",
  {
    className,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx36(
        "path",
        {
          d: "M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4905 2.02168 11.3363C2.16356 9.18203 2.99721 7.13214 4.39828 5.49C5.79935 3.84785 7.69279 2.69637 9.79619 2.20112C11.8996 1.70588 14.1003 1.88759 16.1 2.72L17.5 3.29",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx36(
        "path",
        {
          d: "M22 4L12 14.01L9 11.01",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    ]
  }
);
var CheckCircleIcon = ({
  className,
  size = "md",
  variant = "success",
  testID,
  "aria-label": ariaLabel,
  ...props
}) => {
  const defaultAriaLabel = "Success";
  return /* @__PURE__ */ jsx36(
    "div",
    {
      className: cn(checkCircleIconVariants({ size, variant }), className),
      "data-testid": testID,
      role: "img",
      "aria-label": ariaLabel || defaultAriaLabel,
      ...props,
      children: /* @__PURE__ */ jsx36(CheckCircleIconSVG, { className: "w-full h-full" })
    }
  );
};
CheckCircleIcon.displayName = "CheckCircleIcon";

// src/components/atoms/StatusCounter.tsx
import { jsx as jsx37, jsxs as jsxs21 } from "react/jsx-runtime";
var StatusCounter = ({
  className,
  count,
  label,
  variant = "incomplete",
  testID,
  ...props
}) => {
  const getVariantStyles = (variant2) => {
    const variants = {
      incomplete: {
        container: "bg-bg-warning-primary",
        label: "text-fg-warning-primary"
      },
      requested: {
        container: "bg-status-light-blue",
        label: "text-status-blue-600"
      },
      active: {
        container: "bg-bg-success-primary",
        label: "text-fg-success-primary"
      },
      completed: {
        container: "bg-bg-quaternary",
        label: "text-status-indigo-700"
      }
    };
    return variants[variant2];
  };
  const variantStyles = getVariantStyles(variant);
  return /* @__PURE__ */ jsxs21(
    "div",
    {
      className: cn(
        "rounded-xl px-[16px] py-[12px] flex items-center gap-4 w-full",
        variantStyles.container,
        className
      ),
      "data-testid": testID,
      ...props,
      children: [
        /* @__PURE__ */ jsx37(
          Text,
          {
            as: "span",
            className: "text-[48px] leading-[60px] tracking-[-0.96px] font-normal text-gray-900",
            children: count
          }
        ),
        /* @__PURE__ */ jsx37(
          Text,
          {
            as: "span",
            weight: "normal",
            size: "sm",
            className: cn(variantStyles.label),
            children: label
          }
        )
      ]
    }
  );
};
StatusCounter.displayName = "StatusCounter";

// src/components/molecules/AccordionItem.tsx
import { useState as useState6 } from "react";
import { jsx as jsx38, jsxs as jsxs22 } from "react/jsx-runtime";
function AccordionItem({
  id,
  trigger,
  description,
  content,
  defaultExpanded = false,
  isExpanded: controlledExpanded,
  onExpandedChange,
  className,
  testID,
  triggerProps,
  contentProps,
  disabled = false,
  ref,
  ...props
}) {
  const [internalExpanded, setInternalExpanded] = useState6(defaultExpanded);
  const isExpanded = controlledExpanded !== void 0 ? controlledExpanded : internalExpanded;
  const handleToggle = () => {
    if (disabled) return;
    const newExpanded = !isExpanded;
    if (controlledExpanded === void 0) {
      setInternalExpanded(newExpanded);
    }
    onExpandedChange?.(newExpanded);
  };
  return /* @__PURE__ */ jsxs22(
    "div",
    {
      ref,
      className: cn(
        // Base styles using design tokens
        "border border-brand-tint-300 bg-white",
        "first:border-t last:border-b",
        "[&:not(:first-child)]:border-t-0",
        disabled && "opacity-50",
        className
      ),
      "data-testid": testID,
      ...props,
      children: [
        /* @__PURE__ */ jsx38(
          AccordionTrigger,
          {
            isExpanded,
            onClick: handleToggle,
            disabled,
            description: description ?? "",
            testID: testID ? `${testID}-trigger` : `${id}-trigger`,
            ...triggerProps,
            children: trigger
          }
        ),
        /* @__PURE__ */ jsx38(
          AccordionContent,
          {
            isExpanded,
            testID: testID ? `${testID}-content` : `${id}-content`,
            ...contentProps,
            children: content
          }
        )
      ]
    }
  );
}

// src/components/molecules/BackToTop.tsx
import { useTranslation as useTranslation11 } from "@tasama/i18n";
import { jsx as jsx39, jsxs as jsxs23 } from "react/jsx-runtime";
var BackToTop = ({
  visible,
  onClick,
  className,
  testID = "back-to-top"
}) => {
  const { t } = useTranslation11("portal");
  if (!visible) return null;
  return /* @__PURE__ */ jsxs23(
    "button",
    {
      type: "button",
      onClick,
      className: cn(
        "fixed bottom-[15px] left-1/2 -translate-x-1/2 z-floating-menu",
        "flex items-center gap-md",
        "pl-xl pr-md py-md",
        "rounded-full bg-background shadow-md",
        "cursor-pointer",
        "animate-in fade-in-0 zoom-in-95 duration-200",
        className
      ),
      "data-testid": testID,
      children: [
        /* @__PURE__ */ jsx39("span", { className: "text-xs font-medium leading-[18px] text-fg-primary whitespace-nowrap", children: t("backToTop.label") }),
        /* @__PURE__ */ jsx39("span", { className: "flex items-center justify-center size-6 rounded-full bg-bg-tertiary", children: /* @__PURE__ */ jsx39(Icon, { name: "ArrowUp", size: 12, className: "text-fg-primary" }) })
      ]
    }
  );
};
BackToTop.displayName = "BackToTop";

// src/components/molecules/AlertsAndNotifications.tsx
import React13, { memo as memo3 } from "react";
import { cva as cva10 } from "class-variance-authority";
import { useTranslation as useTranslation12 } from "@tasama/i18n";
import { jsx as jsx40, jsxs as jsxs24 } from "react/jsx-runtime";
var alertVariants = cva10(
  "relative flex items-start gap-4 rounded-2xl border p-6 transition-all duration-200 shadow-[0px_1px_1px_0px_var(--color-shadow-xs)]",
  {
    variants: {
      variant: {
        info: ["bg-white", "border-border", "text-fg-primary"],
        success: ["bg-white", "border-border", "text-fg-primary"],
        warning: ["bg-white", "border-border", "text-fg-primary"],
        error: ["bg-white", "border-border", "text-fg-primary"]
      },
      size: {
        default: "p-4",
        compact: "p-2"
      }
    },
    defaultVariants: {
      variant: "info",
      size: "default"
    }
  }
);
var iconVariantMap = {
  info: "Info",
  warning: "CircleAlert",
  success: "CircleCheck",
  error: "CircleAlert"
};
var iconColorMap = {
  info: "var(--color-brand-blue-500)",
  success: "var(--color-success-500)",
  warning: "var(--color-warning-500)",
  error: "var(--color-error-500)"
};
var iconBgColorMap = {
  info: "var(--color-brand-blue-500)",
  success: "var(--color-success-500)",
  warning: "var(--color-warning-500)",
  error: "var(--color-error-500)"
};
var AlertsAndNotifications = ({
  className,
  variant,
  size = "default",
  title = "",
  description,
  showClose = true,
  onClose,
  testID,
  icon,
  showIcon = true,
  ...props
}) => {
  const { t } = useTranslation12("common");
  const renderIcon = React13.useCallback(() => {
    if (icon) {
      return icon;
    }
    return /* @__PURE__ */ jsx40(
      InfoIconCircularWave_default,
      {
        testID: `${testID}-circular-wave`,
        size: "medium",
        color: iconColorMap[variant || "info"],
        bgColor: iconBgColorMap[variant || "info"],
        iconName: iconVariantMap[variant || "info"]
      }
    );
  }, [icon, variant]);
  return /* @__PURE__ */ jsxs24(
    "div",
    {
      className: cn(
        alertVariants({ variant, size }),
        className,
        "z-notification flex-wrap sm:flex-nowrap gap-x-4 gap-y-2",
        !(title && description) && "sm:items-center"
      ),
      "data-testid": testID,
      role: "alert",
      "aria-live": "polite",
      ...props,
      children: [
        showIcon && /* @__PURE__ */ jsx40(
          "div",
          {
            className: "flex-shrink-0 order-1",
            "data-testid": `${testID}-icon-container`,
            children: renderIcon()
          }
        ),
        showClose && onClose && /* @__PURE__ */ jsx40(
          "button",
          {
            onClick: onClose,
            "data-testid": `${testID}-close-button`,
            "aria-label": t("actions.closeAlert"),
            className: "order-2 sm:order-3 ml-auto sm:ml-0 flex-shrink-0 flex items-center justify-center text-fg-quaternary hover:text-fg-tertiary transition-colors cursor-pointer",
            children: /* @__PURE__ */ jsx40(Icon, { size: 18, name: "X" })
          }
        ),
        /* @__PURE__ */ jsxs24(
          "div",
          {
            className: "flex flex-col order-3 sm:order-2 w-full sm:w-auto sm:flex-1 space-y-1",
            "data-testid": `${testID}-content`,
            children: [
              title && /* @__PURE__ */ jsx40(
                Label,
                {
                  className: "text-sm font-medium leading-5 text-fg-secondary m-0",
                  "data-testid": `${testID}-title`,
                  children: title
                }
              ),
              description && /* @__PURE__ */ jsx40(
                Label,
                {
                  className: "text-sm font-normal leading-5 text-fg-tertiary m-0",
                  "data-testid": `${testID}-description`,
                  children: description
                }
              )
            ]
          }
        )
      ]
    }
  );
};
AlertsAndNotifications.displayName = "AlertsAndNotifications";
var AlertsAndNotifications_default = memo3(AlertsAndNotifications);

// src/components/molecules/Card.tsx
import { jsx as jsx41, jsxs as jsxs25 } from "react/jsx-runtime";
var Card = ({
  title,
  description,
  items = [],
  className,
  testID,
  titleClassName,
  descriptionClassName,
  itemsClassName
}) => {
  return /* @__PURE__ */ jsx41(
    "div",
    {
      className: cn(
        "bg-bg-secondary border border-brand-tint-300 w-full",
        "rounded-[16px] p-4 gap-4 md:rounded-3xl md:p-6 md:gap-6",
        className
      ),
      "data-testid": testID,
      children: /* @__PURE__ */ jsxs25("div", { className: "flex flex-col gap-4 md:gap-6 w-full", children: [
        /* @__PURE__ */ jsxs25("div", { className: "flex flex-col gap-2 md:gap-3 w-full", children: [
          /* @__PURE__ */ jsx41(
            Label,
            {
              className: cn(
                "text-[16px] font-medium leading-[24px] text-[#181D27]",
                "w-fit",
                titleClassName
              ),
              testID: testID ? `${testID}-title` : "card-title",
              children: title
            }
          ),
          /* @__PURE__ */ jsx41(
            Label,
            {
              className: cn(
                "text-[14px] font-light leading-[20px] text-[#181D27]",
                "w-full",
                descriptionClassName
              ),
              testID: testID ? `${testID}-description` : "card-description",
              children: description
            }
          )
        ] }),
        items.length > 0 && /* @__PURE__ */ jsx41(
          "div",
          {
            className: cn(
              "flex flex-col gap-2 md:gap-3 w-full",
              itemsClassName
            ),
            "data-testid": testID ? `${testID}-items` : "card-items",
            children: items.map((item, index) => /* @__PURE__ */ jsxs25(
              "div",
              {
                className: cn("flex items-start gap-3 w-full", "md:gap-4"),
                "data-testid": testID ? `${testID}-item-${index}` : `card-item-${index}`,
                children: [
                  /* @__PURE__ */ jsx41(Icon, { name: "Check", className: "shrink-0 mt-[2px]" }),
                  /* @__PURE__ */ jsx41(
                    Label,
                    {
                      className: cn(
                        "text-[14px] font-light leading-[20px] text-[#181D27]",
                        "flex-1"
                      ),
                      testID: testID ? `${testID}-item-${index}-text` : `card-item-${index}-text`,
                      children: item.text
                    }
                  )
                ]
              },
              index
            ))
          }
        )
      ] })
    }
  );
};
Card.displayName = "Card";

// src/components/molecules/CompanyLogoUpload.tsx
import { useState as useState8, useRef as useRef7, useEffect as useEffect8 } from "react";
import { useTranslation as useTranslation14 } from "@tasama/i18n";

// src/components/molecules/FileUploadBase.tsx
import * as React14 from "react";
import { useTranslation as useTranslation13 } from "@tasama/i18n";
import { jsx as jsx42, jsxs as jsxs26 } from "react/jsx-runtime";
var FileUploadBase = ({
  onFilesSelected,
  onError,
  accept = "*",
  multiple = false,
  disabled = false,
  actionText,
  dragHintText,
  supportingText,
  icon,
  className,
  testID,
  showUpload,
  isFileRemoved,
  ref,
  ...rest
}) => {
  const { t } = useTranslation13("common");
  const isMobile = useIsMobile();
  const defaultActionText = isMobile ? t("fileUpload.tapToUpload") : t("fileUpload.clickToUpload");
  const defaultDragHintText = t("fileUpload.dragAndDrop");
  const defaultSupportingText = t("fileUpload.supportedFormats");
  const inputRef = React14.useRef(null);
  const [isDragging, setIsDragging] = React14.useState(false);
  const [isHovering, setIsHovering] = React14.useState(false);
  const [errorMessage, setErrorMessage] = React14.useState(null);
  React14.useEffect(() => {
    if (isFileRemoved) {
      inputRef.current.value = "";
      setErrorMessage(null);
    }
  }, [isFileRemoved]);
  const openFileDialog = () => {
    if (disabled) return;
    inputRef.current?.click();
  };
  const onInputChange = (e) => {
    if (!e.target.files) return;
    if (!multiple && e.target.files.length > 1) {
      const msg = t("fileUpload.singleFileOnly");
      setErrorMessage(msg);
      onError?.(msg);
      return;
    }
    setErrorMessage(null);
    onFilesSelected?.(e.target.files);
    e.target.value = "";
  };
  const onDragOver = (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(true);
  };
  const onDragLeave = (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(false);
  };
  const onDrop = (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      onFilesSelected?.(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };
  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openFileDialog();
    }
  };
  return /* @__PURE__ */ jsxs26("div", { "data-testid": testID, children: [
    showUpload ? /* @__PURE__ */ jsxs26(
      "div",
      {
        ref,
        role: "group",
        "aria-disabled": disabled,
        tabIndex: disabled ? -1 : 0,
        onKeyDown,
        onClick: openFileDialog,
        onMouseEnter: () => !disabled && setIsHovering(true),
        onMouseLeave: () => !disabled && setIsHovering(false),
        className: cn(
          "group w-full rounded-[12px] border border-brand-tint-500 bg-background text-center",
          "px-6 py-4",
          disabled && "bg-muted pointer-events-none cursor-not-allowed opacity-80",
          !disabled && "transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          isDragging && !disabled && "border-gray-200 bg-bg-quaternary ring-2 ring-gray-200/30 cursor-copy",
          isHovering && !disabled && !isDragging && "border-gray-200 bg-bg-quaternary ring-2 ring-gray-200/30 cursor-pointer",
          className
        ),
        onDragOver,
        onDragLeave,
        onDrop,
        ...rest,
        children: [
          /* @__PURE__ */ jsxs26("div", { className: "flex flex-col items-center", children: [
            /* @__PURE__ */ jsx42(
              "div",
              {
                className: cn(
                  "inline-flex items-center justify-center rounded-lg p-3 text-muted-foreground bg-bg-quaternary",
                  "motion-safe:transition-transform motion-safe:duration-150 group-hover:scale-[1.02]",
                  disabled && "opacity-70"
                ),
                "aria-hidden": "true",
                children: icon ?? /* @__PURE__ */ jsx42(Icon, { name: "CloudUpload" })
              }
            ),
            /* @__PURE__ */ jsxs26("div", { className: "flex items-center justify-center gap-1", children: [
              /* @__PURE__ */ jsx42(
                Button,
                {
                  type: "button",
                  variant: "quaternary",
                  size: "sm",
                  className: cn(
                    "p-0 font-medium",
                    disabled && "pointer-events-none opacity-60"
                  ),
                  onClick: (e) => {
                    e.stopPropagation();
                    openFileDialog();
                  },
                  disabled,
                  label: actionText || defaultActionText,
                  iconPosition: "none"
                }
              ),
              !isMobile && /* @__PURE__ */ jsx42(
                "span",
                {
                  className: "text-sm leading-[1.4286em] font-light text-muted-foreground",
                  "aria-hidden": "true",
                  children: dragHintText || defaultDragHintText
                }
              )
            ] }),
            supportingText || defaultSupportingText ? /* @__PURE__ */ jsx42("p", { className: "text-xs leading-[1.5em] font-light text-muted-foreground", children: supportingText || defaultSupportingText }) : null,
            errorMessage ? /* @__PURE__ */ jsx42(
              "p",
              {
                "data-testid": "file-upload-error",
                className: "text-xs leading-[1.5em] text-destructive mt-1",
                children: errorMessage
              }
            ) : null
          ] }),
          " "
        ]
      }
    ) : null,
    /* @__PURE__ */ jsx42(
      "input",
      {
        ref: inputRef,
        id: "file-input",
        type: "file",
        className: "sr-only",
        onChange: onInputChange,
        accept,
        multiple,
        disabled,
        tabIndex: -1,
        "aria-hidden": "true"
      }
    )
  ] });
};

// src/components/molecules/CompanyLogoUpload.tsx
import { Fragment as Fragment3, jsx as jsx43, jsxs as jsxs27 } from "react/jsx-runtime";
var formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
var formatSizeLimit = (maxSizeMB) => {
  const kb = maxSizeMB * 1e3;
  if (kb < 1e3) return `${Math.round(kb)} KB`;
  return `${maxSizeMB} MB`;
};
var LOGO_SIZE_CLASSES = {
  sm: "w-10 h-10",
  md: "w-[126px] h-[126px] lg:w-32 lg:h-32"
};
var LOGO_ICON_SIZES = {
  sm: 18,
  md: 48
};
var CompanyLogoUpload = ({
  logoSrc,
  logoAlt,
  onFileSelect,
  onSave,
  onCancel,
  loading = false,
  disabled = false,
  className,
  testID = "company-logo-upload",
  accept = "image/png,image/jpeg",
  maxSizeMB = 0.5,
  readOnly = false,
  size = "md",
  isLogoLoading = false,
  uploadError,
  ...props
}) => {
  const { t } = useTranslation14(["onboarding", "common"]);
  const defaultLogoAlt = t("companyLogo.alt");
  const [isEditing, setIsEditing] = useState8(false);
  const [selectedFile, setSelectedFile] = useState8(null);
  const [previewUrl, setPreviewUrl] = useState8(null);
  const [isSaving, setIsSaving] = useState8(false);
  const [imgError, setImgError] = useState8(false);
  const [fileError, setFileError] = useState8(null);
  const [networkError, setNetworkError] = useState8(null);
  const prevLoadingRef = useRef7(loading);
  const prevLogoSrcRef = useRef7(logoSrc);
  const logoSizeClass = LOGO_SIZE_CLASSES[size];
  const iconSize = LOGO_ICON_SIZES[size];
  const sizeDisplay = formatSizeLimit(maxSizeMB);
  useEffect8(() => {
    setImgError(false);
  }, [logoSrc, previewUrl]);
  useEffect8(() => {
    if (prevLogoSrcRef.current !== logoSrc) {
      prevLogoSrcRef.current = logoSrc;
      setPreviewUrl(null);
    }
  }, [logoSrc]);
  useEffect8(() => {
    if (isSaving && prevLoadingRef.current && !loading) {
      setIsSaving(false);
      if (uploadError) {
        setPreviewUrl(null);
      } else {
        setIsEditing(false);
        setSelectedFile(null);
      }
    }
    prevLoadingRef.current = loading;
  }, [loading, isSaving, uploadError]);
  const handleEditClick = () => {
    if (disabled) return;
    setIsEditing(true);
  };
  const handleFileSelect = (files) => {
    const file = files[0];
    if (!file) return;
    if (file.size > maxSizeMB * 1024 * 1024) {
      setFileError(
        t("common:companyLogoUpload.fileTooLarge", { max: sizeDisplay })
      );
      return;
    }
    if (accept && !accept.split(",").some((type) => file.type.includes(type.trim()))) {
      setFileError(t("common:companyLogoUpload.fileTypeInvalid"));
      return;
    }
    setFileError(null);
    setNetworkError(null);
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result);
    };
    reader.readAsDataURL(file);
    onFileSelect?.(file);
  };
  const handleSave = () => {
    if (!selectedFile || loading) return;
    if (!navigator.onLine) {
      setNetworkError(t("common:companyLogoUpload.networkError"));
      return;
    }
    setNetworkError(null);
    setIsSaving(true);
    onSave?.();
  };
  const handleCancel = () => {
    setIsEditing(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setFileError(null);
    setNetworkError(null);
    onCancel?.();
  };
  const displaySrc = isEditing ? logoSrc : previewUrl || logoSrc;
  const renderLogoCircle = () => /* @__PURE__ */ jsx43(
    "div",
    {
      className: cn(
        "relative rounded-full border border-gray-300 overflow-hidden bg-white flex items-center justify-center flex-shrink-0",
        logoSizeClass
      ),
      children: isLogoLoading ? /* @__PURE__ */ jsx43("div", { className: "w-full h-full bg-gray-200 animate-pulse" }) : displaySrc && !imgError ? /* @__PURE__ */ jsx43(
        "img",
        {
          src: displaySrc,
          alt: logoAlt || defaultLogoAlt,
          className: "w-full h-full object-contain",
          onError: () => setImgError(true),
          "data-testid": `${testID}-image`
        }
      ) : /* @__PURE__ */ jsx43(
        Icon,
        {
          name: "Image",
          size: iconSize,
          className: "text-gray-400",
          "data-testid": `${testID}-placeholder`
        }
      )
    }
  );
  if (isEditing) {
    return /* @__PURE__ */ jsxs27(
      "div",
      {
        className: cn(
          "flex flex-col sm:flex-row items-start gap-4 sm:gap-6",
          className
        ),
        "data-testid": testID,
        ...props,
        children: [
          renderLogoCircle(),
          /* @__PURE__ */ jsxs27("div", { className: "flex flex-col gap-3 min-w-0 w-full sm:flex-1", children: [
            selectedFile ? /* @__PURE__ */ jsx43(
              FileUploadItem,
              {
                fileName: selectedFile.name,
                fileSize: formatFileSize(selectedFile.size),
                status: "uploading",
                progress: 0,
                showProgress: false,
                showStatus: false,
                testID: `${testID}-file-item`
              }
            ) : /* @__PURE__ */ jsxs27(Fragment3, { children: [
              /* @__PURE__ */ jsx43(
                FileUploadBase,
                {
                  onFilesSelected: handleFileSelect,
                  accept,
                  supportingText: t("common:companyLogoUpload.supportingText", {
                    max: sizeDisplay
                  }),
                  disabled: disabled || loading,
                  showUpload: true,
                  testID: `${testID}-upload`,
                  icon: /* @__PURE__ */ jsx43(
                    Icon,
                    {
                      name: "CloudUpload",
                      size: 24,
                      className: "text-gray-600"
                    }
                  )
                }
              ),
              fileError && /* @__PURE__ */ jsx43(
                "p",
                {
                  className: "text-sm text-fg-error-primary",
                  "data-testid": `${testID}-file-error`,
                  children: fileError
                }
              )
            ] }),
            (uploadError || networkError) && /* @__PURE__ */ jsx43(
              "p",
              {
                className: "text-sm text-fg-error-primary",
                "data-testid": `${testID}-upload-error`,
                children: networkError ?? uploadError
              }
            ),
            /* @__PURE__ */ jsxs27("div", { className: "flex justify-end items-center gap-4 sm:gap-6", children: [
              /* @__PURE__ */ jsx43(
                Button,
                {
                  variant: "tertiary",
                  size: "md",
                  onClick: handleCancel,
                  disabled: loading,
                  testID: `${testID}-cancel`,
                  label: t("common:actions.cancel").toUpperCase(),
                  iconPosition: "none"
                }
              ),
              /* @__PURE__ */ jsx43(
                Button,
                {
                  variant: "secondary",
                  size: "md",
                  onClick: handleSave,
                  disabled: !selectedFile || loading,
                  loading,
                  testID: `${testID}-save`,
                  label: t("common:actions.save").toUpperCase(),
                  iconName: "Check",
                  iconPosition: "trailing"
                }
              )
            ] })
          ] })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs27(
    "div",
    {
      className: cn("relative inline-block", className),
      "data-testid": testID,
      ...props,
      children: [
        renderLogoCircle(),
        !readOnly && !isEditing && !disabled && /* @__PURE__ */ jsx43(
          Button,
          {
            onClick: handleEditClick,
            className: cn(
              "absolute -bottom-1 -end-1 w-10 h-10 rounded-full",
              "bg-white border border-gray-300 shadow-sm",
              "flex items-center justify-center",
              "hover:bg-gray-50 transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            ),
            testID: `${testID}-edit-button`,
            iconName: "Image",
            iconPosition: "none",
            variant: "quaternary",
            size: "sm",
            "aria-label": t("common:companyLogoUpload.editAriaLabel"),
            children: /* @__PURE__ */ jsx43(Icon, { name: "ImageUp", size: 20 })
          }
        )
      ]
    }
  );
};
CompanyLogoUpload.displayName = "CompanyLogoUpload";

// src/components/molecules/DateTimeInput.tsx
import * as React16 from "react";
import ReactDOM3 from "react-dom";
import { useTranslation as useTranslation15 } from "@tasama/i18n";
import { ar as ar2 } from "date-fns/locale";
import { jsx as jsx44, jsxs as jsxs28 } from "react/jsx-runtime";
var DATE_TIME_SEPARATOR = " ";
var parseDateTimeValue = (value) => {
  const [datePart, timePart] = value.split(DATE_TIME_SEPARATOR);
  return {
    date: datePart ? parseDate(datePart) : void 0,
    time: timePart ? parseTime(timePart) : void 0
  };
};
var formatDateTimeValue = (date, time) => `${formatDate(date)}${DATE_TIME_SEPARATOR}${formatTime(time)}`;
var defaultDraftTime = { hour: 0, minute: 0 };
var DateTimeInput = ({
  label,
  id,
  hintText,
  error = false,
  errorMessage,
  disabled = false,
  required = false,
  testID,
  className,
  value = "",
  onChange,
  onBlur,
  placeholder,
  disabledDays,
  modifiers,
  modifiersClassNames,
  defaultMonth,
  startMonth,
  endMonth,
  showDropdown = true,
  yearRange = 10,
  minuteStep = 5,
  timeFormat = "24h",
  showColumnLabels = true,
  defaultTab = "date"
}) => {
  const now = /* @__PURE__ */ new Date();
  const defaultStartMonth = new Date(now.getFullYear() - yearRange, 0, 1);
  const defaultEndMonth = new Date(now.getFullYear() + yearRange, 11, 31);
  const { t, i18n: i18n2 } = useTranslation15("common");
  const defaultPlaceholder = t("dateTimeInput.placeholder");
  const dateLocale = i18n2.language === "ar" ? ar2 : void 0;
  const [open, setOpen] = React16.useState(false);
  const [activeTab, setActiveTab] = React16.useState(defaultTab);
  const containerRef = React16.useRef(null);
  const popupRef = React16.useRef(null);
  const inputRef = React16.useRef(null);
  const parsedValue = parseDateTimeValue(value);
  const displayValue = parsedValue.date && parsedValue.time ? formatDateTimeValue(parsedValue.date, parsedValue.time) : "";
  const [draftDate, setDraftDate] = React16.useState(
    parsedValue.date
  );
  const [draftTime, setDraftTime] = React16.useState(
    parsedValue.time ?? defaultDraftTime
  );
  const closePopup = React16.useCallback(() => {
    setOpen(false);
    inputRef.current?.focus();
  }, []);
  const { popupStyle } = usePopoverPosition({
    open,
    containerRef,
    popupRef,
    onClose: closePopup,
    recomputeKey: activeTab
  });
  const openPopup = React16.useCallback(() => {
    setDraftDate(parsedValue.date);
    setDraftTime(parsedValue.time ?? defaultDraftTime);
    setActiveTab(defaultTab);
    setOpen(true);
  }, [parsedValue.date, parsedValue.time, defaultTab]);
  const togglePopup = React16.useCallback(() => {
    if (disabled) return;
    if (open) {
      setOpen(false);
    } else {
      openPopup();
    }
  }, [disabled, open, openPopup]);
  const handleDaySelect = (date) => {
    if (!date) return;
    setDraftDate(date);
    setActiveTab("time");
  };
  const handleConfirm = () => {
    onChange?.({
      target: {
        value: formatDateTimeValue(draftDate ?? /* @__PURE__ */ new Date(), draftTime)
      }
    });
    setOpen(false);
    inputRef.current?.focus();
  };
  const handleInputBlur = (e) => {
    if (containerRef.current?.contains(e.relatedTarget)) return;
    if (popupRef.current?.contains(e.relatedTarget)) return;
    setOpen(false);
    onBlur?.(e);
  };
  const describedByIds = [];
  if (hintText) describedByIds.push(`${id}-hint`);
  if (error && errorMessage) describedByIds.push(`${id}-error`);
  const ariaDescribedBy = describedByIds.length ? describedByIds.join(" ") : void 0;
  const inputClasses = cn(
    "flex h-12 w-full rounded-lg border border-solid transition-colors motion-safe:duration-150",
    "focus-visible:outline-none cursor-pointer select-none",
    "ps-10 pe-12 py-[14px]",
    "text-sm font-light",
    "bg-background",
    disabled ? "cursor-not-allowed pointer-events-none border-border text-disabled" : displayValue ? "text-fg-primary" : "text-quaternary",
    error ? "border-error focus:shadow-[0_0_0_4px_var(--color-bg-error-secondary)]" : "border-tertiary focus:shadow-[0_0_0_4px_var(--color-brand-tint-200)]",
    className
  );
  const tabButtonClasses = (tab) => cn(
    "flex-1 -mb-px border-b-2 pb-lg px-2xl text-md font-medium text-center transition-colors cursor-pointer",
    activeTab === tab ? "border-fg-brand-primary text-fg-brand-primary" : "border-transparent text-fg-quaternary"
  );
  const popup = open ? ReactDOM3.createPortal(
    /* @__PURE__ */ jsxs28(
      "div",
      {
        ref: popupRef,
        role: "dialog",
        "aria-label": t("dateTimeInput.chooseDateTime"),
        tabIndex: -1,
        style: popupStyle,
        className: cn(
          "fixed z-popover bg-background border border-border rounded-xl shadow-lg p-3 focus:outline-none",
          dateLocale ? "min-w-[320px]" : "min-w-[280px]"
        ),
        children: [
          /* @__PURE__ */ jsxs28(
            "div",
            {
              className: "flex w-full border-b border-border mb-3",
              role: "tablist",
              children: [
                /* @__PURE__ */ jsx44(
                  "button",
                  {
                    type: "button",
                    role: "tab",
                    "aria-selected": activeTab === "date",
                    "data-testid": testID ? `${testID}-tab-date` : void 0,
                    className: tabButtonClasses("date"),
                    onClick: () => setActiveTab("date"),
                    children: t("dateTimeInput.selectDateTab")
                  }
                ),
                /* @__PURE__ */ jsx44(
                  "button",
                  {
                    type: "button",
                    role: "tab",
                    "aria-selected": activeTab === "time",
                    "data-testid": testID ? `${testID}-tab-time` : void 0,
                    className: tabButtonClasses("time"),
                    onClick: () => setActiveTab("time"),
                    children: t("dateTimeInput.selectTimeTab")
                  }
                )
              ]
            }
          ),
          activeTab === "date" ? /* @__PURE__ */ jsx44(
            DatePickerPanel,
            {
              selected: draftDate,
              onSelect: handleDaySelect,
              defaultMonth,
              startMonth: startMonth ?? defaultStartMonth,
              endMonth: endMonth ?? defaultEndMonth,
              showDropdown,
              disabledDays,
              modifiers,
              modifiersClassNames,
              dateLocale
            }
          ) : /* @__PURE__ */ jsx44(
            TimePickerPanel,
            {
              draft: draftTime,
              onDraftChange: setDraftTime,
              onConfirm: handleConfirm,
              minuteStep,
              format: timeFormat,
              showColumnLabels,
              testID
            }
          )
        ]
      }
    ),
    document.body
  ) : null;
  return /* @__PURE__ */ jsxs28("div", { className: "space-y-sm", "data-testid": testID, children: [
    /* @__PURE__ */ jsxs28(
      Label,
      {
        htmlFor: id,
        className: cn(
          "block",
          "font-normal text-sm",
          "input-label-spacing",
          disabled ? "text-disabled" : "text-fg-secondary"
        ),
        children: [
          label,
          !required && /* @__PURE__ */ jsx44("span", { className: "ms-1", children: t("actions.optional") })
        ]
      }
    ),
    /* @__PURE__ */ jsxs28("div", { className: "relative", ref: containerRef, children: [
      /* @__PURE__ */ jsx44(
        "div",
        {
          className: cn(
            "absolute start-3 top-1/2 -translate-y-1/2 z-10",
            disabled ? "pointer-events-none" : "cursor-pointer"
          ),
          onClick: togglePopup,
          children: /* @__PURE__ */ jsx44(
            Icon,
            {
              name: "Calendar",
              size: 20,
              className: cn(disabled ? "text-disabled" : "text-quaternary"),
              "aria-hidden": true
            }
          )
        }
      ),
      /* @__PURE__ */ jsx44(
        "input",
        {
          ref: inputRef,
          id,
          readOnly: true,
          value: displayValue,
          placeholder: placeholder || defaultPlaceholder,
          className: inputClasses,
          "aria-invalid": error || void 0,
          "aria-describedby": ariaDescribedBy,
          "aria-haspopup": "dialog",
          disabled,
          "data-testid": testID ? `${testID}-input` : void 0,
          onClick: togglePopup,
          onKeyDown: (e) => {
            if (e.key === " " || e.key === "Enter") {
              e.preventDefault();
              togglePopup();
            }
          },
          onBlur: handleInputBlur
        }
      ),
      error && /* @__PURE__ */ jsx44("div", { className: "pointer-events-none absolute end-3 top-1/2 -translate-y-1/2", children: /* @__PURE__ */ jsx44(
        Icon,
        {
          name: "CircleAlert",
          size: 16,
          "aria-hidden": true,
          className: "text-error-primary"
        }
      ) })
    ] }),
    popup,
    !error && hintText && /* @__PURE__ */ jsx44(
      "p",
      {
        id: `${id}-hint`,
        "data-testid": `${testID}-hint`,
        className: cn(
          "text-xs font-normal font-sans",
          disabled ? "text-disabled" : "text-quaternary"
        ),
        children: hintText
      }
    ),
    error && errorMessage && /* @__PURE__ */ jsx44(
      "p",
      {
        id: `${id}-error`,
        "data-testid": `${testID}-error`,
        className: "text-error-primary text-xs font-normal font-sans",
        children: errorMessage
      }
    )
  ] });
};

// src/components/molecules/ServiceCard.tsx
import { useTranslation as useTranslation16 } from "@tasama/i18n";
import { Fragment as Fragment4, jsx as jsx45, jsxs as jsxs29 } from "react/jsx-runtime";
var ServiceCard = ({
  functionTitle,
  serviceCategory,
  serviceName,
  currentStep,
  totalSteps,
  stepDescription,
  status,
  statusText,
  iconName,
  showTitleIcon = true,
  showBadgeIcon = false,
  testID,
  className,
  onClick,
  requestId,
  serviceBadges,
  ...props
}) => {
  const { t } = useTranslation16("portal");
  return /* @__PURE__ */ jsxs29(
    "div",
    {
      className: cn(
        // Container query: switches from stacked → side-by-side based on
        // the nearest @container ancestor's width (the parent Services
        // card). Replaces the previous viewport-based `lg:` breakpoint
        // because at narrow widths the My services card is much smaller
        // than the viewport — viewport queries can't see that.
        "flex flex-col @[560px]:flex-row gap-3 @[560px]:gap-5 p-[20px] @[560px]:p-4 bg-white border border-brand-tint-300 rounded-xl transition-all duration-200 hover:bg-gray-37 hover:shadow-md cursor-pointer",
        className
      ),
      "data-testid": testID,
      onClick,
      ...props,
      children: [
        /* @__PURE__ */ jsx45("div", { className: "@[560px]:hidden", children: /* @__PURE__ */ jsx45(
          StatusBadge,
          {
            status,
            customText: statusText,
            showIcon: showBadgeIcon,
            testID: `${testID}-status-badge-mobile`
          }
        ) }),
        /* @__PURE__ */ jsxs29("div", { className: "flex-2 grid gap-2 @[560px]:gap-3", children: [
          /* @__PURE__ */ jsxs29("div", { className: "flex @[560px]:grid @[560px]:grid-cols-[auto_1fr] gap-3 items-center", children: [
            showTitleIcon && /* @__PURE__ */ jsx45(
              FeaturedIcon,
              {
                variant: "success",
                size: "sm",
                icon: /* @__PURE__ */ jsx45(
                  Icon,
                  {
                    name: iconName,
                    size: 14,
                    strokeWidth: 1.14,
                    className: "text-white"
                  }
                ),
                className: "bg-fg-brand-green hidden @[560px]:flex",
                testID: `${testID}-featured-icon`,
                "aria-label": `${functionTitle} icon`
              }
            ),
            /* @__PURE__ */ jsx45("span", { className: "text-sm font-light leading-[20px] @[560px]:leading-[1.43] text-gray-700 overflow-hidden text-ellipsis", children: functionTitle })
          ] }),
          /* @__PURE__ */ jsx45("div", { className: "flex flex-wrap gap-x-1 @[560px]:grid @[560px]:grid-cols-[auto_1fr] @[560px]:gap-2 items-center", children: /* @__PURE__ */ jsxs29("span", { className: "text-lg leading-[28px] font-normal @[560px]:leading-[1.56] text-fg-primary", children: [
            serviceCategory,
            serviceName && /* @__PURE__ */ jsxs29(Fragment4, { children: [
              ": ",
              /* @__PURE__ */ jsx45("span", { className: "text-lg leading-[28px] font-light @[560px]:leading-[1.56] text-fg-primary", children: serviceName })
            ] })
          ] }) }),
          (serviceBadges?.length || requestId) && /* @__PURE__ */ jsxs29(
            "div",
            {
              className: "flex items-center gap-2 flex-wrap",
              "data-testid": `${testID}-service-id`,
              children: [
                serviceBadges?.map((badgeProps, index) => /* @__PURE__ */ jsx45(
                  ServiceBadge,
                  {
                    ...badgeProps
                  },
                  badgeProps.testID ?? `${testID}-service-badge-${index}`
                )),
                requestId && /* @__PURE__ */ jsxs29("span", { className: "text-xs font-light text-gray-700", children: [
                  /* @__PURE__ */ jsx45("span", { className: "font-normal", children: t("serviceCard.serviceId") }),
                  " ",
                  requestId
                ] })
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsxs29("div", { className: "hidden @[560px]:grid flex-1 gap-2 justify-items-end content-between", children: [
          /* @__PURE__ */ jsx45(
            StatusBadge,
            {
              status,
              customText: statusText,
              showIcon: showBadgeIcon,
              testID: `${testID}-status-badge`
            }
          ),
          currentStep != null && /* @__PURE__ */ jsx45("div", { className: "grid gap-1", children: /* @__PURE__ */ jsxs29("span", { className: "text-xs font-normal leading-[1.5] text-fg-primary text-end truncate", children: [
            /* @__PURE__ */ jsx45("span", { className: "font-normal", children: t("serviceCard.step", { currentStep, totalSteps }) }),
            " ",
            /* @__PURE__ */ jsx45("span", { className: "font-light", children: stepDescription })
          ] }) })
        ] }),
        currentStep != null && /* @__PURE__ */ jsx45("div", { className: "@[560px]:hidden", children: /* @__PURE__ */ jsxs29("span", { className: "text-xs font-normal leading-[18px] text-fg-primary", children: [
          /* @__PURE__ */ jsx45("span", { className: "font-normal", children: t("serviceCard.step", { currentStep, totalSteps }) }),
          " ",
          /* @__PURE__ */ jsx45("span", { className: "font-light", children: stepDescription })
        ] }) })
      ]
    }
  );
};
ServiceCard.displayName = "ServiceCard";

// src/components/molecules/DocumentDownload.tsx
import { Fragment as Fragment5, jsx as jsx46, jsxs as jsxs30 } from "react/jsx-runtime";
var DocumentDownload = ({
  fileName,
  fileSize,
  onDownload,
  description,
  testID,
  className,
  disabled = false,
  downloading = false
}) => {
  const getFileIconMeta = (name) => {
    const ext = name?.split(".").pop()?.toLowerCase() ?? "";
    switch (ext) {
      case "pdf":
        return { text: "PDF", color: "var(--color-error-600)" };
      case "png":
      case "jpg":
        return {
          text: ext.toUpperCase(),
          color: "var(--color-brand-blue-500)"
        };
      default: {
        const t = ext ? ext.slice(0, 3).toUpperCase() : "FILE";
        return { text: t, color: "var(--color-gray-600)" };
      }
    }
  };
  const { text: fileTypeText, color: fileTypeColor } = getFileIconMeta(fileName);
  return /* @__PURE__ */ jsxs30(
    "div",
    {
      "data-testid": testID,
      className: cn(
        "flex items-start gap-3 p-4",
        "bg-white rounded-xl border border-brand-tint-300",
        className
      ),
      children: [
        /* @__PURE__ */ jsx46("div", { className: "flex-shrink-0", children: /* @__PURE__ */ jsx46(
          FileIcon_default,
          {
            variant: "solid",
            text: fileTypeText,
            color: fileTypeColor,
            testID: testID ? `${testID}-file-icon` : void 0
          }
        ) }),
        /* @__PURE__ */ jsxs30("div", { className: "flex-1 min-w-0 space-y-0.5", children: [
          /* @__PURE__ */ jsx46(
            Text,
            {
              size: "sm",
              weight: "light",
              testID: fileName,
              title: fileName,
              className: "text-gray-700 text-sm leading-[20px] block truncate",
              children: fileName
            }
          ),
          /* @__PURE__ */ jsxs30("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx46(
              Text,
              {
                size: "sm",
                weight: "light",
                dir: "ltr",
                testID: fileSize,
                className: "text-gray-600 text-[14px] leading-[20px]",
                children: fileSize
              }
            ),
            description && /* @__PURE__ */ jsxs30(Fragment5, { children: [
              /* @__PURE__ */ jsx46("div", { className: "w-px h-3 bg-brand-tint-100" }),
              /* @__PURE__ */ jsx46(
                Text,
                {
                  size: "sm",
                  weight: "normal",
                  testID: "file-description",
                  className: "text-gray-600 text-[14px] leading-[20px] truncate",
                  children: description
                }
              )
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx46("div", { className: "flex-shrink-0", children: downloading ? /* @__PURE__ */ jsx46(Spinner, { className: "h-5 w-5 text-fg-quaternary" }) : /* @__PURE__ */ jsx46(
          "button",
          {
            type: "button",
            onClick: onDownload,
            disabled,
            "data-testid": testID ? `${testID}-download-btn` : void 0,
            className: "text-fg-quaternary hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed !cursor-pointer transition-colors",
            children: /* @__PURE__ */ jsx46(Icon, { size: 20, name: "Download", strokeWidth: 1.5 })
          }
        ) })
      ]
    }
  );
};
DocumentDownload.displayName = "DocumentDownload";

// src/components/molecules/Dropdown.tsx
import { useState as useState10, useRef as useRef9, useEffect as useEffect9 } from "react";
import { createPortal } from "react-dom";
import { cva as cva11 } from "class-variance-authority";
import { useTranslation as useTranslation17 } from "@tasama/i18n";
import { jsx as jsx47, jsxs as jsxs31 } from "react/jsx-runtime";
var isCountryCode = (text) => {
  return !!text && /^[A-Z]{2}$/i.test(text);
};
var CountryFlag2 = ({
  countryCode,
  className
}) => /* @__PURE__ */ jsx47(
  "span",
  {
    className: cn(
      "fi",
      `fi-${countryCode.toLowerCase()}`,
      "rounded-xxs",
      className
    ),
    "aria-hidden": "true"
  }
);
var dropdownVariants = cva11(
  // Base styles for the dropdown container
  "relative w-full min-w-[125px]",
  {
    variants: {
      // Size variants — text-size intentionally omitted here; it is applied
      // only to the input field via inputVariants. Putting text-sm/base/lg on
      // the outer wrapper restricts the anonymous block's line-height (20px)
      // while sibling components like InputWithLabel inherit the page default
      // (24px), causing a 4 px height mismatch in grid layouts.
      size: {
        sm: "",
        md: "",
        lg: ""
      }
    },
    defaultVariants: {
      size: "sm"
    }
  }
);
var inputVariants = cva11(
  // Base styles for the input field
  "w-full flex items-center justify-between rounded-lg border transition-colors motion-safe:duration-150 cursor-pointer focus-visible:outline-none appearance-none",
  {
    variants: {
      // State variants using design tokens
      state: {
        default: [
          "bg-background border-tertiary text-fg-secondary",
          "hover:border-tertiary hover:shadow-[0_0_0_4px_var(--color-brand-tint-200)] focus:border-tertiary focus:shadow-[0_0_0_4px_var(--color-brand-tint-200)]"
        ],
        filled: [
          "bg-background border-tertiary text-fg-primary",
          "hover:border-tertiary hover:shadow-[0_0_0_4px_var(--color-brand-tint-200)] focus:border-tertiary focus:shadow-[0_0_0_4px_var(--color-brand-tint-200)]"
        ],
        focused: [
          "bg-background border-tertiary text-fg-primary",
          "shadow-[0_0_0_4px_var(--color-brand-tint-200)]"
        ],
        error: [
          "bg-background border-error text-fg-primary",
          "focus:border-error focus:shadow-[0_0_0_4px_var(--color-bg-error-secondary)]"
        ],
        disabled: [
          "bg-background border-border text-disabled cursor-not-allowed"
        ]
      },
      // Size variants
      size: {
        sm: "h-12 px-[18px] py-[14px] text-sm",
        md: "h-14 px-5 py-4 text-base",
        lg: "h-16 px-6 py-5 text-lg"
      }
    },
    defaultVariants: {
      state: "default",
      size: "sm"
    }
  }
);
var menuStyles = "fixed z-dropdown bg-background border border-border rounded-xl shadow-lg py-1 max-h-64 overflow-y-auto overflow-x-hidden";
var menuItemVariants = cva11(
  // Base styles for menu items
  "flex items-center justify-between px-[14px] py-2 mx-[6px] my-[1px] cursor-pointer transition-colors duration-200 rounded-md overflow-hidden text-fg-primary hover:bg-bg-dropdown-item-hover",
  {
    variants: {
      isFocused: {
        true: "bg-bg-dropdown-item-hover",
        false: ""
      }
    },
    defaultVariants: {
      isFocused: false
    }
  }
);
var Dropdown = ({
  className,
  label,
  placeholder,
  options,
  value,
  onChange,
  onBlur,
  disabled = false,
  error = false,
  hintText,
  errorMessage,
  showLeftIcon = true,
  leftIconName = "Mail",
  size = "sm",
  inputClassName,
  required,
  testID,
  menuDir,
  onMenuScrollToBottom,
  isFetchingMore,
  clearable = false,
  ...props
}) => {
  const { t } = useTranslation17("common");
  const defaultPlaceholder = t("dropdown.selectOption");
  const [isOpen, setIsOpen] = useState10(false);
  const [focusedIndex, setFocusedIndex] = useState10(-1);
  const dropdownRef = useRef9(null);
  const inputRef = useRef9(null);
  const menuRef = useRef9(null);
  const menuId = `${testID}-menu`;
  const typeaheadBuffer = useRef9("");
  const typeaheadTimeout = useRef9(null);
  useEffect9(() => {
    const timeout = typeaheadTimeout.current;
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, []);
  const selectedOption = options.find(
    (option) => option.value === value || option.label === value
  );
  const isOptionSelected = (option) => option.value === value || option.label === value;
  const describedByIds = [];
  if (hintText) describedByIds.push(`${testID}-hint`);
  if (error && errorMessage && !hintText)
    describedByIds.push(`${testID}-error`);
  const ariaDescribedBy = describedByIds.length ? describedByIds.join(" ") : void 0;
  const getState = () => {
    if (disabled) return "disabled";
    if (error) return "error";
    if (isOpen) return "focused";
    if (selectedOption) return "filled";
    return "default";
  };
  const currentState = getState();
  useEffect9(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (dropdownRef.current && !dropdownRef.current.contains(target) && (!menuRef.current || !menuRef.current.contains(target))) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const handleKeyDown = (event) => {
    if (disabled) return;
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(
            selectedOption ? options.findIndex(
              (opt) => opt.value === value || opt.label === value
            ) : 0
          );
        } else if (focusedIndex >= 0) {
          const option = options[focusedIndex];
          if (!option?.disabled) {
            onChange?.(
              option && clearable && isOptionSelected(option) ? "" : option?.value ?? ""
            );
            setIsOpen(false);
            setFocusedIndex(-1);
          }
        }
        break;
      case "ArrowDown":
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(
            selectedOption ? options.findIndex(
              (opt) => opt.value === value || opt.label === value
            ) : 0
          );
        } else {
          const nextIndex = focusedIndex < options.length - 1 ? focusedIndex + 1 : 0;
          setFocusedIndex(nextIndex);
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (isOpen) {
          const prevIndex = focusedIndex > 0 ? focusedIndex - 1 : options.length - 1;
          setFocusedIndex(prevIndex);
        }
        break;
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      case "Tab":
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          typeaheadBuffer.current += event.key.toLowerCase();
          if (typeaheadTimeout.current) clearTimeout(typeaheadTimeout.current);
          typeaheadTimeout.current = setTimeout(() => {
            typeaheadBuffer.current = "";
          }, 500);
          const search = typeaheadBuffer.current;
          const matchIndex = options.findIndex(
            (opt) => !opt.disabled && opt.label.toLowerCase().startsWith(search)
          );
          if (matchIndex !== -1) {
            setIsOpen(true);
            setFocusedIndex(matchIndex);
          }
        }
        break;
    }
  };
  const handleOptionSelect = (option) => {
    if (option.disabled || disabled) return;
    const nextValue = clearable && isOptionSelected(option) ? "" : option.value;
    setTimeout(() => {
      onChange?.(nextValue);
      setIsOpen(false);
      setFocusedIndex(-1);
    }, 0);
  };
  useEffect9(() => {
    if (!isOpen || !inputRef.current) return;
    const locked = [];
    let el = inputRef.current.parentElement;
    while (el && el !== document.body) {
      const { overflow, overflowY } = getComputedStyle(el);
      if (/auto|scroll/.test(overflow + overflowY)) {
        locked.push({ el, overflow: el.style.overflow });
        el.style.overflow = "hidden";
      }
      el = el.parentElement;
    }
    const bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      locked.forEach(({ el: lockedEl, overflow }) => {
        lockedEl.style.overflow = overflow;
      });
      document.body.style.overflow = bodyOverflow;
    };
  }, [isOpen]);
  useEffect9(() => {
    if (!isOpen || focusedIndex < 0 || !menuRef.current) return;
    const focusedEl = menuRef.current.querySelector(
      '[data-focused="true"]'
    );
    focusedEl?.scrollIntoView({ block: "nearest" });
  }, [focusedIndex, isOpen]);
  useEffect9(() => {
    if (!isOpen || !menuRef.current || !inputRef.current) return;
    const menu = menuRef.current;
    const inputRect = inputRef.current.getBoundingClientRect();
    const menuHeight = menu.scrollHeight;
    const spaceBelow = window.innerHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;
    const openUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;
    menu.style.left = `${inputRect.left}px`;
    menu.style.width = `${inputRect.width}px`;
    if (openUpward) {
      menu.style.bottom = `${window.innerHeight - inputRect.top + 4}px`;
      menu.style.top = "auto";
    } else {
      menu.style.top = `${inputRect.bottom + 4}px`;
      menu.style.bottom = "auto";
    }
  }, [isOpen]);
  const handleInputClick = () => {
    if (disabled) return;
    if (!isOpen) {
      setFocusedIndex(
        selectedOption ? options.findIndex(
          (opt) => opt.value === value || opt.label === value
        ) : 0
      );
    }
    setIsOpen(!isOpen);
  };
  const handleBlur = (event) => {
    setTimeout(() => {
      if (dropdownRef.current?.contains(document.activeElement) || menuRef.current?.contains(document.activeElement)) {
        return;
      }
      setIsOpen(false);
      setFocusedIndex(-1);
      if (onBlur) {
        onBlur(event);
      }
    }, 150);
  };
  return /* @__PURE__ */ jsxs31(
    "div",
    {
      ref: dropdownRef,
      className: cn(dropdownVariants({ size }), "space-y-sm", className),
      "data-testid": testID,
      ...props,
      children: [
        label && /* @__PURE__ */ jsxs31(
          Label,
          {
            htmlFor: `${testID}-input`,
            className: cn(
              "block",
              "font-normal",
              disabled ? "text-disabled" : "text-fg-secondary"
            ),
            children: [
              label,
              required === false && /* @__PURE__ */ jsx47("span", { className: "ms-1", children: t("actions.optional") })
            ]
          }
        ),
        /* @__PURE__ */ jsxs31("div", { className: "relative", children: [
          /* @__PURE__ */ jsxs31(
            "div",
            {
              ref: inputRef,
              id: `${testID}-input`,
              className: cn(
                inputVariants({ state: currentState, size }),
                inputClassName,
                "flex items-center justify-between px-0 relative"
              ),
              onClick: handleInputClick,
              onKeyDown: handleKeyDown,
              onBlur: handleBlur,
              tabIndex: disabled ? -1 : 0,
              role: "combobox",
              "aria-expanded": isOpen,
              "aria-controls": menuId,
              "aria-haspopup": "listbox",
              "aria-labelledby": label ? `${testID}-label` : void 0,
              "aria-describedby": ariaDescribedBy,
              "data-testid": `${testID}-input`,
              children: [
                /* @__PURE__ */ jsxs31("div", { className: "flex items-center gap-2 flex-1 min-w-0 ms-4", children: [
                  selectedOption?.icon ? /* @__PURE__ */ jsx47("div", { className: "w-5 h-5 shrink-0 flex items-center justify-center", children: selectedOption.icon }) : isCountryCode(selectedOption?.supportingText) ? /* @__PURE__ */ jsx47("div", { className: "w-5 h-5 shrink-0 flex items-center justify-center", children: /* @__PURE__ */ jsx47(CountryFlag2, { countryCode: selectedOption.supportingText }) }) : showLeftIcon ? /* @__PURE__ */ jsx47(
                    Icon,
                    {
                      name: leftIconName,
                      className: cn(
                        "w-5 h-5 shrink-0 me-2",
                        disabled ? "text-disabled" : "text-quaternary"
                      )
                    }
                  ) : null,
                  /* @__PURE__ */ jsxs31(
                    "span",
                    {
                      className: cn(
                        "text-start font-light text-sm truncate flex-1 min-w-0",
                        selectedOption ? "text-fg-primary" : "text-placeholder",
                        disabled && "text-disabled"
                      ),
                      title: selectedOption ? selectedOption.label : placeholder || defaultPlaceholder,
                      "data-testid": `${testID}-selected-label`,
                      children: [
                        menuDir === "ltr" ? "\u200E" : "",
                        selectedOption ? selectedOption.label : placeholder || defaultPlaceholder
                      ]
                    }
                  )
                ] }),
                /* @__PURE__ */ jsx47("div", { className: "shrink-0 me-4 pointer-events-none self-center", children: /* @__PURE__ */ jsx47(
                  Icon,
                  {
                    name: "ChevronDown",
                    className: cn(
                      "w-5 h-5 transition-transform duration-200",
                      isOpen && "rotate-180",
                      disabled ? "text-disabled" : "text-quaternary"
                    )
                  }
                ) })
              ]
            }
          ),
          isOpen && createPortal(
            /* @__PURE__ */ jsxs31(
              "div",
              {
                ref: menuRef,
                id: menuId,
                className: cn(menuStyles),
                role: "listbox",
                "aria-labelledby": label ? `${testID}-label` : void 0,
                "data-testid": `${testID}-menu`,
                onMouseDown: (e) => e.preventDefault(),
                onScroll: onMenuScrollToBottom ? (e) => {
                  const el = e.currentTarget;
                  const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
                  console.log("[Dropdown] scroll", {
                    scrollTop: el.scrollTop,
                    clientHeight: el.clientHeight,
                    scrollHeight: el.scrollHeight,
                    distanceFromBottom
                  });
                  if (distanceFromBottom <= 20) {
                    console.log(
                      "[Dropdown] bottom reached \u2014 firing onMenuScrollToBottom"
                    );
                    onMenuScrollToBottom();
                  }
                } : void 0,
                children: [
                  options.map((option, index) => {
                    const isSelected = option.value === value || option.label === value;
                    const isFocused = index === focusedIndex;
                    return /* @__PURE__ */ jsx47(
                      "div",
                      {
                        className: cn(
                          menuItemVariants({ isFocused }),
                          option.disabled && "opacity-50 cursor-not-allowed",
                          "relative"
                        ),
                        onClick: () => handleOptionSelect(option),
                        role: "option",
                        "aria-selected": isSelected,
                        "aria-disabled": option.disabled,
                        "data-focused": isFocused,
                        "data-testid": `${testID}-option-${option.value}`,
                        children: option.icon || isCountryCode(option.supportingText) ? /* @__PURE__ */ jsxs31("div", { className: "grid grid-cols-10 items-center w-full min-w-0 relative gap-1", children: [
                          /* @__PURE__ */ jsx47("div", { className: "col-span-3 flex items-center justify-center", children: option.icon ? option.icon : /* @__PURE__ */ jsx47(CountryFlag2, { countryCode: option.supportingText }) }),
                          /* @__PURE__ */ jsxs31("div", { className: "flex col-span-6 flex-col min-w-0", children: [
                            /* @__PURE__ */ jsxs31(
                              "span",
                              {
                                className: "text-sm font-light text-fg-primary truncate block w-full",
                                title: option.label,
                                style: { maxWidth: "100%" },
                                children: [
                                  menuDir === "ltr" ? "\u200E" : "",
                                  option.label
                                ]
                              }
                            ),
                            option.supportingText && !isCountryCode(option.supportingText) && /* @__PURE__ */ jsx47(
                              "span",
                              {
                                className: "text-xs font-light font-sans text-fg-tertiary truncate block w-full",
                                title: option.supportingText,
                                style: { maxWidth: "100%" },
                                children: option.supportingText
                              }
                            )
                          ] }),
                          /* @__PURE__ */ jsx47("div", { className: "flex col-span-1 items-center justify-center", children: isSelected ? /* @__PURE__ */ jsx47(
                            Icon,
                            {
                              name: "Check",
                              className: "w-5 h-5 text-fg-brand-primary shrink-0"
                            }
                          ) : null })
                        ] }) : /* @__PURE__ */ jsxs31("div", { className: "grid grid-cols-10 items-center w-full min-w-0 relative gap-1", children: [
                          /* @__PURE__ */ jsxs31("div", { className: "col-span-9 flex flex-col min-w-0", children: [
                            /* @__PURE__ */ jsxs31(
                              "span",
                              {
                                className: "text-sm font-light text-fg-primary truncate block w-full",
                                title: option.label,
                                style: { maxWidth: "100%" },
                                children: [
                                  menuDir === "ltr" ? "\u200E" : "",
                                  option.label
                                ]
                              }
                            ),
                            option.supportingText && !isCountryCode(option.supportingText) && /* @__PURE__ */ jsx47(
                              "span",
                              {
                                className: "text-xs font-light text-fg-tertiary truncate block w-full",
                                title: option.supportingText,
                                style: { maxWidth: "100%" },
                                children: option.supportingText
                              }
                            )
                          ] }),
                          /* @__PURE__ */ jsx47("div", { className: "col-span-1 flex items-center justify-center", children: isSelected ? /* @__PURE__ */ jsx47(
                            Icon,
                            {
                              name: "Check",
                              className: "w-5 h-5 text-fg-brand-primary shrink-0"
                            }
                          ) : null })
                        ] })
                      },
                      `${option.value}_${index}`
                    );
                  }),
                  isFetchingMore && /* @__PURE__ */ jsx47(
                    "div",
                    {
                      className: "flex items-center justify-center py-2",
                      "data-testid": `${testID}-fetching-more`,
                      children: /* @__PURE__ */ jsx47(Spinner, { className: "text-fg-secondary" })
                    }
                  )
                ]
              }
            ),
            document.body
          )
        ] }),
        !error && hintText && /* @__PURE__ */ jsx47(
          "p",
          {
            id: `${testID}-hint`,
            "data-testid": `${testID}-hint`,
            className: cn(
              "text-xs font-normal",
              disabled ? "text-disabled" : "text-quaternary"
            ),
            children: hintText
          }
        ),
        error && errorMessage && /* @__PURE__ */ jsx47(
          "p",
          {
            id: `${testID}-error`,
            "data-testid": `${testID}-error`,
            className: "text-error-primary text-xs font-light",
            children: errorMessage
          }
        )
      ]
    }
  );
};
Dropdown.displayName = "Dropdown";

// src/components/molecules/SearchInputSelection.tsx
import React18, { useState as useState11, useRef as useRef10, useEffect as useEffect10 } from "react";
import { cva as cva12 } from "class-variance-authority";
import { useTranslation as useTranslation18 } from "@tasama/i18n";
import { jsx as jsx48, jsxs as jsxs32 } from "react/jsx-runtime";
var searchInputSelectionVariants = cva12(
  // Base styles for the container
  "relative w-full min-w-[125px]",
  {
    variants: {
      // Size variants
      size: {
        sm: "text-sm",
        md: "text-base",
        lg: "text-lg"
      }
    },
    defaultVariants: {
      size: "sm"
    }
  }
);
var inputVariants2 = cva12(
  // Base styles for the input field
  "w-full flex items-center justify-between rounded-xl border transition-colors motion-safe:duration-150 cursor-text focus-visible:outline-none appearance-none",
  {
    variants: {
      // State variants using design tokens
      state: {
        default: [
          "bg-background border-tertiary text-quaternary",
          "hover:border-tertiary hover:shadow-[0_0_0_4px_var(--color-brand-tint-200)] focus-within:border-tertiary focus-within:shadow-[0_0_0_4px_var(--color-brand-tint-200)]"
        ],
        filled: [
          "bg-background border-tertiary text-fg-primary",
          "hover:border-tertiary hover:shadow-[0_0_0_4px_var(--color-brand-tint-200)] focus-within:border-tertiary focus-within:shadow-[0_0_0_4px_var(--color-brand-tint-200)]"
        ],
        focused: [
          "bg-background border-tertiary text-fg-primary",
          "shadow-[0_0_0_4px_var(--color-brand-tint-200)]"
        ],
        error: [
          "bg-background border-error text-fg-primary",
          "focus-within:border-error focus-within:shadow-[0_0_0_4px_var(--color-bg-error-secondary)]"
        ],
        disabled: [
          "bg-background border-border text-disabled cursor-not-allowed"
        ]
      },
      // Size variants
      size: {
        sm: "h-12 px-[18px] py-[14px] text-sm",
        md: "h-14 px-5 py-4 text-base",
        lg: "h-16 px-6 py-5 text-lg"
      }
    },
    defaultVariants: {
      state: "default",
      size: "sm"
    }
  }
);
var menuVariants = cva12(
  // Base styles for the dropdown menu
  "absolute z-dropdown w-full mt-1 bg-background border border-border rounded-xl shadow-lg py-1 max-h-64 overflow-y-auto overflow-x-hidden",
  {
    variants: {
      isOpen: {
        true: "opacity-100 visible",
        false: "opacity-0 invisible"
      }
    },
    defaultVariants: {
      isOpen: false
    }
  }
);
var menuItemVariants2 = cva12(
  // Base styles for menu items
  "flex items-center justify-between px-[14px] py-2 mx-[6px] my-[1px] cursor-pointer transition-colors duration-200 rounded-md overflow-hidden text-fg-primary hover:bg-bg-dropdown-item-hover",
  {
    variants: {
      isSelected: {
        true: "bg-bg-dropdown-item-hover",
        false: ""
      }
    },
    defaultVariants: {
      isSelected: false
    }
  }
);
var SearchInputSelection = ({
  className,
  label,
  placeholder,
  options,
  value,
  onChange,
  onSearch,
  onBlur,
  disabled = false,
  error = false,
  hintText,
  errorMessage,
  size = "sm",
  inputClassName,
  testID,
  minSearchLength = 0,
  debounceDelay = 300,
  showLeftIcon: _showLeftIcon,
  ...props
}) => {
  const { t } = useTranslation18("common");
  const defaultPlaceholder = t("labels.searchAndSelect");
  const [isOpen, setIsOpen] = useState11(false);
  const [focusedIndex, setFocusedIndex] = useState11(-1);
  const [searchValue, setSearchValue] = useState11("");
  const containerRef = useRef10(null);
  const inputRef = useRef10(null);
  const menuRef = useRef10(null);
  const menuId = `${testID}-menu`;
  const selectedOption = options.find(
    (option) => option.value === value || option.label === value
  );
  const filteredOptions = React18.useMemo(() => {
    if (onSearch) {
      return options;
    }
    if (searchValue && searchValue.length >= minSearchLength) {
      const searchLower = searchValue.toLowerCase();
      return options.filter(
        (option) => option.label.toLowerCase().includes(searchLower) || option.supportingText?.toLowerCase().includes(searchLower)
      );
    }
    if (selectedOption && !searchValue) {
      const selectedLower = selectedOption.label.toLowerCase();
      const selectedWords = selectedLower.split(" ").filter((word) => word.length > 2);
      return options.filter((option) => {
        if (option.value === selectedOption.value) {
          return true;
        }
        const optionLower = option.label.toLowerCase();
        return selectedWords.some((word) => optionLower.includes(word)) || selectedWords.some((selectedWord) => {
          const optionWords = optionLower.split(" ").filter((word) => word.length > 2);
          return optionWords.some(
            (optionWord) => selectedWord.includes(optionWord) || optionWord.includes(selectedWord)
          );
        });
      });
    }
    return options;
  }, [options, searchValue, minSearchLength, selectedOption, onSearch]);
  const describedByIds = [];
  if (hintText) describedByIds.push(`${testID}-hint`);
  if (error && errorMessage && !hintText)
    describedByIds.push(`${testID}-error`);
  const ariaDescribedBy = describedByIds.length ? describedByIds.join(" ") : void 0;
  const getState = () => {
    if (disabled) return "disabled";
    if (error) return "error";
    if (isOpen) return "focused";
    if (selectedOption) return "filled";
    return "default";
  };
  const currentState = getState();
  const handleInputChange = (event) => {
    const newValue = event.target.value;
    if (selectedOption && newValue !== selectedOption.label) {
      onChange?.("");
    }
    setIsOpen(true);
    setFocusedIndex(-1);
    setSearchValue(newValue);
  };
  const handleInputFocus = () => {
    if (disabled) return;
    setIsOpen(true);
  };
  const handleInputBlur = (event) => {
    setTimeout(() => {
      if (!menuRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        setFocusedIndex(-1);
        setSearchValue("");
        onBlur?.(event);
      }
    }, 150);
  };
  const handleOptionSelect = (option) => {
    if (option.disabled || disabled) return;
    setSearchValue("");
    setIsOpen(false);
    setFocusedIndex(-1);
    onChange?.(option.value);
  };
  const handleKeyDown = (event) => {
    if (disabled) return;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          const nextIndex = focusedIndex < filteredOptions.length - 1 ? focusedIndex + 1 : 0;
          setFocusedIndex(nextIndex);
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (isOpen) {
          const prevIndex = focusedIndex > 0 ? focusedIndex - 1 : filteredOptions.length - 1;
          setFocusedIndex(prevIndex);
        }
        break;
      case "Enter":
        event.preventDefault();
        if (isOpen && focusedIndex >= 0 && filteredOptions[focusedIndex]) {
          handleOptionSelect(filteredOptions[focusedIndex]);
        }
        break;
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        setSearchValue("");
        inputRef.current?.blur();
        break;
      case "Tab":
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };
  useEffect10(() => {
    if (!onSearch) return;
    const handle = setTimeout(() => onSearch(searchValue), debounceDelay);
    return () => clearTimeout(handle);
  }, [searchValue, onSearch, debounceDelay]);
  useEffect10(() => {
    if (!isOpen || focusedIndex < 0 || !menuRef.current) return;
    const focusedEl = menuRef.current.querySelector(
      '[data-focused="true"]'
    );
    focusedEl?.scrollIntoView({ block: "nearest" });
  }, [focusedIndex, isOpen]);
  useEffect10(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const displayValue = searchValue || (selectedOption ? selectedOption.label : "");
  return /* @__PURE__ */ jsxs32(
    "div",
    {
      ref: containerRef,
      className: cn(
        searchInputSelectionVariants({ size }),
        "space-y-sm",
        className
      ),
      "data-testid": testID,
      ...props,
      children: [
        label && /* @__PURE__ */ jsx48(
          Label,
          {
            htmlFor: `${testID}-input`,
            className: cn(
              "inline-block",
              "font-normal",
              disabled ? "text-disabled" : "text-fg-secondary"
            ),
            children: label
          }
        ),
        /* @__PURE__ */ jsxs32("div", { className: "relative", children: [
          /* @__PURE__ */ jsx48(
            "div",
            {
              className: cn(
                inputVariants2({ state: currentState, size }),
                inputClassName,
                "pt-1 pb-1"
              ),
              children: /* @__PURE__ */ jsxs32("div", { className: "flex items-center gap-2 flex-1 min-w-0 h-full", children: [
                selectedOption?.icon && !searchValue ? /* @__PURE__ */ jsx48("div", { className: "w-5 h-5 flex-shrink-0 flex items-center justify-center", children: selectedOption.icon }) : /* @__PURE__ */ jsx48(
                  Icon,
                  {
                    name: "Search",
                    className: cn(
                      "w-5 h-5 flex-shrink-0",
                      disabled ? "text-disabled" : "text-quaternary"
                    )
                  }
                ),
                /* @__PURE__ */ jsx48(
                  "input",
                  {
                    ref: inputRef,
                    id: `${testID}-input`,
                    type: "text",
                    value: displayValue,
                    onChange: handleInputChange,
                    onFocus: handleInputFocus,
                    onBlur: handleInputBlur,
                    onKeyDown: handleKeyDown,
                    placeholder: placeholder || defaultPlaceholder,
                    disabled,
                    className: cn(
                      "flex-1 min-w-0 h-full text-start truncate font-light font-sans text-sm bg-transparent border-0 outline-none appearance-none shadow-none ring-0 focus:outline-none focus:ring-0 focus:shadow-none",
                      displayValue ? "text-fg-primary" : "text-quaternary placeholder:text-quaternary",
                      disabled && "text-disabled cursor-not-allowed"
                    ),
                    role: "combobox",
                    "aria-expanded": isOpen,
                    "aria-controls": menuId,
                    "aria-haspopup": "listbox",
                    "aria-labelledby": label ? `${testID}-label` : void 0,
                    "aria-describedby": ariaDescribedBy,
                    "data-testid": `${testID}-input`
                  }
                )
              ] })
            }
          ),
          /* @__PURE__ */ jsxs32(
            "div",
            {
              ref: menuRef,
              id: menuId,
              className: cn(menuVariants({ isOpen })),
              role: "listbox",
              "aria-labelledby": label ? `${testID}-label` : void 0,
              "data-testid": `${testID}-menu`,
              children: [
                filteredOptions.map((option, index) => {
                  const isSelected = option.value === value || option.label === value;
                  const isFocused = index === focusedIndex;
                  return /* @__PURE__ */ jsxs32(
                    "div",
                    {
                      className: cn(
                        menuItemVariants2({ isSelected: isSelected || isFocused }),
                        option.disabled && "opacity-50 cursor-not-allowed"
                      ),
                      onMouseDown: (e) => e.preventDefault(),
                      onClick: () => handleOptionSelect(option),
                      role: "option",
                      "aria-selected": isSelected,
                      "aria-disabled": option.disabled,
                      "data-focused": isFocused,
                      "data-testid": `${testID}-option-${option.value}`,
                      children: [
                        /* @__PURE__ */ jsxs32("div", { className: "flex items-center gap-2 flex-1 min-w-0 w-[90%]", children: [
                          option.icon && /* @__PURE__ */ jsx48("div", { className: "w-5 h-5 flex-shrink-0 flex items-center justify-center", children: option.icon }),
                          /* @__PURE__ */ jsxs32("div", { className: "flex flex-col flex-1 min-w-0 max-w-[calc(100%-2rem)]", children: [
                            /* @__PURE__ */ jsx48(
                              "span",
                              {
                                className: "text-sm font-light text-fg-primary truncate",
                                title: option.label,
                                children: option.label
                              }
                            ),
                            option.supportingText && /* @__PURE__ */ jsx48(
                              "span",
                              {
                                className: "text-xs font-light font-sans text-fg-tertiary truncate",
                                title: option.supportingText,
                                children: option.supportingText
                              }
                            )
                          ] })
                        ] }),
                        isSelected && /* @__PURE__ */ jsx48(
                          Icon,
                          {
                            name: "Check",
                            className: "w-5 h-5 text-brand-blue-500 flex-shrink-0 ms-2"
                          }
                        )
                      ]
                    },
                    `${option.value}_${index}`
                  );
                }),
                filteredOptions.length === 0 && searchValue.length >= minSearchLength && /* @__PURE__ */ jsxs32("div", { className: "flex flex-col items-center justify-center py-8 text-center", children: [
                  /* @__PURE__ */ jsx48(Icon, { name: "Search", className: "w-8 h-8 text-quaternary mb-2" }),
                  /* @__PURE__ */ jsx48("p", { className: "text-sm text-quaternary", children: t("search.noResults") }),
                  /* @__PURE__ */ jsx48("p", { className: "text-xs text-quaternary mt-1", children: t("search.adjustSearchTerms") })
                ] })
              ]
            }
          )
        ] }),
        !error && hintText && /* @__PURE__ */ jsx48(
          "p",
          {
            id: `${testID}-hint`,
            "data-testid": `${testID}-hint`,
            className: cn(
              "text-xs font-normal font-sans",
              disabled ? "text-disabled" : "text-quaternary"
            ),
            children: hintText
          }
        ),
        error && errorMessage && /* @__PURE__ */ jsx48(
          "p",
          {
            id: `${testID}-error`,
            "data-testid": `${testID}-error`,
            className: "text-error-primary text-xs font-normal font-sans",
            children: errorMessage
          }
        )
      ]
    }
  );
};
SearchInputSelection.displayName = "SearchInputSelection";

// src/components/molecules/FilterTriggerButton.tsx
import { jsx as jsx49, jsxs as jsxs33 } from "react/jsx-runtime";
var FilterTriggerButton = ({
  label,
  onClick,
  iconName = "Filter",
  testID,
  className
}) => {
  return /* @__PURE__ */ jsxs33(
    "button",
    {
      type: "button",
      onClick,
      "data-testid": testID,
      className: cn(
        "flex h-12 w-full items-center gap-md rounded-xl border border-solid border-tertiary bg-background px-[18px] py-[14px]",
        "text-sm font-normal text-quaternary",
        "transition-shadow motion-safe:duration-150",
        "focus-visible:outline-none focus-visible:shadow-[0_0_0_4px_var(--color-brand-tint-200)]",
        className
      ),
      children: [
        /* @__PURE__ */ jsx49(Icon, { name: iconName, size: 20, className: "text-quaternary", "aria-hidden": true }),
        /* @__PURE__ */ jsx49("span", { className: "truncate", children: label })
      ]
    }
  );
};
FilterTriggerButton.displayName = "FilterTriggerButton";

// src/components/molecules/Flex.tsx
import { jsx as jsx50 } from "react/jsx-runtime";
function Flex({
  className,
  direction = "row",
  align = "start",
  justify = "start",
  wrap = "nowrap",
  gap = "none",
  testID,
  children,
  ref,
  ...props
}) {
  const directionClasses = {
    row: "flex-row",
    col: "flex-col",
    "row-reverse": "flex-row-reverse",
    "col-reverse": "flex-col-reverse"
  };
  const alignClasses = {
    start: "items-start",
    center: "items-center",
    end: "items-end",
    stretch: "items-stretch",
    baseline: "items-baseline"
  };
  const justifyClasses = {
    start: "justify-start",
    center: "justify-center",
    end: "justify-end",
    between: "justify-between",
    around: "justify-around",
    evenly: "justify-evenly"
  };
  const wrapClasses = {
    nowrap: "flex-nowrap",
    wrap: "flex-wrap",
    "wrap-reverse": "flex-wrap-reverse"
  };
  const gapClasses = {
    none: "",
    xs: "gap-2",
    sm: "gap-4",
    md: "gap-6",
    lg: "gap-8",
    xl: "gap-12"
  };
  return /* @__PURE__ */ jsx50(
    "div",
    {
      ref,
      "data-testid": testID,
      className: cn(
        "flex",
        directionClasses[direction],
        alignClasses[align],
        justifyClasses[justify],
        wrapClasses[wrap],
        gapClasses[gap],
        className
      ),
      ...props,
      children
    }
  );
}
Flex.displayName = "Flex";

// src/components/molecules/FloatingMenu.tsx
import React19, { useState as useState12, useRef as useRef11, useEffect as useEffect11 } from "react";
import { createPortal as createPortal2 } from "react-dom";
import { cva as cva13 } from "class-variance-authority";
import { jsx as jsx51, jsxs as jsxs34 } from "react/jsx-runtime";
var menuItemVariants3 = cva13(
  "flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors duration-200 text-xs font-normal focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-blue-500 rounded-sm",
  {
    variants: {
      variant: {
        default: "text-fg-primary hover:bg-bg-secondary",
        destructive: "text-error-primary hover:bg-error-primary/10"
      },
      isDisabled: {
        true: "opacity-50 cursor-not-allowed pointer-events-none",
        false: ""
      },
      isUnread: {
        true: "bg-bg-brand-primary",
        false: ""
      }
    },
    defaultVariants: {
      variant: "default",
      isDisabled: false,
      isUnread: false
    }
  }
);
var menuContainerVariants = cva13(
  "fixed z-floating-menu bg-background border border-border rounded-xl shadow-lg py-2 min-w-[200px] overflow-hidden font-normal max-h-[400px] overflow-y-auto",
  {
    variants: {
      size: {
        sm: "min-w-[160px]",
        md: "min-w-[200px]",
        lg: "min-w-[320px]"
      }
    },
    defaultVariants: {
      size: "md"
    }
  }
);
var FloatingMenu = ({
  items,
  isOpen,
  onClose,
  anchorRef,
  placement = "bottom-start",
  size = "md",
  testID,
  className,
  ...props
}) => {
  const menuRef = useRef11(null);
  const itemRefs = useRef11([]);
  const [focusedIndex, setFocusedIndex] = useState12(-1);
  const dir = useDir();
  useEffect11(() => {
    if (!isOpen) return;
    setFocusedIndex(-1);
    const firstEnabled = items.findIndex((item) => !item.disabled);
    if (firstEnabled >= 0) {
      requestAnimationFrame(() => {
        itemRefs.current[firstEnabled]?.focus();
        setFocusedIndex(firstEnabled);
      });
    } else {
      menuRef.current?.focus();
    }
  }, [isOpen, items]);
  useEffect11(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      const target = event.target;
      if (menuRef.current && !menuRef.current.contains(target) && anchorRef.current && !anchorRef.current.contains(target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, anchorRef]);
  useEffect11(() => {
    if (!isOpen) return;
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);
  useEffect11(() => {
    if (!isOpen || !anchorRef.current || !menuRef.current) return;
    let rafId;
    const isRtl = dir === "rtl";
    const rtlPlacementMap = {
      "bottom-start": "bottom-end",
      "bottom-end": "bottom-start",
      "top-start": "top-end",
      "top-end": "top-start"
    };
    const resolved = isRtl ? rtlPlacementMap[placement] ?? placement : placement;
    const syncPosition = () => {
      const menu = menuRef.current;
      const anchor = anchorRef.current;
      if (!menu || !anchor) {
        rafId = requestAnimationFrame(syncPosition);
        return;
      }
      const anchorRect = anchor.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      let top = 0;
      let left = 0;
      switch (resolved) {
        case "bottom-start":
          top = anchorRect.bottom + 8;
          left = anchorRect.left;
          break;
        case "bottom-end":
          top = anchorRect.bottom + 8;
          left = anchorRect.right - menuRect.width;
          break;
        case "top-start":
          top = anchorRect.top - menuRect.height - 8;
          left = anchorRect.left;
          break;
        case "top-end":
          top = anchorRect.top - menuRect.height - 8;
          left = anchorRect.right - menuRect.width;
          break;
      }
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      if (left + menuRect.width > viewportWidth) {
        left = viewportWidth - menuRect.width - 8;
      }
      if (left < 8) {
        left = 8;
      }
      if (top + menuRect.height > viewportHeight) {
        top = viewportHeight - menuRect.height - 8;
      }
      if (top < 8) {
        top = 8;
      }
      menu.style.top = `${top}px`;
      menu.style.left = `${left}px`;
      rafId = requestAnimationFrame(syncPosition);
    };
    rafId = requestAnimationFrame(syncPosition);
    return () => cancelAnimationFrame(rafId);
  }, [isOpen, placement, anchorRef, dir]);
  const focusItem = (index) => {
    setFocusedIndex(index);
    itemRefs.current[index]?.focus();
  };
  const handleKeyDown = (event) => {
    const enabledIndices = items.map((item, index) => !item.disabled ? index : -1).filter((index) => index !== -1);
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (focusedIndex === -1 || focusedIndex === enabledIndices[enabledIndices.length - 1]) {
          const nextIndex = enabledIndices[0];
          if (nextIndex !== void 0) focusItem(nextIndex);
        } else {
          const currentPos = enabledIndices.indexOf(focusedIndex);
          const nextIndex = enabledIndices[currentPos + 1];
          if (nextIndex !== void 0) focusItem(nextIndex);
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (focusedIndex === -1 || focusedIndex === enabledIndices[0]) {
          const prevIndex = enabledIndices[enabledIndices.length - 1];
          if (prevIndex !== void 0) focusItem(prevIndex);
        } else {
          const currentPos = enabledIndices.indexOf(focusedIndex);
          const prevIndex = enabledIndices[currentPos - 1];
          if (prevIndex !== void 0) focusItem(prevIndex);
        }
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (focusedIndex >= 0 && !items[focusedIndex]?.disabled) {
          items[focusedIndex]?.onClick?.();
          onClose();
        }
        break;
      case "Tab": {
        event.preventDefault();
        const currentPos = enabledIndices.indexOf(focusedIndex);
        if (event.shiftKey) {
          if (currentPos <= 0) {
            onClose();
            anchorRef.current?.focus();
          } else {
            const prevIndex = enabledIndices[currentPos - 1];
            if (prevIndex !== void 0) focusItem(prevIndex);
          }
        } else {
          if (currentPos === enabledIndices.length - 1 || currentPos === -1) {
            onClose();
            anchorRef.current?.focus();
          } else {
            const nextIndex = enabledIndices[currentPos + 1];
            if (nextIndex !== void 0) focusItem(nextIndex);
          }
        }
        break;
      }
    }
  };
  const handleItemClick = (item) => {
    if (item.disabled) return;
    item.onClick?.();
    onClose();
  };
  if (!isOpen) return null;
  return createPortal2(
    /* @__PURE__ */ jsx51(
      "div",
      {
        ref: menuRef,
        className: cn(menuContainerVariants({ size }), className),
        role: "menu",
        "aria-orientation": "vertical",
        onKeyDown: handleKeyDown,
        tabIndex: -1,
        "data-testid": testID,
        ...props,
        children: items.length === 0 ? /* @__PURE__ */ jsx51("div", { className: "px-4 py-6 text-center text-sm text-fg-quaternary", children: "No notifications" }) : items.map((item, index) => /* @__PURE__ */ jsx51(React19.Fragment, { children: /* @__PURE__ */ jsx51(
          "div",
          {
            ref: (el) => {
              itemRefs.current[index] = el;
            },
            className: cn(
              menuItemVariants3({
                variant: item.variant,
                isDisabled: item.disabled,
                isUnread: item.isUnread
              }),
              focusedIndex === index && !item.disabled && "bg-bg-secondary",
              item.description && "flex-col items-start gap-1"
            ),
            role: "menuitem",
            "aria-disabled": item.disabled,
            tabIndex: item.disabled ? -1 : 0,
            onClick: () => handleItemClick(item),
            onMouseEnter: () => !item.disabled && setFocusedIndex(index),
            onFocus: () => !item.disabled && setFocusedIndex(index),
            "data-testid": `${testID}-item-${item.id}`,
            children: /* @__PURE__ */ jsxs34("div", { className: "flex items-center gap-3 w-full", children: [
              item.icon && /* @__PURE__ */ jsx51("div", { className: "flex items-center justify-center w-8 h-8 rounded-full bg-brand-green-500 p-1.5 flex-shrink-0", children: /* @__PURE__ */ jsx51(
                Icon,
                {
                  name: item.icon,
                  size: 16,
                  className: "text-primary-on-brand"
                }
              ) }),
              /* @__PURE__ */ jsxs34("div", { className: "flex-1 min-w-0", children: [
                /* @__PURE__ */ jsxs34("div", { className: "flex items-center justify-between gap-2", children: [
                  /* @__PURE__ */ jsx51(
                    "span",
                    {
                      className: cn(
                        item.description ? "text-sm font-medium text-fg-primary" : "uppercase",
                        "truncate"
                      ),
                      children: item.label
                    }
                  ),
                  item.time && /* @__PURE__ */ jsx51("span", { className: "text-xs text-fg-quaternary shrink-0", children: item.time })
                ] }),
                item.description && /* @__PURE__ */ jsx51("p", { className: "text-xs text-fg-tertiary mt-1 line-clamp-2", children: item.description })
              ] }),
              item.isUnread && /* @__PURE__ */ jsx51("div", { className: "w-2 h-2 rounded-full bg-fg-brand-primary flex-shrink-0" })
            ] })
          }
        ) }, item.id))
      }
    ),
    document.body
  );
};
FloatingMenu.displayName = "FloatingMenu";

// src/components/molecules/Grid.tsx
import { jsx as jsx52 } from "react/jsx-runtime";
function Grid({
  className,
  cols = 1,
  gap = "md",
  responsive,
  testID,
  children,
  ref,
  ...props
}) {
  const colsClasses = {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6",
    12: "grid-cols-12"
  };
  const gapClasses = {
    none: "gap-0",
    xs: "gap-2",
    sm: "gap-4",
    md: "gap-6",
    lg: "gap-8",
    xl: "gap-12"
  };
  const responsiveClasses = responsive ? [
    responsive.sm && `sm:grid-cols-${responsive.sm}`,
    responsive.md && `md:grid-cols-${responsive.md}`,
    responsive.lg && `lg:grid-cols-${responsive.lg}`,
    responsive.xl && `xl:grid-cols-${responsive.xl}`
  ].filter(Boolean).join(" ") : "";
  return /* @__PURE__ */ jsx52(
    "div",
    {
      ref,
      "data-testid": testID,
      className: cn(
        "grid",
        colsClasses[cols],
        gapClasses[gap],
        responsiveClasses,
        className
      ),
      ...props,
      children
    }
  );
}
Grid.displayName = "Grid";

// src/components/molecules/InputWithLabel.tsx
import { useTranslation as useTranslation19 } from "@tasama/i18n";

// src/lib/inputFormatters.ts
import { toAsciiDigits } from "@tasama/utils";
function formatThousands(value) {
  const numeric = toAsciiDigits(value).replace(/[^\d]/g, "");
  if (!numeric) return "";
  return Number(numeric).toLocaleString("en-US");
}
function parseNumeric(value) {
  return toAsciiDigits(value).replace(/[^\d]/g, "");
}
function applyFormat(value, format2) {
  switch (format2) {
    case "thousands":
      return formatThousands(value);
  }
}
function stripFormat(value, format2) {
  switch (format2) {
    case "thousands":
      return parseNumeric(value);
  }
}

// src/components/molecules/InputWithLabel.tsx
import { jsx as jsx53, jsxs as jsxs35 } from "react/jsx-runtime";
var InputWithLabel = ({
  label,
  id,
  hintText,
  error = false,
  errorMessage,
  disabled = false,
  required = false,
  testID,
  size = "md",
  icon,
  trailingIcon,
  inputClassName,
  variant = "input",
  format: format2,
  ref,
  role,
  ...props
}) => {
  const { t } = useTranslation19("common");
  const displayValue = format2 && typeof props.value === "string" ? applyFormat(props.value, format2) : props.value;
  const handleChange = format2 ? (e) => {
    if (!props.onChange) return;
    const stripped = stripFormat(e.target.value, format2);
    const syntheticEvent = {
      ...e,
      target: { ...e.target, value: stripped },
      currentTarget: { ...e.currentTarget, value: stripped }
    };
    props.onChange(syntheticEvent);
  } : props.onChange;
  const resolvedProps = format2 ? { ...props, value: displayValue, onChange: handleChange } : props;
  const sizeClasses2 = size === "sm" ? "h-10 text-sm py-2 px-3" : "h-12 py-[14px] px-[18px]";
  const hasStartIcon = !!icon;
  const hasEndIcon = error || !!trailingIcon;
  const describedByIds = [];
  if (hintText && variant === "input") describedByIds.push(`${id}-hint`);
  if (error && errorMessage && !hintText && variant === "input")
    describedByIds.push(`${id}-error`);
  const internalAriaDescribedBy = describedByIds.length ? describedByIds.join(" ") : void 0;
  const ariaDescribedBy = variant === "search" ? props["aria-describedby"] : internalAriaDescribedBy;
  const renderStartIcon = () => {
    if (!icon) return null;
    return /* @__PURE__ */ jsx53(
      Icon,
      {
        name: icon,
        size: 20,
        className: cn(disabled ? "text-disabled" : "text-quaternary"),
        "aria-hidden": true
      }
    );
  };
  const renderEndIcon = () => {
    if (error) {
      return /* @__PURE__ */ jsx53(
        Icon,
        {
          name: "CircleAlert",
          size: 16,
          "aria-hidden": true,
          className: "text-error-primary"
        }
      );
    }
    if (trailingIcon) {
      return /* @__PURE__ */ jsx53(
        Icon,
        {
          name: trailingIcon,
          size: 20,
          className: cn(disabled ? "text-disabled" : "text-quaternary"),
          "aria-hidden": true
        }
      );
    }
    return null;
  };
  if (variant === "search") {
    return /* @__PURE__ */ jsxs35("div", { className: "relative", "data-testid": testID, children: [
      hasStartIcon && /* @__PURE__ */ jsx53("div", { className: "pointer-events-none absolute start-3 top-1/2 -translate-y-1/2", children: renderStartIcon() }),
      /* @__PURE__ */ jsx53(
        Input,
        {
          ref,
          id,
          role,
          "aria-invalid": error || void 0,
          "aria-describedby": ariaDescribedBy,
          disabled,
          "data-testid": `${testID}-input`,
          className: cn(
            // Base styles
            "transition-colors motion-safe:duration-150 appearance-none",
            "focus-visible:outline-none",
            // Size classes
            sizeClasses2,
            // Icon spacing
            hasStartIcon && "ps-10",
            hasEndIcon && "pe-10",
            // Disabled state
            "disabled:cursor-not-allowed disabled:border-border",
            // Border radius
            "rounded-xl",
            // Border styles
            "border border-solid border-tertiary",
            // Background
            "bg-background",
            // Text color
            "text-fg-primary disabled:text-disabled",
            // Typography
            "text-sm font-normal",
            // Focus styles with box shadow
            error ? "border-error focus:shadow-[0_0_0_4px_var(--color-bg-error-secondary)]" : "border-tertiary focus:shadow-[0_0_0_4px_var(--color-brand-tint-200)]",
            // Custom input className
            inputClassName
          ),
          ...resolvedProps
        }
      ),
      hasEndIcon && /* @__PURE__ */ jsx53("div", { className: "pointer-events-none absolute end-3 top-1/2 -translate-y-1/2", children: renderEndIcon() })
    ] });
  }
  return /* @__PURE__ */ jsxs35("div", { className: "space-y-sm", "data-testid": testID, children: [
    label && /* @__PURE__ */ jsxs35(
      Label,
      {
        htmlFor: id,
        className: cn(
          "block",
          "font-normal",
          disabled ? "text-disabled" : "text-fg-secondary"
        ),
        children: [
          label,
          !required && /* @__PURE__ */ jsx53("span", { className: "ms-1", children: t("actions.optional") })
        ]
      }
    ),
    /* @__PURE__ */ jsxs35("div", { className: "relative", children: [
      hasStartIcon && /* @__PURE__ */ jsx53("div", { className: "pointer-events-none absolute start-3 top-1/2 -translate-y-1/2", children: renderStartIcon() }),
      /* @__PURE__ */ jsx53(
        Input,
        {
          ref,
          id,
          role,
          "aria-invalid": error || void 0,
          "aria-describedby": ariaDescribedBy,
          disabled,
          "data-testid": `${testID}-input`,
          className: cn(
            // Base styles
            "transition-colors motion-safe:duration-150 appearance-none",
            "focus-visible:outline-none",
            // Size classes
            sizeClasses2,
            // Icon spacing
            hasStartIcon && "ps-10",
            hasEndIcon && "pe-10",
            // Disabled state
            "disabled:cursor-not-allowed disabled:border-border",
            // Border radius
            "rounded-xl",
            // Border styles
            "border border-solid border-tertiary",
            // Background
            "bg-background",
            // Text color
            "text-fg-primary disabled:text-disabled",
            // Typography
            "text-sm font-light",
            // Focus styles with box shadow
            error ? "border-error focus:shadow-[0_0_0_4px_var(--color-bg-error-secondary)]" : "border-tertiary focus:shadow-[0_0_0_4px_var(--color-brand-tint-200)]",
            // Custom input className
            inputClassName
          ),
          ...resolvedProps
        }
      ),
      hasEndIcon && /* @__PURE__ */ jsx53("div", { className: "pointer-events-none absolute end-3 top-1/2 -translate-y-1/2", children: renderEndIcon() })
    ] }),
    !error && hintText && /* @__PURE__ */ jsx53(
      "p",
      {
        id: `${id}-hint`,
        dir: "auto",
        "data-testid": `${testID}-hint`,
        className: cn(
          "text-xs font-light",
          disabled ? "text-disabled" : "text-placeholder"
        ),
        children: hintText
      }
    ),
    error && errorMessage && /* @__PURE__ */ jsx53(
      "p",
      {
        id: `${id}-error`,
        dir: "auto",
        "data-testid": `${testID}-error`,
        className: "text-error-primary text-xs font-light",
        children: errorMessage
      }
    )
  ] });
};

// src/components/molecules/Messages.tsx
import { memo as memo4 } from "react";
import { jsx as jsx54, jsxs as jsxs36 } from "react/jsx-runtime";
var Messages = ({
  type = "default",
  title,
  description,
  className,
  showIcon = true,
  testID
}) => {
  const isError = type === "error";
  const isInfo = type === "info";
  return /* @__PURE__ */ jsxs36(
    "div",
    {
      className: cn("flex flex-col items-center gap-6 lg:gap-8 p-4", className),
      "data-testid": testID,
      children: [
        showIcon && /* @__PURE__ */ jsx54("div", { className: "flex-shrink-0", children: isError ? /* @__PURE__ */ jsx54(
          Icon,
          {
            name: "CircleAlert",
            size: 112,
            strokeWidth: 3,
            absoluteStrokeWidth: true,
            className: "text-fg-error-primary"
          }
        ) : isInfo ? /* @__PURE__ */ jsx54(
          Icon,
          {
            name: "Info",
            size: 112,
            strokeWidth: 3,
            absoluteStrokeWidth: true,
            className: "text-fg-brand-primary"
          }
        ) : /* @__PURE__ */ jsx54(
          Icon,
          {
            name: "CircleCheck",
            size: 112,
            strokeWidth: 3,
            absoluteStrokeWidth: true,
            className: "text-fg-brand-green"
          }
        ) }),
        /* @__PURE__ */ jsxs36("div", { className: "flex flex-col items-center text-center space-y-3", children: [
          /* @__PURE__ */ jsx54("h2", { className: "text-xl lg:text-3xl font-medium leading-7 lg:leading-3xl text-fg-secondary", children: title }),
          /* @__PURE__ */ jsx54("p", { className: "text-sm lg:text-base font-light leading-5 lg:leading-6 text-fg-tertiary whitespace-pre-line", children: description })
        ] })
      ]
    }
  );
};
Messages.displayName = "Messages";
var Messages_default = memo4(Messages);

// src/components/molecules/MobileInputField.tsx
import { useTranslation as useTranslation20 } from "@tasama/i18n";
import { toAsciiDigits as toAsciiDigits2 } from "@tasama/utils";
import { jsx as jsx55, jsxs as jsxs37 } from "react/jsx-runtime";
function MobileInputField({
  label,
  required = false,
  countries,
  selectedCountry,
  onCountryChange,
  phone,
  onPhoneChange,
  onPhoneBlur,
  placeholder,
  size = "sm",
  disabled = false,
  countryDropdownDisabled = false,
  error = false,
  hintText,
  errorMessage,
  className,
  testID,
  ...rest
}) {
  const { t } = useTranslation20("common");
  const defaultPlaceholder = t("mobileInput.placeholder");
  const dir = useDir();
  return /* @__PURE__ */ jsxs37(
    "div",
    {
      className: cn("w-full space-y-sm", className),
      "data-testid": testID,
      ...rest,
      children: [
        label && /* @__PURE__ */ jsxs37(
          Label,
          {
            htmlFor: `${testID}-input`,
            className: cn(
              "block font-normal",
              disabled ? "text-disabled" : "text-fg-secondary"
            ),
            children: [
              label,
              !required && /* @__PURE__ */ jsx55("span", { className: "ms-1", children: t("actions.optional") })
            ]
          }
        ),
        /* @__PURE__ */ jsxs37(
          "div",
          {
            className: cn(
              "flex w-full gap-2.5",
              dir === "rtl" && "flex-row-reverse"
            ),
            children: [
              /* @__PURE__ */ jsx55("div", { className: "max-w-[220px] w-auto", children: /* @__PURE__ */ jsx55(
                Dropdown,
                {
                  testID: `${testID}-country`,
                  options: countries,
                  value: selectedCountry,
                  onChange: onCountryChange,
                  size,
                  disabled: countryDropdownDisabled,
                  error,
                  showLeftIcon: false,
                  inputClassName: "w-[120px]",
                  menuDir: "ltr"
                }
              ) }),
              /* @__PURE__ */ jsx55("div", { className: "flex-1 min-w-0", children: /* @__PURE__ */ jsx55(
                InputWithLabel,
                {
                  dir,
                  id: `${testID}-input`,
                  label: "",
                  type: "tel",
                  value: phone || "",
                  onChange: (e) => onPhoneChange?.(toAsciiDigits2(e.target.value)),
                  onBlur: onPhoneBlur,
                  placeholder: placeholder || defaultPlaceholder,
                  error,
                  errorMessage,
                  hintText,
                  disabled,
                  testID: `${testID}-number`
                }
              ) })
            ]
          }
        )
      ]
    }
  );
}

// src/components/molecules/ModalActions.tsx
import { jsx as jsx56 } from "react/jsx-runtime";
var ModalActions = ({
  actions,
  layout = "horizontal",
  className,
  testID = "modal-actions"
}) => {
  const isVertical = layout === "vertical";
  const containerClass = cn(
    isVertical ? "flex flex-col items-stretch" : "flex items-center",
    // gap between buttons - 12px as per Figma
    "gap-3",
    // alignment helpers for right layout
    layout === "right" ? "justify-end w-full" : "",
    className
  );
  const brandVariantMap = {
    primary: "primary",
    secondary: "secondary",
    tertiary: "tertiary",
    "link-color": "quaternary",
    "destructive-primary": "tertiary",
    "destructive-secondary": "tertiary",
    "destructive-tertiary": "tertiary",
    "destructive-link": "tertiary",
    ghost: "tertiary",
    soft: "tertiary",
    pill: "primary"
  };
  return /* @__PURE__ */ jsx56("div", { className: containerClass, "data-testid": testID, children: actions.map((action, idx) => {
    const key = action.testID || `${testID}-button-${idx}`;
    const variant = action.variant || "secondary";
    const brandVariant = brandVariantMap[variant] || "secondary";
    return /* @__PURE__ */ jsx56(
      Button,
      {
        variant: brandVariant,
        size: action.size === "xs" ? "sm" : action.size || "md",
        loading: !!action.loading,
        disabled: !!action.disabled,
        onClick: action.onClick,
        testID: action.testID,
        label: action.label,
        iconPosition: action.iconPosition,
        iconName: action.ctaIcon,
        className: cn(isVertical && "w-full", action.className)
      },
      key
    );
  }) });
};
ModalActions.displayName = "ModalActions";

// src/components/molecules/MultiSelectDropdown.tsx
import { useState as useState13, useRef as useRef12, useEffect as useEffect12 } from "react";
import { cva as cva14 } from "class-variance-authority";
import { useTranslation as useTranslation21 } from "@tasama/i18n";
import { jsx as jsx57, jsxs as jsxs38 } from "react/jsx-runtime";
var multiSelectDropdownVariants = cva14(
  // Base styles for the dropdown container
  "relative w-full min-w-[125px]",
  {
    variants: {
      // Size variants
      size: {
        sm: "text-sm",
        md: "text-base",
        lg: "text-lg"
      }
    },
    defaultVariants: {
      size: "sm"
    }
  }
);
var inputVariants3 = cva14(
  // Base styles for the input field - Updated to match Figma 12px border radius
  "w-full flex items-center justify-between border transition-colors motion-safe:duration-150 cursor-pointer focus-visible:outline-none appearance-none rounded-xl",
  {
    variants: {
      // State variants using design tokens
      state: {
        default: [
          "bg-background border border-solid border-tertiary text-fg-quaternary",
          "hover:border-tertiary hover:shadow-[0_0_0_4px_var(--color-brand-tint-200)] focus:border-tertiary focus:shadow-[0_0_0_4px_var(--color-brand-tint-200)]"
        ],
        filled: [
          "bg-background border-tertiary text-fg-primary",
          "hover:border-tertiary hover:shadow-[0_0_0_4px_var(--color-brand-tint-200)] focus:border-tertiary focus:shadow-[0_0_0_4px_var(--color-brand-tint-200)]"
        ],
        focused: [
          "bg-background border-tertiary text-fg-primary",
          "shadow-[0_0_0_4px_var(--color-brand-tint-200)]"
        ],
        error: [
          "bg-background border-error text-fg-primary",
          "focus:border-error focus:shadow-[0_0_0_4px_var(--color-bg-error-secondary)]"
        ],
        disabled: [
          "bg-background border-border text-disabled cursor-not-allowed"
        ]
      },
      // Size variants - Updated to match Figma specifications (48px height for sm)
      size: {
        sm: "min-h-12 px-[18px] py-[14px] text-sm",
        md: "min-h-14 px-5 py-4 text-base",
        lg: "min-h-16 px-6 py-5 text-lg"
      }
    },
    defaultVariants: {
      state: "default",
      size: "sm"
    }
  }
);
var menuVariants2 = cva14(
  // Base styles for the dropdown menu - Updated to match Figma design with proper shadow
  "absolute z-dropdown w-full mt-1 bg-background border border-border rounded-xl shadow-lg py-1 max-h-64 overflow-y-auto",
  {
    variants: {
      isOpen: {
        true: "opacity-100 visible shadow-[0px_2px_2px_-1px_rgba(10,13,18,0.04),0px_4px_6px_-2px_rgba(10,13,18,0.03),0px_12px_16px_-4px_rgba(10,13,18,0.08)]",
        false: "opacity-0 invisible"
      }
    },
    defaultVariants: {
      isOpen: false
    }
  }
);
var menuItemVariants4 = cva14(
  // Base styles for menu items with proper spacing and border radius (6px from Figma)
  "flex items-center justify-between px-[14px] py-2 mx-[6px] my-[1px] cursor-pointer transition-colors duration-200 rounded-sm text-fg-primary hover:bg-bg-dropdown-item-hover",
  {
    variants: {
      isFocused: {
        true: "bg-bg-dropdown-item-hover",
        false: ""
      }
    },
    defaultVariants: {
      isFocused: false
    }
  }
);
var MultiSelectDropdown = ({
  className,
  label,
  placeholder,
  options,
  value = [],
  onChange,
  disabled = false,
  error = false,
  hintText,
  errorMessage,
  showLeftIcon = true,
  maxSelections,
  size = "sm",
  inputClassName,
  testID,
  ...props
}) => {
  const { t } = useTranslation21("common");
  const defaultPlaceholder = t("multiSelect.selectOptions");
  const [isOpen, setIsOpen] = useState13(false);
  const [focusedIndex, setFocusedIndex] = useState13(-1);
  const dropdownRef = useRef12(null);
  const inputRef = useRef12(null);
  const menuRef = useRef12(null);
  const menuId = `${testID}-menu`;
  const selectedOptions = options.filter(
    (option) => value.includes(option.value)
  );
  const describedByIds = [];
  if (hintText) describedByIds.push(`${testID}-hint`);
  if (error && errorMessage && !hintText)
    describedByIds.push(`${testID}-error`);
  const ariaDescribedBy = describedByIds.length ? describedByIds.join(" ") : void 0;
  const getState = () => {
    if (disabled) return "disabled";
    if (error) return "error";
    if (isOpen) return "focused";
    if (selectedOptions.length > 0) return "filled";
    return "default";
  };
  const currentState = getState();
  useEffect12(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const handleKeyDown = (event) => {
    if (disabled) return;
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else if (focusedIndex >= 0) {
          const option = options[focusedIndex];
          if (!option?.disabled) {
            handleOptionToggle(option);
          }
        }
        break;
      case "ArrowDown":
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else {
          const nextIndex = focusedIndex < options.length - 1 ? focusedIndex + 1 : 0;
          setFocusedIndex(nextIndex);
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (isOpen) {
          const prevIndex = focusedIndex > 0 ? focusedIndex - 1 : options.length - 1;
          setFocusedIndex(prevIndex);
        }
        break;
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      case "Tab":
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };
  const handleOptionToggle = (option) => {
    if (option.disabled || disabled) return;
    const isSelected = value.includes(option.value);
    let newValue;
    if (isSelected) {
      newValue = value.filter((v) => v !== option.value);
    } else {
      if (maxSelections && value.length >= maxSelections) {
        return;
      }
      newValue = [...value, option.value];
    }
    onChange?.(newValue);
  };
  const handleInputClick = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (isOpen) {
      setFocusedIndex(-1);
    }
  };
  return /* @__PURE__ */ jsxs38(
    "div",
    {
      ref: dropdownRef,
      className: cn(
        multiSelectDropdownVariants({ size }),
        "space-y-sm",
        className
      ),
      "data-testid": testID,
      ...props,
      children: [
        label && /* @__PURE__ */ jsx57(
          Label,
          {
            htmlFor: `${testID}-input`,
            className: cn(
              "inline-block",
              "font-medium",
              disabled ? "text-disabled" : "text-fg-secondary"
            ),
            children: label
          }
        ),
        /* @__PURE__ */ jsxs38("div", { className: "relative", children: [
          /* @__PURE__ */ jsxs38(
            "div",
            {
              ref: inputRef,
              id: `${testID}-input`,
              className: cn(
                inputVariants3({ state: currentState, size }),
                inputClassName
              ),
              onClick: handleInputClick,
              onKeyDown: handleKeyDown,
              tabIndex: disabled ? -1 : 0,
              role: "combobox",
              "aria-expanded": isOpen,
              "aria-controls": menuId,
              "aria-haspopup": "listbox",
              "aria-labelledby": label ? `${testID}-label` : void 0,
              "aria-describedby": ariaDescribedBy,
              "data-testid": `${testID}-input`,
              children: [
                /* @__PURE__ */ jsxs38("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [
                  showLeftIcon && /* @__PURE__ */ jsx57(
                    Icon,
                    {
                      name: "Mail",
                      className: cn(
                        "w-5 h-5 flex-shrink-0",
                        disabled ? "text-fg-disabled" : "text-fg-quaternary"
                      )
                    }
                  ),
                  /* @__PURE__ */ jsx57("div", { className: "flex items-center gap-1 flex-1 min-w-0 flex-wrap", children: selectedOptions.length > 0 ? (
                    // Show truncated text for selected options to match Figma design
                    /* @__PURE__ */ jsx57(
                      "span",
                      {
                        className: cn(
                          "flex-1 text-start truncate font-normal text-sm",
                          "text-fg-primary",
                          disabled && "text-disabled"
                        ),
                        children: selectedOptions.length === 1 ? selectedOptions[0]?.label : selectedOptions.length <= 3 ? selectedOptions.map((opt) => opt.label).join(", ") : `${selectedOptions.slice(0, 2).map((opt) => opt.label).join(", ")}, ...`
                      }
                    )
                  ) : /* @__PURE__ */ jsx57(
                    "span",
                    {
                      className: cn(
                        "flex-1 text-start truncate font-normal font-sans text-sm",
                        "text-quaternary",
                        disabled && "text-disabled"
                      ),
                      children: placeholder || defaultPlaceholder
                    }
                  ) })
                ] }),
                /* @__PURE__ */ jsx57(
                  Icon,
                  {
                    name: "ChevronDown",
                    className: cn(
                      "w-5 h-5 flex-shrink-0 transition-transform duration-200",
                      isOpen && "rotate-180",
                      disabled ? "text-fg-disabled" : "text-fg-quaternary"
                    )
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx57(
            "div",
            {
              ref: menuRef,
              id: menuId,
              className: cn(menuVariants2({ isOpen })),
              role: "listbox",
              "aria-labelledby": label ? `${testID}-label` : void 0,
              "aria-multiselectable": "true",
              "data-testid": `${testID}-menu`,
              children: options.map((option, index) => {
                const isSelected = value.includes(option.value);
                const isFocused = index === focusedIndex;
                const isAtMaxLimit = maxSelections ? value.length >= maxSelections : false;
                const isDisabled = option.disabled || isAtMaxLimit && !isSelected;
                return /* @__PURE__ */ jsx57(
                  "div",
                  {
                    className: cn(
                      menuItemVariants4({ isFocused }),
                      isDisabled && "opacity-50 cursor-not-allowed"
                    ),
                    onClick: () => !isDisabled && handleOptionToggle(option),
                    role: "option",
                    "aria-selected": isSelected,
                    "aria-disabled": isDisabled,
                    "data-testid": `${testID}-option-${option.value}`,
                    children: /* @__PURE__ */ jsxs38("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [
                      /* @__PURE__ */ jsx57(
                        Checkbox,
                        {
                          checked: isSelected,
                          onChange: () => {
                          },
                          disabled: isDisabled,
                          tabIndex: -1,
                          uiSize: "sm",
                          className: "flex-shrink-0"
                        }
                      ),
                      /* @__PURE__ */ jsxs38("div", { className: "flex flex-col flex-1 min-w-0", children: [
                        /* @__PURE__ */ jsx57("span", { className: "text-sm font-medium text-fg-primary truncate", children: option.label }),
                        option.supportingText && /* @__PURE__ */ jsx57("span", { className: "text-xs font-normal font-sans text-fg-tertiary truncate", children: option.supportingText })
                      ] })
                    ] })
                  },
                  option.value
                );
              })
            }
          )
        ] }),
        !error && hintText && /* @__PURE__ */ jsx57(
          "p",
          {
            id: `${testID}-hint`,
            "data-testid": `${testID}-hint`,
            className: cn(
              "text-xs font-normal font-sans",
              disabled ? "text-disabled" : "text-quaternary"
            ),
            children: hintText
          }
        ),
        error && errorMessage && /* @__PURE__ */ jsx57(
          "p",
          {
            id: `${testID}-error`,
            "data-testid": `${testID}-error`,
            className: "text-error-primary text-xs font-normal font-sans",
            children: errorMessage
          }
        ),
        maxSelections && /* @__PURE__ */ jsx57(
          "p",
          {
            className: cn(
              "text-xs font-normal font-sans",
              disabled ? "text-disabled" : "text-quaternary"
            ),
            "data-testid": `${testID}-count`,
            children: t("multiSelect.selectionInfo", {
              current: value.length,
              max: maxSelections
            })
          }
        )
      ]
    }
  );
};
MultiSelectDropdown.displayName = "MultiSelectDropdown";

// src/components/molecules/LanguageSwitcher.tsx
import { useTranslation as useTranslation22 } from "@tasama/i18n";
import { jsx as jsx58 } from "react/jsx-runtime";
var LanguageSwitcher = ({
  testID = "language-switcher"
}) => {
  const { i18n: i18n2, t } = useTranslation22("common");
  const isArabic = i18n2.language === "ar";
  const targetLang = isArabic ? "en" : "ar";
  const label = isArabic ? "ENGLISH" : "\u0627\u0644\u0639\u0631\u0628\u064A\u0629";
  const handleSwitch = () => {
    void i18n2.changeLanguage(targetLang);
  };
  return /* @__PURE__ */ jsx58(
    Button,
    {
      variant: "quaternary",
      label,
      onClick: handleSwitch,
      "aria-label": t("languageSwitcher.switchTo"),
      "data-testid": testID,
      className: "text-xs hover:text-fg-brand-primary",
      iconPosition: "none"
    }
  );
};
LanguageSwitcher.displayName = "LanguageSwitcher";

// src/components/molecules/NavigationButtonBar.tsx
import { cva as cva15 } from "class-variance-authority";
import { jsx as jsx59, jsxs as jsxs39 } from "react/jsx-runtime";
var navigationButtonBarVariants = cva15(
  // Base styles for the navigation button bar container
  "flex items-center w-full",
  {
    variants: {
      // Layout variants for button positioning
      layout: {
        "space-between": "justify-between",
        end: "justify-end",
        start: "justify-start",
        center: "justify-center"
      },
      // Size variants affecting button sizes
      size: {
        sm: "sm",
        md: "md",
        lg: "lg"
      },
      // Spacing variants for gap between buttons
      spacing: {
        none: "gap-0",
        sm: "gap-2",
        md: "gap-4",
        lg: "gap-6",
        xl: "gap-8"
      }
    },
    defaultVariants: {
      layout: "space-between",
      size: "md",
      spacing: "none"
    }
  }
);
var NavigationButtonBar = ({
  className,
  layout,
  size = "md",
  spacing,
  backButton,
  skipButton,
  continueButton,
  testID,
  ...props
}) => {
  const showBackButton = backButton?.show !== false && backButton;
  const showSkipButton = skipButton?.show !== false && skipButton;
  const showContinueButton = continueButton?.show !== false && continueButton;
  if (!showBackButton && !showSkipButton && !showContinueButton) {
    return null;
  }
  const hasRightContent = showSkipButton || showContinueButton;
  const effectiveLayout = layout || (showBackButton && hasRightContent ? "space-between" : hasRightContent ? "end" : "start");
  const effectiveSpacing = spacing || (effectiveLayout === "space-between" ? "none" : "md");
  return /* @__PURE__ */ jsxs39(
    "div",
    {
      className: cn(
        navigationButtonBarVariants({
          layout: effectiveLayout,
          size,
          spacing: effectiveSpacing
        }),
        className
      ),
      "data-testid": testID,
      ...props,
      children: [
        showBackButton && /* @__PURE__ */ jsx59(
          Button,
          {
            variant: backButton.variant || "tertiary",
            size,
            disabled: backButton.disabled,
            iconPosition: backButton.iconPosition || "leading",
            iconName: backButton.iconName || "ArrowLeft",
            onClick: backButton.onClick,
            testID: backButton.testID || `${testID}-back`,
            loading: backButton.loading,
            label: backButton.text,
            className: "ps-none"
          }
        ),
        (showSkipButton || showContinueButton) && /* @__PURE__ */ jsxs39("div", { className: "flex items-center gap-4", children: [
          showSkipButton && /* @__PURE__ */ jsx59(
            Button,
            {
              variant: skipButton.variant || "tertiary",
              size,
              disabled: skipButton.disabled,
              iconPosition: skipButton.iconPosition || "none",
              onClick: skipButton.onClick,
              testID: skipButton.testID || `${testID}-skip`,
              loading: skipButton.loading,
              label: skipButton.text,
              className: "font-semibold"
            }
          ),
          showContinueButton && /* @__PURE__ */ jsx59(
            Button,
            {
              variant: continueButton.variant || "secondary",
              size,
              disabled: continueButton.disabled,
              iconPosition: continueButton.iconPosition || "trailing",
              iconName: continueButton.iconName || "ArrowRight",
              onClick: continueButton.onClick,
              testID: continueButton.testID || `${testID}-continue`,
              loading: continueButton.loading,
              label: continueButton.text
            }
          )
        ] })
      ]
    }
  );
};
NavigationButtonBar.displayName = "NavigationButtonBar";

// src/components/molecules/Pagination.tsx
import { useTranslation as useTranslation23 } from "@tasama/i18n";
import { jsx as jsx60, jsxs as jsxs40 } from "react/jsx-runtime";
var Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  showPrevious = true,
  showNext = true,
  maxVisiblePages,
  testID,
  className
}) => {
  const { t } = useTranslation23("common");
  const isMobile = useIsMobile();
  const effectiveMaxVisible = maxVisiblePages ?? (isMobile ? 5 : 7);
  const getVisiblePages = () => {
    if (totalPages <= effectiveMaxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const DOTS = "...";
    const edgeCount = effectiveMaxVisible - 2;
    const middleCount = Math.max(effectiveMaxVisible - 4, 1);
    const halfMiddle = Math.floor(middleCount / 2);
    if (currentPage <= edgeCount) {
      return [
        ...Array.from({ length: edgeCount }, (_, i) => i + 1),
        DOTS,
        totalPages
      ];
    }
    if (currentPage >= totalPages - edgeCount + 1) {
      return [
        1,
        DOTS,
        ...Array.from(
          { length: edgeCount },
          (_, i) => totalPages - edgeCount + 1 + i
        )
      ];
    }
    return [
      1,
      DOTS,
      ...Array.from(
        { length: middleCount },
        (_, i) => currentPage - halfMiddle + i
      ),
      DOTS,
      totalPages
    ];
  };
  const visiblePages = getVisiblePages();
  const isPreviousDisabled = currentPage <= 1;
  const isNextDisabled = currentPage >= totalPages;
  const handlePreviousClick = () => {
    if (!isPreviousDisabled) {
      onPageChange(currentPage - 1);
    }
  };
  const handleNextClick = () => {
    if (!isNextDisabled) {
      onPageChange(currentPage + 1);
    }
  };
  const handlePageClick = (page) => {
    if (typeof page === "number" && page !== currentPage) {
      onPageChange(page);
    }
  };
  return /* @__PURE__ */ jsxs40(
    "nav",
    {
      className: cn("flex items-center justify-center gap-2xl", className),
      "data-testid": testID,
      role: "navigation",
      "aria-label": t("pagination.ariaLabel"),
      children: [
        showPrevious && /* @__PURE__ */ jsx60(
          Button,
          {
            variant: "tertiary",
            size: "md",
            disabled: isPreviousDisabled,
            onClick: handlePreviousClick,
            testID: `${testID}-previous`,
            "aria-label": t("pagination.previousPage"),
            iconName: "ArrowLeft"
          }
        ),
        /* @__PURE__ */ jsx60("div", { className: "flex gap-[2px]", children: visiblePages.map((page, index) => {
          const isCurrentPage = page === currentPage;
          const isEllipsis = page === "...";
          if (isEllipsis) {
            return /* @__PURE__ */ jsx60(
              "div",
              {
                className: cn(
                  "flex items-center justify-center",
                  "w-[40px] h-[40px] rounded-[480px]",
                  "text-sm font-normal leading-[1.4285714285714286em] text-center",
                  "text-[var(--color-gray-500,#717680)]"
                ),
                "aria-hidden": "true",
                children: "..."
              },
              `ellipsis-${index}`
            );
          }
          return /* @__PURE__ */ jsx60(
            Button,
            {
              onClick: () => handlePageClick(page),
              variant: isCurrentPage ? "secondary" : "quaternary",
              className: cn(
                "w-[40px] h-[40px] flex items-center p-0 m-0 rounded-full",
                isCurrentPage ? "text-gray-700" : "text-gray-500"
              ),
              labelClassName: "flex justify-center w-[100%]",
              "data-testid": `${testID}-page-${page}`,
              "aria-label": t("pagination.goToPage", { page }),
              "aria-current": isCurrentPage ? "page" : void 0,
              label: `${page}`,
              iconPosition: "none"
            },
            page
          );
        }) }),
        showNext && /* @__PURE__ */ jsx60(
          Button,
          {
            variant: "tertiary",
            size: "md",
            disabled: isNextDisabled,
            onClick: handleNextClick,
            testID: `${testID}-next`,
            "aria-label": t("pagination.nextPage"),
            iconName: "ArrowRight"
          }
        )
      ]
    }
  );
};
Pagination.displayName = "Pagination";

// src/components/molecules/RadioGroup/RadioGroup.tsx
import * as React22 from "react";
import { jsx as jsx61, jsxs as jsxs41 } from "react/jsx-runtime";
var RadioGroup = ({
  name,
  options,
  value,
  defaultValue,
  onChange,
  disabled = false,
  required = false,
  itemClassName,
  optionContainerClassName,
  className,
  testID,
  ...rest
}) => {
  const isControlled = value !== void 0;
  const dir = useDir();
  const autoId = React22.useId();
  const groupName = name ?? `rg-${autoId}`;
  const initialDefault = React22.useMemo(() => {
    if (defaultValue == null) return void 0;
    if (disabled) return void 0;
    const match = options.find((o) => o.value === defaultValue && !o.disabled);
    return match ? match.value : void 0;
  }, [defaultValue, options, disabled]);
  const [internal, setInternal] = React22.useState(
    initialDefault
  );
  const selected = isControlled ? value : internal;
  const inputsRef = React22.useRef([]);
  const enabledOptions = React22.useMemo(
    () => options.map((o, i) => ({ ...o, index: i })).filter((o) => !o.disabled && !disabled),
    [options, disabled]
  );
  const selectAt = React22.useCallback(
    (idx) => {
      const opt = enabledOptions[idx];
      if (!opt) return;
      if (!isControlled) setInternal(opt.value);
      onChange?.(opt.value);
      const inputEl = inputsRef.current[opt.index];
      inputEl?.focus();
    },
    [enabledOptions, isControlled, onChange]
  );
  const onKeyDown = (e) => {
    if (disabled) return;
    if (![
      "ArrowRight",
      "ArrowDown",
      "ArrowLeft",
      "ArrowUp",
      "Home",
      "End"
    ].includes(e.key))
      return;
    e.preventDefault();
    const currentIdx = enabledOptions.findIndex((o) => o.value === selected);
    if (e.key === "Home") {
      selectAt(0);
      return;
    }
    if (e.key === "End") {
      selectAt(enabledOptions.length - 1);
      return;
    }
    const isRtl = dir === "rtl";
    const isForward = e.key === "ArrowDown" || e.key === "ArrowRight" && !isRtl || e.key === "ArrowLeft" && isRtl;
    const isBackward = e.key === "ArrowUp" || e.key === "ArrowLeft" && !isRtl || e.key === "ArrowRight" && isRtl;
    if (isForward) {
      const next = currentIdx < 0 ? 0 : (currentIdx + 1) % enabledOptions.length;
      selectAt(next);
    } else if (isBackward) {
      const next = currentIdx < 0 ? 0 : (currentIdx - 1 + enabledOptions.length) % enabledOptions.length;
      selectAt(next);
    }
  };
  const handleOnChange = (isItemDisabled, opt) => {
    if (isItemDisabled) return;
    if (!isControlled) setInternal(opt.value);
    onChange?.(opt.value);
  };
  return /* @__PURE__ */ jsx61(
    "div",
    {
      role: "radiogroup",
      "aria-required": required || void 0,
      "aria-disabled": disabled || void 0,
      "data-testid": testID,
      className: cn("relative", "flex flex-wrap gap-3", className),
      onKeyDown,
      ...rest,
      children: options.map((opt, index) => {
        const isChecked = selected === opt.value;
        const isItemDisabled = disabled || !!opt.disabled;
        return /* @__PURE__ */ jsxs41(
          "div",
          {
            className: cn("contents", optionContainerClassName),
            children: [
              /* @__PURE__ */ jsxs41(
                "label",
                {
                  className: cn(
                    // Item wrapper styling
                    "group/radio w-full flex items-center justify-between gap-3 rounded-lg border-2 p-3 transition-colors border-border",
                    "bg-background text-foreground",
                    "cursor-pointer",
                    // Hover and focus outlines (tokens) — focus-visible only so click doesn't show ring
                    !isItemDisabled && "hover:bg-gray-50",
                    // Disabled visuals
                    isItemDisabled && "opacity-60 cursor-not-allowed",
                    // itemClassName before isChecked so twMerge lets checked state override border color
                    itemClassName,
                    // Checked accent — must come after itemClassName so twMerge keeps these
                    isChecked && "border-selection text-brand-blue-500"
                  ),
                  children: [
                    /* @__PURE__ */ jsxs41("div", { className: "flex min-w-0 items-center gap-3", children: [
                      opt.icon ? /* @__PURE__ */ jsx61(
                        "span",
                        {
                          className: cn(
                            "flex w-[40px] h-[40px] p-[10px] justify-center items-center aspect-square shrink-0 rounded-xl bg-bg-quaternary",
                            isChecked && "bg-bg-brand-primary",
                            "text-muted-foreground"
                          ),
                          "aria-hidden": "true",
                          children: React22.cloneElement(
                            opt.icon,
                            {
                              color: isChecked ? "var(--color-fg-primary)" : "var(--color-gray-500)"
                            }
                          )
                        }
                      ) : null,
                      /* @__PURE__ */ jsxs41("div", { className: "flex min-w-0 flex-col", children: [
                        /* @__PURE__ */ jsx61(
                          "span",
                          {
                            className: cn(
                              "text-[14px] font-normal leading-[20px]",
                              isChecked ? "text-[#10069F]" : "text-[#181D27]"
                            ),
                            children: opt.label
                          }
                        ),
                        opt.description ? /* @__PURE__ */ jsx61(
                          "span",
                          {
                            className: cn(
                              "text-[12px] font-light leading-[18px]",
                              isChecked ? "text-[#10069F]" : "text-[#717680]"
                            ),
                            children: opt.description
                          }
                        ) : null
                      ] })
                    ] }),
                    /* @__PURE__ */ jsx61(
                      RadioButton,
                      {
                        ref: (el) => {
                          inputsRef.current[index] = el;
                        },
                        name: groupName,
                        value: opt.value,
                        uiSize: "md",
                        checked: isChecked,
                        onChange: () => handleOnChange(isItemDisabled, opt),
                        disabled: isItemDisabled,
                        "aria-label": typeof opt.label === "string" ? opt.label : void 0,
                        testID: opt.testID,
                        className: "ms-auto"
                      }
                    )
                  ]
                }
              ),
              isChecked && opt.content
            ]
          },
          opt.value
        );
      })
    }
  );
};

// src/components/molecules/Section.tsx
import { jsx as jsx62 } from "react/jsx-runtime";
function Section({
  className,
  spacing = "lg",
  background = "default",
  fullHeight = false,
  testID,
  children,
  ref,
  ...props
}) {
  const spacingClasses = {
    none: "",
    sm: "py-8",
    md: "py-12",
    lg: "py-16",
    xl: "py-24"
  };
  const backgroundClasses = {
    default: "bg-background",
    muted: "bg-muted",
    accent: "bg-accent"
  };
  return /* @__PURE__ */ jsx62(
    "section",
    {
      ref,
      "data-testid": testID,
      className: cn(
        spacingClasses[spacing],
        backgroundClasses[background],
        fullHeight && "min-h-screen",
        className
      ),
      ...props,
      children
    }
  );
}
Section.displayName = "Section";

// src/components/molecules/SidebarItem.tsx
import { useDirection } from "@tasama/i18n";
import { jsx as jsx63, jsxs as jsxs42 } from "react/jsx-runtime";
var SidebarItem = ({
  icon,
  label,
  isActive = false,
  badge,
  onClick,
  className,
  testID,
  isCollapsed = false
}) => {
  const hasBadge = badge !== void 0 && badge !== "" && badge !== "0" && badge !== 0;
  const dir = useDirection();
  return /* @__PURE__ */ jsxs42(
    "div",
    {
      onClick,
      "data-testid": testID,
      title: isCollapsed ? label : void 0,
      className: cn(
        `
        relative flex items-center
        h-12 w-full
        rounded-md overflow-hidden
        transition-all duration-300 ease-in-out
        lg:px-lg
        `,
        isCollapsed ? "gap-0" : "gap-md",
        isActive ? `
            ps-[12px] pe-[12px] lg:px-lg
            bg-transparent
            before:absolute before:inset-0
            before:bg-[url("/assets/images/sidebar-bg-image.png")]
            before:bg-cover before:bg-center
            before:pointer-events-none
            before:z-0
          ` : "ps-[12px] pe-[12px] lg:px-lg hover:bg-bg-primary-hover",
        className
      ),
      children: [
        /* @__PURE__ */ jsx63("div", { className: "relative z-10 flex items-center justify-center w-4xl h-4xl rounded-full bg-brand-green-500 shrink-0", children: /* @__PURE__ */ jsx63(Icon, { name: icon, size: 18, className: "text-white" }) }),
        /* @__PURE__ */ jsx63(
          "span",
          {
            className: cn(
              `
          relative z-10
          text-base leading-6 lg:leading-normal font-medium uppercase tracking-wide
          whitespace-nowrap overflow-hidden
          transition-all duration-200 ease-in-out
          `,
              isActive ? "text-fg-white" : "text-fg-primary",
              isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100",
              dir === "rtl" ? "text-sm" : "lg:text-xs"
            ),
            children: label
          }
        ),
        /* @__PURE__ */ jsx63(
          "div",
          {
            className: cn(
              "relative z-10 ms-auto transition-all duration-200",
              isCollapsed ? "opacity-0 scale-75" : "opacity-100 scale-100"
            ),
            children: hasBadge && /* @__PURE__ */ jsx63(
              Badge,
              {
                variant: "brand",
                size: "sm",
                className: cn(
                  "px-2 py-0.5 text-xs",
                  isActive ? "bg-background text-fg-primary" : ""
                ),
                children: badge
              }
            )
          }
        ),
        hasBadge && isCollapsed && /* @__PURE__ */ jsx63("div", { className: "absolute z-10 top-2 end-2", children: /* @__PURE__ */ jsx63("div", { className: "w-2 h-2 rounded-full bg-fg-brand-primary" }) })
      ]
    }
  );
};
SidebarItem.displayName = "SidebarItem";

// src/components/molecules/Stepper.tsx
import * as React23 from "react";
import { useDirection as useDirection2 } from "@tasama/i18n";
import { jsx as jsx64, jsxs as jsxs43 } from "react/jsx-runtime";
var DEFAULT_THEMES = {
  dark: {
    // Icon colors (original design for dark backgrounds)
    // NOTE: #E2F9D4 is not yet in design system - should be added as --color-brand-green-100
    completedIconBgGradient: {
      from: "#E2F9D4",
      to: "var(--color-brand-green-500)"
    },
    completedIconColor: "var(--color-brand-blue-500)",
    currentIconBorder: "var(--color-white)",
    currentIconDot: "var(--color-white)",
    incompleteIconBorder: "var(--color-white)",
    // Connector colors
    completedConnector: "var(--color-brand-green-500)",
    currentConnector: "var(--color-white)",
    incompleteConnector: "var(--color-white)",
    // Label colors
    completedLabel: "var(--color-brand-green-500)",
    currentLabel: "var(--color-white)",
    incompleteLabel: "var(--color-brand-blue-50)",
    // Far-future / disabled mirrors incomplete in dark mode so every non-current
    // step looks identically dimmed (Onboarding sidebar Figma spec).
    disabledLabel: "var(--color-brand-blue-50)",
    disabledIconBorder: "var(--color-white)",
    disabledConnector: "var(--color-white)",
    // Opacity
    incompleteOpacity: 0.6,
    disabledOpacity: 0.6,
    currentConnectorOpacity: 0.9,
    // Waiting state (amber/warning tone for dark backgrounds)
    waitingIconBgGradient: { from: "#FEF3C7", to: "#F59E0B" },
    waitingIconColor: "var(--color-brand-blue-500)",
    waitingConnector: "#F59E0B",
    waitingLabel: "#F59E0B"
  },
  light: {
    // Icon colors (design for light backgrounds using design system colors)
    completedIconBgGradient: {
      from: "var(--color-success-600)",
      to: "var(--color-success-600)"
    },
    completedIconColor: "var(--color-white)",
    currentIconBorder: "var(--color-brand-blue-500)",
    currentIconDot: "var(--color-brand-blue-500)",
    incompleteIconBorder: "var(--color-gray-700)",
    // Connector colors
    completedConnector: "var(--color-success-600)",
    currentConnector: "var(--color-brand-blue-500)",
    incompleteConnector: "var(--color-gray-600)",
    // Label colors
    completedLabel: "var(--color-success-600)",
    currentLabel: "var(--color-brand-blue-500)",
    incompleteLabel: "var(--color-gray-700)",
    // Disabled label/icon for far-future steps (Figma: text-disabled #A4A7AE)
    disabledLabel: "#A4A7AE",
    disabledIconBorder: "#A4A7AE",
    disabledConnector: "#A4A7AE",
    // Opacity
    incompleteOpacity: 1,
    currentConnectorOpacity: 1,
    // Waiting state (amber/warning tone for light backgrounds)
    waitingIconBgGradient: { from: "#FEF3C7", to: "#F59E0B" },
    waitingIconColor: "var(--color-white)",
    waitingConnector: "#F59E0B",
    waitingLabel: "#F59E0B"
  }
};
var StepIcon = ({ step, isCompleted, isActive, isSelected, showShadow = true }) => {
  if (isCompleted) {
    return /* @__PURE__ */ jsx64(
      "div",
      {
        className: cn(
          "relative w-2xl h-2xl rounded-full flex items-center justify-center",
          showShadow && "shadow-[0px_4px_4px_0px_rgba(0,0,0,0.12)]"
        ),
        style: {
          backgroundImage: "linear-gradient(to bottom, var(--stepper-gradient-from), var(--stepper-gradient-to))"
        },
        children: /* @__PURE__ */ jsx64(
          Icon,
          {
            name: "Check",
            size: 12,
            color: "var(--stepper-completed-icon-color)"
          }
        )
      }
    );
  }
  if (isActive) {
    return /* @__PURE__ */ jsx64("div", { className: "flex flex-col items-center gap-[4px]", children: /* @__PURE__ */ jsx64(
      "div",
      {
        className: "relative w-2xl h-2xl rounded-full border-2 flex items-center justify-center",
        style: { borderColor: "var(--stepper-current-border)" },
        children: /* @__PURE__ */ jsx64(
          "div",
          {
            className: "w-md h-md rounded-full",
            style: { backgroundColor: "var(--stepper-current-dot)" }
          }
        )
      }
    ) });
  }
  if (step.disabled) {
    return /* @__PURE__ */ jsx64("div", { className: "flex flex-col items-center gap-[4px]", children: /* @__PURE__ */ jsx64(
      "div",
      {
        className: `relative w-2xl h-2xl rounded-full flex items-center justify-center border`,
        style: {
          borderColor: "var(--stepper-disabled-icon-border)",
          opacity: "var(--stepper-disabled-opacity)"
        }
      }
    ) });
  }
  return /* @__PURE__ */ jsx64("div", { className: "flex flex-col items-center gap-[4px]", children: /* @__PURE__ */ jsx64(
    "div",
    {
      className: `relative w-2xl h-2xl rounded-full flex items-center justify-center ${isSelected ? "border-2" : "border"}`,
      style: {
        borderColor: "var(--stepper-incomplete-border)",
        opacity: "var(--stepper-incomplete-opacity)"
      }
    }
  ) });
};
var StepConnector = ({ isCompleted, isActive, isLast, isDisabled }) => {
  if (isLast) return null;
  const getConnectorStyle = () => {
    if (isCompleted) {
      return { backgroundColor: "var(--stepper-completed-connector)" };
    }
    if (isActive) {
      return {
        backgroundColor: "var(--stepper-current-connector)",
        opacity: "var(--stepper-current-connector-opacity)"
      };
    }
    if (isDisabled) {
      return { backgroundColor: "var(--stepper-disabled-connector)" };
    }
    return {
      backgroundColor: "var(--stepper-incomplete-connector)",
      opacity: "var(--stepper-incomplete-opacity)"
    };
  };
  return /* @__PURE__ */ jsx64("div", { className: "w-px flex-1 mx-auto", style: getConnectorStyle() });
};
var StepLabel = ({ step, isCompleted, isActive, isSelected }) => {
  const dir = useDirection2();
  const getLabelStyle = () => {
    if (isCompleted) {
      return { color: "var(--stepper-completed-label)" };
    }
    if (isActive) {
      return { color: "var(--stepper-current-label)" };
    }
    if (step.disabled) {
      return {
        color: "var(--stepper-disabled-label)",
        opacity: "var(--stepper-disabled-opacity)"
      };
    }
    return {
      color: "var(--stepper-incomplete-label)",
      opacity: "var(--stepper-incomplete-opacity)"
    };
  };
  const isBold = isSelected && !isActive && !isCompleted;
  return /* @__PURE__ */ jsx64(
    Text,
    {
      size: "sm",
      weight: isBold ? "semibold" : "normal",
      className: `transition-colors duration-200 ${dir === "rtl" ? "text-base" : "text-sm"}`,
      style: getLabelStyle(),
      children: step.title || step.label
    }
  );
};
var Stepper = ({
  className,
  steps,
  currentStep = 0,
  variant = "dark",
  theme: customTheme,
  testID,
  onStepClick,
  clickable = false,
  showShadow = true,
  ref,
  style,
  ...props
}) => {
  const theme = React23.useMemo(
    () => ({
      ...DEFAULT_THEMES[variant],
      ...customTheme
    }),
    [variant, customTheme]
  );
  const cssVars = React23.useMemo(
    () => ({
      "--stepper-gradient-from": theme.completedIconBgGradient?.from || theme.completedIconBg || "#E2F9D4",
      // Not in design system yet
      "--stepper-gradient-to": theme.completedIconBgGradient?.to || theme.completedIconBg || "var(--color-brand-green-500)",
      "--stepper-completed-icon-color": theme.completedIconColor || "var(--color-brand-blue-500)",
      "--stepper-current-border": theme.currentIconBorder || "var(--color-white)",
      "--stepper-current-dot": theme.currentIconDot || "var(--color-white)",
      "--stepper-incomplete-border": theme.incompleteIconBorder || "var(--color-white)",
      "--stepper-completed-connector": theme.completedConnector || "var(--color-brand-green-500)",
      "--stepper-current-connector": theme.currentConnector || "var(--color-white)",
      "--stepper-incomplete-connector": theme.incompleteConnector || "var(--color-white)",
      "--stepper-completed-label": theme.completedLabel || "var(--color-brand-green-500)",
      "--stepper-current-label": theme.currentLabel || "var(--color-white)",
      "--stepper-incomplete-label": theme.incompleteLabel || "var(--color-brand-blue-50)",
      "--stepper-incomplete-opacity": theme.incompleteOpacity?.toString() || "0.6",
      "--stepper-disabled-opacity": theme.disabledOpacity?.toString() || "1",
      "--stepper-current-connector-opacity": theme.currentConnectorOpacity?.toString() || "0.9",
      "--stepper-disabled-label": theme.disabledLabel || theme.incompleteLabel || "var(--color-gray-700)",
      "--stepper-disabled-icon-border": theme.disabledIconBorder || theme.incompleteIconBorder || "var(--color-gray-700)",
      "--stepper-disabled-connector": theme.disabledConnector || theme.incompleteConnector || "var(--color-gray-700)",
      "--stepper-waiting-gradient-from": theme.waitingIconBgGradient?.from || "#FEF3C7",
      "--stepper-waiting-gradient-to": theme.waitingIconBgGradient?.to || "#F59E0B",
      "--stepper-waiting-icon-color": theme.waitingIconColor || "var(--color-white)",
      "--stepper-waiting-connector": theme.waitingConnector || "#F59E0B",
      "--stepper-waiting-label": theme.waitingLabel || "#F59E0B",
      ...style
    }),
    [theme, style]
  );
  const handleStepClick = React23.useCallback(
    (stepIndex, step) => {
      if (clickable && !step.disabled && onStepClick) {
        onStepClick(stepIndex, step);
      }
    },
    [clickable, onStepClick]
  );
  return /* @__PURE__ */ jsx64(
    "div",
    {
      ref,
      "data-testid": testID,
      className: cn("stepper flex flex-col", className),
      style: cssVars,
      ...props,
      children: steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const isCompleted = step.completed === true || index < currentStep && !step.skipped;
        const isActive = !isCompleted && index === currentStep;
        return /* @__PURE__ */ jsx64("div", { className: "flex flex-col", children: /* @__PURE__ */ jsxs43(
          "div",
          {
            className: cn(
              "step-item flex flex-row items-start gap-lg",
              clickable && !step.disabled && "cursor-pointer",
              "transition-all duration-200"
            ),
            "data-testid": testID ? `${testID}-step-${index}` : void 0,
            onClick: () => handleStepClick(index, step),
            role: clickable && !step.disabled ? "button" : void 0,
            tabIndex: clickable && !step.disabled ? 0 : void 0,
            onKeyDown: (e) => {
              if (clickable && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                handleStepClick(index, step);
              }
            },
            children: [
              /* @__PURE__ */ jsxs43("div", { className: "flex flex-col items-center self-stretch gap-xs pb-xs", children: [
                /* @__PURE__ */ jsx64(
                  StepIcon,
                  {
                    step,
                    isCompleted,
                    isActive,
                    isSelected: !!step.selected,
                    showShadow
                  }
                ),
                /* @__PURE__ */ jsx64(
                  StepConnector,
                  {
                    isCompleted,
                    isActive,
                    isLast,
                    isDisabled: !!step.disabled
                  }
                )
              ] }),
              /* @__PURE__ */ jsx64("div", { className: cn("flex-1", !isLast && "pb-8"), children: /* @__PURE__ */ jsx64(
                StepLabel,
                {
                  step,
                  isCompleted,
                  isActive,
                  isSelected: !!step.selected
                }
              ) })
            ]
          }
        ) }, step.id);
      })
    }
  );
};
Stepper.displayName = "Stepper";

// src/components/molecules/TabBar.tsx
import { cva as cva16 } from "class-variance-authority";
import { jsx as jsx65, jsxs as jsxs44 } from "react/jsx-runtime";
var tabBarVariants = cva16(
  "border-0 border-b-2 border-transparent rounded-none bg-transparent hover:bg-transparent pb-lg px-2xl inline-flex items-center justify-center whitespace-nowrap font-medium transition-all duration-200 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline focus-visible:outline-2 focus-visible:[outline-offset:-3px] focus-visible:outline-brand-blue-500",
  {
    variants: {
      current: {
        true: [
          "border-b-2 border-b-brand-blue-500",
          "text-brand-blue-500",
          "gap-md"
        ],
        false: [
          "border-b-transparent",
          "text-gray-500",
          "hover:text-gray-700",
          "gap-md"
        ]
      },
      disabled: {
        true: "opacity-50 cursor-not-allowed",
        false: "cursor-pointer"
      }
    },
    defaultVariants: {
      current: false,
      disabled: false
    }
  }
);
var TabBar = ({
  className,
  current,
  label,
  badgeCount,
  badgeOverride,
  badgeVariant = "brand",
  disabled,
  testID,
  onClick,
  ..._props
}) => {
  return /* @__PURE__ */ jsxs44(
    Button,
    {
      variant: "quaternary",
      className: cn(
        tabBarVariants({ current, disabled }),
        "relative",
        className
      ),
      label,
      testID,
      onClick: disabled ? void 0 : onClick,
      disabled,
      iconPosition: "none",
      ..._props,
      children: [
        /* @__PURE__ */ jsx65("span", { className: "text-base", children: label }),
        badgeOverride !== void 0 ? /* @__PURE__ */ jsx65(Badge, { variant: current ? "brand" : badgeVariant, className: "min-h-7", children: badgeOverride }) : badgeCount !== void 0 && badgeCount > 0 && /* @__PURE__ */ jsx65(
          Badge,
          {
            variant: current ? "brand" : badgeVariant,
            className: !current ? "text-gray-700 bg-gray-50" : "",
            children: badgeCount
          }
        )
      ]
    }
  );
};
TabBar.displayName = "TabBar";

// src/components/molecules/TextareaWithCharacterCount.tsx
import { useTranslation as useTranslation24 } from "@tasama/i18n";
import { jsx as jsx66, jsxs as jsxs45 } from "react/jsx-runtime";
var TextareaWithCharacterCount = ({
  label,
  id,
  hintText,
  error = false,
  errorMessage,
  disabled = false,
  required = false,
  testID,
  size = "md",
  className,
  value = "",
  maxLength = 1e3,
  showCharacterCount = true,
  ...props
}) => {
  const { t } = useTranslation24("common");
  const currentLength = typeof value === "string" ? value.length : 0;
  const describedByIds = [];
  if (hintText) describedByIds.push(`${id}-hint`);
  if (error && errorMessage && !hintText) describedByIds.push(`${id}-error`);
  const ariaDescribedBy = describedByIds.length ? describedByIds.join(" ") : void 0;
  return /* @__PURE__ */ jsxs45("div", { className: "space-y-sm", "data-testid": testID, children: [
    /* @__PURE__ */ jsxs45(
      Label,
      {
        htmlFor: id,
        className: cn(
          "inline-block",
          "font-normal",
          disabled ? "text-disabled" : "text-fg-primary"
        ),
        children: [
          label,
          !required && /* @__PURE__ */ jsx66("span", { className: "ms-1", children: t("actions.optional") })
        ]
      }
    ),
    /* @__PURE__ */ jsxs45("div", { className: "relative", children: [
      /* @__PURE__ */ jsx66(
        Textarea,
        {
          id,
          "aria-invalid": error || void 0,
          "aria-describedby": ariaDescribedBy,
          disabled,
          uiSize: size,
          "data-testid": `${testID}-textarea`,
          className: cn(
            error ? "border-error focus:shadow-[0_0_0_4px_var(--color-bg-error-secondary)]" : "border-tertiary focus:shadow-[0_0_0_4px_var(--color-brand-tint-200)]",
            className
          ),
          value,
          maxLength,
          ...props
        }
      ),
      showCharacterCount && /* @__PURE__ */ jsxs45("div", { className: "absolute bottom-2 end-2 text-xs text-quaternary pointer-events-none", children: [
        currentLength,
        "/",
        maxLength
      ] })
    ] }),
    !error && hintText && /* @__PURE__ */ jsx66(
      "p",
      {
        id: `${id}-hint`,
        "data-testid": `${testID}-hint`,
        className: cn(
          "text-xs font-light font-sans",
          disabled ? "text-disabled" : "text-quaternary"
        ),
        children: hintText
      }
    ),
    error && errorMessage && /* @__PURE__ */ jsx66(
      "p",
      {
        id: `${id}-error`,
        "data-testid": `${testID}-error`,
        className: "text-error-primary text-xs font-light font-sans",
        children: errorMessage
      }
    )
  ] });
};
TextareaWithCharacterCount.displayName = "TextareaWithCharacterCount";

// src/components/molecules/TextareaWithLabel.tsx
import { useTranslation as useTranslation25 } from "@tasama/i18n";
import { jsx as jsx67, jsxs as jsxs46 } from "react/jsx-runtime";
var TextareaWithLabel = ({
  label,
  id,
  hintText,
  error = false,
  errorMessage,
  disabled = false,
  required = false,
  testID,
  size = "md",
  className,
  ...props
}) => {
  const { t } = useTranslation25("common");
  const describedByIds = [];
  if (hintText) describedByIds.push(`${id}-hint`);
  if (error && errorMessage && !hintText) describedByIds.push(`${id}-error`);
  const ariaDescribedBy = describedByIds.length ? describedByIds.join(" ") : void 0;
  return /* @__PURE__ */ jsxs46("div", { className: "space-y-sm", "data-testid": testID, children: [
    /* @__PURE__ */ jsxs46(
      Label,
      {
        htmlFor: id,
        className: cn(
          "inline-block",
          "font-normal",
          disabled ? "text-disabled" : "text-fg-secondary"
        ),
        children: [
          label,
          !required && /* @__PURE__ */ jsx67("span", { className: "ms-1", children: t("actions.optional") })
        ]
      }
    ),
    /* @__PURE__ */ jsx67("div", { className: "relative", children: /* @__PURE__ */ jsx67(
      Textarea,
      {
        id,
        "aria-invalid": error || void 0,
        "aria-describedby": ariaDescribedBy,
        disabled,
        uiSize: size,
        "data-testid": `${testID}-textarea`,
        className: cn(
          error ? "border-error focus:shadow-[0_0_0_4px_var(--color-bg-error-secondary)]" : "border-tertiary focus:shadow-[0_0_0_4px_var(--color-brand-tint-200)]",
          className
        ),
        ...props
      }
    ) }),
    !error && hintText && /* @__PURE__ */ jsx67(
      "p",
      {
        id: `${id}-hint`,
        "data-testid": `${testID}-hint`,
        className: cn(
          "text-xs font-light font-sans",
          disabled ? "text-disabled" : "text-quaternary"
        ),
        children: hintText
      }
    ),
    error && errorMessage && /* @__PURE__ */ jsx67(
      "p",
      {
        id: `${id}-error`,
        "data-testid": `${testID}-error`,
        className: "text-error-primary text-xs font-light font-sans",
        children: errorMessage
      }
    )
  ] });
};
TextareaWithLabel.displayName = "TextareaWithLabel";

// src/components/molecules/ServiceCategoryCard.tsx
import { useMemo as useMemo5 } from "react";
import { jsx as jsx68, jsxs as jsxs47 } from "react/jsx-runtime";
var ServiceCategoryCard = ({
  title,
  description,
  imageUrl = "",
  mobileImageUrl,
  ctaLabel = "LEARN MORE",
  onClick,
  ctaHref,
  className,
  cardIndex = 0,
  testID = "service-category-card"
}) => {
  const plainTitle = useMemo5(
    () => typeof document !== "undefined" ? new DOMParser().parseFromString(title, "text/html").body.textContent ?? title : title.replace(/<[^>]*>/g, ""),
    [title]
  );
  const truncatedDescription = useMemo5(() => {
    if (!description) {
      return "";
    }
    if (description.length <= 220) {
      return description;
    }
    return description.slice(0, 220).trim() + "...";
  }, [description]);
  const patternSvg = cardIndex === 2 ? "/assets/images/service-card-pattern-blue.svg" : "/assets/images/service-card-pattern-green.svg";
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };
  const ctaContent = /* @__PURE__ */ jsxs47(
    "span",
    {
      className: "inline-flex items-center gap-[12px] lg:gap-[16px] p-0",
      "data-testid": `${testID}-cta`,
      children: [
        /* @__PURE__ */ jsx68(
          "span",
          {
            className: `text-sm font-medium text-fg-primary uppercase tracking-normal leading-sm ${cardIndex === 1 || cardIndex === 2 ? "text-white" : "text-fg-primary"}`,
            children: ctaLabel
          }
        ),
        /* @__PURE__ */ jsx68("span", { className: "inline-flex items-center justify-center size-[28px] lg:size-[32px] rounded-full bg-gray-100", children: /* @__PURE__ */ jsx68(
          Icon,
          {
            name: "ArrowRight",
            size: 16,
            className: "text-fg-primary rtl:scale-x-[-1]"
          }
        ) })
      ]
    }
  );
  return /* @__PURE__ */ jsxs47(
    "div",
    {
      role: onClick ? "button" : void 0,
      tabIndex: onClick ? 0 : -1,
      onClick,
      onKeyDown: onClick ? handleKeyDown : void 0,
      "aria-label": `${plainTitle} card`,
      className: cn(
        "group relative w-full lg:w-[344px] h-auto lg:h-[467px] rounded-[16px] overflow-hidden",
        "flex flex-col gap-3xl lg:gap-0 lg:justify-between",
        "p-[20px] lg:pt-4xl lg:pe-3xl lg:pb-4xl lg:ps-3xl",
        "transition-all duration-200 cursor-pointer",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        cardIndex === 0 && "bg-white border border-gray-200",
        cardIndex === 1 && "bg-[#f5f5f5]",
        cardIndex === 2 && "bg-[#10069f]",
        cardIndex > 2 && "bg-white border border-gray-200",
        className
      ),
      "data-testid": testID,
      children: [
        imageUrl ? (
          // Note: <picture> doesn't accept `aria-hidden` (jsx-a11y rule); the
          // inner <img> with `alt=""` already marks this as decorative.
          /* @__PURE__ */ jsxs47("picture", { className: "absolute inset-0 block pointer-events-none", children: [
            /* @__PURE__ */ jsx68("source", { media: "(min-width: 56.25rem)", srcSet: imageUrl }),
            /* @__PURE__ */ jsx68(
              "img",
              {
                src: mobileImageUrl || imageUrl,
                alt: "",
                className: "block w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-110"
              }
            )
          ] })
        ) : (
          /* Decorative SVG pattern. Single <img> with mobile-first Tailwind
             classes that flip the position/size/opacity at the `lg` breakpoint
             (900px). The mobile crop is anchored to the bottom-right with
             negative offsets (Figma 211×212); the desktop crop is anchored
             top-left at fixed coordinates (Figma 350×351). Card 2 (the blue
             card) renders the pattern at 30% opacity in both layouts; cards
             0 and 1 use 50% on mobile and full opacity on desktop. */
          /* @__PURE__ */ jsx68(
            "img",
            {
              src: patternSvg,
              alt: "",
              "aria-hidden": "true",
              className: cn(
                "absolute pointer-events-none transition-transform duration-300 group-hover:scale-110",
                // Mobile (default): 211×212, bottom-right with negative offsets
                "end-[-69px] w-[211px] h-[212px]",
                cardIndex === 2 ? "bottom-[-128px] opacity-30" : "bottom-[-115px] opacity-50",
                // Desktop (>= lg / 900px): 350×351, start-aligned at fixed coords.
                // Reset end/bottom to auto so they don't fight the new anchors.
                "lg:end-auto lg:bottom-auto lg:top-[259px] lg:start-[117px]",
                "lg:w-[350px] lg:h-[351px]",
                cardIndex !== 2 && "lg:opacity-100"
              )
            }
          )
        ),
        /* @__PURE__ */ jsxs47("div", { className: "relative z-10 flex flex-col gap-4", children: [
          /* @__PURE__ */ jsx68(
            Heading,
            {
              level: 3,
              size: "3xl",
              weight: "normal",
              className: "text-fg-brand-gold tracking-normal text-[24px] leading-[32px] lg:text-3xl lg:leading-3xl !font-normal font-display",
              testID: `${testID}-title`,
              children: plainTitle
            }
          ),
          /* @__PURE__ */ jsx68(
            Text,
            {
              size: "sm",
              leading: "normal",
              weight: "normal",
              className: `${cardIndex === 0 ? "text-gray-700" : "text-white"} text-xs leading-[18px] lg:text-sm lg:!leading-sm tracking-normal`,
              testID: `${testID}-description`,
              children: truncatedDescription
            }
          )
        ] }),
        /* @__PURE__ */ jsx68("div", { className: "relative z-10", children: ctaHref ? /* @__PURE__ */ jsx68("a", { href: ctaHref, "aria-label": `${ctaLabel} - ${plainTitle}`, children: ctaContent }) : ctaContent })
      ]
    }
  );
};
ServiceCategoryCard.displayName = "ServiceCategoryCard";

// src/components/molecules/ServiceCardL2.tsx
import { useMemo as useMemo6 } from "react";
import { useTranslation as useTranslation26 } from "@tasama/i18n";
import { Fragment as Fragment6, jsx as jsx69, jsxs as jsxs48 } from "react/jsx-runtime";
var CARD_TYPE_BACKGROUND_MAP = {
  service: "/assets/images/service-card-background.png",
  bundle: "/assets/images/bundle-card-background.png",
  "add-on": "/assets/images/add-on-card-background.png"
};
var LEGACY_BACKGROUND_IMAGE = "/assets/images/service-card-bg.jpg";
var LEGACY_PATTERN_IMAGE = "/assets/images/service-card-pattern-green.svg";
var LEGACY_GRADIENT_OVERLAY = "linear-gradient(90deg, rgba(16,6,159,0.35) 0%, rgba(16,6,159,0.42) 40.75%, rgba(16,6,159,0.63) 71.31%, rgba(16,6,159,0.7) 100%)";
var ServiceCardL2 = ({
  title,
  description,
  shortDescription,
  onClick,
  className,
  testID = "service-card-l2",
  variant = "default",
  preTitle,
  statusBadge,
  cardType,
  serviceBadges,
  stepInfo,
  footerContent,
  descriptionSize = "md",
  requestId,
  allowHoverEffect = true
}) => {
  const { t } = useTranslation26("portal");
  const backgroundImage = cardType ? CARD_TYPE_BACKGROUND_MAP[cardType] : LEGACY_BACKGROUND_IMAGE;
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };
  const getTruncatedText = (text, maxLength = 135) => {
    if (!text) {
      return "";
    }
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };
  const plainTitle = useMemo6(
    () => typeof document !== "undefined" ? new DOMParser().parseFromString(title, "text/html").body.textContent ?? title : title.replace(/<[^>]*>/g, ""),
    [title]
  );
  return /* @__PURE__ */ jsxs48(
    "div",
    {
      role: onClick ? "button" : void 0,
      tabIndex: onClick ? 0 : -1,
      onClick,
      onKeyDown: onClick ? handleKeyDown : void 0,
      "aria-label": onClick ? t("serviceCard.ariaLabel", { title: plainTitle }) : void 0,
      className: cn(
        "w-full bg-gray-100 rounded-2xl overflow-hidden group flex flex-col",
        "transition-all duration-200 ease-in-out",
        allowHoverEffect && "hover:bg-gray-300 hover:-translate-y-1",
        "border border-gray-200 lg:border-0",
        // Always use min-height (never a fixed height): the grid's default
        // align-items: stretch then equalizes row heights across siblings,
        // whether or not a given card has a 'bundles' footer (TDF-1486 —
        // mixing a fixed h-[280px] with min-h-[280px] siblings broke stretch
        // and misaligned bundled vs. non-bundled cards in the same row).
        // Unprefixed (not lg:-only) — sm/md already run a 2-col grid (TDF-1493:
        // cards without a description shrank to title-only height there since
        // the min-height only kicked in at lg).
        "min-h-[280px]",
        onClick && "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className
      ),
      "data-testid": testID,
      children: [
        /* @__PURE__ */ jsxs48("div", { className: "relative h-[108px] overflow-hidden", children: [
          /* @__PURE__ */ jsx69(
            "div",
            {
              className: "absolute inset-0 bg-cover bg-center",
              style: { backgroundImage: `url(${backgroundImage})` },
              "aria-hidden": true
            }
          ),
          !cardType && /* @__PURE__ */ jsxs48(Fragment6, { children: [
            /* @__PURE__ */ jsx69(
              "div",
              {
                className: "absolute inset-0",
                style: { background: LEGACY_GRADIENT_OVERLAY },
                "aria-hidden": true
              }
            ),
            /* @__PURE__ */ jsx69(
              "img",
              {
                src: LEGACY_PATTERN_IMAGE,
                className: "absolute pointer-events-none",
                style: { width: 220, height: 221, insetInlineEnd: -40, top: 0 },
                "aria-hidden": true,
                alt: ""
              }
            )
          ] }),
          serviceBadges && serviceBadges.length > 0 && /* @__PURE__ */ jsx69("div", { className: "absolute top-4 start-4 z-10 flex flex-wrap gap-2", children: serviceBadges.map((badgeProps, index) => /* @__PURE__ */ jsx69(
            ServiceBadge,
            {
              ...badgeProps
            },
            badgeProps.testID ?? `${testID}-service-badge-${index}`
          )) }),
          !serviceBadges && statusBadge && /* @__PURE__ */ jsx69("div", { className: "absolute top-4 start-4 z-10", children: statusBadge }),
          requestId && /* @__PURE__ */ jsxs48(
            "div",
            {
              className: "absolute bottom-4 start-4 z-10 text-xs text-white",
              "data-testid": `${testID}-service-id`,
              children: [
                /* @__PURE__ */ jsx69("span", { className: "font-normal", children: t("serviceCard.serviceId") }),
                /* @__PURE__ */ jsxs48("span", { className: "font-light", children: [
                  " ",
                  requestId
                ] })
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsx69(
          "div",
          {
            className: cn(
              "flex flex-col flex-grow min-h-[172px] px-[20px] pt-[20px] pb-[20px] lg:px-5 lg:pt-6 lg:pb-6 bg-gray-100 transition-colors duration-200",
              allowHoverEffect && "group-hover:bg-gray-300"
            ),
            children: /* @__PURE__ */ jsxs48("div", { className: "flex flex-col flex-grow gap-2xl", children: [
              preTitle && /* @__PURE__ */ jsx69(
                "div",
                {
                  className: "text-xs font-light lg:font-normal text-gray-500 lg:text-gray-900 tracking-normal",
                  "data-testid": `${testID}-pre-title`,
                  children: preTitle
                }
              ),
              variant === "expanded" && description ? /* @__PURE__ */ jsxs48(
                "div",
                {
                  className: "flex flex-wrap items-start gap-x-2 text-sm leading-sm lg:text-lg lg:leading-lg text-fg-primary tracking-normal",
                  "data-testid": `${testID}-title`,
                  children: [
                    /* @__PURE__ */ jsx69(
                      "span",
                      {
                        className: cn(
                          "font-medium transition-colors duration-200",
                          allowHoverEffect && "group-hover:text-fg-brand-primary"
                        ),
                        children: plainTitle
                      }
                    ),
                    /* @__PURE__ */ jsx69(
                      "span",
                      {
                        className: "font-light text-gray-700",
                        "data-testid": `${testID}-description`,
                        children: getTruncatedText(shortDescription || description)
                      }
                    )
                  ]
                }
              ) : /* @__PURE__ */ jsxs48(Fragment6, { children: [
                /* @__PURE__ */ jsx69(
                  "div",
                  {
                    className: cn(
                      "text-sm leading-sm lg:text-lg lg:leading-lg font-medium text-fg-primary tracking-normal transition-colors duration-200",
                      allowHoverEffect && "group-hover:text-fg-brand-primary"
                    ),
                    "data-testid": `${testID}-title`,
                    children: plainTitle
                  }
                ),
                (shortDescription || description) && /* @__PURE__ */ jsx69(
                  "div",
                  {
                    className: `text-sm leading-sm ${descriptionSize === "lg" ? "lg:text-lg" : ""} font-light text-gray-700 tracking-normal`,
                    "data-testid": `${testID}-description`,
                    children: getTruncatedText(shortDescription || description)
                  }
                )
              ] }),
              stepInfo && /* @__PURE__ */ jsx69(
                "div",
                {
                  className: "mt-auto pt-4 flex items-center",
                  "data-testid": `${testID}-step-info`,
                  children: /* @__PURE__ */ jsxs48("div", { className: "px-3 py-2 bg-white border border-gray-dark-mode-300 rounded-full inline-flex items-center gap-2 max-w-full overflow-hidden", children: [
                    /* @__PURE__ */ jsx69("span", { className: "text-xs font-normal text-gray-900 shrink-0 whitespace-nowrap", children: t("serviceCard.step", {
                      currentStep: stepInfo.currentStep,
                      totalSteps: stepInfo.totalSteps
                    }) }),
                    /* @__PURE__ */ jsx69("span", { className: "text-xs font-light text-gray-600 truncate min-w-0", children: stepInfo.stepTitle })
                  ] })
                }
              ),
              !stepInfo && footerContent?.type === "bundles" && /* @__PURE__ */ jsxs48(
                "div",
                {
                  className: "mt-auto pt-4",
                  "data-testid": `${testID}-footer-bundles`,
                  children: [
                    /* @__PURE__ */ jsx69("div", { className: "text-sm font-medium text-fg-primary mb-2", children: footerContent.label }),
                    /* @__PURE__ */ jsx69("div", { className: "flex flex-wrap gap-2", children: footerContent.bundleNames.map((bundleName) => /* @__PURE__ */ jsx69(
                      ServiceBadge,
                      {
                        variant: "neutral",
                        size: "s",
                        text: bundleName,
                        testID: `${testID}-footer-bundle-${bundleName}`
                      },
                      bundleName
                    )) })
                  ]
                }
              ),
              !stepInfo && footerContent?.type === "selection" && /* @__PURE__ */ jsxs48(
                "div",
                {
                  className: "mt-auto pt-4 flex items-center gap-5",
                  "data-testid": `${testID}-footer-selection`,
                  children: [
                    /* @__PURE__ */ jsxs48("label", { className: "flex items-center gap-2 cursor-pointer", children: [
                      /* @__PURE__ */ jsx69(
                        Checkbox,
                        {
                          checked: footerContent.checked,
                          onChange: (e) => footerContent.onCheckedChange(e.target.checked),
                          testID: `${testID}-footer-selection-checkbox`
                        }
                      ),
                      /* @__PURE__ */ jsx69("span", { className: "text-sm font-normal text-fg-secondary", children: footerContent.checkboxLabel ?? t("serviceCard.select") })
                    ] }),
                    /* @__PURE__ */ jsx69(
                      Button,
                      {
                        variant: "tertiary",
                        size: "sm",
                        iconPosition: "none",
                        className: "p-0 h-auto text-fg-secondary hover:text-fg-secondary",
                        label: footerContent.moreDetailsLabel ?? t("serviceCard.moreDetails"),
                        onClick: footerContent.onMoreDetailsClick,
                        testID: `${testID}-footer-selection-more-details`
                      }
                    )
                  ]
                }
              )
            ] })
          }
        )
      ]
    }
  );
};
ServiceCardL2.displayName = "ServiceCardL2";

// src/components/molecules/Banner.tsx
import DOMPurify from "dompurify";
import { useTranslation as useTranslation27, useDirection as useDirection4 } from "@tasama/i18n";

// src/components/molecules/Breadcrumbs.tsx
import { useDirection as useDirection3 } from "@tasama/i18n";
import { jsx as jsx70, jsxs as jsxs49 } from "react/jsx-runtime";
var Breadcrumbs = ({
  items,
  className,
  testID = "breadcrumbs",
  separator = /* @__PURE__ */ jsx70(Icon, { name: "ChevronRight", size: 16, className: "mx-2" }),
  showTrailingSeparator = false,
  ariaLabel = "Breadcrumb"
}) => {
  const dir = useDirection3();
  if (!items || items.length === 0) return null;
  const itemBaseClasses = "font-light text-xs leading-xs";
  const interactiveClasses = "bg-transparent border-0 p-0 text-start cursor-pointer hover:underline";
  return /* @__PURE__ */ jsx70(
    "nav",
    {
      "aria-label": ariaLabel,
      className: cn("flex items-center overflow-hidden", className),
      "data-testid": testID,
      children: /* @__PURE__ */ jsx70("ol", { className: "flex items-center flex-nowrap list-none m-0 p-0 w-full overflow-hidden", children: items.map((item, index) => {
        const isLast = index === items.length - 1;
        const sizingClasses = isLast ? "min-w-0 overflow-hidden" : "shrink-0";
        const labelTruncateClasses = isLast ? "truncate block" : "";
        return /* @__PURE__ */ jsxs49(
          "li",
          {
            className: cn("flex items-center", sizingClasses),
            children: [
              item.href ? /* @__PURE__ */ jsx70(
                "a",
                {
                  href: item.href,
                  className: cn(itemBaseClasses, interactiveClasses),
                  onClick: (e) => {
                    if (item.onClick) {
                      e.preventDefault();
                      item.onClick();
                    }
                  },
                  "data-testid": `${testID}-item-${index}`,
                  children: item.label
                }
              ) : item.onClick ? /* @__PURE__ */ jsx70(
                "button",
                {
                  type: "button",
                  className: cn(itemBaseClasses, interactiveClasses),
                  onClick: item.onClick,
                  "data-testid": `${testID}-item-${index}`,
                  children: item.label
                }
              ) : /* @__PURE__ */ jsx70(
                "span",
                {
                  className: cn(itemBaseClasses, labelTruncateClasses),
                  "data-testid": `${testID}-item-${index}`,
                  "aria-current": isLast ? "page" : void 0,
                  children: item.label
                }
              ),
              (!isLast || showTrailingSeparator) && /* @__PURE__ */ jsx70(
                "span",
                {
                  className: `flex items-center shrink-0 ${dir === "rtl" && "icon-rtl-flip"}`,
                  "aria-hidden": "true",
                  children: separator
                }
              )
            ]
          },
          `breadcrumb-${index}`
        );
      }) })
    }
  );
};
Breadcrumbs.displayName = "Breadcrumbs";

// src/components/molecules/Banner.tsx
import { jsx as jsx71, jsxs as jsxs50 } from "react/jsx-runtime";
var Banner = ({
  title,
  description,
  buttonText,
  onButtonClick,
  className,
  testID,
  bgImage = "/assets/images/banner-welcome.png",
  variant: _variant = "welcome",
  breadcrumbs,
  breadcrumbsShowTrailingSeparator = false,
  breadcrumbsAriaLabel,
  statusBadge,
  ...props
}) => {
  const { t } = useTranslation27("common");
  const dir = useDirection4();
  const resolvedTitle = title ?? t("banner.defaultTitle");
  const patternSrc = dir === "rtl" ? "/assets/images/banner-pattern-ar.svg" : "/assets/images/banner-pattern-en.svg";
  return /* @__PURE__ */ jsxs50(
    "div",
    {
      style: { backgroundImage: `url('${bgImage}')` },
      className: cn(
        "relative flex flex-col gap-2xl p-[20px] md:p-4xl rounded-[16px] md:rounded-2xl overflow-hidden",
        "bg-cover bg-no-repeat bg-center before:absolute before:pointer-events-none",
        className
      ),
      "data-testid": testID,
      ...props,
      children: [
        /* @__PURE__ */ jsx71(
          "div",
          {
            "aria-hidden": "true",
            className: cn(
              "pointer-events-none absolute end-0 overflow-hidden",
              _variant === "service-details" ? "hidden md:block top-0 h-[209px] w-[429px]" : "hidden min-[1201px]:block bottom-0 h-1/2 min-[1400px]:h-3/4 min-[1600px]:h-full"
            ),
            style: _variant === "service-details" ? void 0 : { aspectRatio: "429 / 209" },
            children: /* @__PURE__ */ jsx71("img", { src: patternSrc, alt: "", className: "block w-full h-full" })
          }
        ),
        /* @__PURE__ */ jsxs50("div", { className: "flex flex-col gap-md max-w-full z-10", children: [
          breadcrumbs && breadcrumbs.length > 0 && /* @__PURE__ */ jsx71(
            Breadcrumbs,
            {
              items: breadcrumbs,
              showTrailingSeparator: breadcrumbsShowTrailingSeparator,
              ...breadcrumbsAriaLabel && { ariaLabel: breadcrumbsAriaLabel },
              className: "text-white opacity-80 mb-1"
            }
          ),
          /* @__PURE__ */ jsx71(
            "h2",
            {
              className: cn(
                "font-display font-normal text-primary-on-brand text-[24px] leading-[32px] md:text-[30px] md:leading-[38px] text-start m-0",
                // At ≤750px, force any inline span (e.g. the gold "Tasama") onto
                // its own line so "Welcome to" and "Tasama" break cleanly.
                "max-[750px]:[&>span]:block",
                breadcrumbs && breadcrumbs.length > 0 && "pt-4 lg:pt-10"
              ),
              dangerouslySetInnerHTML: {
                __html: DOMPurify.sanitize(
                  resolvedTitle.replace(/<p>|<\/p>/g, ""),
                  {
                    ALLOWED_TAGS: ["span", "strong", "em"],
                    ALLOWED_ATTR: ["class"]
                  }
                )
              }
            }
          ),
          statusBadge && /* @__PURE__ */ jsx71("div", { className: "mt-4", children: statusBadge }),
          description && /* @__PURE__ */ jsx71("p", { className: "text-secondary-on-brand max-w-[670px] font-light text-xs lg:text-sm leading-[18px] lg:leading-[1.4286] text-start m-0", children: description })
        ] }),
        buttonText && /* @__PURE__ */ jsx71("div", { className: "flex", children: /* @__PURE__ */ jsx71(
          Button,
          {
            variant: "primary",
            size: "sm",
            iconPosition: "trailing",
            iconName: "ArrowRight",
            onClick: onButtonClick,
            testID: `${testID}-button`,
            label: buttonText
          }
        ) })
      ]
    }
  );
};
Banner.displayName = "Banner";

// src/components/molecules/TermsAndConditionsContent.tsx
import { jsx as jsx72, jsxs as jsxs51 } from "react/jsx-runtime";
var TermsAndConditionsContent = ({ className, testID = "terms-conditions-content", sections }) => {
  const renderSection = (section) => {
    const paragraphs = section.content.split("\n").filter((text) => text.trim());
    return /* @__PURE__ */ jsxs51("div", { className: "space-y-4", children: [
      paragraphs.map((paragraph, index) => /* @__PURE__ */ jsx72(
        "p",
        {
          className: "text-sm leading-relaxed text-fg-secondary block",
          children: paragraph.trim()
        },
        index
      )),
      section.items && section.items.length > 0 && /* @__PURE__ */ jsx72("ul", { className: "list-disc ps-6 space-y-2 text-sm text-fg-secondary mt-4", children: section.items.map((item, index) => /* @__PURE__ */ jsx72("li", { className: "leading-relaxed", children: item }, index)) }),
      section.additionalContent && /* @__PURE__ */ jsx72("div", { className: "mt-4", children: section.additionalContent.split("\n").filter((text) => text.trim()).map((paragraph, index) => /* @__PURE__ */ jsx72(
        "p",
        {
          className: "text-sm leading-relaxed text-fg-secondary block",
          children: paragraph.trim()
        },
        index
      )) })
    ] });
  };
  return /* @__PURE__ */ jsx72(
    "div",
    {
      className: cn("w-full text-fg-primary font-normal", className),
      "data-testid": testID,
      children: /* @__PURE__ */ jsx72("div", { className: "space-y-4", children: Object.entries(sections).map(([key, section]) => /* @__PURE__ */ jsxs51("div", { className: "block", children: [
        /* @__PURE__ */ jsx72("h2", { className: "text-base text-sm font-medium text-fg-primary mb-4 block text-start", children: section.title }),
        /* @__PURE__ */ jsx72("div", { className: "block", children: renderSection(section) })
      ] }, key)) })
    }
  );
};
TermsAndConditionsContent.displayName = "TermsAndConditionsContent";

// src/components/molecules/DocumentUploadSection.tsx
import { jsx as jsx73, jsxs as jsxs52 } from "react/jsx-runtime";
var dt = (files) => {
  const transfer = new DataTransfer();
  files.forEach((f) => transfer.items.add(f));
  return transfer.files;
};
var DocumentUploadSection = ({
  label,
  isCompleted = false,
  files = [],
  onFilesSelected,
  onFileRemove,
  disableRemove = false,
  accept = ".pdf,.jpg,.png",
  multiple = false,
  // Left undefined on purpose: FileUploadBase falls back to t('fileUpload.supportedFormats'),
  // so an unset hint is translated instead of hardcoded English. (TDF-1410 FE-3)
  supportingText,
  isFileRemoved,
  maxFiles = 1,
  onMaxFilesExceeded,
  showUpload,
  filesPosition = "below",
  errorMessage,
  className,
  testID
}) => {
  const handleFilesSelected = (incoming) => {
    if (!onFilesSelected) return;
    const existingNames = new Set(files.map((f) => f.fileName));
    let unique = Array.from(incoming).filter((f) => !existingNames.has(f.name));
    let exceeded = false;
    if (maxFiles !== void 0) {
      const remaining = Math.max(0, maxFiles - files.length);
      exceeded = unique.length > remaining;
      unique = unique.slice(0, remaining);
    }
    if (unique.length > 0) {
      onFilesSelected(dt(unique));
    }
    if (exceeded) onMaxFilesExceeded?.();
  };
  const fileList = files.length > 0 ? /* @__PURE__ */ jsx73("div", { className: "space-y-3", children: files.map((file) => /* @__PURE__ */ jsx73(
    FileUploadItem,
    {
      fileName: file.fileName,
      fileSize: file.fileSize,
      progress: file.progress,
      status: file.status === "pending" ? "uploading" : file.status,
      onRemove: () => onFileRemove?.(file.id),
      disableRemove,
      testID: testID ? `${testID}-file-${file.id}` : void 0
    },
    file.id
  )) }) : null;
  const uploadZone = /* @__PURE__ */ jsx73(
    FileUploadBase,
    {
      isFileRemoved: isFileRemoved ?? false,
      onFilesSelected: handleFilesSelected,
      accept,
      multiple,
      ...supportingText !== void 0 ? { supportingText } : {},
      testID: testID ? `${testID}-upload` : void 0,
      disabled: !multiple && files.length >= 1 || maxFiles !== void 0 && files.length >= maxFiles,
      showUpload: showUpload !== void 0 ? showUpload : maxFiles !== void 0 ? files.length < maxFiles : multiple || files.length < 1
    }
  );
  return /* @__PURE__ */ jsxs52("div", { className: cn("space-y-6", className), "data-testid": testID, children: [
    label && /* @__PURE__ */ jsxs52("div", { className: "flex items-start gap-3", children: [
      /* @__PURE__ */ jsx73(
        CheckIcon2,
        {
          className: "shrink-0",
          state: isCompleted ? "completed" : "required",
          testID: testID ? `${testID}-check-icon` : "document-upload-check-icon"
        }
      ),
      /* @__PURE__ */ jsx73(
        Text,
        {
          size: "base",
          weight: "light",
          color: "default",
          className: "text-fg-primary",
          children: label
        }
      )
    ] }),
    filesPosition === "above" ? fileList : null,
    uploadZone,
    filesPosition === "below" ? fileList : null,
    errorMessage && /* @__PURE__ */ jsx73(
      "div",
      {
        className: "px-1",
        "data-testid": testID ? `${testID}-error` : void 0,
        children: /* @__PURE__ */ jsx73(Text, { className: "text-sm text-fg-error-primary", children: errorMessage })
      }
    )
  ] });
};
DocumentUploadSection.displayName = "DocumentUploadSection";

// src/components/molecules/NotificationItem.tsx
import { useEffect as useEffect13, useRef as useRef14, useCallback as useCallback11, useMemo as useMemo7, memo as memo5 } from "react";
import { useTranslation as useTranslation28, i18n } from "@tasama/i18n";
import { jsx as jsx74, jsxs as jsxs53 } from "react/jsx-runtime";
var getPrimaryAction = (actions) => {
  if (!actions || actions.length === 0) return null;
  const view = actions.find((a) => a.actionType?.toUpperCase() === "VIEW");
  return view || actions[0];
};
var NotificationItemComponent = ({
  notification,
  time,
  isUnread = false,
  isHighlighted = false,
  onView,
  onRegisterForViewport,
  onUnregisterFromViewport,
  onNotificationClick,
  testID = "notification-item",
  className
}) => {
  const { t } = useTranslation28("common");
  const elementRef = useRef14(null);
  const { notificationId, title, message, actions, notificationCategory } = notification;
  const primaryAction = useMemo7(() => getPrimaryAction(actions), [actions]);
  useEffect13(() => {
    if (notificationId && elementRef.current && onRegisterForViewport) {
      onRegisterForViewport(notificationId, elementRef.current, isUnread);
    }
    return () => {
      if (notificationId && onUnregisterFromViewport) {
        onUnregisterFromViewport(notificationId);
      }
    };
  }, [
    notificationId,
    isUnread,
    onRegisterForViewport,
    onUnregisterFromViewport
  ]);
  const handleNotificationClick = useCallback11(() => {
    if (notificationId && onNotificationClick) {
      onNotificationClick(notificationId, primaryAction?.url);
    }
  }, [notificationId, onNotificationClick, primaryAction?.url]);
  const handleViewClick = useCallback11(
    (e) => {
      e.stopPropagation();
      if (onView && primaryAction?.url) {
        onView(primaryAction.url);
      }
      handleNotificationClick();
    },
    [onView, primaryAction?.url, handleNotificationClick]
  );
  return /* @__PURE__ */ jsxs53(
    "div",
    {
      ref: elementRef,
      className: cn(
        "flex flex-col gap-2 p-3 border border-brand-tint-300 rounded-xl w-full min-w-0",
        "transition-all duration-200 cursor-pointer",
        "hover:shadow-md hover:bg-gray-37 hover:border-brand-tint-400",
        isHighlighted && [
          "bg-bg-secondary",
          "shadow-[0px_2px_4px_-2px_rgba(10,13,18,0.06),0px_4px_6px_-1px_rgba(10,13,18,0.1)]"
        ],
        !isHighlighted && "bg-white",
        className
      ),
      onClick: handleNotificationClick,
      "data-testid": testID,
      "data-notification-id": notificationId,
      children: [
        /* @__PURE__ */ jsxs53("div", { className: "flex justify-between items-start gap-2 lg:gap-4 min-w-0", children: [
          /* @__PURE__ */ jsxs53("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [
            /* @__PURE__ */ jsx74(
              "div",
              {
                className: cn(
                  "w-2 h-2 rounded-full flex-shrink-0 transition-all duration-150",
                  isUnread ? "bg-brand-blue-500 visible opacity-100" : "invisible opacity-0"
                ),
                "data-testid": `${testID}-unread-dot`
              }
            ),
            /* @__PURE__ */ jsx74(
              Text,
              {
                as: "span",
                size: "sm",
                weight: "medium",
                className: "text-fg-primary flex-1 min-w-0 truncate",
                testID: `${testID}-title`,
                children: title || notificationCategory || t("notification.defaultTitle")
              }
            )
          ] }),
          /* @__PURE__ */ jsx74(
            Text,
            {
              as: "span",
              size: "sm",
              className: "text-fg-tertiary shrink-0",
              testID: `${testID}-time`,
              children: time
            }
          )
        ] }),
        /* @__PURE__ */ jsxs53("div", { className: "flex justify-between items-end gap-2 ps-5 min-w-0", children: [
          /* @__PURE__ */ jsx74(
            Text,
            {
              as: "p",
              size: "sm",
              className: "text-fg-tertiary leading-[1.43] flex-1",
              testID: `${testID}-message`,
              children: message
            }
          ),
          primaryAction && /* @__PURE__ */ jsx74(
            Button,
            {
              variant: "quaternary",
              size: "sm",
              onClick: handleViewClick,
              className: "text-fg-brand-primary hover:text-fg-brand-primary shrink-0 p-0 h-auto",
              testID: `${testID}-view-button`,
              label: i18n.t(primaryAction.label),
              iconPosition: "none"
            }
          )
        ] })
      ]
    }
  );
};
var areEqual = (prev, next) => {
  if (prev.time !== next.time) return false;
  if (prev.isUnread !== next.isUnread) return false;
  if (prev.isHighlighted !== next.isHighlighted) return false;
  if (prev.className !== next.className) return false;
  if (prev.testID !== next.testID) return false;
  const pn = prev.notification;
  const nn = next.notification;
  if (pn.notificationId !== nn.notificationId) return false;
  if (pn.title !== nn.title) return false;
  if (pn.message !== nn.message) return false;
  if (pn.notificationCategory !== nn.notificationCategory) return false;
  const pPA = getPrimaryAction(pn.actions);
  const nPA = getPrimaryAction(nn.actions);
  if ((pPA?.label ?? null) !== (nPA?.label ?? null)) return false;
  if ((pPA?.url ?? null) !== (nPA?.url ?? null)) return false;
  return true;
};
var NotificationItem = memo5(NotificationItemComponent, areEqual);
NotificationItem.displayName = "NotificationItem";

// src/components/molecules/SearchInput.tsx
import { useState as useState15, useRef as useRef15, useEffect as useEffect14, useCallback as useCallback12 } from "react";
import { cva as cva17 } from "class-variance-authority";
import { useTranslation as useTranslation29 } from "@tasama/i18n";
import { jsx as jsx75, jsxs as jsxs54 } from "react/jsx-runtime";
var dropdownVariants2 = cva17(
  "absolute z-50 w-full mt-1 bg-background border border-tertiary rounded-lg shadow-lg max-h-80 overflow-y-auto",
  {
    variants: {
      isOpen: {
        true: "opacity-100 visible transform translate-y-0",
        false: "opacity-0 invisible pointer-events-none transform -translate-y-1"
      }
    },
    defaultVariants: {
      isOpen: false
    }
  }
);
var resultItemVariants = cva17(
  "flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg",
  {
    variants: {
      isHighlighted: {
        true: "bg-bg-secondary",
        false: "hover:bg-bg-secondary"
      },
      type: {
        result: "",
        recent: "text-quaternary",
        suggestion: "text-fg-tertiary",
        category: "border-b border-tertiary last:border-b-0"
      }
    },
    defaultVariants: {
      isHighlighted: false,
      type: "result"
    }
  }
);
var SearchInput = ({
  className,
  placeholder,
  value = "",
  onChange,
  onSearch,
  onSelect,
  onFocus,
  onBlur,
  results = [],
  recentSearches = [],
  loading = false,
  error = false,
  errorMessage,
  showRecentSearches = true,
  enableAutocomplete = true,
  minSearchLength = 1,
  debounceDelay = 300,
  searchIcon = "Search",
  clearIcon = "X",
  showClearButton = true,
  size = "md",
  testID = "search-input",
  inputClassName,
  dropdownClassName,
  disabled,
  showSearchButton: _showSearchButton,
  ...props
}) => {
  const { t } = useTranslation29("common");
  const [isOpen, setIsOpen] = useState15(false);
  const [highlightedIndex, setHighlightedIndex] = useState15(-1);
  const [internalValue, setInternalValue] = useState15(value);
  const dropdownRef = useRef15(null);
  const debounceRef = useRef15(null);
  useEffect14(() => {
    setInternalValue(value);
  }, [value]);
  const debouncedOnChange = useCallback12(
    (searchValue) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        onChange?.(searchValue);
      }, debounceDelay);
    },
    [onChange, debounceDelay]
  );
  const handleInputChange = (event) => {
    const newValue = event.target.value;
    setInternalValue(newValue);
    setHighlightedIndex(-1);
    if (enableAutocomplete && newValue.length >= minSearchLength) {
      setIsOpen(true);
    } else if (newValue.length === 0 && showRecentSearches) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
    debouncedOnChange(newValue);
  };
  const handleInputFocus = (event) => {
    if (disabled) return;
    if (internalValue.length >= minSearchLength && enableAutocomplete || internalValue.length === 0 && showRecentSearches && recentSearches.length > 0) {
      setIsOpen(true);
    }
    onFocus?.(event);
  };
  const handleInputBlur = (event) => {
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }, 150);
    onBlur?.(event);
  };
  const handleSearch = () => {
    if (disabled || !internalValue.trim()) return;
    onSearch?.(internalValue.trim());
    setIsOpen(false);
    setHighlightedIndex(-1);
  };
  const handleClear = () => {
    if (disabled) return;
    setInternalValue("");
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange?.("");
    const inputElement = document.querySelector(
      `[data-testid="${testID}-input"]`
    );
    inputElement?.focus();
  };
  const handleResultSelect = (result) => {
    if (disabled) return;
    setInternalValue(result.title);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onSelect?.(result);
    onChange?.(result.title);
  };
  const handleKeyDown = (event) => {
    if (disabled) return;
    const allResults = [
      ...showRecentSearches && internalValue.length === 0 ? recentSearches : [],
      ...results
    ];
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(
            (prev) => prev < allResults.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (isOpen) {
          setHighlightedIndex(
            (prev) => prev > 0 ? prev - 1 : allResults.length - 1
          );
        }
        break;
      case "Enter":
        event.preventDefault();
        if (isOpen && highlightedIndex >= 0 && allResults[highlightedIndex]) {
          handleResultSelect(allResults[highlightedIndex]);
        } else {
          handleSearch();
        }
        break;
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };
  useEffect14(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect14(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);
  const displayResults = showRecentSearches && internalValue.length === 0 ? recentSearches : results;
  const showDropdown = isOpen && (displayResults.length > 0 || loading || internalValue.length >= minSearchLength);
  const shouldShowClearButton = showClearButton && internalValue.length > 0;
  return /* @__PURE__ */ jsxs54("div", { className: cn("relative w-full", className), "data-testid": testID, children: [
    /* @__PURE__ */ jsx75("div", { className: "relative flex items-center gap-2", children: /* @__PURE__ */ jsxs54("div", { className: "flex-1", children: [
      /* @__PURE__ */ jsx75(
        InputWithLabel,
        {
          id: `${testID}-search`,
          variant: "search",
          type: "text",
          value: internalValue,
          onChange: handleInputChange,
          onFocus: handleInputFocus,
          onBlur: handleInputBlur,
          onKeyDown: handleKeyDown,
          placeholder: placeholder ?? t("search.placeholder"),
          disabled,
          error,
          icon: searchIcon,
          size: size === "lg" ? "md" : size,
          inputClassName: cn(
            shouldShowClearButton ? "pe-20" : "pe-10",
            inputClassName
          ),
          role: "searchbox",
          "aria-describedby": error && errorMessage ? `${testID}-error` : void 0,
          testID,
          ...props
        }
      ),
      shouldShowClearButton && /* @__PURE__ */ jsx75(
        "button",
        {
          type: "button",
          onClick: handleClear,
          disabled,
          className: cn(
            "absolute end-3 top-1/2 -translate-y-1/2",
            "flex items-center justify-center",
            "w-5 h-5",
            "text-quaternary hover:text-fg-secondary",
            "transition-colors duration-150",
            "disabled:cursor-not-allowed disabled:text-disabled",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-tint-500 focus-visible:ring-offset-2 rounded-sm"
          ),
          "aria-label": t("search.clearAriaLabel"),
          "data-testid": `${testID}-clear`,
          children: /* @__PURE__ */ jsx75(Icon, { name: clearIcon, size: 16, "aria-hidden": true })
        }
      )
    ] }) }),
    error && errorMessage && /* @__PURE__ */ jsx75(
      "p",
      {
        id: `${testID}-error`,
        className: "mt-1 text-xs font-light font-sans text-error-primary",
        "data-testid": `${testID}-error`,
        children: errorMessage
      }
    ),
    showDropdown && /* @__PURE__ */ jsxs54(
      "div",
      {
        ref: dropdownRef,
        className: cn(
          dropdownVariants2({ isOpen: showDropdown }),
          dropdownClassName
        ),
        role: "listbox",
        "aria-label": t("search.resultsAriaLabel"),
        "data-testid": `${testID}-dropdown`,
        children: [
          loading && /* @__PURE__ */ jsxs54("div", { className: "flex items-center justify-center py-8", children: [
            /* @__PURE__ */ jsx75(
              Icon,
              {
                name: "Loader",
                className: "w-6 h-6 animate-spin text-quaternary"
              }
            ),
            /* @__PURE__ */ jsx75("span", { className: "ms-2 text-sm text-quaternary", children: t("search.searching") })
          ] }),
          !loading && displayResults.map((result, index) => {
            const isHighlighted = index === highlightedIndex;
            return /* @__PURE__ */ jsxs54(
              "div",
              {
                className: cn(
                  resultItemVariants({
                    isHighlighted,
                    type: result.type || "result"
                  })
                ),
                onClick: () => handleResultSelect(result),
                role: "option",
                "aria-selected": isHighlighted,
                "data-testid": `${testID}-result-${result.id}`,
                children: [
                  result.icon && /* @__PURE__ */ jsx75(
                    Icon,
                    {
                      name: result.icon,
                      className: "w-5 h-5 flex-shrink-0 text-quaternary",
                      "aria-hidden": true
                    }
                  ),
                  result.imageUrl && /* @__PURE__ */ jsx75(
                    "img",
                    {
                      src: result.imageUrl,
                      alt: `${result.title} image`,
                      className: "w-8 h-8 rounded-md object-cover flex-shrink-0"
                    }
                  ),
                  !result.icon && !result.imageUrl && result.type === "recent" && /* @__PURE__ */ jsx75(
                    Icon,
                    {
                      name: "Clock",
                      className: "w-5 h-5 flex-shrink-0 text-quaternary",
                      "aria-hidden": true
                    }
                  ),
                  /* @__PURE__ */ jsxs54("div", { className: "flex-1 min-w-0", children: [
                    /* @__PURE__ */ jsx75("p", { className: "text-sm font-medium text-fg-primary truncate", children: result.title }),
                    result.subtitle && /* @__PURE__ */ jsx75("p", { className: "text-xs text-fg-tertiary truncate mt-0.5", children: result.subtitle }),
                    result.category && /* @__PURE__ */ jsx75("p", { className: "text-xs text-quaternary truncate mt-0.5", children: t("search.inCategory", { category: result.category }) })
                  ] }),
                  result.type === "recent" && /* @__PURE__ */ jsx75(
                    Icon,
                    {
                      name: "ArrowUpLeft",
                      className: "w-4 h-4 flex-shrink-0 text-quaternary",
                      "aria-hidden": true
                    }
                  )
                ]
              },
              result.id
            );
          }),
          !loading && displayResults.length === 0 && internalValue.length >= minSearchLength && /* @__PURE__ */ jsxs54("div", { className: "flex flex-col items-center justify-center py-8 text-center", children: [
            /* @__PURE__ */ jsx75(Icon, { name: "Search", className: "w-8 h-8 text-quaternary mb-2" }),
            /* @__PURE__ */ jsx75("p", { className: "text-sm text-quaternary", children: t("search.noResults") }),
            /* @__PURE__ */ jsx75("p", { className: "text-xs text-quaternary mt-1", children: t("search.adjustSearchTerms") })
          ] })
        ]
      }
    )
  ] });
};
SearchInput.displayName = "SearchInput";

// src/components/molecules/BAContextualButton.tsx
import React28 from "react";
import { jsx as jsx76, jsxs as jsxs55 } from "react/jsx-runtime";
var BAContextualButton = React28.memo(
  ({ promptText, onClick, className, testID }) => {
    return /* @__PURE__ */ jsxs55(
      "button",
      {
        type: "button",
        onClick,
        "data-testid": testID,
        className: cn(
          "group relative w-full flex items-center gap-2 px-4 py-3 rounded-[99px] overflow-hidden cursor-pointer",
          className
        ),
        children: [
          /* @__PURE__ */ jsx76(
            "span",
            {
              "aria-hidden": "true",
              className: "absolute inset-0 rounded-[99px] bg-cover bg-center",
              style: { backgroundImage: "url(/assets/images/banner-welcome.png)" }
            }
          ),
          /* @__PURE__ */ jsx76(
            "span",
            {
              "aria-hidden": "true",
              className: "absolute inset-0 rounded-[99px] bg-brand-blue-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            }
          ),
          /* @__PURE__ */ jsx76(AISparkleIcon, { className: "relative shrink-0 w-5 h-5" }),
          /* @__PURE__ */ jsx76("span", { className: "relative text-sm font-light leading-5 text-fg-white text-left", children: promptText })
        ]
      }
    );
  }
);
BAContextualButton.displayName = "BAContextualButton";

// src/components/molecules/TaskCard.tsx
import { Fragment as Fragment7, jsx as jsx77, jsxs as jsxs56 } from "react/jsx-runtime";
var TaskCard = ({
  className,
  serviceName,
  taskTitle,
  taskDescription,
  status,
  variant = "default",
  clickable = false,
  // Status pills render WITHOUT the icon glyph by default — matches the
  // `StatusBadge` atom's own default and the Figma treatment. Storybook
  // stories that demonstrate the icon-on variant pass `showIcon={true}`
  // explicitly.
  showIcon = false,
  testID,
  ...props
}) => {
  const isDashboard = variant === "dashboard";
  return /* @__PURE__ */ jsx77(
    "div",
    {
      className: cn(
        "bg-white rounded-xl flex items-start w-full",
        isDashboard ? "p-[20px] 2xl:p-6 flex-col 2xl:flex-row gap-3 2xl:gap-4" : "p-4 flex-col gap-3 border border-brand-tint-300",
        clickable && "cursor-pointer hover:bg-[var(--color-gray-37)] hover:shadow-sm transition-all",
        className
      ),
      "data-testid": testID,
      ...props,
      children: isDashboard ? /* @__PURE__ */ jsxs56(Fragment7, { children: [
        /* @__PURE__ */ jsx77(
          StatusBadge,
          {
            status,
            showIcon,
            className: "2xl:order-2"
          }
        ),
        /* @__PURE__ */ jsxs56("div", { className: "flex flex-col gap-1 w-full 2xl:order-1 2xl:flex-1", children: [
          /* @__PURE__ */ jsx77("h3", { className: "text-gray-900 font-normal text-[20px] leading-[30px] 2xl:text-lg 2xl:leading-normal m-0", children: taskTitle }),
          taskDescription && /* @__PURE__ */ jsx77("p", { className: "text-gray-900 font-light text-xs leading-[18px] 2xl:leading-relaxed m-0", children: taskDescription })
        ] })
      ] }) : (
        // Default variant uses a single grid with two layouts:
        //   < 2xl → implicit 1-col grid: badge, serviceName, title, desc
        //   ≥ 2xl → 2-col grid [1fr auto]: serviceName + badge on row 1,
        //           title spanning both cols on row 2 (and desc on row 3)
        // Explicit col/row placements only kick in at ≥2xl, so at narrow
        // widths everything auto-places into rows. No hidden branches.
        /* @__PURE__ */ jsxs56("div", { className: "grid gap-2 w-full 2xl:grid-cols-[1fr_auto] 2xl:items-center 2xl:gap-x-3", children: [
          /* @__PURE__ */ jsx77(
            StatusBadge,
            {
              status,
              showIcon,
              className: "justify-self-start 2xl:col-start-2 2xl:row-start-1 2xl:justify-self-end"
            }
          ),
          /* @__PURE__ */ jsx77(
            Text,
            {
              as: "span",
              weight: "light",
              size: "xs",
              color: "muted",
              className: "text-gray-700 2xl:col-start-1 2xl:row-start-1",
              children: serviceName
            }
          ),
          /* @__PURE__ */ jsx77("h3", { className: "text-gray-900 font-normal text-lg leading-[28px] 2xl:text-base 2xl:leading-normal m-0 2xl:col-span-2", children: taskTitle }),
          taskDescription && /* @__PURE__ */ jsx77("p", { className: "text-gray-900 font-light text-sm leading-[20px] m-0 2xl:col-span-2", children: taskDescription })
        ] })
      )
    }
  );
};
TaskCard.displayName = "TaskCard";

// src/components/organisms/EmptyScreen.tsx
import { jsx as jsx78, jsxs as jsxs57 } from "react/jsx-runtime";
var EmptyScreen = ({
  heading,
  text,
  iconName,
  testID
}) => {
  return /* @__PURE__ */ jsx78(
    "div",
    {
      className: "flex-1 flex items-center justify-center",
      "data-testid": testID,
      children: /* @__PURE__ */ jsxs57("div", { className: "flex flex-col items-center gap-4", children: [
        iconName && /* @__PURE__ */ jsx78("div", { className: "w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center", children: /* @__PURE__ */ jsx78(Icon, { name: iconName, className: "text-fg-tertiary" }) }),
        /* @__PURE__ */ jsxs57("div", { className: "flex flex-col items-center gap-1 max-w-[352px]", children: [
          /* @__PURE__ */ jsx78("div", { className: "text-base leading-md font-medium text-fg-primary text-center", children: heading }),
          text && /* @__PURE__ */ jsx78("p", { className: "text-sm leading-sm font-light text-fg-tertiary text-center", children: text })
        ] })
      ] })
    }
  );
};

// src/components/organisms/Tasks.tsx
import { useTranslation as useTranslation30 } from "@tasama/i18n";
import { jsx as jsx79, jsxs as jsxs58 } from "react/jsx-runtime";
var Tasks = ({
  className,
  title,
  subtitle,
  incompleteCount = 0,
  counterLabel,
  tasks = [],
  loading = false,
  error,
  onViewAllClick,
  onTaskClick,
  testID,
  ...props
}) => {
  const { t } = useTranslation30("common");
  const defaultTitle = t("home.myTasks");
  const defaultSubtitle = t("home.tasksSubtitle");
  const defaultCounterLabel = t("home.incomplete");
  return /* @__PURE__ */ jsxs58(
    "div",
    {
      className: cn(
        "bg-white rounded-[16px] lg:rounded-2xl p-[20px] lg:p-6 flex flex-col gap-[20px] lg:gap-6 w-full",
        className
      ),
      "data-testid": testID,
      ...props,
      children: [
        /* @__PURE__ */ jsxs58("div", { className: "flex justify-between items-baseline gap-lg w-full", children: [
          /* @__PURE__ */ jsxs58("div", { className: "flex flex-col gap-1", children: [
            /* @__PURE__ */ jsx79(
              Heading,
              {
                level: 2,
                size: "xl",
                weight: "normal",
                className: "text-gray-900 leading-normal",
                children: title || defaultTitle
              }
            ),
            /* @__PURE__ */ jsx79(
              Text,
              {
                size: "xs",
                weight: "light",
                className: "text-gray-500 leading-normal text-end",
                children: subtitle || defaultSubtitle
              }
            )
          ] }),
          /* @__PURE__ */ jsx79(
            Button,
            {
              variant: "quaternary",
              size: "md",
              onClick: onViewAllClick,
              testID: testID ? `${testID}-view-all-button` : void 0,
              label: t("common:actions.viewAll"),
              className: "-translate-y-1"
            }
          )
        ] }),
        !loading && !error && /* @__PURE__ */ jsx79("div", { className: "flex w-full", children: /* @__PURE__ */ jsx79(
          StatusCounter,
          {
            count: incompleteCount,
            label: counterLabel || defaultCounterLabel,
            testID: testID ? `${testID}-counter` : void 0
          }
        ) }),
        /* @__PURE__ */ jsx79("div", { className: "flex flex-col gap-xl w-full", children: loading ? (
          // Loading state
          /* @__PURE__ */ jsx79(Skeleton, { className: "w-full h-9xl rounded-xl" })
        ) : error ? (
          // Error state
          /* @__PURE__ */ jsx79("div", { className: "p-6 bg-error-50 text-error-700 rounded-xl", children: t("errors.tasksLoadError", { error }) })
        ) : tasks.length > 0 ? (
          // Tasks list
          tasks.map((task) => /* @__PURE__ */ jsx79(
            TaskCard,
            {
              serviceName: task.serviceName,
              taskTitle: task.taskTitle,
              status: task.status,
              showIcon: false,
              clickable: true,
              testID: testID ? `${testID}-task-${task.id}` : void 0,
              onClick: onTaskClick
            },
            task.id
          ))
        ) : (
          // Empty state
          /* @__PURE__ */ jsx79("div", { className: "p-6 rounded-xl", children: /* @__PURE__ */ jsx79(EmptyScreen, { iconName: "BookOpen", heading: t("tasks.noTasks") }) })
        ) })
      ]
    }
  );
};
Tasks.displayName = "Tasks";

// src/components/molecules/MyTaskCard.tsx
import { useTranslation as useTranslation31 } from "@tasama/i18n";
import { jsx as jsx80, jsxs as jsxs59 } from "react/jsx-runtime";
var MyTaskCard = ({
  serviceName,
  activityName,
  activityId: _activityId,
  description,
  status,
  onClick,
  testID,
  className
}) => {
  const { t } = useTranslation31("portal");
  return /* @__PURE__ */ jsxs59(
    "div",
    {
      role: "button",
      tabIndex: 0,
      className: cn(
        "bg-white border border-brand-tint-300 rounded-xl p-4",
        "cursor-pointer transition-all duration-200",
        "hover:shadow-md hover:bg-gray-37 hover:border-brand-tint-400",
        "flex flex-col gap-0 w-full",
        className
      ),
      onClick,
      onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      },
      "data-testid": testID,
      children: [
        /* @__PURE__ */ jsxs59("div", { className: "flex items-center justify-between gap-3 min-w-0 mb-2", children: [
          /* @__PURE__ */ jsx80(Text, { as: "span", size: "sm", className: "text-fg-secondary truncate", children: serviceName }),
          /* @__PURE__ */ jsx80(StatusBadge, { status, className: "flex-shrink-0" })
        ] }),
        /* @__PURE__ */ jsx80(Text, { as: "p", size: "sm", className: "text-fg-primary font-semibold m-0", children: t(activityName) }),
        /* @__PURE__ */ jsx80(Text, { as: "p", size: "sm", className: "text-fg-secondary mt-1 m-0", children: description })
      ]
    }
  );
};
MyTaskCard.displayName = "MyTaskCard";

// src/components/molecules/BusinessActivityTaskCard/index.tsx
import React29 from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation as useTranslation32 } from "@tasama/i18n";
import { jsx as jsx81, jsxs as jsxs60 } from "react/jsx-runtime";
var BusinessActivityTaskCard = ({
  testID = "business-activity-task-card",
  children,
  className
}) => {
  return /* @__PURE__ */ jsx81(
    "div",
    {
      className: cn(
        "bg-bg-primary border border-brand-tint-300 rounded-2xl p-4 lg:p-5",
        className
      ),
      "data-testid": testID,
      children: /* @__PURE__ */ jsx81("div", { className: "flex flex-col gap-3", children })
    }
  );
};
var StatusSection = ({
  status,
  showIcon = false,
  testID = "status-badge",
  className,
  customText
}) => {
  return /* @__PURE__ */ jsx81("div", { className: cn("flex justify-start", className), children: /* @__PURE__ */ jsx81(
    StatusBadge,
    {
      status,
      showIcon,
      testID,
      customText
    }
  ) });
};
var TitleSection = ({
  title,
  testID = "title",
  className
}) => {
  return /* @__PURE__ */ jsx81("div", { className: cn("flex flex-col gap-1", className), children: /* @__PURE__ */ jsx81(
    "h3",
    {
      className: "text-xl font-normal text-fg-primary leading-normal",
      "data-testid": testID,
      children: title
    }
  ) });
};
var DescriptionSection = ({
  description,
  testID = "description",
  className
}) => {
  return /* @__PURE__ */ jsx81("div", { className: cn("flex flex-col gap-2", className), children: /* @__PURE__ */ jsx81(
    "p",
    {
      className: "text-sm font-normal leading-[20px] text-fg-secondary whitespace-pre-wrap",
      "data-testid": testID,
      children: description.replace(/\\n/g, "\n")
    }
  ) });
};
var PrimaryAction = ({
  label,
  action,
  disabled = false,
  testID = "action-button",
  className
}) => {
  const navigate = useNavigate();
  const handleClick = () => {
    if (typeof action === "string") {
      navigate(action);
    } else {
      action();
    }
  };
  return /* @__PURE__ */ jsx81("div", { className: cn("flex items-start", className), children: /* @__PURE__ */ jsx81(
    Button,
    {
      variant: "secondary",
      iconPosition: "trailing",
      iconName: "ArrowRight",
      onClick: handleClick,
      disabled,
      testID,
      label
    }
  ) });
};
var DownloadAction = ({
  label,
  action,
  title,
  description,
  testID = "download-button",
  className
}) => {
  return /* @__PURE__ */ jsxs60(
    "div",
    {
      className: cn(
        "rounded-md bg-brand-gold-50 p-2 px-3 flex flex-row justify-between items-center gap-2",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs60("div", { className: "min-w-0", children: [
          title && /* @__PURE__ */ jsxs60("span", { className: "text-xs font-medium leading-xs text-text-secondary-700", children: [
            title,
            ":",
            " "
          ] }),
          description && /* @__PURE__ */ jsx81("span", { className: "text-xs font-light leading-xs text-text-tertiary-600", children: description })
        ] }),
        /* @__PURE__ */ jsxs60("div", { className: "shrink-0 flex items-center", children: [
          /* @__PURE__ */ jsx81(
            "button",
            {
              type: "button",
              onClick: action,
              className: "lg:hidden text-brand-blue-500 hover:text-gray-700 cursor-pointer transition-colors",
              children: /* @__PURE__ */ jsx81(Icon, { name: "Download", size: 20, strokeWidth: 1.5 })
            }
          ),
          /* @__PURE__ */ jsx81(
            Button,
            {
              variant: "quaternary",
              size: "sm",
              onClick: action,
              testID,
              label,
              className: "hidden lg:inline-flex"
            }
          )
        ] })
      ]
    }
  );
};
var DateInfo = ({
  startedDate,
  completedDate,
  estimatedSLA,
  expectedDate,
  status,
  testID = "date-info",
  className
}) => {
  const { t } = useTranslation32("common");
  const showTitleDescriptionOnly = !status || status === "NOT_STARTED" || status === "not-started";
  const estimatedSLALabel = React29.useMemo(() => {
    if (estimatedSLA === 1) {
      return t("dateInfo.oneBusinessDay");
    }
    if (estimatedSLA === 2) {
      return t("dateInfo.twoBusinessDays");
    }
    return t("dateInfo.nBusinessDays", { estimatedSLA });
  }, [estimatedSLA, t]);
  const isExpectedDatePast = React29.useMemo(() => {
    if (!expectedDate) return false;
    const [d, m, y] = expectedDate.split("/").map(Number);
    if (!d || !m || !y) return false;
    const parsed = new Date(y, m - 1, d);
    const today = /* @__PURE__ */ new Date();
    today.setHours(0, 0, 0, 0);
    return parsed < today;
  }, [expectedDate]);
  if (showTitleDescriptionOnly) {
    return null;
  }
  const showEstimatedSla = status === "IN_PROGRESS";
  const isCompleted = status === "COMPLETED" || status === "COMPLETE";
  return /* @__PURE__ */ jsxs60(
    "div",
    {
      className: cn("flex flex-col items-start md:items-end gap-2", className),
      "data-testid": testID,
      children: [
        !isCompleted && startedDate && /* @__PURE__ */ jsxs60("p", { className: "text-sm font-normal text-black whitespace-nowrap", children: [
          t("dateInfo.startedOn"),
          " ",
          startedDate
        ] }),
        !isCompleted && expectedDate && /* @__PURE__ */ jsxs60(
          "p",
          {
            className: cn(
              "text-xs font-normal whitespace-nowrap",
              isExpectedDatePast ? "text-error-600" : "text-black"
            ),
            children: [
              t("dateInfo.expectedBy"),
              " ",
              expectedDate
            ]
          }
        ),
        isCompleted && completedDate && /* @__PURE__ */ jsxs60("p", { className: "text-sm font-normal text-black whitespace-nowrap", children: [
          t("dateInfo.completedOn"),
          " ",
          completedDate
        ] }),
        showEstimatedSla && estimatedSLA && /* @__PURE__ */ jsxs60("p", { className: "text-xs font-normal text-black whitespace-nowrap", children: [
          t("dateInfo.estimatedSLA"),
          " ",
          estimatedSLALabel
        ] })
      ]
    }
  );
};
var HeaderLayout = ({
  children,
  testID = "header",
  className
}) => {
  return /* @__PURE__ */ jsx81("div", { className: "flex flex-col gap-4", children: /* @__PURE__ */ jsx81(
    "div",
    {
      className: cn(
        "flex flex-col gap-3 md:flex-row md:justify-between md:items-start md:gap-4",
        className
      ),
      "data-testid": testID,
      children
    }
  ) });
};
BusinessActivityTaskCard.StatusSection = StatusSection;
BusinessActivityTaskCard.TitleSection = TitleSection;
BusinessActivityTaskCard.DescriptionSection = DescriptionSection;
BusinessActivityTaskCard.PrimaryAction = PrimaryAction;
BusinessActivityTaskCard.DownloadAction = DownloadAction;
BusinessActivityTaskCard.DateInfo = DateInfo;
BusinessActivityTaskCard.HeaderLayout = HeaderLayout;
BusinessActivityTaskCard.displayName = "BusinessActivityTaskCard";
StatusSection.displayName = "BusinessActivityTaskCard.StatusSection";
TitleSection.displayName = "BusinessActivityTaskCard.TitleSection";
DescriptionSection.displayName = "BusinessActivityTaskCard.DescriptionSection";
PrimaryAction.displayName = "BusinessActivityTaskCard.PrimaryAction";
DownloadAction.displayName = "BusinessActivityTaskCard.DownloadAction";
DateInfo.displayName = "BusinessActivityTaskCard.DateInfo";
HeaderLayout.displayName = "BusinessActivityTaskCard.HeaderLayout";

// src/components/organisms/ErrorBoundary.tsx
import React30 from "react";
import { useTranslation as useTranslation33 } from "@tasama/i18n";
import { jsx as jsx82, jsxs as jsxs61 } from "react/jsx-runtime";
var DefaultErrorFallback = ({
  error,
  resetErrorBoundary,
  testID
}) => {
  const { t } = useTranslation33("common");
  const isDevelopment = typeof window !== "undefined" && window.__DEV__ === true;
  return /* @__PURE__ */ jsxs61(
    "div",
    {
      className: cn(
        "flex flex-col items-center justify-center",
        "min-h-[400px] p-8 text-center",
        "bg-background border border-destructive/20 rounded-lg"
      ),
      role: "alert",
      "data-testid": `${testID}-fallback`,
      children: [
        /* @__PURE__ */ jsx82(
          Icon,
          {
            name: "CircleAlert",
            size: 80,
            testID: `${testID}-error-icon`,
            className: "mb-6 opacity-80 text-destructive"
          }
        ),
        /* @__PURE__ */ jsx82(
          Heading,
          {
            level: 3,
            className: "mb-4 text-destructive",
            testID: `${testID}-error-title`,
            children: t("errors.somethingWentWrong")
          }
        ),
        /* @__PURE__ */ jsx82(
          Text,
          {
            className: "mb-6 text-muted-foreground",
            testID: `${testID}-error-message`,
            children: t("errors.unexpectedError")
          }
        ),
        /* @__PURE__ */ jsxs61("div", { className: "flex flex-col sm:flex-row gap-3", children: [
          /* @__PURE__ */ jsx82(
            Button,
            {
              variant: "secondary",
              onClick: resetErrorBoundary,
              className: "flex items-center gap-2",
              testID: `${testID}-retry-button`,
              label: t("errors.tryAgain"),
              iconPosition: "none"
            }
          ),
          /* @__PURE__ */ jsx82(
            Button,
            {
              variant: "tertiary",
              onClick: () => window.location.reload(),
              testID: `${testID}-reload-button`,
              label: t("errors.reloadPage"),
              iconPosition: "none"
            }
          )
        ] }),
        isDevelopment && /* @__PURE__ */ jsxs61("details", { className: "mt-6 w-full max-w-2xl", children: [
          /* @__PURE__ */ jsx82("summary", { className: "cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors", children: t("errors.errorDetails") }),
          /* @__PURE__ */ jsx82("pre", { className: "mt-2 p-4 bg-muted rounded text-xs text-start overflow-auto", children: error.stack })
        ] })
      ]
    }
  );
};
var ErrorBoundary = class extends React30.Component {
  resetTimeoutId = null;
  prevResetKeys = [];
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.prevResetKeys = props.resetKeys || [];
  }
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error, errorInfo) {
    console.error("[ErrorBoundary] Component error caught:", {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: (/* @__PURE__ */ new Date()).toISOString()
    });
    this.setState({
      error,
      errorInfo
    });
    this.props.onError?.(error, errorInfo);
  }
  componentDidUpdate(prevProps) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;
    if (hasError && resetKeys) {
      const hasResetKeyChanged = resetKeys.some((key, index) => key !== this.prevResetKeys[index]) || resetKeys.length !== this.prevResetKeys.length;
      if (hasResetKeyChanged) {
        this.prevResetKeys = resetKeys;
        this.resetErrorBoundary();
      }
    }
    if (hasError && resetOnPropsChange && prevProps !== this.props) {
      this.resetErrorBoundary();
    }
  }
  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }
  resetErrorBoundary = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
    this.resetTimeoutId = window.setTimeout(() => {
      this.setState({
        hasError: false
      });
    }, 100);
  };
  render() {
    const { hasError, error } = this.state;
    const {
      children,
      fallback: CustomFallback,
      className,
      testID
    } = this.props;
    if (hasError && error) {
      if (CustomFallback) {
        return /* @__PURE__ */ jsx82(
          "div",
          {
            className: cn("error-boundary-wrapper", className),
            "data-testid": testID,
            children: /* @__PURE__ */ jsx82(
              CustomFallback,
              {
                error,
                resetErrorBoundary: this.resetErrorBoundary,
                testID
              }
            )
          }
        );
      }
      return /* @__PURE__ */ jsx82(
        "div",
        {
          className: cn("error-boundary-wrapper", className),
          "data-testid": testID,
          children: /* @__PURE__ */ jsx82(
            DefaultErrorFallback,
            {
              error,
              resetErrorBoundary: this.resetErrorBoundary,
              testID
            }
          )
        }
      );
    }
    return children;
  }
};

// src/components/organisms/Hero.tsx
import { jsx as jsx83, jsxs as jsxs62 } from "react/jsx-runtime";
function Hero({
  className,
  title,
  subtitle,
  description,
  primaryAction,
  secondaryAction,
  align = "center",
  testID,
  children,
  ref,
  ...props
}) {
  const alignmentClasses = {
    left: "text-start",
    center: "text-center",
    right: "text-end"
  };
  const justifyClasses = {
    left: "start",
    center: "center",
    right: "end"
  };
  return /* @__PURE__ */ jsxs62(
    "div",
    {
      ref,
      "data-testid": testID,
      className: cn("space-y-6", alignmentClasses[align], className),
      ...props,
      children: [
        /* @__PURE__ */ jsxs62("div", { className: "space-y-4", children: [
          /* @__PURE__ */ jsxs62(
            Heading,
            {
              level: 1,
              size: "4xl",
              className: "sm:text-6xl",
              testID: testID ? `${testID}-title` : void 0,
              children: [
                title,
                subtitle && /* @__PURE__ */ jsxs62("span", { className: "text-fg-primary", children: [
                  " ",
                  subtitle
                ] })
              ]
            }
          ),
          description && /* @__PURE__ */ jsx83(
            Text,
            {
              size: "lg",
              color: "muted",
              leading: "relaxed",
              className: "mt-6",
              testID: testID ? `${testID}-description` : void 0,
              children: description
            }
          )
        ] }),
        (primaryAction || secondaryAction) && /* @__PURE__ */ jsxs62(
          Flex,
          {
            justify: justifyClasses[align],
            gap: "md",
            className: "mt-8",
            testID: testID ? `${testID}-actions` : void 0,
            children: [
              primaryAction && /* @__PURE__ */ jsx83(
                Button,
                {
                  variant: "primary",
                  size: "lg",
                  onClick: primaryAction.onClick,
                  testID: testID ? `${testID}-primary-action` : void 0,
                  label: primaryAction.label,
                  iconPosition: "none"
                }
              ),
              secondaryAction && /* @__PURE__ */ jsx83(
                Button,
                {
                  variant: "secondary",
                  size: "lg",
                  onClick: secondaryAction.onClick,
                  testID: testID ? `${testID}-secondary-action` : void 0,
                  label: secondaryAction.label,
                  iconPosition: "none"
                }
              )
            ]
          }
        ),
        children
      ]
    }
  );
}
Hero.displayName = "Hero";

// src/components/organisms/OnboardingHeader.tsx
import { jsx as jsx84, jsxs as jsxs63 } from "react/jsx-runtime";
var OnboardingHeader = ({
  size = "lg",
  title,
  description,
  icon,
  className,
  testID,
  ref,
  iconColorClass = "text-white",
  iconClass = "bg-fg-brand-green",
  ...props
}) => {
  const containerClasses = {
    sm: "",
    lg: ""
  };
  const titleSizes = {
    sm: { level: 2, size: "lg", weight: "medium" },
    lg: {
      level: 1,
      size: "2xl",
      weight: "medium"
    }
  };
  const descriptionSizes = {
    sm: { size: "sm", leading: "snug" },
    lg: { size: "base", leading: "relaxed" }
  };
  const isDesktop = useIsDesktop();
  const responsiveTitleClasses = isDesktop ? "text-2xl" : "text-lg";
  const responsiveDescriptionClasses = isDesktop ? "text-base" : "text-sm";
  return /* @__PURE__ */ jsxs63(
    "div",
    {
      ref,
      className: cn(
        "w-full flex mb-0 flex-col gap-lg",
        containerClasses[size],
        className
      ),
      "data-testid": testID,
      ...props,
      children: [
        /* @__PURE__ */ jsxs63(
          Flex,
          {
            direction: "row",
            align: "center",
            gap: "sm",
            className: "w-full",
            testID: testID ? `${testID}-header` : void 0,
            children: [
              icon && /* @__PURE__ */ jsxs63(
                "div",
                {
                  className: `flex items-center justify-between w-[40px] h-[40px] p-65 rounded-[50%] ${iconClass}`,
                  children: [
                    " ",
                    /* @__PURE__ */ jsx84(
                      Icon,
                      {
                        name: icon,
                        size: 20,
                        className: cn(`${iconColorClass} `) || "color-quaternary",
                        testID: testID ? `${testID}-icon` : void 0
                      }
                    )
                  ]
                }
              ),
              /* @__PURE__ */ jsx84(Flex, { direction: "col", justify: "center", className: "flex-1", children: /* @__PURE__ */ jsx84(
                Heading,
                {
                  level: titleSizes[size].level,
                  size: titleSizes[size].size,
                  weight: titleSizes[size].weight,
                  className: cn("text-fg-primary", responsiveTitleClasses),
                  testID: testID ? `${testID}-title` : void 0,
                  children: title
                }
              ) })
            ]
          }
        ),
        /* @__PURE__ */ jsx84(
          "div",
          {
            className: "w-full mb-4xl",
            "data-testid": testID ? `${testID}-description-container` : void 0,
            children: /* @__PURE__ */ jsx84(
              Text,
              {
                size: descriptionSizes[size].size,
                leading: descriptionSizes[size].leading,
                color: "muted",
                className: cn("text-placeholder", responsiveDescriptionClasses),
                testID: testID ? `${testID}-description` : void 0,
                children: description
              }
            )
          }
        )
      ]
    }
  );
};
OnboardingHeader.displayName = "OnboardingHeader";

// src/components/organisms/OnboardingNavHeader.tsx
import { useTranslation as useTranslation34 } from "@tasama/i18n";
import { jsx as jsx85, jsxs as jsxs64 } from "react/jsx-runtime";
var isIconName = (v) => typeof v === "string";
var NavLink = ({
  href,
  children,
  onClick,
  testID,
  leftIcon,
  className: extraClassName
}) => {
  const Component = href ? "a" : "button";
  return /* @__PURE__ */ jsxs64(
    Component,
    {
      href,
      onClick,
      className: cn(
        "inline-flex items-center gap-2",
        "text-fg-tertiary text-sm font-medium leading-[1.43]",
        "hover:text-fg-brand-primary transition-colors duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm",
        !href && "bg-transparent border-none cursor-pointer",
        extraClassName
      ),
      "data-testid": testID,
      children: [
        leftIcon ? /* @__PURE__ */ jsx85(
          "span",
          {
            "aria-hidden": "true",
            className: "inline-flex items-center justify-center w-4 h-4",
            "data-testid": testID ? `${testID}-icon` : void 0,
            children: isIconName(leftIcon) ? /* @__PURE__ */ jsx85(Icon, { name: leftIcon, size: 16 }) : leftIcon
          }
        ) : null,
        /* @__PURE__ */ jsx85("span", { className: "truncate", children })
      ]
    }
  );
};
var NavMenu = ({
  links,
  testID,
  variant = "card"
}) => {
  const { t } = useTranslation34("common");
  const isMobile = useIsMobile();
  const isBranded = variant === "branded";
  const effectiveLinks = links ?? [
    { label: t("onboardingNav.links.howItWorks") },
    { label: t("onboardingNav.links.whyTasama") },
    { label: t("onboardingNav.links.resources") },
    { label: t("onboardingNav.links.contactUs") }
  ];
  return /* @__PURE__ */ jsx85(
    "nav",
    {
      className: cn(
        "flex items-center",
        // Branded: tight 12px gap (spacing-lg per Figma) at every breakpoint.
        // Card (legacy onboarding): wider responsive gap.
        isBranded ? "gap-3 lg:gap-4" : "gap-4 sm:gap-6 md:gap-8"
      ),
      "data-testid": testID,
      role: "navigation",
      "aria-label": t("onboardingNav.aria.mainNavigation"),
      children: effectiveLinks.map((link, index) => {
        if (link.variant === "button") {
          const isIconOnlyOnMobile = link.mobileIconOnly && isMobile;
          const resolvedLabel = isIconOnlyOnMobile ? void 0 : link.mobileLabel && isMobile ? link.mobileLabel : link.label;
          return /* @__PURE__ */ jsx85(
            Button,
            {
              iconName: link.iconName || "X",
              iconPosition: link.iconPosition,
              variant: "secondary",
              onClick: link.onClick,
              size: "sm",
              testID: testID ? `${testID}-button-${index}` : void 0,
              label: resolvedLabel,
              className: cn(link.className, isIconOnlyOnMobile && "w-9"),
              "aria-label": isIconOnlyOnMobile ? link.label : void 0
            },
            link.label
          );
        }
        return /* @__PURE__ */ jsx85(
          NavLink,
          {
            href: link.href,
            onClick: link.onClick,
            leftIcon: link.icon,
            className: link.className,
            testID: testID ? `${testID}-link-${index}` : void 0,
            children: link.label
          },
          link.label
        );
      })
    }
  );
};
var OnboardingNavHeader = ({
  logo = {},
  navigation = {},
  variant = "card",
  className,
  testID,
  ref,
  ...props
}) => {
  const { onClick: logoOnClick } = logo;
  const { links } = navigation;
  const { t } = useTranslation34("common");
  const isDesktop = useIsDesktop();
  const isBranded = variant === "branded";
  return /* @__PURE__ */ jsx85(
    "header",
    {
      ref,
      className: cn(
        "w-full bg-white",
        // Sticky to viewport top so content scrolls underneath.
        "sticky top-0 z-sticky",
        // Branded variant: flat edge-to-edge panel with a bottom divider.
        // Card variant: floating rounded card (legacy onboarding look).
        // TODO: Re-enable bottom border when client approves.
        isBranded ? "" : isDesktop ? "rounded-2xl" : "rounded-xl",
        className
      ),
      "data-testid": testID,
      role: "banner",
      ...props,
      children: /* @__PURE__ */ jsx85(
        Container,
        {
          maxWidth: "full",
          className: cn(
            // Branded variant: spacing-xl (16px) horizontal, spacing-2xl (20px)
            // vertical at every breakpoint. Sourced from semantic tokens —
            // matches Figma `padding: var(--spacing-2xl) var(--spacing-xl)`.
            // Card variant: legacy responsive padding.
            isBranded ? "px-xl py-2xl" : "px-[20px] py-[20px] md:px-6 md:py-5 lg:px-4xl lg:py-3xl"
          ),
          testID: testID ? `${testID}-container` : void 0,
          children: /* @__PURE__ */ jsxs64(
            Flex,
            {
              direction: "row",
              justify: "between",
              align: "center",
              className: "w-full",
              testID: testID ? `${testID}-content` : void 0,
              children: [
                /* @__PURE__ */ jsx85(
                  "div",
                  {
                    className: "shrink-0",
                    "data-testid": testID ? `${testID}-logo-section` : void 0,
                    children: (() => {
                      const brandedSize = isBranded ? { width: 166, height: 35 } : {};
                      const logoClassName = isBranded ? "" : "w-header-logo-width h-header-logo-height";
                      return logoOnClick ? /* @__PURE__ */ jsx85(
                        Button,
                        {
                          onClick: logoOnClick,
                          className: "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-lg",
                          "aria-label": t("onboardingNav.logo.aria.goToHomepage"),
                          "data-testid": testID ? `${testID}-logo-button` : void 0,
                          children: /* @__PURE__ */ jsx85(
                            TasamaTextLogoWithTagline,
                            {
                              testID: testID ? `${testID}-logo` : void 0,
                              title: t("onboardingNav.logo.title"),
                              className: logoClassName,
                              ...brandedSize
                            }
                          )
                        }
                      ) : /* @__PURE__ */ jsx85(
                        TasamaTextLogoWithTagline,
                        {
                          testID: testID ? `${testID}-logo` : void 0,
                          title: t("onboardingNav.logo.title"),
                          className: logoClassName,
                          ...brandedSize
                        }
                      );
                    })()
                  }
                ),
                /* @__PURE__ */ jsx85(
                  "div",
                  {
                    className: "flex-shrink-0",
                    "data-testid": testID ? `${testID}-nav-section` : void 0,
                    children: /* @__PURE__ */ jsx85(
                      NavMenu,
                      {
                        links,
                        testID: testID ? `${testID}-nav-menu` : void 0,
                        variant
                      }
                    )
                  }
                )
              ]
            }
          )
        }
      )
    }
  );
};
OnboardingNavHeader.displayName = "OnboardingNavHeader";

// src/components/organisms/OnboardingStepper.tsx
import { useState as useState16 } from "react";
import { useTranslation as useTranslation35, normalizeLanguage } from "@tasama/i18n";
import { Fragment as Fragment8, jsx as jsx86, jsxs as jsxs65 } from "react/jsx-runtime";
var getDefaultSteps = (t) => [
  {
    id: "company-type",
    label: t("navigation.steps.companyType"),
    current: true
  },
  {
    id: "your-details",
    label: t("navigation.steps.yourDetails")
  },
  {
    id: "upload-documents",
    label: t("navigation.steps.uploadDocuments")
  },
  {
    id: "company-details",
    label: t("navigation.steps.companyDetails")
  },
  {
    id: "service-needs",
    label: t("navigation.steps.serviceNeeds")
  }
];
var OnboardingStepper = ({
  className,
  variant,
  currentStep = 0,
  steps,
  lang = "en",
  onStartOnboarding,
  onStepClick,
  testID,
  ref,
  sidebarContent,
  ...props
}) => {
  const normalizedLang = normalizeLanguage(lang);
  const { t } = useTranslation35("onboarding");
  const [isDrawerOpen, setIsDrawerOpen] = useState16(false);
  const isStepperVariant = variant === "stepper" || variant === "nda";
  const featureItems = t("stepper.login.features", {
    returnObjects: true
  });
  const defaultSteps = getDefaultSteps(t);
  const actualSteps = steps || defaultSteps;
  const renderLoginVariant = () => /* @__PURE__ */ jsxs65("div", { className: "flex flex-col gap-8 lg:gap-16", children: [
    /* @__PURE__ */ jsxs65("div", { className: "hidden md:flex flex-col gap-4 lg:gap-12", children: [
      /* @__PURE__ */ jsxs65("div", { className: "flex flex-col", children: [
        /* @__PURE__ */ jsx86(
          Heading,
          {
            level: 1,
            size: "2xl",
            weight: "normal",
            className: "text-white font-light text-[24px] leading-[32px] md:text-[27px] lg:text-[30px] lg:leading-3xl",
            children: t("stepper.login.welcome")
          }
        ),
        /* @__PURE__ */ jsx86(
          Heading,
          {
            level: 1,
            size: "2xl",
            weight: "normal",
            className: "text-fg-brand-gold font-medium text-[24px] leading-[32px] md:text-[27px] lg:text-[30px] lg:leading-3xl",
            children: t("stepper.login.brand")
          }
        )
      ] }),
      /* @__PURE__ */ jsx86("div", { className: "hidden lg:flex flex-col gap-3", children: featureItems.map((item, index) => /* @__PURE__ */ jsxs65("div", { className: "flex flex-row gap-3", children: [
        /* @__PURE__ */ jsx86(Icon, { name: "Check", size: 24, className: "text-white" }),
        /* @__PURE__ */ jsx86(
          Text,
          {
            size: "sm",
            weight: "normal",
            className: "text-brand-tint-100 text-[14px] leading-[20px] flex-1",
            children: item
          }
        )
      ] }, index)) })
    ] }),
    /* @__PURE__ */ jsxs65("div", { className: "flex flex-col gap-4", children: [
      /* @__PURE__ */ jsx86(
        Text,
        {
          size: "lg",
          className: "text-white font-light text-[16px] leading-[24px] lg:text-[20px] lg:leading-xl",
          children: t("stepper.login.cta.question")
        }
      ),
      /* @__PURE__ */ jsx86(
        Button,
        {
          variant: "primary",
          size: "md",
          onClick: onStartOnboarding,
          testID: "start-onboarding-button",
          label: t("stepper.login.cta.button"),
          iconPosition: "trailing",
          className: "me-auto focus-visible:ring-white focus-visible:ring-offset-brand-blue-800"
        }
      )
    ] })
  ] });
  const renderStepperVariant = () => /* @__PURE__ */ jsxs65("div", { className: "flex flex-col gap-8", children: [
    /* @__PURE__ */ jsxs65("div", { className: "flex lg:hidden flex-col gap-1", children: [
      /* @__PURE__ */ jsx86("span", { className: "text-[12px] font-light leading-[18px] text-brand-tint-100", children: t("stepper.journey.currentStep") }),
      /* @__PURE__ */ jsx86("span", { className: "text-[14px] font-normal leading-[20px] text-white", children: actualSteps[currentStep]?.label || "" })
    ] }),
    /* @__PURE__ */ jsxs65("div", { className: "hidden lg:flex flex-col gap-4", children: [
      /* @__PURE__ */ jsxs65("div", { className: "flex flex-col", children: [
        /* @__PURE__ */ jsx86(
          Heading,
          {
            level: 1,
            size: "2xl",
            weight: "normal",
            className: "text-white font-light text-3xl",
            children: sidebarContent?.title || t("stepper.journey.title")
          }
        ),
        !sidebarContent?.title && /* @__PURE__ */ jsx86(
          Heading,
          {
            level: 1,
            size: "2xl",
            weight: "normal",
            className: "text-fg-brand-gold font-medium text-3xl",
            children: t("stepper.journey.subtitle")
          }
        )
      ] }),
      sidebarContent?.description !== null && /* @__PURE__ */ jsx86(
        Text,
        {
          size: "sm",
          className: "text-brand-tint-100 opacity-80 font-light text-sm",
          children: sidebarContent?.description || t("stepper.journey.description")
        }
      )
    ] }),
    /* @__PURE__ */ jsx86("div", { className: "hidden lg:block px-4 py-4xl w-full lg:w-[340px]", children: /* @__PURE__ */ jsx86(
      Stepper,
      {
        steps: actualSteps,
        currentStep,
        onStepClick,
        clickable: !!onStepClick,
        testID: "onboarding-stepper"
      }
    ) })
  ] });
  const renderNewAccountVariant = () => /* @__PURE__ */ jsx86("div", { className: "flex flex-col gap-16", children: /* @__PURE__ */ jsxs65("div", { className: "lex flex-col gap-4", children: [
    /* @__PURE__ */ jsx86(
      Heading,
      {
        level: 1,
        size: "2xl",
        weight: "normal",
        className: "text-white font-light text-[30px] leading-3xl",
        children: sidebarContent?.title
      }
    ),
    /* @__PURE__ */ jsx86(
      Text,
      {
        size: "sm",
        className: "text-brand-blue-50 text-[14px] leading-[20px] flex-1 font-normal",
        children: sidebarContent?.description
      }
    )
  ] }) });
  const renderNdaVariant = () => /* @__PURE__ */ jsxs65("div", { className: "flex flex-col gap-8", children: [
    /* @__PURE__ */ jsxs65("div", { className: "flex flex-col gap-4", children: [
      /* @__PURE__ */ jsxs65("div", { className: "flex flex-col", children: [
        /* @__PURE__ */ jsx86(
          Heading,
          {
            level: 1,
            size: "2xl",
            weight: "normal",
            className: "text-white font-light text-[30px] leading-3xl",
            children: t("stepper.nda.title")
          }
        ),
        /* @__PURE__ */ jsx86(
          Heading,
          {
            level: 1,
            size: "2xl",
            weight: "normal",
            className: "text-white font-light text-[30px] leading-3xl",
            children: t("stepper.nda.subtitle")
          }
        )
      ] }),
      /* @__PURE__ */ jsx86(
        Text,
        {
          size: "sm",
          className: "text-brand-tint-100 opacity-80 font-light text-[14px] leading-[20px]",
          children: t("stepper.nda.description")
        }
      )
    ] }),
    /* @__PURE__ */ jsx86("div", { className: "p-4 w-full lg:w-[340px]", children: /* @__PURE__ */ jsx86(
      Stepper,
      {
        steps: actualSteps,
        currentStep,
        onStepClick,
        clickable: !!onStepClick,
        testID: "onboarding-stepper"
      }
    ) })
  ] });
  const stepperVariant = {
    login: renderLoginVariant,
    accountActivation: renderNewAccountVariant,
    stepper: renderStepperVariant,
    nda: renderNdaVariant
  };
  return /* @__PURE__ */ jsxs65(Fragment8, { children: [
    /* @__PURE__ */ jsxs65(
      "div",
      {
        ref,
        "data-testid": testID,
        className: cn(
          // Base container — Figma: compact card on mobile for stepper, 404px desktop
          "w-full lg:w-[404px] flex flex-col",
          isStepperVariant ? "h-auto lg:h-sidebar-md" : "h-auto lg:h-sidebar-md",
          "lg:gap-12",
          // Mobile: compact padding for stepper variant, normal for login
          isStepperVariant ? "px-[20px] py-[20px] lg:p-12 lg:pt-12 lg:pb-12 lg:ps-8 lg:pe-8" : "px-[20px] py-[32px] md:px-8 md:py-10 lg:p-12 lg:pt-12 lg:pb-12 lg:ps-8 lg:pe-8 gap-[32px] md:gap-10",
          // Figma: border-radius 16px mobile, scales up at md/lg
          "rounded-[16px] md:rounded-2xl lg:rounded-4xl relative",
          // Safari-safe border-radius clipping
          "[mask-image:linear-gradient(white,white)] overflow-hidden",
          "items-start flex-shrink-0",
          "lg:max-w-[404px]",
          className
        ),
        style: {
          // Exact Figma background: gradient overlay + image
          background: `linear-gradient(90deg, rgba(16, 6, 159, 0.35) 0%, rgba(16, 6, 159, 0.42) 40.75%, rgba(16, 6, 159, 0.63) 71.31%, rgba(16, 6, 159, 0.70) 100%), url('/assets/images/stepper-brand-image.png') center / cover no-repeat`
        },
        ...props,
        children: [
          variant !== "login" && /* @__PURE__ */ jsx86(
            "div",
            {
              className: cn(
                "hidden lg:block absolute bottom-[0] end-[0] h-full w-[65%]",
                "bg-no-repeat bg-contain bg-[position:bottom_right]",
                "opacity-50 pointer-events-none",
                `bg-[url('/assets/images/tasama-pattern-${normalizedLang}.svg')]`
              ),
              "aria-hidden": "true"
            }
          ),
          isStepperVariant && /* @__PURE__ */ jsx86(
            "button",
            {
              type: "button",
              className: "lg:hidden absolute top-1/2 -translate-y-1/2 end-[20px] z-10 w-[24px] h-[24px] flex items-center justify-center",
              onClick: () => setIsDrawerOpen(true),
              "aria-label": t("stepper.journey.viewAllSteps"),
              children: /* @__PURE__ */ jsx86(
                Icon,
                {
                  name: "MoreVertical",
                  size: 20,
                  className: "text-white",
                  "aria-hidden": "true"
                }
              )
            }
          ),
          stepperVariant[variant](),
          variant === "login" && /* @__PURE__ */ jsx86("div", { className: "hidden lg:block absolute bottom-6xl", children: /* @__PURE__ */ jsx86(
            "img",
            {
              src: `/assets/images/tasama-stepper-branding-${normalizedLang}.svg`,
              alt: "Tasama Branding",
              className: "h-6 w-auto",
              "aria-hidden": "true"
            }
          ) })
        ]
      }
    ),
    isStepperVariant && /* @__PURE__ */ jsxs65(
      "div",
      {
        className: cn(
          "lg:hidden fixed inset-0 z-50 transition-opacity duration-300 ease-in-out",
          isDrawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        ),
        children: [
          /* @__PURE__ */ jsx86(
            "div",
            {
              className: "absolute inset-0 bg-black/40",
              onClick: () => setIsDrawerOpen(false)
            }
          ),
          /* @__PURE__ */ jsxs65(
            "div",
            {
              className: cn(
                "absolute bottom-0 inset-x-0 bg-[#f5f5f5] rounded-t-[24px] max-h-[80vh] overflow-y-auto p-[24px] flex flex-col gap-[24px]",
                "transition-transform duration-300 ease-in-out shadow-[0px_-12px_16px_0px_rgba(10,13,18,0.08)]",
                isDrawerOpen ? "translate-y-0" : "translate-y-full"
              ),
              children: [
                /* @__PURE__ */ jsx86("div", { className: "flex justify-end", children: /* @__PURE__ */ jsx86(
                  "button",
                  {
                    type: "button",
                    onClick: () => setIsDrawerOpen(false),
                    className: "w-8 h-8 flex items-center justify-center rounded-full bg-white hover:bg-gray-100",
                    "aria-label": t("stepper.journey.closeSteps"),
                    children: /* @__PURE__ */ jsx86(Icon, { name: "X", size: 16, className: "text-brand-blue-500" })
                  }
                ) }),
                /* @__PURE__ */ jsx86("div", { className: "px-2", children: /* @__PURE__ */ jsx86(
                  Stepper,
                  {
                    steps: actualSteps,
                    currentStep,
                    onStepClick,
                    clickable: !!onStepClick,
                    testID: "onboarding-stepper-drawer"
                  }
                ) })
              ]
            }
          )
        ]
      }
    )
  ] });
};
OnboardingStepper.displayName = "OnboardingStepper";

// src/components/organisms/OnboardingStepperMobile.tsx
import { useState as useState17, useCallback as useCallback13, useEffect as useEffect15 } from "react";
import { createPortal as createPortal3 } from "react-dom";
import { useTranslation as useTranslation36 } from "@tasama/i18n";
import { useLocation } from "react-router-dom";
import { Fragment as Fragment9, jsx as jsx87, jsxs as jsxs66 } from "react/jsx-runtime";
var getDefaultSteps2 = (t) => [
  {
    id: "company-type",
    label: t("navigation.steps.companyType"),
    current: true
  },
  {
    id: "your-details",
    label: t("navigation.steps.yourDetails")
  },
  {
    id: "upload-documents",
    label: t("navigation.steps.uploadDocuments")
  },
  {
    id: "company-details",
    label: t("navigation.steps.companyDetails")
  },
  {
    id: "service-needs",
    label: t("navigation.steps.serviceNeeds")
  }
];
var OnboardingStepperMobile = ({
  className,
  variant,
  currentStep = 0,
  steps: stepsProp,
  onStartOnboarding,
  onStepClick,
  testID,
  ref,
  sidebarContent,
  showStepperDetails = false,
  onToggleDetails,
  isDrawerOpen = false,
  onDrawerToggle,
  ...props
}) => {
  const location = useLocation();
  const { t } = useTranslation36("onboarding");
  const steps = stepsProp ?? getDefaultSteps2(t);
  const [isExpanded, setIsExpanded] = useState17(showStepperDetails);
  const [internalDrawerOpen, setInternalDrawerOpen] = useState17(isDrawerOpen);
  useEffect15(() => {
    setIsExpanded(showStepperDetails);
  }, [showStepperDetails]);
  useEffect15(() => {
    setInternalDrawerOpen(isDrawerOpen);
  }, [isDrawerOpen]);
  const handleToggle = useCallback13(() => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    onToggleDetails?.();
  }, [isExpanded, onToggleDetails]);
  const handleDrawerToggle = useCallback13(() => {
    const newState = !internalDrawerOpen;
    setInternalDrawerOpen(newState);
    onDrawerToggle?.();
  }, [internalDrawerOpen, onDrawerToggle]);
  useEffect15(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && internalDrawerOpen) {
        handleDrawerToggle();
      }
    };
    if (internalDrawerOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [internalDrawerOpen, handleDrawerToggle]);
  const renderLoginVariant = () => /* @__PURE__ */ jsxs66("div", { className: "flex flex-col gap-6", children: [
    /* @__PURE__ */ jsx87("div", { className: "flex flex-col gap-4", children: /* @__PURE__ */ jsx87("div", { className: "flex flex-col gap-3", children: /* @__PURE__ */ jsxs66("div", { className: "flex flex-col", children: [
      /* @__PURE__ */ jsx87(
        Heading,
        {
          level: 2,
          size: "lg",
          weight: "normal",
          className: "text-white font-normal text-[20px] leading-[28px]",
          children: t("stepper.login.welcome")
        }
      ),
      /* @__PURE__ */ jsx87(
        Heading,
        {
          level: 2,
          size: "lg",
          weight: "normal",
          className: "text-fg-brand-gold font-normal text-[20px] leading-[28px]",
          children: t("stepper.login.brand")
        }
      )
    ] }) }) }),
    /* @__PURE__ */ jsxs66("div", { className: "flex flex-col gap-3", children: [
      /* @__PURE__ */ jsx87(
        Text,
        {
          size: "sm",
          className: "text-white font-normal text-[14px] leading-[20px]",
          children: t("stepper.login.cta.question")
        }
      ),
      /* @__PURE__ */ jsx87(
        Button,
        {
          variant: "primary",
          size: "sm",
          onClick: onStartOnboarding,
          testID: "start-onboarding-button",
          label: t("stepper.login.cta.button"),
          iconPosition: "trailing",
          className: "h-9 px-3 py-2 me-auto font-semibold text-[12px] leading-[16px]"
        }
      )
    ] })
  ] });
  const renderStepperVariant = () => /* @__PURE__ */ jsxs66("div", { className: "flex flex-col gap-4", children: [
    /* @__PURE__ */ jsx87("div", { className: "flex flex-col gap-4", children: /* @__PURE__ */ jsxs66("div", { className: "flex flex-col gap-3", children: [
      /* @__PURE__ */ jsxs66("div", { className: "flex flex-col", children: [
        /* @__PURE__ */ jsx87(
          Heading,
          {
            level: 2,
            size: "lg",
            weight: "normal",
            className: "text-white font-normal text-[20px] leading-[28px]",
            children: sidebarContent?.title || t("stepper.journey.title")
          }
        ),
        !sidebarContent?.title && /* @__PURE__ */ jsx87(
          Heading,
          {
            level: 2,
            size: "lg",
            weight: "normal",
            className: "text-fg-brand-gold font-normal text-[20px] leading-[28px]",
            children: t("stepper.journey.subtitle")
          }
        )
      ] }),
      /* @__PURE__ */ jsx87(
        Text,
        {
          size: "xs",
          className: "text-brand-tint-100 opacity-80 font-normal text-[12px] leading-[16px]",
          children: sidebarContent?.description || t("stepper.journey.description")
        }
      )
    ] }) }),
    /* @__PURE__ */ jsx87("div", { className: "w-full", children: /* @__PURE__ */ jsx87(
      Stepper,
      {
        steps,
        currentStep,
        onStepClick,
        clickable: !!onStepClick,
        testID: "onboarding-stepper-mobile",
        className: "scale-90 origin-left"
      }
    ) })
  ] });
  const positionClasses = {
    bottom: {
      panel: "bottom-0 inset-x-0",
      transform: isExpanded ? "translate-y-0" : "translate-y-full"
    }
  };
  const renderCompactVariant = () => {
    const currentStepData = currentStep < 0 ? steps[steps.length - 1] : steps[currentStep];
    return /* @__PURE__ */ jsxs66(Fragment9, { children: [
      /* @__PURE__ */ jsxs66(
        "div",
        {
          className: "flex items-center justify-between cursor-pointer",
          onClick: handleToggle,
          role: "button",
          tabIndex: 0,
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleToggle();
            }
          },
          "aria-expanded": isExpanded,
          "aria-controls": testID ? `${testID}-stepper-details` : void 0,
          children: [
            /* @__PURE__ */ jsxs66("div", { className: "flex flex-col flex-1", children: [
              /* @__PURE__ */ jsx87(
                Text,
                {
                  size: "xs",
                  className: "text-brand-blue-50 font-normal text-xs leading-[16px]",
                  children: t("stepper.journey.currentStep")
                }
              ),
              /* @__PURE__ */ jsx87(
                Text,
                {
                  size: "sm",
                  className: "text-white font-normal text-sm leading-[20px]",
                  children: currentStepData?.label
                }
              )
            ] }),
            /* @__PURE__ */ jsx87(Icon, { name: "EllipsisVertical", size: 20, className: "text-white" })
          ]
        }
      ),
      createPortal3(
        /* @__PURE__ */ jsxs66(Fragment9, { children: [
          /* @__PURE__ */ jsx87(
            "div",
            {
              className: cn(
                "fixed inset-0 transition-opacity duration-300",
                isExpanded ? "opacity-100 visible" : "opacity-0 invisible"
              ),
              style: { zIndex: 10030 },
              onClick: handleToggle,
              "data-testid": testID ? `${testID}-compact-backdrop` : void 0,
              "aria-hidden": "true"
            }
          ),
          /* @__PURE__ */ jsxs66(
            "div",
            {
              id: testID ? `${testID}-stepper-details` : void 0,
              className: cn(
                "fixed z-notification",
                "bg-brand-blue-800 rounded-t-4xl",
                "transition-all duration-300 ease-in-out",
                "max-h-[80vh] overflow-hidden",
                positionClasses.bottom.panel,
                positionClasses.bottom.transform
              ),
              role: "dialog",
              "aria-modal": "true",
              "aria-labelledby": testID ? `${testID}-drawer-title` : void 0,
              children: [
                /* @__PURE__ */ jsx87("div", { className: "flex justify-end pt-3xl pb-2 px-4", children: /* @__PURE__ */ jsx87(
                  "button",
                  {
                    className: "flex items-center justify-center w-6 h-6 rounded-full cursor-pointer hover:opacity-80 transition-opacity duration-200 bg-fg-brand-green",
                    onClick: handleToggle,
                    type: "button",
                    "aria-label": t("stepper.journey.closeSteps"),
                    children: /* @__PURE__ */ jsx87(
                      Icon,
                      {
                        name: "X",
                        className: "text-white",
                        size: 12,
                        testID: testID ? `${testID}-close-icon` : void 0
                      }
                    )
                  }
                ) }),
                /* @__PURE__ */ jsx87("div", { className: "px-4 pb-6 mt-3xl overflow-y-auto max-h-stepper-mobile", children: /* @__PURE__ */ jsx87(
                  Stepper,
                  {
                    steps,
                    currentStep,
                    onStepClick,
                    clickable: !!onStepClick,
                    testID: "onboarding-stepper-mobile-expanded",
                    className: "w-full"
                  }
                ) })
              ]
            }
          )
        ] }),
        document.body
      )
    ] });
  };
  const renderNewAccountVariant = () => {
    const variant2 = location.pathname.includes("reset-password") ? "re-activate" : "activate";
    const activateVariant = () => /* @__PURE__ */ jsx87("div", { className: "flex flex-col gap-6", children: /* @__PURE__ */ jsx87("div", { className: "flex flex-col gap-4", children: /* @__PURE__ */ jsxs66("div", { className: "flex flex-col gap-3", children: [
      /* @__PURE__ */ jsx87("div", { className: "flex flex-col", children: /* @__PURE__ */ jsx87(
        Heading,
        {
          level: 2,
          size: "lg",
          weight: "normal",
          className: "text-white font-normal text-[20px] leading-[28px]",
          children: sidebarContent?.title
        }
      ) }),
      /* @__PURE__ */ jsx87("div", { className: "flex flex-col gap-2", children: /* @__PURE__ */ jsx87(
        Text,
        {
          size: "xs",
          className: "text-brand-tint-100 text-[12px] leading-[16px] flex-1",
          children: sidebarContent?.description
        }
      ) })
    ] }) }) });
    const reactivateVariant = () => /* @__PURE__ */ jsx87("div", { className: "flex flex-col gap-6", children: /* @__PURE__ */ jsx87("div", { className: "flex flex-col gap-4", children: /* @__PURE__ */ jsxs66("div", { className: "flex flex-col gap-3", children: [
      /* @__PURE__ */ jsx87("div", { className: "flex flex-col", children: /* @__PURE__ */ jsx87(
        Heading,
        {
          level: 2,
          size: "lg",
          weight: "normal",
          className: "text-white font-normal text-[20px] leading-[28px]",
          children: sidebarContent?.title
        }
      ) }),
      /* @__PURE__ */ jsx87("div", { className: "flex flex-col gap-2", children: /* @__PURE__ */ jsx87(
        Text,
        {
          size: "xs",
          className: "text-brand-tint-100 text-[12px] leading-[16px] flex-1",
          children: sidebarContent?.description
        }
      ) })
    ] }) }) });
    return variant2 === "activate" ? activateVariant() : reactivateVariant();
  };
  const stepperVariant = {
    login: renderLoginVariant(),
    accountActivation: renderNewAccountVariant(),
    stepper: renderStepperVariant(),
    compact: renderCompactVariant()
  };
  if (variant === "drawer") {
    return /* @__PURE__ */ jsx87(
      "div",
      {
        ref,
        "data-testid": testID,
        className: cn("relative", className),
        ...props,
        children: stepperVariant[variant]
      }
    );
  }
  return /* @__PURE__ */ jsx87(
    "div",
    {
      ref,
      "data-testid": testID,
      className: cn(
        // Base container styles optimized for mobile
        "w-full flex flex-col relative overflow-hidden",
        // Responsive padding based on breakpoints
        "p-[var(--spacing-responsive-xs)]",
        "sm:p-[var(--spacing-responsive-sm)]",
        "md:p-[var(--spacing-responsive-md)]",
        // Responsive border radius
        "rounded-xl sm:rounded-2xl md:rounded-3xl",
        // Background image with mobile optimization
        "bg-[url('/assets/images/sidebar-bg-image.png')]",
        "bg-no-repeat bg-center bg-cover",
        // Responsive gap
        "gap-[var(--spacing-responsive-xs)]",
        "sm:gap-[var(--spacing-responsive-sm)]",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx87("div", { className: "", children: stepperVariant[variant] })
    }
  );
};
OnboardingStepperMobile.displayName = "OnboardingStepperMobile";

// src/components/organisms/PersonalInformationForm.tsx
import { useTranslation as useTranslation37 } from "@tasama/i18n";
import { jsx as jsx88, jsxs as jsxs67 } from "react/jsx-runtime";
var prefixOptions = [
  { value: "Mr", label: "Mr" },
  { value: "Ms", label: "Ms" },
  { value: "Mrs", label: "Mrs" },
  { value: "Miss", label: "Miss" },
  { value: "Dr", label: "Dr" }
];
var countryOptions = [
  {
    value: "+966",
    label: "+966",
    supportingText: "Saudi Arabia"
  },
  {
    value: "+971",
    label: "+971",
    supportingText: "United Arab Emirates"
  },
  {
    value: "+1",
    label: "+1",
    supportingText: "United States"
  },
  {
    value: "+44",
    label: "+44",
    supportingText: "United Kingdom"
  }
];
var PersonalInformationForm = ({
  data,
  onChange,
  isEditing = false,
  onEditModeChange,
  disabled = false,
  testID = "personal-info-form",
  className
}) => {
  const { t } = useTranslation37("portal");
  const handleFieldChange = (field, value) => {
    onChange({
      ...data,
      [field]: value
    });
  };
  const handleEditClick = () => {
    onEditModeChange?.(!isEditing);
  };
  return /* @__PURE__ */ jsxs67("div", { className: cn("w-full space-y-6", className), "data-testid": testID, children: [
    /* @__PURE__ */ jsxs67("div", { className: "flex items-end justify-between", children: [
      /* @__PURE__ */ jsx88("div", { className: "flex-1", children: /* @__PURE__ */ jsx88(
        Heading,
        {
          level: 3,
          size: "lg",
          weight: "medium",
          className: "text-fg-primary font-medium text-lg leading-7",
          children: t("myAccount.myDetails.personalInformation")
        }
      ) }),
      /* @__PURE__ */ jsx88(
        Button,
        {
          variant: "secondary",
          size: "sm",
          onClick: handleEditClick,
          disabled,
          testID: `${testID}-edit-button`,
          className: "bg-gray-200 text-black border-transparent",
          label: t("myAccount.myDetails.edit"),
          iconName: "Pencil",
          iconPosition: "leading"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs67("div", { className: "space-y-6", children: [
      /* @__PURE__ */ jsxs67("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-6", children: [
        /* @__PURE__ */ jsx88(
          InputWithLabel,
          {
            id: `${testID}-first-name`,
            label: t("myAccount.myDetails.fields.firstName"),
            value: data.firstName,
            onChange: (e) => handleFieldChange("firstName", e.target.value),
            disabled: !isEditing || disabled,
            testID: `${testID}-first-name`,
            className: "w-full"
          }
        ),
        /* @__PURE__ */ jsx88(
          InputWithLabel,
          {
            id: `${testID}-last-name`,
            label: t("myAccount.myDetails.fields.lastName"),
            value: data.lastName,
            onChange: (e) => handleFieldChange("lastName", e.target.value),
            disabled: !isEditing || disabled,
            testID: `${testID}-last-name`,
            className: "w-full"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs67("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-6", children: [
        /* @__PURE__ */ jsx88(
          Dropdown,
          {
            label: t("myAccount.myDetails.fields.prefix"),
            options: prefixOptions,
            value: data.prefix,
            onChange: (value) => handleFieldChange("prefix", value),
            disabled: !isEditing || disabled,
            testID: `${testID}-prefix`,
            showLeftIcon: false,
            placeholder: t("myAccount.myDetails.fields.selectPrefix")
          }
        ),
        /* @__PURE__ */ jsx88(
          InputWithLabel,
          {
            id: `${testID}-job-title`,
            label: t("myAccount.myDetails.fields.jobTitle"),
            value: data.jobTitle,
            onChange: (e) => handleFieldChange("jobTitle", e.target.value),
            disabled: !isEditing || disabled,
            testID: `${testID}-job-title`,
            className: "w-full"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs67("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-6", children: [
        /* @__PURE__ */ jsx88(
          InputWithLabel,
          {
            id: `${testID}-email`,
            label: t("myAccount.myDetails.fields.email"),
            type: "email",
            value: data.email,
            onChange: (e) => handleFieldChange("email", e.target.value),
            disabled: !isEditing || disabled,
            testID: `${testID}-email`,
            className: "w-full"
          }
        ),
        /* @__PURE__ */ jsx88(
          MobileInputField,
          {
            label: t("myAccount.myDetails.fields.mobilePhone"),
            countries: countryOptions,
            selectedCountry: data.countryCode,
            onCountryChange: (value) => handleFieldChange("countryCode", value),
            phone: data.mobilePhone,
            onPhoneChange: (value) => handleFieldChange("mobilePhone", value),
            disabled: !isEditing || disabled,
            countryDropdownDisabled: !isEditing || disabled,
            testID: `${testID}-mobile`,
            placeholder: t("myAccount.myDetails.fields.mobilePhonePlaceholder")
          }
        )
      ] })
    ] })
  ] });
};
PersonalInformationForm.displayName = "PersonalInformationForm";

// src/components/organisms/SidebarItems.tsx
import { NavLink as NavLink2 } from "react-router-dom";
import { jsx as jsx89 } from "react/jsx-runtime";
var SidebarItems = ({
  items,
  testID = "sidebar-items",
  isCollapsed = false
}) => {
  return /* @__PURE__ */ jsx89(
    "nav",
    {
      className: "flex flex-col gap-md lg:gap-xs w-full min-w-0",
      "data-testid": testID,
      children: items.map((item) => /* @__PURE__ */ jsx89(NavLink2, { to: item.to, end: item.exact ?? false, children: ({ isActive }) => /* @__PURE__ */ jsx89(
        SidebarItem,
        {
          icon: item.icon,
          label: item.label,
          isActive,
          ...item.badge !== void 0 && { badge: item.badge },
          isCollapsed,
          ...item.testID !== void 0 && { testID: item.testID }
        }
      ) }, item.to))
    }
  );
};

// src/components/organisms/TabsContainer.tsx
import { useState as useState18 } from "react";
import { jsx as jsx90, jsxs as jsxs68 } from "react/jsx-runtime";
var TabsContainer = ({
  tabs,
  defaultActiveTab,
  activeTab,
  onTabChange,
  className,
  tabBarClassName,
  contentClassName,
  testID
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState18(
    defaultActiveTab || tabs[0]?.id || ""
  );
  const currentActiveTab = activeTab || internalActiveTab;
  const handleTabClick = (tabId) => {
    const tab = tabs.find((t) => t.id === tabId);
    if (tab?.disabled) return;
    if (!activeTab) {
      setInternalActiveTab(tabId);
    }
    onTabChange?.(tabId);
  };
  const activeTabContent = tabs.find(
    (tab) => tab.id === currentActiveTab
  )?.content;
  return /* @__PURE__ */ jsxs68("div", { className: cn("w-full", className), "data-testid": testID, children: [
    /* @__PURE__ */ jsx90(
      "div",
      {
        className: cn(
          "flex overflow-x-auto border-b border-brand-tint-300",
          tabBarClassName
        ),
        role: "tablist",
        children: tabs.map((tab) => /* @__PURE__ */ jsx90(
          TabBar,
          {
            current: currentActiveTab === tab.id,
            label: tab.label,
            badgeCount: tab.badgeCount,
            badgeVariant: tab.badgeVariant,
            badgeOverride: tab.badgeOverride,
            onClick: () => handleTabClick(tab.id),
            testID: `${testID}-tab-${tab.id}`,
            className: cn(
              "flex-shrink-0",
              tab.disabled && "opacity-50 cursor-not-allowed"
            ),
            disabled: tab.disabled,
            role: "tab",
            "aria-selected": currentActiveTab === tab.id,
            "aria-controls": `${testID}-panel-${tab.id}`,
            id: `${testID}-tab-${tab.id}`
          },
          tab.id
        ))
      }
    ),
    /* @__PURE__ */ jsx90(
      "div",
      {
        className: cn(contentClassName),
        role: "tabpanel",
        "aria-labelledby": `${testID}-tab-${currentActiveTab}`,
        id: `${testID}-panel-${currentActiveTab}`,
        children: activeTabContent
      }
    )
  ] });
};
TabsContainer.displayName = "TabsContainer";

// src/components/organisms/Table.tsx
import { useState as useState19, useMemo as useMemo8 } from "react";
import { ChevronsUpDown, ChevronUp, ChevronDown as ChevronDown2 } from "lucide-react";
import { jsx as jsx91, jsxs as jsxs69 } from "react/jsx-runtime";
var SORT_CYCLE = {
  asc: "desc",
  desc: void 0
};
function autoSort(a, b) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  const aDate = typeof a === "string" ? Date.parse(a) : NaN;
  const bDate = typeof b === "string" ? Date.parse(b) : NaN;
  if (!isNaN(aDate) && !isNaN(bDate)) return aDate - bDate;
  return String(a ?? "").localeCompare(String(b ?? ""));
}
var Table = ({
  columns,
  data,
  actions,
  className,
  testID,
  hideHeader = false,
  onRowClick,
  sortKey: controlledSortKey,
  sortDirection: controlledSortDir,
  onSort,
  cellPaddingX = "px-3xl"
}) => {
  const isControlled = typeof onSort === "function";
  const [internalSortKey, setInternalSortKey] = useState19();
  const [internalSortDir, setInternalSortDir] = useState19();
  const activeSortKey = isControlled ? controlledSortKey : internalSortKey;
  const activeSortDir = isControlled ? controlledSortDir : internalSortDir;
  const handleHeaderClick = (column) => {
    if (!column.sortable) return;
    const nextDir = activeSortKey === column.key ? SORT_CYCLE[activeSortDir ?? "desc"] : "asc";
    if (isControlled) {
      onSort(column.key, nextDir);
    } else {
      setInternalSortKey(nextDir === void 0 ? void 0 : column.key);
      setInternalSortDir(nextDir);
    }
  };
  const sortedData = useMemo8(() => {
    if (isControlled || !activeSortKey || !activeSortDir) return data;
    const column = columns.find((c) => c.key === activeSortKey);
    const comparator = column?.sortFn ?? autoSort;
    return [...data].sort((rowA, rowB) => {
      const result = comparator(rowA[activeSortKey], rowB[activeSortKey]);
      return activeSortDir === "asc" ? result : -result;
    });
  }, [data, isControlled, activeSortKey, activeSortDir, columns]);
  const renderSortIcon = (column) => {
    if (!column.sortable) return null;
    const isActive = activeSortKey === column.key;
    if (!isActive || activeSortDir === void 0) {
      return /* @__PURE__ */ jsx91(ChevronsUpDown, { className: "inline-block ms-1 size-3.5 text-fg-quaternary" });
    }
    return activeSortDir === "asc" ? /* @__PURE__ */ jsx91(ChevronUp, { className: "inline-block ms-1 size-3.5 text-fg-brand-primary" }) : /* @__PURE__ */ jsx91(ChevronDown2, { className: "inline-block ms-1 size-3.5 text-fg-brand-primary" });
  };
  const renderCellContent = (column, value, row, index) => {
    if (column.render) {
      return column.render(value, row, index);
    }
    return /* @__PURE__ */ jsx91("span", { className: "text-sm font-normal leading-normal text-gray-600", children: String(value) });
  };
  const getColumnAlignment = (align) => {
    switch (align) {
      case "center":
        return "text-center";
      case "right":
        return "text-end";
      default:
        return "text-start";
    }
  };
  return /* @__PURE__ */ jsx91(
    "div",
    {
      className: cn(
        "w-full bg-white border border-brand-tint-300 rounded-xl overflow-auto",
        className
      ),
      "data-testid": testID,
      children: /* @__PURE__ */ jsxs69("table", { className: "w-full", children: [
        !hideHeader && /* @__PURE__ */ jsx91("thead", { children: /* @__PURE__ */ jsxs69("tr", { className: "bg-gray-50 border-b border-brand-tint-300", children: [
          columns.map((column) => /* @__PURE__ */ jsx91(
            "th",
            {
              className: cn(
                `${cellPaddingX} py-lg text-xs font-medium leading-normal text-gray-500`,
                getColumnAlignment(column.align),
                column.width
              ),
              children: column.sortable ? /* @__PURE__ */ jsxs69(
                "button",
                {
                  type: "button",
                  onClick: () => handleHeaderClick(column),
                  className: "inline-flex items-center gap-0.5 cursor-pointer select-none hover:text-fg-primary transition-colors",
                  children: [
                    column.header,
                    renderSortIcon(column)
                  ]
                }
              ) : column.header
            },
            column.key
          )),
          actions && actions.length > 0 && /* @__PURE__ */ jsx91("th", { className: `${cellPaddingX} py-lg w-auto` })
        ] }) }),
        /* @__PURE__ */ jsx91("tbody", { children: sortedData.map((row, rowIndex) => {
          const isLastRow = rowIndex === data.length - 1;
          return /* @__PURE__ */ jsxs69(
            "tr",
            {
              onClick: onRowClick ? () => onRowClick(row, rowIndex) : void 0,
              className: cn(
                !isLastRow && "border-b border-brand-tint-300",
                onRowClick && "cursor-pointer hover:bg-gray-50"
              ),
              children: [
                columns.map((column) => {
                  const value = row[column.key];
                  return /* @__PURE__ */ jsx91(
                    "td",
                    {
                      className: cn(
                        `${cellPaddingX} py-xl`,
                        getColumnAlignment(column.align),
                        column.width,
                        column.rowClass
                      ),
                      children: renderCellContent(column, value, row, rowIndex)
                    },
                    column.key
                  );
                }),
                actions && actions.length > 0 && /* @__PURE__ */ jsx91("td", { className: `${cellPaddingX} py-xl`, children: /* @__PURE__ */ jsx91("div", { className: "flex items-center justify-end gap-xl", children: actions.map((action, actionIndex) => {
                  const shouldShow = action.show ? action.show(row, rowIndex) : true;
                  if (!shouldShow) return null;
                  const variant = action.variant || "tertiary";
                  const brandVariantMap = {
                    primary: "primary",
                    secondary: "secondary",
                    tertiary: "tertiary",
                    "destructive-tertiary": "tertiary"
                  };
                  return /* @__PURE__ */ jsx91(
                    Button,
                    {
                      variant: brandVariantMap[variant] || "tertiary",
                      size: "sm",
                      onClick: () => action.onClick(row, rowIndex),
                      testID: `action-${action.label}-${rowIndex}`,
                      label: action.label,
                      iconPosition: "none"
                    },
                    actionIndex
                  );
                }) }) })
              ]
            },
            rowIndex
          );
        }) })
      ] })
    }
  );
};
Table.displayName = "Table";

// src/components/organisms/DynamicRowsTable.tsx
import { useMemo as useMemo9, useState as useState20 } from "react";
import { useTranslation as useTranslation39 } from "@tasama/i18n";

// src/components/organisms/modal/Modal.tsx
import { useEffect as useEffect16, useId as useId2, useRef as useRef16 } from "react";
import { createPortal as createPortal4 } from "react-dom";
import { cva as cva20 } from "class-variance-authority";

// src/components/organisms/modal/ModalContext.ts
import { createContext } from "react";
var ModalContext = createContext(null);

// src/components/organisms/modal/ModalHeader.tsx
import { use } from "react";
import { useTranslation as useTranslation38 } from "react-i18next";
import { cva as cva18 } from "class-variance-authority";
import { Fragment as Fragment10, jsx as jsx92, jsxs as jsxs70 } from "react/jsx-runtime";
var headerVariants = cva18(
  // Sticky so title stays visible when Modal.Content scrolls
  "relative sticky top-0 z-10 bg-white px-6 pt-6 pb-4",
  {
    variants: {
      type: {
        "horizontal-left": "",
        center: "",
        left: ""
      }
    },
    defaultVariants: {
      type: "horizontal-left"
    }
  }
);
var contentVariants = cva18("flex", {
  variants: {
    type: {
      "horizontal-left": "flex-row items-start gap-4",
      center: "flex-col items-center text-center gap-4",
      left: "flex-col items-start gap-4"
    }
  },
  defaultVariants: {
    type: "horizontal-left"
  }
});
var textContainerVariants = cva18("flex flex-col gap-1", {
  variants: {
    type: {
      "horizontal-left": "flex-1 text-start",
      center: "text-center w-full",
      left: "text-start w-full"
    }
  },
  defaultVariants: {
    type: "horizontal-left"
  }
});
var ModalHeader = ({
  type = "horizontal-left",
  title,
  description,
  icon,
  showCloseButton = true,
  children,
  testID,
  className,
  descriptionClassName
}) => {
  const { t } = useTranslation38("common");
  const ctx = use(ModalContext);
  const onClose = ctx?.onClose;
  const titleId = ctx?.titleId;
  const closeButtonRef = ctx?.closeButtonRef;
  return /* @__PURE__ */ jsxs70(
    "div",
    {
      className: cn(headerVariants({ type }), className),
      "data-testid": testID,
      children: [
        showCloseButton && /* @__PURE__ */ jsx92(
          Button,
          {
            ref: closeButtonRef,
            variant: "tertiary",
            size: "lg",
            onClick: onClose,
            "aria-label": t("modalHeader.close"),
            tabIndex: 0,
            className: "absolute top-4 end-4 w-8 h-8 flex items-center justify-center p-0",
            testID: testID ? `${testID}-close-button` : "modal-header-close",
            children: /* @__PURE__ */ jsx92(Icon, { name: "X", size: 32, color: "var(--color-gray-400)" })
          }
        ),
        children ? (
          // Consumer-controlled header content
          /* @__PURE__ */ jsx92(Fragment10, { children })
        ) : (
          // Default header: icon + title + description
          /* @__PURE__ */ jsxs70("div", { className: cn(contentVariants({ type }), "pe-10"), children: [
            icon,
            /* @__PURE__ */ jsxs70("div", { className: cn(textContainerVariants({ type })), children: [
              title && /* @__PURE__ */ jsx92(
                Heading,
                {
                  level: 3,
                  size: "base",
                  weight: "medium",
                  id: titleId,
                  className: "text-fg-primary leading-6",
                  testID: testID ? `${testID}-title` : void 0,
                  children: title
                }
              ),
              description && /* @__PURE__ */ jsx92(
                Text,
                {
                  size: "sm",
                  weight: "normal",
                  className: cn(
                    "text-fg-tertiary leading-5",
                    descriptionClassName
                  ),
                  testID: testID ? `${testID}-description` : void 0,
                  children: description
                }
              )
            ] })
          ] })
        )
      ]
    }
  );
};
ModalHeader.displayName = "Modal.Header";

// src/components/organisms/modal/ModalContent.tsx
import { use as use2 } from "react";
import { jsx as jsx93 } from "react/jsx-runtime";
var ModalContent = ({ children, className, testID }) => {
  const ctx = use2(ModalContext);
  const descriptionId = ctx?.descriptionId;
  return /* @__PURE__ */ jsx93(
    "div",
    {
      id: descriptionId,
      className: cn("flex-1 overflow-y-auto px-6 py-4", className),
      "data-testid": testID,
      children
    }
  );
};
ModalContent.displayName = "Modal.Content";

// src/components/organisms/modal/ModalFooter.tsx
import { cva as cva19 } from "class-variance-authority";
import { jsx as jsx94, jsxs as jsxs71 } from "react/jsx-runtime";
var footerVariants = cva19("px-6 py-4", {
  variants: {
    alignment: {
      left: "flex justify-start gap-3",
      center: "flex justify-center gap-3",
      right: "flex justify-end gap-3",
      between: "flex justify-between gap-3"
    }
  },
  defaultVariants: {
    alignment: "right"
  }
});
var ModalFooter = ({
  divider = false,
  actions,
  alignment = "right",
  children,
  testID,
  className
}) => {
  const hasContent = children || actions && actions.length > 0;
  if (!hasContent) return null;
  return /* @__PURE__ */ jsxs71(
    "div",
    {
      "data-testid": testID,
      className: cn("sticky bottom-0 bg-background z-10", className),
      children: [
        divider && /* @__PURE__ */ jsx94("hr", { className: "border-t border-border" }),
        /* @__PURE__ */ jsxs71("div", { className: cn(footerVariants({ alignment })), children: [
          children,
          actions && actions.length > 0 && /* @__PURE__ */ jsx94(
            ModalActions,
            {
              actions,
              layout: alignment === "right" ? "right" : "horizontal",
              testID: testID ? `${testID}-actions` : "modal-footer-actions"
            }
          )
        ] })
      ]
    }
  );
};
ModalFooter.displayName = "Modal.Footer";

// src/components/organisms/modal/Modal.tsx
import { jsx as jsx95 } from "react/jsx-runtime";
var overlayVariants = cva20(
  "fixed inset-0 z-modal-backdrop flex items-center justify-center p-4 bg-alpha-modal-backgrop backdrop-blur-[8px] animate-in fade-in duration-200"
);
var panelVariants = cva20(
  "relative bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh] w-full animate-in zoom-in-95 duration-200 overflow-y-auto",
  {
    variants: {
      size: {
        sm: "max-w-(--modal-width-sm)",
        md: "max-w-(--modal-width-md)",
        lg: "max-w-(--modal-width-lg)",
        xl: "max-w-(--modal-width-xl)",
        /** Use 'full' then add a className width override for fixed-px widths */
        full: "max-w-[95vw]"
      }
    },
    defaultVariants: {
      size: "md"
    }
  }
);
var ModalRoot = ({
  isOpen,
  onClose,
  size = "md",
  closeOnOverlayClick = true,
  closeOnEscape = true,
  children,
  testID,
  className,
  overlayClassName
}) => {
  const panelRef = useRef16(null);
  const closeButtonRef = useRef16(null);
  const previouslyFocused = useRef16(null);
  const titleId = useId2();
  const descriptionId = useId2();
  useEffect16(() => {
    if (!isOpen || !closeOnEscape) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closeOnEscape, onClose]);
  useEffect16(() => {
    if (isOpen) {
      previouslyFocused.current = document.activeElement;
      requestAnimationFrame(() => {
        (closeButtonRef.current ?? panelRef.current)?.focus();
      });
      document.body.style.overflow = "hidden";
    } else {
      previouslyFocused.current?.focus();
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  if (!isOpen) return null;
  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) onClose?.();
  };
  return createPortal4(
    /* @__PURE__ */ jsx95(
      ModalContext.Provider,
      {
        value: { onClose, titleId, descriptionId, closeButtonRef },
        children: /* @__PURE__ */ jsx95(
          "div",
          {
            className: cn(overlayVariants(), overlayClassName),
            onClick: handleOverlayClick,
            "data-testid": `${testID}-overlay`,
            children: /* @__PURE__ */ jsx95(
              "div",
              {
                ref: panelRef,
                role: "dialog",
                "aria-modal": "true",
                "aria-labelledby": titleId,
                "aria-describedby": descriptionId,
                className: cn(panelVariants({ size }), className),
                "data-testid": testID,
                tabIndex: -1,
                children
              }
            )
          }
        )
      }
    ),
    document.body
  );
};
ModalRoot.displayName = "Modal";
var Modal = Object.assign(ModalRoot, {
  Header: ModalHeader,
  Content: ModalContent,
  Footer: ModalFooter
});

// src/components/organisms/DynamicRowsTable.tsx
import { jsx as jsx96, jsxs as jsxs72 } from "react/jsx-runtime";
var emptyDraft = (columns) => columns.reduce((acc, column) => {
  acc[column.key] = column.type === "checkbox" ? false : "";
  return acc;
}, {});
var formatDisplayValue = (column, raw) => {
  if (column.type === "checkbox") return raw ? "\u2713" : "";
  if (column.type === "select") {
    return column.options?.find((o) => o.value === raw)?.label ?? String(raw ?? "");
  }
  return String(raw ?? "");
};
var DynamicRowsTable = ({
  columns,
  value,
  onChange,
  title,
  addButtonLabel,
  modalTitle,
  editModalTitle,
  emptyStateHeading,
  emptyStateText,
  className,
  testID
}) => {
  const { t } = useTranslation39("common");
  const [editingIndex, setEditingIndex] = useState20(null);
  const [isAdding, setIsAdding] = useState20(false);
  const [draft, setDraft] = useState20(() => emptyDraft(columns));
  const [draftErrors, setDraftErrors] = useState20({});
  const [rowPendingDelete, setRowPendingDelete] = useState20(null);
  const [removeAllPending, setRemoveAllPending] = useState20(false);
  const isModalOpen = isAdding || editingIndex !== null;
  const isEditing = editingIndex !== null;
  const missingFieldLabels = columns.filter((column) => draftErrors[column.key]).map((column) => column.label);
  const openAddModal = () => {
    setDraft(emptyDraft(columns));
    setDraftErrors({});
    setIsAdding(true);
  };
  const openEditModal = (index) => {
    setDraft({ ...value[index] });
    setDraftErrors({});
    setEditingIndex(index);
  };
  const closeModal = () => {
    setIsAdding(false);
    setEditingIndex(null);
    setDraft(emptyDraft(columns));
    setDraftErrors({});
  };
  const handleDraftChange = (key, fieldValue) => {
    setDraft((prev) => ({ ...prev, [key]: fieldValue }));
    setDraftErrors((prev) => ({ ...prev, [key]: false }));
  };
  const handleSubmit = () => {
    const nextErrors = {};
    columns.forEach((column) => {
      if (column.required && !draft[column.key]) {
        nextErrors[column.key] = true;
      }
    });
    if (Object.values(nextErrors).some(Boolean)) {
      setDraftErrors(nextErrors);
      return;
    }
    if (isEditing && editingIndex !== null) {
      onChange(value.map((row, i) => i === editingIndex ? draft : row));
    } else {
      onChange([...value, draft]);
    }
    closeModal();
  };
  const handleConfirmDeleteRow = () => {
    if (rowPendingDelete === null) return;
    onChange(value.filter((_, i) => i !== rowPendingDelete));
    setRowPendingDelete(null);
  };
  const handleConfirmRemoveAll = () => {
    onChange([]);
    setRemoveAllPending(false);
  };
  const tableColumns = useMemo9(
    () => [
      ...columns.map((column) => ({
        header: column.label,
        key: column.key,
        ...column.width ? { width: column.width } : {},
        render: (raw) => /* @__PURE__ */ jsx96("span", { className: "text-sm font-light leading-sm text-fg-tertiary", children: formatDisplayValue(column, raw) })
      })),
      {
        header: "",
        key: "__actions",
        render: (_value, _row, index) => /* @__PURE__ */ jsxs72("div", { className: "flex items-center justify-end gap-lg", children: [
          /* @__PURE__ */ jsx96(
            "button",
            {
              type: "button",
              className: "inline-flex items-center justify-center text-fg-brand-primary cursor-pointer",
              onClick: () => openEditModal(index),
              "data-testid": `${testID}-edit-row-${index}`,
              "aria-label": t("actions.edit"),
              children: /* @__PURE__ */ jsx96(Icon, { name: "Edit", size: 20 })
            }
          ),
          /* @__PURE__ */ jsx96(
            "button",
            {
              type: "button",
              className: "inline-flex items-center justify-center text-fg-brand-primary cursor-pointer",
              onClick: () => setRowPendingDelete(index),
              "data-testid": `${testID}-delete-row-${index}`,
              "aria-label": t("actions.delete"),
              children: /* @__PURE__ */ jsx96(Icon, { name: "CircleX", size: 20 })
            }
          )
        ] })
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, testID, value]
  );
  const renderField = (column) => {
    const hasError = !!draftErrors[column.key];
    const errorMessage = hasError ? t("dynamicRowsTable.requiredField") : void 0;
    const fieldTestID = `${testID}-field-${column.key}`;
    switch (column.type) {
      case "date":
        return /* @__PURE__ */ jsx96(
          DateInput,
          {
            id: fieldTestID,
            label: column.label,
            required: column.required,
            value: draft[column.key] ?? "",
            onChange: (e) => handleDraftChange(column.key, e.target.value),
            error: hasError,
            errorMessage,
            startMonth: new Date((/* @__PURE__ */ new Date()).getFullYear() - 100, 0, 1),
            endMonth: new Date((/* @__PURE__ */ new Date()).getFullYear() + 10, 11, 31),
            testID: fieldTestID
          },
          column.key
        );
      case "select":
        return /* @__PURE__ */ jsx96(
          Dropdown,
          {
            label: column.label,
            required: column.required,
            options: column.options ?? [],
            value: draft[column.key] ?? "",
            onChange: (selected) => handleDraftChange(column.key, selected),
            error: hasError,
            errorMessage,
            testID: fieldTestID
          },
          column.key
        );
      case "checkbox":
        return /* @__PURE__ */ jsxs72("div", { className: "flex items-center gap-md", children: [
          /* @__PURE__ */ jsx96(
            Checkbox,
            {
              checked: !!draft[column.key],
              onChange: (e) => handleDraftChange(column.key, e.target.checked),
              testID: fieldTestID
            }
          ),
          /* @__PURE__ */ jsx96("span", { className: "text-sm font-normal text-fg-secondary", children: column.label }),
          hasError && /* @__PURE__ */ jsx96(
            "span",
            {
              "data-testid": `${fieldTestID}-error`,
              className: "text-error-primary text-xs font-light",
              children: errorMessage
            }
          )
        ] }, column.key);
      case "text":
      default:
        return /* @__PURE__ */ jsx96(
          InputWithLabel,
          {
            id: fieldTestID,
            label: column.label,
            required: column.required,
            value: draft[column.key] ?? "",
            onChange: (e) => handleDraftChange(column.key, e.target.value),
            error: hasError,
            errorMessage,
            testID: fieldTestID
          },
          column.key
        );
    }
  };
  return /* @__PURE__ */ jsxs72("div", { className: cn("flex flex-col items-end gap-xl w-full", className), children: [
    /* @__PURE__ */ jsxs72("div", { className: "flex flex-col gap-1.5 items-start w-full", children: [
      title && /* @__PURE__ */ jsx96("p", { className: "text-sm font-normal text-fg-secondary w-full", children: title }),
      value.length === 0 ? /* @__PURE__ */ jsx96("div", { className: "border border-border rounded-2xl px-xl py-3xl w-full flex items-center justify-center", children: /* @__PURE__ */ jsx96(
        EmptyScreen,
        {
          heading: emptyStateHeading,
          ...emptyStateText ? { text: emptyStateText } : {},
          iconName: "User",
          testID: `${testID}-empty-state`
        }
      ) }) : /* @__PURE__ */ jsx96(
        Table,
        {
          columns: tableColumns,
          data: value,
          testID: `${testID}-table`
        }
      )
    ] }),
    /* @__PURE__ */ jsxs72("div", { className: "flex gap-xl items-center", children: [
      /* @__PURE__ */ jsx96(
        Button,
        {
          label: t("dynamicRowsTable.removeAll"),
          variant: "tertiary",
          size: "sm",
          iconPosition: "none",
          disabled: value.length === 0,
          onClick: () => setRemoveAllPending(true),
          testID: `${testID}-remove-all`,
          className: "uppercase"
        }
      ),
      /* @__PURE__ */ jsx96(
        Button,
        {
          label: addButtonLabel,
          variant: "secondary",
          size: "sm",
          iconName: "Plus",
          iconPosition: "trailing",
          onClick: openAddModal,
          testID: `${testID}-add`,
          className: "uppercase"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs72(
      Modal,
      {
        isOpen: isModalOpen,
        onClose: closeModal,
        testID: `${testID}-form-modal`,
        size: "lg",
        children: [
          /* @__PURE__ */ jsx96(
            Modal.Header,
            {
              type: "left",
              title: isEditing ? editModalTitle ?? modalTitle : modalTitle,
              icon: /* @__PURE__ */ jsx96("span", { className: "flex items-center justify-center rounded-full bg-fg-brand-green size-12 shrink-0", children: /* @__PURE__ */ jsx96(Icon, { name: "User", size: 24, className: "text-fg-white" }) }),
              testID: `${testID}-form-modal-header`
            }
          ),
          /* @__PURE__ */ jsx96(Modal.Content, { testID: `${testID}-form-modal-content`, children: /* @__PURE__ */ jsxs72("div", { className: "flex flex-col gap-xl w-full", children: [
            missingFieldLabels.length > 0 && /* @__PURE__ */ jsx96(
              "p",
              {
                role: "alert",
                "data-testid": `${testID}-form-modal-error-summary`,
                className: "text-error-primary text-sm font-normal",
                children: t("dynamicRowsTable.incompleteFields", {
                  fields: missingFieldLabels.join(", ")
                })
              }
            ),
            columns.map((column) => renderField(column))
          ] }) }),
          /* @__PURE__ */ jsx96(
            Modal.Footer,
            {
              divider: true,
              alignment: "right",
              testID: `${testID}-form-modal-footer`,
              actions: [
                {
                  label: t("actions.cancel"),
                  variant: "tertiary",
                  onClick: closeModal,
                  testID: `${testID}-form-modal-cancel`,
                  iconPosition: "none",
                  className: "uppercase"
                },
                {
                  label: addButtonLabel,
                  variant: "secondary",
                  onClick: handleSubmit,
                  testID: `${testID}-form-modal-confirm`,
                  iconPosition: "trailing",
                  ctaIcon: "Plus"
                }
              ]
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs72(
      Modal,
      {
        isOpen: rowPendingDelete !== null,
        onClose: () => setRowPendingDelete(null),
        testID: `${testID}-delete-row-modal`,
        size: "lg",
        children: [
          /* @__PURE__ */ jsx96(
            Modal.Header,
            {
              type: "left",
              title: t("dynamicRowsTable.confirmationTitle"),
              description: t("dynamicRowsTable.deleteRow.description"),
              icon: /* @__PURE__ */ jsx96(
                InfoIconWithBackground_default,
                {
                  size: "large",
                  testID: `${testID}-delete-row-modal-icon`
                }
              ),
              testID: `${testID}-delete-row-modal-header`
            }
          ),
          /* @__PURE__ */ jsx96(
            Modal.Footer,
            {
              divider: true,
              alignment: "right",
              testID: `${testID}-delete-row-modal-footer`,
              actions: [
                {
                  label: t("actions.cancel"),
                  variant: "tertiary",
                  onClick: () => setRowPendingDelete(null),
                  testID: `${testID}-delete-row-modal-cancel`,
                  iconPosition: "none",
                  className: "uppercase"
                },
                {
                  label: t("dynamicRowsTable.confirmLabel"),
                  variant: "secondary",
                  onClick: handleConfirmDeleteRow,
                  testID: `${testID}-delete-row-modal-confirm`,
                  iconPosition: "trailing",
                  ctaIcon: "ArrowRight"
                }
              ]
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs72(
      Modal,
      {
        isOpen: removeAllPending,
        onClose: () => setRemoveAllPending(false),
        testID: `${testID}-remove-all-modal`,
        size: "lg",
        children: [
          /* @__PURE__ */ jsx96(
            Modal.Header,
            {
              type: "left",
              title: t("dynamicRowsTable.confirmationTitle"),
              description: t("dynamicRowsTable.removeAllConfirm.description"),
              icon: /* @__PURE__ */ jsx96(
                InfoIconWithBackground_default,
                {
                  size: "large",
                  testID: `${testID}-remove-all-modal-icon`
                }
              ),
              testID: `${testID}-remove-all-modal-header`
            }
          ),
          /* @__PURE__ */ jsx96(
            Modal.Footer,
            {
              divider: true,
              alignment: "right",
              testID: `${testID}-remove-all-modal-footer`,
              actions: [
                {
                  label: t("actions.cancel"),
                  variant: "tertiary",
                  onClick: () => setRemoveAllPending(false),
                  testID: `${testID}-remove-all-modal-cancel`,
                  iconPosition: "none",
                  className: "uppercase"
                },
                {
                  label: t("dynamicRowsTable.confirmLabel"),
                  variant: "secondary",
                  onClick: handleConfirmRemoveAll,
                  testID: `${testID}-remove-all-modal-confirm`,
                  iconPosition: "trailing",
                  ctaIcon: "ArrowRight"
                }
              ]
            }
          )
        ]
      }
    )
  ] });
};
DynamicRowsTable.displayName = "DynamicRowsTable";

// src/components/organisms/SidePanel.tsx
import { useEffect as useEffect17, useId as useId3, useState as useState21 } from "react";
import { useTranslation as useTranslation40, useDirection as useDirection5 } from "@tasama/i18n";

// src/stores/useRefreshPageStore.ts
import { createBaseStore as createBaseStore2 } from "@tasama/utils";
var useRefreshPageStore = createBaseStore2(
  {
    isOpen: false
  },
  (set) => ({
    // Actions
    open: () => set({
      isOpen: true
    }),
    close: () => set({
      isOpen: false
    })
  })
);

// src/stores/useSomeThingWentWrongModalStore.ts
import { createBaseStore as createBaseStore3 } from "@tasama/utils";
var useSomeThingWentWrongModalStore = createBaseStore3(
  {
    isOpen: false,
    retryCallback: null
  },
  (set, get) => ({
    // Actions
    open: (retryCallback) => set({
      isOpen: true,
      retryCallback: retryCallback || null
    }),
    close: () => set({
      isOpen: false,
      retryCallback: null
    }),
    handleTryAgain: () => {
      const { retryCallback, close } = get();
      if (retryCallback) {
        retryCallback();
      }
      close();
    }
  })
);

// src/stores/useMobileOverlayStore.ts
import { createBaseStore as createBaseStore4 } from "@tasama/utils";
var useMobileOverlayStore = createBaseStore4(
  {
    openOverlayIds: /* @__PURE__ */ new Set()
  },
  (set, get) => ({
    registerOverlayOpen: (id) => {
      const next = new Set(get().openOverlayIds);
      next.add(id);
      set({ openOverlayIds: next });
    },
    registerOverlayClose: (id) => {
      const next = new Set(get().openOverlayIds);
      next.delete(id);
      set({ openOverlayIds: next });
    }
  })
);
var useIsMobileOverlayOpen = () => useMobileOverlayStore((state) => state.openOverlayIds.size > 0);

// src/components/organisms/SidePanel.tsx
import { Fragment as Fragment11, jsx as jsx97, jsxs as jsxs73 } from "react/jsx-runtime";
var SidePanel = ({
  children,
  defaultOpen = false,
  isOpen: controlledIsOpen,
  onOpenChange,
  position = "right",
  width = "xs",
  expandedWidth = "sm",
  showBackdrop = true,
  closeOnBackdropClick = true,
  title,
  subtitle,
  showCloseButton = true,
  showHeaderDivider = true,
  headerClassName,
  className,
  contentClassName,
  testID = "side-panel",
  expandable = false,
  defaultExpanded = false,
  isExpanded: controlledIsExpanded,
  onExpandedChange
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState21(defaultOpen);
  const isOpen = controlledIsOpen !== void 0 ? controlledIsOpen : internalIsOpen;
  const isMobile = useIsMobile();
  const overlayId = useId3();
  const registerOverlayOpen = useMobileOverlayStore(
    (state) => state.registerOverlayOpen
  );
  const registerOverlayClose = useMobileOverlayStore(
    (state) => state.registerOverlayClose
  );
  useEffect17(() => {
    if (isMobile && isOpen) {
      registerOverlayOpen(overlayId);
    } else {
      registerOverlayClose(overlayId);
    }
    return () => registerOverlayClose(overlayId);
  }, [isMobile, isOpen, overlayId, registerOverlayOpen, registerOverlayClose]);
  const { t } = useTranslation40("common");
  const dir = useDirection5();
  const [internalIsExpanded, setInternalIsExpanded] = useState21(defaultExpanded);
  const isExpanded = controlledIsExpanded !== void 0 ? controlledIsExpanded : internalIsExpanded;
  const handleOpenChange = (newIsOpen) => {
    if (controlledIsOpen === void 0) {
      setInternalIsOpen(newIsOpen);
    }
    onOpenChange?.(newIsOpen);
  };
  const handleExpandedChange = (newIsExpanded) => {
    if (controlledIsExpanded === void 0) {
      setInternalIsExpanded(newIsExpanded);
    }
    onExpandedChange?.(newIsExpanded);
  };
  const toggleExpanded = () => {
    handleExpandedChange(!isExpanded);
  };
  const handleClose = () => {
    handleOpenChange(false);
  };
  const handleBackdropClick = () => {
    if (closeOnBackdropClick) {
      handleClose();
    }
  };
  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      handleClose();
    }
  };
  const isRtl = dir === "rtl";
  const positionClasses = {
    left: {
      panel: "start-0",
      transform: isOpen ? "translate-x-0" : isRtl ? "translate-x-full" : "-translate-x-full"
    },
    right: {
      panel: "end-0",
      transform: isOpen ? "translate-x-0" : isRtl ? "-translate-x-full" : "translate-x-full"
    }
  };
  const widthClasses = {
    xs: "w-sidebar-xs",
    sm: "w-sidebar-sm",
    md: "w-sidebar-md",
    lg: "w-sidebar-lg"
  };
  const getWidthClass = (value = width) => {
    if (value && widthClasses[value]) {
      return widthClasses[value];
    }
    return "";
  };
  return /* @__PURE__ */ jsxs73(Fragment11, { children: [
    showBackdrop && /* @__PURE__ */ jsx97(
      "div",
      {
        className: cn(
          "fixed inset-0 bg-alpha-black-50 transition-opacity duration-300 z-sidepanel-backdrop",
          isOpen ? "opacity-0 visible" : "opacity-0 invisible"
        ),
        onClick: handleBackdropClick,
        "data-testid": `${testID}-backdrop`,
        "aria-hidden": "true"
      }
    ),
    /* @__PURE__ */ jsxs73(
      "div",
      {
        className: cn(
          "fixed top-0 h-full bg-background shadow-2xl transition-[transform,width] duration-300 ease-in-out z-sidepanel flex flex-col max-w-full",
          positionClasses[position].panel,
          positionClasses[position].transform,
          className,
          isExpanded ? getWidthClass(expandedWidth) : getWidthClass()
        ),
        style: {
          ...width && !widthClasses[width] && !isExpanded ? { width } : {}
        },
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": title ? `${testID}-title` : void 0,
        onKeyDown: handleKeyDown,
        "data-testid": testID,
        children: [
          (title || showCloseButton || expandable) && /* @__PURE__ */ jsxs73(
            "div",
            {
              className: cn(
                "flex items-center justify-between px-4 py-4 lg:px-6",
                showHeaderDivider && "border-b border-border",
                headerClassName
              ),
              children: [
                title && /* @__PURE__ */ jsxs73("div", { className: "flex flex-col gap-1", children: [
                  /* @__PURE__ */ jsx97(
                    "h2",
                    {
                      id: `${testID}-title`,
                      className: "text-lg font-medium lg:font-semibold text-fg-primary me-4xl",
                      "data-testid": `${testID}-title`,
                      children: title
                    }
                  ),
                  subtitle && /* @__PURE__ */ jsx97(
                    "p",
                    {
                      className: "text-sm font-light text-fg-tertiary overflow-hidden text-ellipsis",
                      style: {
                        fontFamily: "var(--font-tasama)",
                        fontSize: "var(--font-size-sm)",
                        lineHeight: "var(--line-height-sm)"
                      },
                      "data-testid": `${testID}-subtitle`,
                      children: subtitle
                    }
                  )
                ] }),
                /* @__PURE__ */ jsxs73("div", { className: "flex items-center gap-2 ms-auto", children: [
                  expandable && /* @__PURE__ */ jsx97(
                    Button,
                    {
                      onClick: toggleExpanded,
                      variant: "quaternary",
                      size: "md",
                      iconClassName: "text-fg-brand-primary bg-transparent",
                      iconPosition: "none",
                      testID: `${testID}-expand-button`,
                      "aria-label": isExpanded ? t("sidePanel.collapse") : t("sidePanel.expand"),
                      className: "hidden lg:inline-flex",
                      children: /* @__PURE__ */ jsx97(
                        Icon,
                        {
                          size: 20,
                          name: isExpanded ? "ChevronsLeft" : "ChevronsRight",
                          className: isRtl ? "icon-rtl-flip" : void 0
                        }
                      )
                    }
                  ),
                  showCloseButton && /* @__PURE__ */ jsx97(
                    "button",
                    {
                      type: "button",
                      onClick: handleClose,
                      "data-testid": `${testID}-close-button`,
                      "aria-label": t("sidePanel.close"),
                      className: "flex items-center justify-center p-1 cursor-pointer bg-transparent border-none text-fg-brand-primary hover:opacity-70 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm",
                      children: /* @__PURE__ */ jsx97(Icon, { size: 20, name: "X" })
                    }
                  )
                ] })
              ]
            }
          ),
          /* @__PURE__ */ jsx97(
            "div",
            {
              className: cn("flex-1 overflow-y-auto", contentClassName),
              "data-testid": `${testID}-content`,
              children
            }
          )
        ]
      }
    )
  ] });
};
SidePanel.displayName = "SidePanel";

// src/components/organisms/FilterDrawer.tsx
import { useEffect as useEffect18, useState as useState22 } from "react";
import { useTranslation as useTranslation41 } from "@tasama/i18n";
import { jsx as jsx98, jsxs as jsxs74 } from "react/jsx-runtime";
function FilterDrawer({
  isOpen,
  onOpenChange,
  appliedValues,
  onApply,
  onClear,
  title,
  testID = "filter-drawer",
  children
}) {
  const { t } = useTranslation41("common");
  const [draft, setDraftState] = useState22(appliedValues);
  useEffect18(() => {
    if (isOpen) {
      setDraftState(appliedValues);
    }
  }, [isOpen]);
  const setDraft = (patch) => {
    setDraftState((prev) => ({ ...prev, ...patch }));
  };
  const handleClear = () => {
    const cleared = onClear();
    if (cleared !== void 0) {
      setDraftState(cleared);
    }
  };
  const handleApply = () => {
    onApply(draft);
    onOpenChange(false);
  };
  return /* @__PURE__ */ jsxs74(
    SidePanel,
    {
      isOpen,
      onOpenChange,
      position: "right",
      width: "xs",
      title: title ?? t("filters.title"),
      showCloseButton: true,
      testID,
      contentClassName: "flex flex-col h-full p-0",
      children: [
        /* @__PURE__ */ jsx98("div", { className: "flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-4", children: children(draft, setDraft) }),
        /* @__PURE__ */ jsxs74("div", { className: "shrink-0 flex items-center justify-end gap-4 border-t border-border p-4", children: [
          /* @__PURE__ */ jsx98(
            Button,
            {
              variant: "tertiary",
              iconPosition: "none",
              label: t("filters.clearFilters"),
              className: "uppercase",
              onClick: handleClear,
              testID: `${testID}-clear-button`
            }
          ),
          /* @__PURE__ */ jsx98(
            Button,
            {
              variant: "primary",
              label: t("filters.apply"),
              iconPosition: "trailing",
              onClick: handleApply,
              testID: `${testID}-apply-button`
            }
          )
        ] })
      ]
    }
  );
}
FilterDrawer.displayName = "FilterDrawer";

// src/components/organisms/DateRangePicker.tsx
import * as React38 from "react";
import ReactDOM4 from "react-dom";
import { useTranslation as useTranslation42 } from "@tasama/i18n";
import { ar as ar3 } from "date-fns/locale";
import {
  startOfDay,
  endOfDay,
  subDays,
  subYears,
  startOfMonth,
  startOfWeek,
  addMonths,
  addDays,
  isSameDay,
  format
} from "date-fns";
import {
  DayPicker as DayPicker2
} from "react-day-picker";
import { Fragment as Fragment12, jsx as jsx99, jsxs as jsxs75 } from "react/jsx-runtime";
var isSameRange = (a, b) => {
  const sameFrom = a.from && b.from ? isSameDay(a.from, b.from) : a.from === b.from;
  const sameTo = a.to && b.to ? isSameDay(a.to, b.to) : a.to === b.to;
  return sameFrom && sameTo;
};
var useDefaultDateRangePresets = () => {
  const { t } = useTranslation42("common");
  const now = /* @__PURE__ */ new Date();
  return [
    {
      key: "last7Days",
      label: t("dateRangePicker.presets.last7Days"),
      range: { from: startOfDay(subDays(now, 6)), to: endOfDay(now) }
    },
    {
      key: "last30Days",
      label: t("dateRangePicker.presets.last30Days"),
      range: { from: startOfDay(subDays(now, 29)), to: endOfDay(now) }
    },
    {
      key: "last90Days",
      label: t("dateRangePicker.presets.last90Days"),
      range: { from: startOfDay(subDays(now, 89)), to: endOfDay(now) }
    },
    {
      key: "last1Year",
      label: t("dateRangePicker.presets.last1Year"),
      range: { from: startOfDay(subYears(now, 1)), to: endOfDay(now) }
    },
    {
      key: "all",
      label: t("dateRangePicker.presets.all"),
      range: { from: void 0, to: void 0 }
    }
  ];
};
var rangeClassNames = {
  // The shared config's single-date `selected` style also fires for every day
  // in a range (start/middle/end all carry the `selected` modifier too) and
  // would otherwise paint the whole range navy, drowning out range_middle's
  // lighter tint below — clear it so only the range_* keys apply in range mode.
  selected: "",
  range_start: "[&>button]:bg-bg-brand-solid [&>button]:text-white [&>button]:hover:bg-bg-brand-solid",
  range_end: "[&>button]:bg-bg-brand-solid [&>button]:text-white [&>button]:hover:bg-bg-brand-solid",
  range_middle: "[&>button]:bg-bg-brand-primary [&>button]:text-fg-primary [&>button]:hover:bg-bg-brand-primary"
};
var RangeEndpointField = ({ label, date, placeholder, onClear, clearAriaLabel, testID }) => /* @__PURE__ */ jsxs75("div", { className: "flex-1 space-y-sm", children: [
  /* @__PURE__ */ jsx99(Label, { className: "block font-normal text-sm text-fg-secondary", children: label }),
  /* @__PURE__ */ jsxs75("div", { className: "relative", children: [
    /* @__PURE__ */ jsx99("div", { className: "absolute start-3 top-1/2 -translate-y-1/2", children: /* @__PURE__ */ jsx99(
      Icon,
      {
        name: "Calendar",
        size: 20,
        className: "text-quaternary",
        "aria-hidden": true
      }
    ) }),
    /* @__PURE__ */ jsx99(
      "input",
      {
        readOnly: true,
        value: date ? formatDate(date) : "",
        placeholder,
        className: cn(
          "flex h-11 w-full rounded-lg border border-border bg-background",
          "ps-10 py-[10px] text-sm font-light",
          date ? "pe-9 text-fg-primary" : "pe-3 text-quaternary",
          "cursor-default select-none focus-visible:outline-none"
        ),
        "data-testid": testID,
        tabIndex: -1
      }
    ),
    date && /* @__PURE__ */ jsx99(
      "button",
      {
        type: "button",
        onClick: onClear,
        "aria-label": clearAriaLabel,
        "data-testid": testID ? `${testID}-clear-button` : void 0,
        className: "absolute end-3 top-1/2 flex -translate-y-1/2 cursor-pointer items-center justify-center text-quaternary hover:text-fg-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm",
        children: /* @__PURE__ */ jsx99(Icon, { name: "X", size: 16, "aria-hidden": true })
      }
    )
  ] })
] });
var PresetsRail = ({ presets, activeKey, onSelect, testID }) => {
  const { t } = useTranslation42("common");
  const items = [
    { key: "custom", label: t("dateRangePicker.presets.custom") },
    ...presets
  ];
  return /* @__PURE__ */ jsx99("div", { className: "flex w-[168px] shrink-0 flex-col gap-1 border-e border-border p-2", children: items.map((item) => {
    const isActive = item.key === activeKey;
    return /* @__PURE__ */ jsx99(
      "button",
      {
        type: "button",
        "data-testid": testID ? `${testID}-preset-${item.key}` : void 0,
        onClick: () => "range" in item && onSelect(item.range),
        className: cn(
          "rounded-lg p-lg text-start text-sm font-light transition-colors cursor-pointer",
          isActive ? "bg-bg-brand-solid text-white" : "text-fg-primary hover:bg-bg-brand-primary"
        ),
        children: item.label
      },
      item.key
    );
  }) });
};
var MOBILE_MONTHS_CHUNK = 6;
var MOBILE_MONTHS_MAX_EACH_DIRECTION = 240;
var StackedWeekdaysHeader = ({ dateLocale }) => {
  const weekdayLabels = React38.useMemo(() => {
    const options = dateLocale ? { locale: dateLocale } : void 0;
    const start = startOfWeek(/* @__PURE__ */ new Date(), options);
    return Array.from(
      { length: 7 },
      (_, i) => format(addDays(start, i), "EEEEEE", options)
    );
  }, [dateLocale]);
  return /* @__PURE__ */ jsx99(
    "div",
    {
      className: "flex gap-0.5 px-1",
      dir: document.documentElement.dir || "ltr",
      "aria-hidden": true,
      children: weekdayLabels.map((label, i) => /* @__PURE__ */ jsx99(
        "div",
        {
          className: "h-9 flex flex-1 items-center justify-center text-xs font-light text-quaternary",
          children: label
        },
        i
      ))
    }
  );
};
var StackedRangeCalendar = ({
  anchorMonth,
  initialMonthsAfter,
  selected,
  onSelect,
  disabledDays,
  dateLocale
}) => {
  const [monthsBefore, setMonthsBefore] = React38.useState(0);
  const [monthsAfter, setMonthsAfter] = React38.useState(initialMonthsAfter);
  const [scrollEl, setScrollEl] = React38.useState(null);
  const prevScrollHeightRef = React38.useRef(null);
  const months = React38.useMemo(() => {
    const start = addMonths(anchorMonth, -monthsBefore);
    return Array.from(
      { length: monthsBefore + monthsAfter },
      (_, i) => addMonths(start, i)
    );
  }, [anchorMonth, monthsBefore, monthsAfter]);
  React38.useLayoutEffect(() => {
    if (!scrollEl || prevScrollHeightRef.current === null) return;
    scrollEl.scrollTop += scrollEl.scrollHeight - prevScrollHeightRef.current;
    prevScrollHeightRef.current = null;
  }, [monthsBefore, scrollEl]);
  const loadEarlierMonths = React38.useCallback(() => {
    if (!scrollEl) return;
    prevScrollHeightRef.current = scrollEl.scrollHeight;
    setMonthsBefore((n) => n + MOBILE_MONTHS_CHUNK);
  }, [scrollEl]);
  const loadLaterMonths = React38.useCallback(() => {
    setMonthsAfter((n) => n + MOBILE_MONTHS_CHUNK);
  }, []);
  const { sentinelRef: topSentinelRef } = useInfiniteScrollWithQuery({
    fetchNextPage: loadEarlierMonths,
    hasNextPage: monthsBefore < MOBILE_MONTHS_MAX_EACH_DIRECTION,
    isFetchingNextPage: false,
    root: scrollEl,
    threshold: 100
  });
  const { sentinelRef: bottomSentinelRef } = useInfiniteScrollWithQuery({
    fetchNextPage: loadLaterMonths,
    hasNextPage: monthsAfter < MOBILE_MONTHS_MAX_EACH_DIRECTION,
    isFetchingNextPage: false,
    root: scrollEl,
    threshold: 100
  });
  return /* @__PURE__ */ jsxs75("div", { ref: setScrollEl, className: "flex flex-col gap-4 overflow-y-auto px-1", children: [
    /* @__PURE__ */ jsx99("div", { ref: topSentinelRef, "aria-hidden": true, className: "h-px shrink-0" }),
    months.map((month) => /* @__PURE__ */ jsx99(
      DayPicker2,
      {
        mode: "range",
        month,
        captionLayout: "label",
        hideNavigation: true,
        selected,
        onSelect,
        dir: document.documentElement.dir || "ltr",
        locale: dateLocale,
        disabled: disabledDays,
        classNames: buildDatePickerClassNames(dateLocale, false, {
          ...rangeClassNames,
          month_caption: "flex items-center h-8",
          caption_label: "text-sm font-semibold text-fg-primary",
          // Rendered once above the month list via StackedWeekdaysHeader
          // instead of once per month.
          weekdays: "hidden",
          // The shared config only stretches day cells to flex-1 for
          // Arabic; every other locale falls back to a fixed w-9, which is
          // fine for the desktop popup (sized around its cells) but leaves
          // a gap on the side of this full-width mobile drawer. Force the
          // stretch here regardless of locale.
          day: "h-9 p-0 text-center relative flex flex-1 items-center justify-center"
        })
      },
      month.toISOString()
    )),
    /* @__PURE__ */ jsx99("div", { ref: bottomSentinelRef, "aria-hidden": true, className: "h-px shrink-0" })
  ] });
};
var DateRangePicker = ({
  label,
  id,
  hintText,
  error = false,
  errorMessage,
  disabled = false,
  required = false,
  testID,
  placeholder,
  value,
  onChange,
  presets,
  disabledDays,
  mobileMonthsToShow = 12
}) => {
  const { t, i18n: i18n2 } = useTranslation42("common");
  const dateLocale = i18n2.language === "ar" ? ar3 : void 0;
  const isDesktop = useBreakpoint("lg");
  const defaultPresets = useDefaultDateRangePresets();
  const resolvedPresets = presets === false ? [] : presets ?? defaultPresets;
  const [open, setOpen] = React38.useState(false);
  const [draft, setDraft] = React38.useState(value);
  const containerRef = React38.useRef(null);
  const popupRef = React38.useRef(null);
  const inputRef = React38.useRef(null);
  const displayValue = value.from && value.to ? `${formatDate(value.from)} - ${formatDate(value.to)}` : "";
  const openPicker = React38.useCallback(() => {
    if (disabled) return;
    setDraft(value);
    setOpen(true);
  }, [disabled, value]);
  const closeWithoutApplying = React38.useCallback(() => {
    setOpen(false);
    inputRef.current?.focus();
  }, []);
  const { popupStyle } = usePopoverPosition({
    open: open && isDesktop,
    containerRef,
    popupRef,
    onClose: closeWithoutApplying
  });
  const handleApply = () => {
    onChange(draft);
    setOpen(false);
    inputRef.current?.focus();
  };
  const activePresetKey = resolvedPresets.find((p) => isSameRange(p.range, draft))?.key ?? "custom";
  const mobileAnchorMonth = React38.useMemo(
    () => startOfMonth(draft.from ?? /* @__PURE__ */ new Date()),
    [draft.from]
  );
  const body = /* @__PURE__ */ jsxs75("div", { className: "flex min-h-0 flex-1 flex-col lg:flex-row", children: [
    isDesktop && resolvedPresets.length > 0 && /* @__PURE__ */ jsx99(
      PresetsRail,
      {
        presets: resolvedPresets,
        activeKey: activePresetKey,
        onSelect: setDraft,
        testID
      }
    ),
    /* @__PURE__ */ jsxs75("div", { className: "flex min-h-0 flex-1 flex-col gap-3xl px-4 pb-4 lg:gap-4 lg:p-4", children: [
      /* @__PURE__ */ jsxs75("div", { className: "flex gap-4", children: [
        /* @__PURE__ */ jsx99(
          RangeEndpointField,
          {
            label: t("dateRangePicker.startDate"),
            date: draft.from,
            placeholder: t("dateInput.placeholder"),
            onClear: () => setDraft((prev) => ({ ...prev, from: void 0 })),
            clearAriaLabel: t("dateRangePicker.clearStartDateAriaLabel"),
            testID: testID ? `${testID}-start-date` : void 0
          }
        ),
        /* @__PURE__ */ jsx99(
          RangeEndpointField,
          {
            label: t("dateRangePicker.endDate"),
            date: draft.to,
            placeholder: t("dateInput.placeholder"),
            onClear: () => setDraft((prev) => ({ ...prev, to: void 0 })),
            clearAriaLabel: t("dateRangePicker.clearEndDateAriaLabel"),
            testID: testID ? `${testID}-end-date` : void 0
          }
        )
      ] }),
      isDesktop ? /* @__PURE__ */ jsx99(
        DayPicker2,
        {
          mode: "range",
          numberOfMonths: 2,
          navLayout: "around",
          selected: draft,
          onSelect: (range) => setDraft(range ?? { from: void 0, to: void 0 }),
          defaultMonth: draft.from ?? /* @__PURE__ */ new Date(),
          dir: document.documentElement.dir || "ltr",
          locale: dateLocale,
          disabled: disabledDays,
          classNames: buildDatePickerClassNames(dateLocale, false, {
            ...rangeClassNames,
            // Two months side-by-side on desktop — the shared config leaves
            // `months` unstyled since single-month consumers don't need it.
            months: "flex flex-row gap-6"
          })
        }
      ) : /* @__PURE__ */ jsxs75(Fragment12, { children: [
        /* @__PURE__ */ jsx99(StackedWeekdaysHeader, { dateLocale }),
        /* @__PURE__ */ jsx99("div", { className: "-mx-4 border-b border-border" }),
        /* @__PURE__ */ jsx99(
          StackedRangeCalendar,
          {
            anchorMonth: mobileAnchorMonth,
            initialMonthsAfter: mobileMonthsToShow,
            selected: draft,
            onSelect: (range) => setDraft(range ?? { from: void 0, to: void 0 }),
            disabledDays,
            dateLocale
          }
        )
      ] })
    ] })
  ] });
  const footer = /* @__PURE__ */ jsxs75("div", { className: "flex shrink-0 items-center justify-end gap-2 border-t border-border p-4", children: [
    /* @__PURE__ */ jsx99(
      Button,
      {
        variant: "tertiary",
        iconPosition: "none",
        label: t("actions.cancel"),
        className: "uppercase",
        onClick: closeWithoutApplying,
        testID: testID ? `${testID}-cancel-button` : void 0
      }
    ),
    /* @__PURE__ */ jsx99(
      Button,
      {
        variant: "primary",
        label: t("actions.apply"),
        iconPosition: "trailing",
        onClick: handleApply,
        testID: testID ? `${testID}-apply-button` : void 0
      }
    )
  ] });
  const inputClasses = cn(
    "flex h-12 w-full rounded-lg border border-solid transition-colors motion-safe:duration-150",
    "focus-visible:outline-none cursor-pointer select-none",
    "ps-10 pe-12 py-[14px] truncate",
    "text-sm font-light",
    "bg-background",
    disabled ? "cursor-not-allowed pointer-events-none border-border text-disabled" : displayValue ? "text-fg-primary" : "text-quaternary",
    error ? "border-error focus:shadow-[0_0_0_4px_var(--color-bg-error-secondary)]" : "border-tertiary focus:shadow-[0_0_0_4px_var(--color-brand-tint-200)]"
  );
  const describedByIds = [];
  if (hintText) describedByIds.push(`${id}-hint`);
  if (error && errorMessage) describedByIds.push(`${id}-error`);
  const ariaDescribedBy = describedByIds.length ? describedByIds.join(" ") : void 0;
  const desktopPopup = open && isDesktop ? ReactDOM4.createPortal(
    /* @__PURE__ */ jsxs75(
      "div",
      {
        ref: popupRef,
        role: "dialog",
        "aria-label": t("dateRangePicker.chooseDateRange"),
        tabIndex: -1,
        style: popupStyle,
        className: "fixed z-popover flex flex-col rounded-xl border border-border bg-background shadow-lg focus:outline-none",
        children: [
          body,
          footer
        ]
      }
    ),
    document.body
  ) : null;
  return /* @__PURE__ */ jsxs75("div", { className: "space-y-sm", "data-testid": testID, children: [
    label && /* @__PURE__ */ jsxs75(
      Label,
      {
        htmlFor: id,
        className: cn(
          "block font-normal text-sm input-label-spacing",
          disabled ? "text-disabled" : "text-fg-secondary"
        ),
        children: [
          label,
          !required && /* @__PURE__ */ jsx99("span", { className: "ms-1", children: t("actions.optional") })
        ]
      }
    ),
    /* @__PURE__ */ jsxs75("div", { className: "relative", ref: containerRef, children: [
      /* @__PURE__ */ jsx99(
        "div",
        {
          className: cn(
            "absolute start-3 top-1/2 -translate-y-1/2 z-10",
            disabled ? "pointer-events-none" : "cursor-pointer"
          ),
          onClick: openPicker,
          children: /* @__PURE__ */ jsx99(
            Icon,
            {
              name: "Calendar",
              size: 20,
              className: cn(disabled ? "text-disabled" : "text-quaternary"),
              "aria-hidden": true
            }
          )
        }
      ),
      /* @__PURE__ */ jsx99(
        "input",
        {
          ref: inputRef,
          id,
          readOnly: true,
          value: displayValue,
          placeholder: placeholder || t("dateRangePicker.placeholder"),
          className: inputClasses,
          "aria-invalid": error || void 0,
          "aria-describedby": ariaDescribedBy,
          "aria-label": label ? void 0 : placeholder || t("dateRangePicker.placeholder"),
          "aria-haspopup": "dialog",
          disabled,
          "data-testid": testID ? `${testID}-input` : void 0,
          onClick: openPicker,
          onKeyDown: (e) => {
            if (e.key === " " || e.key === "Enter") {
              e.preventDefault();
              openPicker();
            }
          }
        }
      ),
      error && /* @__PURE__ */ jsx99("div", { className: "pointer-events-none absolute end-3 top-1/2 -translate-y-1/2", children: /* @__PURE__ */ jsx99(
        Icon,
        {
          name: "CircleAlert",
          size: 16,
          "aria-hidden": true,
          className: "text-error-primary"
        }
      ) })
    ] }),
    desktopPopup,
    !isDesktop && /* @__PURE__ */ jsxs75(
      SidePanel,
      {
        isOpen: open,
        onOpenChange: (next) => next ? openPicker() : closeWithoutApplying(),
        position: "right",
        width: "xs",
        title: t("dateRangePicker.mobileTitle"),
        showCloseButton: true,
        showHeaderDivider: false,
        headerClassName: "py-3xl",
        testID: testID ? `${testID}-drawer` : "date-range-picker-drawer",
        contentClassName: "flex h-full flex-col p-0",
        children: [
          body,
          footer
        ]
      }
    ),
    !error && hintText && /* @__PURE__ */ jsx99(
      "p",
      {
        id: `${id}-hint`,
        "data-testid": `${testID}-hint`,
        className: cn(
          "text-xs font-normal font-sans",
          disabled ? "text-disabled" : "text-quaternary"
        ),
        children: hintText
      }
    ),
    error && errorMessage && /* @__PURE__ */ jsx99(
      "p",
      {
        id: `${id}-error`,
        "data-testid": `${testID}-error`,
        className: "text-error-primary text-xs font-normal font-sans",
        children: errorMessage
      }
    )
  ] });
};

// src/components/organisms/Header.tsx
import { Suspense } from "react";
import { useTranslation as useTranslation43 } from "@tasama/i18n";
import { jsx as jsx100, jsxs as jsxs76 } from "react/jsx-runtime";
var Header = ({
  firstName,
  companyImageSrc,
  companyImageAlt,
  welcomeText,
  className,
  testID = "header-organism",
  showLogo = true,
  ...rest
}) => {
  const { t } = useTranslation43("common");
  const defaultCompanyImageAlt = t("header.companyImageAlt");
  const defaultWelcomeText = t("header.welcomeText");
  return /* @__PURE__ */ jsxs76(
    "div",
    {
      className: cn("w-full flex max-w-[1600px]", className),
      "data-testid": testID,
      ...rest,
      children: [
        /* @__PURE__ */ jsx100(
          "div",
          {
            className: "flex flex-row items-center gap-3 flex-1",
            "data-testid": `${testID}-welcome-block`,
            children: /* @__PURE__ */ jsxs76(
              "h1",
              {
                className: "text-fg-primary font-display font-medium text-[24px] leading-[32px] lg:text-[30px] lg:leading-[38px] lg:font-normal",
                "data-testid": `${testID}-welcome-text`,
                children: [
                  welcomeText || defaultWelcomeText,
                  " ",
                  /* @__PURE__ */ jsx100("span", { className: "capitalize", children: firstName })
                ]
              }
            )
          }
        ),
        showLogo ? /* @__PURE__ */ jsx100(
          "div",
          {
            className: "flex items-center",
            "data-testid": `${testID}-company-image-block`,
            children: /* @__PURE__ */ jsx100("div", { className: "w-10 h-10 rounded-full border border-brand-tint-300 bg-background flex items-center justify-center overflow-hidden", children: /* @__PURE__ */ jsx100(
              ProfileLogo,
              {
                src: companyImageSrc,
                alt: companyImageAlt || defaultCompanyImageAlt,
                testID: `${testID}-company-image`
              }
            ) })
          }
        ) : null
      ]
    }
  );
};
Header.displayName = "Header";
function HeaderWithBoundary(props) {
  return /* @__PURE__ */ jsxs76(Suspense, { fallback: /* @__PURE__ */ jsx100("div", { style: { height: 88 } }), children: [
    " ",
    /* @__PURE__ */ jsx100(Header, { ...props })
  ] });
}

// src/components/organisms/Accordion.tsx
import { useState as useState24, useCallback as useCallback15 } from "react";
import { useTranslation as useTranslation44 } from "@tasama/i18n";
import { jsx as jsx101 } from "react/jsx-runtime";
var Accordion = ({
  items,
  type = "single",
  collapsible = false,
  defaultValue,
  value: controlledValue,
  onValueChange,
  className,
  testID,
  itemClassName,
  disabled = false,
  ref,
  ...props
}) => {
  const { t } = useTranslation44("common");
  const getInitialValue = () => {
    if (defaultValue !== void 0) {
      return defaultValue;
    }
    if (type === "single") {
      const defaultItem = items.find((item) => item.defaultExpanded);
      return defaultItem ? defaultItem.id : collapsible ? "" : items[0]?.id || "";
    } else {
      return items.filter((item) => item.defaultExpanded).map((item) => item.id);
    }
  };
  const [internalValue, setInternalValue] = useState24(
    getInitialValue
  );
  const currentValue = controlledValue !== void 0 ? controlledValue : internalValue;
  const handleValueChange = useCallback15(
    (newValue) => {
      if (controlledValue === void 0) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [controlledValue, onValueChange]
  );
  const handleItemToggle = useCallback15(
    (itemId, expanded) => {
      if (disabled) return;
      if (type === "single") {
        if (expanded) {
          handleValueChange(itemId);
        } else if (collapsible) {
          handleValueChange("");
        }
      } else {
        const currentArray = Array.isArray(currentValue) ? currentValue : [];
        if (expanded) {
          const newValue = [...currentArray, itemId];
          handleValueChange(newValue);
        } else {
          const newValue = currentArray.filter((id) => id !== itemId);
          handleValueChange(newValue);
        }
      }
    },
    [type, collapsible, currentValue, handleValueChange, disabled]
  );
  const isItemExpanded = useCallback15(
    (itemId) => {
      if (type === "single") {
        return currentValue === itemId;
      } else {
        return Array.isArray(currentValue) && currentValue.includes(itemId);
      }
    },
    [type, currentValue]
  );
  return /* @__PURE__ */ jsx101(
    "div",
    {
      ref,
      className: cn(
        // Base styles using design tokens
        "w-full",
        // Container with proper spacing
        "space-y-0",
        // Ensure max content width for horizontal layouts
        "max-w-full",
        disabled && "pointer-events-none opacity-50",
        className
      ),
      "data-testid": testID,
      role: "region",
      "aria-label": t("accordion.ariaLabel"),
      ...props,
      children: items.map((item, _index) => /* @__PURE__ */ jsx101(
        AccordionItem,
        {
          id: item.id,
          trigger: item.trigger,
          description: item.description ?? "",
          content: item.content,
          isExpanded: isItemExpanded(item.id),
          onExpandedChange: (expanded) => handleItemToggle(item.id, expanded),
          disabled: disabled || item.disabled,
          className: cn(
            // Ensure proper borders between items
            itemClassName
          ),
          testID: testID ? `${testID}-item-${item.id}` : `accordion-item-${item.id}`,
          triggerProps: item.triggerProps,
          contentProps: item.contentProps
        },
        item.id
      ))
    }
  );
};

// src/components/organisms/AccordionGroup.tsx
import { jsx as jsx102, jsxs as jsxs77 } from "react/jsx-runtime";
var AccordionRow = ({
  label,
  value,
  className
}) => /* @__PURE__ */ jsxs77(
  "div",
  {
    className: cn(
      "flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0",
      className
    ),
    children: [
      /* @__PURE__ */ jsx102("span", { className: "text-sm font-medium text-gray-700", children: label }),
      /* @__PURE__ */ jsx102("span", { className: "text-sm text-gray-600", children: value })
    ]
  }
);
var AccordionGroup = ({
  items,
  showAsRows = false,
  className,
  ref,
  ...accordionProps
}) => {
  const transformedItems = items.map((item) => {
    let content = item.content;
    if (showAsRows && item.rows && item.rows.length > 0) {
      content = /* @__PURE__ */ jsx102("div", { className: "space-y-0", children: item.rows.map((row, index) => /* @__PURE__ */ jsx102(
        AccordionRow,
        {
          label: row.label,
          value: row.value,
          className: row.className
        },
        index
      )) });
    }
    return {
      id: item.id,
      trigger: item.trigger,
      content,
      disabled: item.disabled,
      defaultExpanded: item.defaultExpanded
    };
  });
  return /* @__PURE__ */ jsx102(
    Accordion,
    {
      ref,
      items: transformedItems,
      className: cn(
        // Additional styling for grouped accordions
        "space-y-2",
        // Add space between accordion groups if needed
        className
      ),
      ...accordionProps
    }
  );
};

// src/components/organisms/RefreshPage.tsx
import { RotateCw as RotateCw2 } from "lucide-react";
import { useTranslation as useTranslation45 } from "@tasama/i18n";
import { jsx as jsx103 } from "react/jsx-runtime";
var RefreshPage = ({
  testID = "refresh-page-modal"
}) => {
  const { t } = useTranslation45("common");
  const { isOpen, close } = useRefreshPageStore();
  return /* @__PURE__ */ jsx103(Modal, { size: "xl", isOpen, onClose: close, testID, children: /* @__PURE__ */ jsx103(
    Modal.Header,
    {
      title: t("errors.refreshPage"),
      description: t("errors.unableToComplete"),
      type: "left",
      icon: /* @__PURE__ */ jsx103("div", { className: "w-12 h-12 rounded-full bg-brand-green-500 flex items-center justify-center", children: /* @__PURE__ */ jsx103(RotateCw2, { className: "text-white", strokeWidth: 1 }) }),
      showCloseButton: true,
      className: "pb-6"
    }
  ) });
};

// src/components/organisms/Services.tsx
import { useTranslation as useTranslation46 } from "@tasama/i18n";
import { Fragment as Fragment13, jsx as jsx104, jsxs as jsxs78 } from "react/jsx-runtime";
var Services = ({
  title,
  subtitle,
  statusCounters,
  serviceCards,
  viewAllText,
  viewAllIcon = "ArrowRight",
  onViewAllClick,
  onServiceCardClick,
  loading = false,
  error,
  emptyHeading,
  testID,
  className,
  ...props
}) => {
  const { t } = useTranslation46("common");
  const resolvedEmptyHeading = emptyHeading ?? t("services.emptyHeading");
  return /* @__PURE__ */ jsxs78(
    "div",
    {
      className: cn(
        // `@container` makes this card a container query root so its
        // children (status counters, header) can respond to the card's
        // own width rather than the viewport width. Required because
        // the card's width depends on the row layout above it, not on
        // the viewport directly.
        "@container flex flex-col gap-[20px] lg:gap-3xl p-[20px] lg:p-3xl bg-white rounded-[16px] lg:rounded-2xl max-w-[1200px] w-full",
        className
      ),
      "data-testid": testID,
      ...props,
      children: [
        /* @__PURE__ */ jsxs78("div", { className: "flex justify-between items-baseline gap-lg w-full", children: [
          /* @__PURE__ */ jsxs78("div", { className: "flex flex-col gap-xs", children: [
            /* @__PURE__ */ jsx104("h2", { className: "text-xl font-normal leading-[1.5] text-gray-900", children: title }),
            /* @__PURE__ */ jsx104("p", { className: "text-xs font-light leading-[1.5] text-gray-500", children: subtitle })
          ] }),
          /* @__PURE__ */ jsx104(
            Button,
            {
              variant: "quaternary",
              size: "md",
              iconName: viewAllIcon,
              onClick: onViewAllClick,
              testID: `${testID}-view-all-button`,
              label: viewAllText,
              className: "-translate-y-1"
            }
          )
        ] }),
        !loading && !error && /* @__PURE__ */ jsx104("div", { className: "flex flex-col @[480px]:flex-row justify-stretch items-stretch gap-[var(--spacing-lg)] @[480px]:gap-[var(--spacing-xl)] w-full", children: statusCounters.map((counter, index) => /* @__PURE__ */ jsx104(
          StatusCounter,
          {
            count: counter.count,
            label: counter.label,
            variant: counter.variant,
            testID: `${testID}-status-counter-${index}`,
            className: "flex-1"
          },
          `${testID}-status-${index}`
        )) }),
        /* @__PURE__ */ jsx104("div", { className: "flex flex-col gap-[var(--spacing-xl)] w-full", children: loading ? (
          // Loading state
          /* @__PURE__ */ jsxs78(Fragment13, { children: [
            /* @__PURE__ */ jsx104("div", { className: "h-24 bg-gray-100 animate-pulse rounded-xl" }),
            /* @__PURE__ */ jsx104("div", { className: "h-24 bg-gray-100 animate-pulse rounded-xl" })
          ] })
        ) : error ? (
          // Error state
          /* @__PURE__ */ jsx104("div", { className: "p-6 bg-error-50 text-error-700 rounded-xl", children: t("services.failedToLoad", { error }) })
        ) : serviceCards?.length > 0 ? (
          // Services list
          serviceCards.map((service) => /* @__PURE__ */ jsx104(
            ServiceCard,
            {
              functionTitle: service.functionTitle,
              serviceCategory: service.serviceCategory,
              ...service.serviceName !== void 0 && {
                serviceName: service.serviceName
              },
              currentStep: service.currentStep,
              totalSteps: service.totalSteps,
              stepDescription: service.stepDescription,
              status: service.status,
              statusText: service.statusText,
              iconName: service.iconName,
              showTitleIcon: service.showTitleIcon || false,
              showBadgeIcon: service.showBadgeIcon || false,
              requestId: service.requestId,
              serviceBadges: service.serviceBadges,
              testID: `${testID}-service-card-${service.id}`,
              onClick: () => onServiceCardClick?.(service.id)
            },
            service.id
          ))
        ) : (
          // Empty state
          /* @__PURE__ */ jsx104("div", { className: "border border-brand-tint-300 p-6 rounded-2xl", children: /* @__PURE__ */ jsx104(EmptyScreen, { iconName: "BookOpen", heading: resolvedEmptyHeading }) })
        ) })
      ]
    }
  );
};
Services.displayName = "Services";

// src/components/organisms/MilestoneStepper.tsx
import * as React41 from "react";
import { Fragment as Fragment14, jsx as jsx105, jsxs as jsxs79 } from "react/jsx-runtime";
var MilestoneStepper = ({
  headerText,
  stages,
  currentStepId,
  selectedStepId,
  accordionValue,
  onAccordionChange,
  stepsClickable = false,
  onStepClick,
  showShadow = true,
  className,
  testID,
  ref,
  ...props
}) => {
  const stageWithCurrentStep = React41.useMemo(() => {
    for (const stage of stages) {
      const hasActiveStep = stage.subItems?.some(
        (item) => item.waiting || item.current || item.id === currentStepId
      );
      if (hasActiveStep) {
        return stage.id;
      }
    }
    return null;
  }, [stages, currentStepId]);
  const getStepperData = React41.useCallback(
    (stage) => {
      if (!stage.subItems) return { steps: [], currentStep: 0 };
      const steps = stage.subItems.map((item) => ({
        id: item.id,
        label: item.label,
        waiting: item.waiting ?? false,
        ...item.title !== void 0 && { title: item.title },
        ...item.description !== void 0 && {
          description: item.description
        },
        completed: item.completed ?? false,
        current: item.current || item.id === currentStepId,
        disabled: item.disabled ?? false,
        selected: item.id === selectedStepId
      }));
      const waitingIndex = steps.findIndex((step) => step.waiting);
      if (waitingIndex !== -1) {
        const frozenSteps = steps.map(
          (step, index) => index >= waitingIndex ? { ...step, completed: false, current: false } : step
        );
        return { steps: frozenSteps, currentStep: waitingIndex - 1 };
      }
      const currentStepIndex = steps.findIndex(
        (step) => step.current || step.id === currentStepId
      );
      return {
        steps,
        currentStep: currentStepIndex >= 0 ? currentStepIndex : -1
      };
    },
    [currentStepId, selectedStepId]
  );
  const prevAccordionValueRef = React41.useRef(
    accordionValue
  );
  const handleAccordionChange = React41.useCallback(
    (newValue) => {
      const prevValue = prevAccordionValueRef.current;
      prevAccordionValueRef.current = newValue;
      onAccordionChange?.(newValue);
      if (!stepsClickable || !onStepClick) return;
      const expandedId = Array.isArray(newValue) ? newValue[0] : newValue;
      const prevId = Array.isArray(prevValue) ? prevValue?.[0] : prevValue;
      if (!expandedId || expandedId === prevId) return;
      const stage = stages.find((s) => s.id === expandedId);
      if (!stage?.subItems?.length) return;
      const { steps, currentStep } = getStepperData(stage);
      if (!steps.length) return;
      const targetIndex = currentStep >= 0 ? currentStep : 0;
      const subItem = stage.subItems[targetIndex];
      if (subItem) onStepClick(stage.id, targetIndex, subItem);
    },
    [onAccordionChange, stepsClickable, onStepClick, stages, getStepperData]
  );
  const accordionItems = stages.map((stage) => {
    const { steps, currentStep } = getStepperData(stage);
    return {
      id: stage.id,
      trigger: /* @__PURE__ */ jsxs79("div", { className: "flex items-center gap-3 w-full min-w-0", children: [
        /* @__PURE__ */ jsx105(
          Text,
          {
            size: "sm",
            weight: "medium",
            className: "text-brand-blue-500 font-medium flex-shrink-0",
            children: stage.stageLabel
          }
        ),
        /* @__PURE__ */ jsxs79("div", { children: [
          /* @__PURE__ */ jsx105(
            Text,
            {
              size: "sm",
              weight: "medium",
              className: "flex-1 text-brand-blue-500 font-medium min-w-0 truncate",
              children: stage.title
            }
          ),
          stage.description && /* @__PURE__ */ jsx105("div", { className: "text-xs font-normal text-brand-blue-500 mt-1 truncate", children: stage.description })
        ] })
      ] }),
      content: steps.length > 0 ? /* @__PURE__ */ jsx105("div", { className: "py-4 pb-0", children: /* @__PURE__ */ jsx105(
        Stepper,
        {
          steps,
          currentStep,
          variant: "light",
          clickable: stepsClickable,
          showShadow,
          onStepClick: stepsClickable && onStepClick ? (stepIndex) => {
            const subItem = stage.subItems?.[stepIndex];
            if (subItem) onStepClick(stage.id, stepIndex, subItem);
          } : void 0,
          testID: testID ? `${testID}-${stage.id}-stepper` : void 0
        }
      ) }) : null,
      defaultExpanded: stage.id === stageWithCurrentStep,
      disabled: stage.disabled,
      triggerProps: {
        className: cn(
          "py-2 px-3 bg-brand-blue-50 hover:bg-brand-blue-100 rounded-sm h-auto",
          "focus-visible:ring-2 focus-visible:ring-ring"
        ),
        icon: /* @__PURE__ */ jsx105(Icon, { name: "ChevronDown", className: "h-4 w-4 text-fg-brand-primary" })
      },
      contentProps: {
        className: "bg-transparent border-0",
        innerClassName: "pb-0 px-0"
      }
    };
  });
  if (stages.length === 1) {
    const stage = stages[0];
    const { steps, currentStep } = getStepperData(stage);
    return /* @__PURE__ */ jsx105(Fragment14, { children: /* @__PURE__ */ jsxs79(
      "div",
      {
        ref,
        className: cn(
          "flex flex-col gap-4 p-6 bg-bg-tertiary rounded-lg",
          className
        ),
        "data-testid": testID,
        ...props,
        children: [
          headerText && /* @__PURE__ */ jsx105(
            Text,
            {
              size: "sm",
              weight: "normal",
              className: "text-gray-900 leading-relaxed",
              children: headerText
            }
          ),
          stage.stageLabel && /* @__PURE__ */ jsxs79("div", { className: "flex items-center gap-3 mb-2", children: [
            /* @__PURE__ */ jsx105(
              Text,
              {
                size: "sm",
                weight: "medium",
                className: "text-brand-blue-500 font-medium",
                children: stage.stageLabel
              }
            ),
            /* @__PURE__ */ jsx105(
              Text,
              {
                size: "sm",
                weight: "medium",
                className: "text-gray-900 font-medium",
                children: stage.title
              }
            )
          ] }),
          steps.length > 0 && /* @__PURE__ */ jsx105(
            Stepper,
            {
              steps,
              currentStep,
              variant: "light",
              clickable: stepsClickable,
              showShadow,
              onStepClick: stepsClickable && onStepClick ? (stepIndex) => {
                const subItem = stage.subItems?.[stepIndex];
                if (subItem) onStepClick(stage.id, stepIndex, subItem);
              } : void 0,
              testID: testID ? `${testID}-stepper` : void 0
            }
          )
        ]
      }
    ) });
  }
  return /* @__PURE__ */ jsx105(Fragment14, { children: /* @__PURE__ */ jsxs79(
    "div",
    {
      ref,
      className: cn("flex flex-col gap-4 p-6 bg-gray-50", className),
      "data-testid": testID,
      ...props,
      children: [
        headerText && /* @__PURE__ */ jsx105(
          Text,
          {
            size: "sm",
            weight: "normal",
            className: "text-gray-900 leading-relaxed",
            children: headerText
          }
        ),
        /* @__PURE__ */ jsx105(
          Accordion,
          {
            items: accordionItems,
            type: "single",
            itemClassName: "bg-transparent border-0 first:border-0 last:border-0 mb-xl",
            collapsible: true,
            value: accordionValue,
            onValueChange: handleAccordionChange,
            className: "overflow-hidden bg-transparent",
            testID: testID ? `${testID}-accordion` : void 0
          }
        )
      ]
    }
  ) });
};

// src/components/organisms/PageLoader.tsx
import { useTranslation as useTranslation47 } from "@tasama/i18n";
import { jsx as jsx106 } from "react/jsx-runtime";
var PageLoader = ({
  testID = "page-loader"
}) => {
  const { t } = useTranslation47("common");
  const { isVisible, label } = usePageLoaderStore();
  const resolvedLabel = label || t("labels.loading");
  if (!isVisible) return null;
  return /* @__PURE__ */ jsx106(
    "div",
    {
      "data-testid": testID,
      className: "fixed inset-0 flex items-center justify-center backdrop-blur-lg",
      style: {
        backgroundColor: "var(--color-alpha-black-70)",
        zIndex: "var(--z-page-loader)"
      },
      role: "dialog",
      "aria-modal": "true",
      "aria-label": t("labels.loading"),
      children: /* @__PURE__ */ jsx106(
        Loader2,
        {
          size: "xl",
          color: "var(--color-gray-200)",
          labelClassname: "text-[var(--color-white)]",
          showLabel: true,
          label: resolvedLabel,
          testID: `${testID}-spinner`
        }
      )
    }
  );
};

// src/components/organisms/SomeThingWentWrongModal.tsx
import { Info as Info2 } from "lucide-react";
import { useTranslation as useTranslation48 } from "@tasama/i18n";
import { jsx as jsx107, jsxs as jsxs80 } from "react/jsx-runtime";
var SomeThingWentWrongModal = ({
  testID = "something-went-wrong-modal"
}) => {
  const { t } = useTranslation48("common");
  const { isOpen, close, handleTryAgain } = useSomeThingWentWrongModalStore();
  return /* @__PURE__ */ jsxs80(Modal, { size: "xl", isOpen, onClose: close, testID, children: [
    /* @__PURE__ */ jsx107(
      Modal.Header,
      {
        title: t("errors.somethingWentWrong"),
        description: t("errors.somethingWentWrongDescription"),
        type: "left",
        icon: /* @__PURE__ */ jsx107("div", { className: "w-12 h-12 rounded-full bg-brand-green-500 flex items-center justify-center", children: /* @__PURE__ */ jsx107(Info2, { className: "text-white", strokeWidth: 1 }) })
      }
    ),
    /* @__PURE__ */ jsx107(
      Modal.Footer,
      {
        divider: true,
        alignment: "right",
        actions: [
          {
            label: t("errors.tryAgain").toUpperCase(),
            variant: "secondary",
            onClick: handleTryAgain,
            ctaIcon: "RotateCw",
            iconPosition: "trailing"
          }
        ]
      }
    )
  ] });
};
var SomeThingWentWrongModal_default = SomeThingWentWrongModal;

// src/components/organisms/businessAdvisorAi/components/modalContent/Suggestions.tsx
import { useTranslation as useTranslation49 } from "@tasama/i18n";
import { jsx as jsx108 } from "react/jsx-runtime";
var Suggestions = ({
  suggestions,
  dynamicCta = [],
  testID,
  className,
  onSuggestionClick,
  onCtaClick,
  isAuthenticated = false,
  variant: _variant = "home"
}) => {
  const { t } = useTranslation49("common");
  const itemsToShow = dynamicCta.length > 0 ? dynamicCta : suggestions.map((s) => ({
    label: s,
    url: "",
    type: "SUGGESTION"
  }));
  if (!itemsToShow?.length) return null;
  const handleItemClick = (item) => {
    if (dynamicCta.length > 0) {
      if (onCtaClick) {
        onCtaClick(item);
        return;
      }
      if (item.url) {
        if (isAuthenticated) {
          window.location.href = item.url;
        } else {
          window.open(item.url, "_blank", "noopener,noreferrer");
        }
      }
    } else {
      onSuggestionClick?.(item.label);
    }
  };
  return /* @__PURE__ */ jsx108(
    "ul",
    {
      className: cn("flex flex-col gap-lg p-0 m-0", className),
      "data-testid": testID || "suggestions-list",
      children: itemsToShow.map((item, idx) => /* @__PURE__ */ jsx108("li", { className: "list-none w-fit", children: /* @__PURE__ */ jsx108(
        "button",
        {
          type: "button",
          className: cn(
            "h-11 rounded-lg bg-bg-secondary px-4 py-3",
            "flex items-center font-sans cursor-pointer transition-colors hover:bg-gray-200",
            "border border-brand-tint-100",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          ),
          onClick: () => handleItemClick(item),
          "aria-label": `${dynamicCta.length > 0 ? t("businessAdvisor.aria.cta") : t("businessAdvisor.aria.suggestion")}: ${item.label}`,
          "data-testid": `${testID || "suggestions"}-item-${idx}`,
          children: /* @__PURE__ */ jsx108(
            Text,
            {
              className: "hover:text-brand-blue-500 transition-colors",
              size: "sm",
              weight: "light",
              color: "primary",
              children: item.label
            }
          )
        }
      ) }, idx))
    }
  );
};
Suggestions.displayName = "Suggestions";
var Suggestions_default = Suggestions;

// src/components/organisms/businessAdvisorAi/components/modalContent/AdvisorHeader.tsx
import { useTranslation as useTranslation50, useDirection as useDirection6 } from "@tasama/i18n";
import { jsx as jsx109, jsxs as jsxs81 } from "react/jsx-runtime";
var AdvisorHeader = ({
  title,
  description,
  testID,
  className,
  isExpanded = false,
  onToggleExpand,
  onClose
}) => {
  const { t } = useTranslation50("common");
  const dir = useDirection6();
  const showSplitTitle = dir === "rtl" && !isExpanded;
  return /* @__PURE__ */ jsxs81(
    "div",
    {
      className: cn(
        "relative overflow-hidden",
        "w-full h-full",
        "flex justify-between items-start",
        "rounded-t-4xl",
        "bg-brand-blue-950",
        className
      ),
      "data-testid": testID || "advisor-header",
      children: [
        /* @__PURE__ */ jsxs81("div", { className: "w-full h-full absolute flex justify-center items-end", children: [
          /* @__PURE__ */ jsx109(
            "img",
            {
              src: "/assets/images/ba_ai_header_bg_v1.gif",
              alt: "",
              "aria-hidden": "true",
              className: "inset-0 w-full h-full object-contain"
            }
          ),
          /* @__PURE__ */ jsx109(
            "img",
            {
              src: "/assets/images/ba_ai_header_human.png",
              alt: "",
              "aria-hidden": "true",
              className: "absolute w-[120px] h-[94px] object-contain"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs81(
          "div",
          {
            className: cn(
              "relative z-10 flex flex-col gap-xs flex-1",
              showSplitTitle ? "px-3xl py-xl" : "p-3xl"
            ),
            children: [
              showSplitTitle ? /* @__PURE__ */ jsxs81("div", { className: "font-light font-sans tracking-tight mb-xxs text-brand-green-500 leading-none", children: [
                /* @__PURE__ */ jsx109("span", { className: "text-2xl block leading-tight", children: t("businessAdvisor.headerTitleLine1") }),
                /* @__PURE__ */ jsx109("span", { className: "text-sm block", children: t("businessAdvisor.headerTitleLine2") })
              ] }) : /* @__PURE__ */ jsx109(
                Heading,
                {
                  level: 1,
                  size: "2xl",
                  weight: "light",
                  className: "font-sans tracking-tight mb-xxs text-brand-green-500",
                  children: title
                }
              ),
              /* @__PURE__ */ jsx109(
                Text,
                {
                  size: "sm",
                  weight: "light",
                  leading: "tight",
                  className: "text-fg-quaternary",
                  children: description
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs81("div", { className: "relative z-10 flex-1 flex justify-end items-center gap-sm p-3xl", children: [
          onToggleExpand && /* @__PURE__ */ jsx109(
            Button,
            {
              onClick: onToggleExpand,
              testID: "advisor-toggle-btn",
              className: "!p-0 flex items-center justify-center w-[30px] h-[30px] bg-alpha-white-10 border-none hover:bg-alpha-white-30 hover:border-none",
              "aria-label": isExpanded ? t("businessAdvisor.collapseModal") : t("businessAdvisor.expandModal"),
              iconPosition: "none",
              children: /* @__PURE__ */ jsx109(
                Icon,
                {
                  name: isExpanded ? "ChevronsRightLeft" : "ChevronsLeftRight",
                  size: 16,
                  className: "text-white"
                }
              )
            }
          ),
          /* @__PURE__ */ jsx109(
            Button,
            {
              onClick: onClose,
              testID: "advisor-close-btn",
              className: "flex items-center justify-center p-0 bg-transparent w-[30px] h-[30px] bg-brand-green-500 hover:bg-brand-green-700 border-none hover:border-none",
              "aria-label": t("businessAdvisor.closeModal"),
              children: /* @__PURE__ */ jsx109(Icon, { name: "X", size: 16, className: "text-white" })
            }
          )
        ] })
      ]
    }
  );
};
AdvisorHeader.displayName = "AdvisorHeader";
var AdvisorHeader_default = AdvisorHeader;

// src/components/organisms/businessAdvisorAi/components/modalContent/AdvisorModal.tsx
import { useCallback as useCallback17, useState as useState25 } from "react";
import { useTranslation as useTranslation52 } from "react-i18next";

// src/components/organisms/businessAdvisorAi/components/modalContent/AdvisorMessageInput.tsx
import { useRef as useRef19, useEffect as useEffect19 } from "react";
import { useTranslation as useTranslation51 } from "@tasama/i18n";

// src/components/organisms/businessAdvisorAi/constants/constants.ts
var CurrentPage = /* @__PURE__ */ ((CurrentPage2) => {
  CurrentPage2["ONBOARDING_COMPANY_TYPE"] = "ONBOARDING_COMPANY_TYPE";
  CurrentPage2["ONBOARDING_YOUR_DETAILS"] = "ONBOARDING_YOUR_DETAILS";
  CurrentPage2["ONBOARDING_UPLOAD_DOCUMENTS"] = "ONBOARDING_UPLOAD_DOCUMENTS";
  CurrentPage2["ONBOARDING_SERVICE_NEEDS"] = "ONBOARDING_SERVICE_NEEDS";
  CurrentPage2["ONBOARDING_COMPANY_DETAILS"] = "ONBOARDING_COMPANY_DETAILS";
  CurrentPage2["LOGIN"] = "LOGIN";
  CurrentPage2["PORTAL"] = "PORTAL";
  CurrentPage2["PORTAL_HOME"] = "PORTAL_HOME";
  CurrentPage2["PORTAL_SERVICE_CATALOGUE"] = "PORTAL_SERVICE_CATALOGUE";
  CurrentPage2["PORTAL_SUPPORT_TICKETS"] = "PORTAL_SUPPORT_TICKETS";
  CurrentPage2["PORTAL_MY_SERVICES"] = "PORTAL_MY_SERVICES";
  CurrentPage2["PORTAL_NOTIFICATIONS"] = "PORTAL_NOTIFICATIONS";
  CurrentPage2["PORTAL_MY_ACCOUNT"] = "PORTAL_MY_ACCOUNT";
  CurrentPage2["KYB"] = "KYB";
  CurrentPage2["NDA"] = "NDA";
  CurrentPage2["MSA"] = "MSA";
  CurrentPage2["SR_ACTIVITIES"] = "SR_ACTIVITIES";
  CurrentPage2["ACTIVITY_REQUEST_FORM"] = "ACTIVITY_REQUEST_FORM";
  return CurrentPage2;
})(CurrentPage || {});
var CompanyType = /* @__PURE__ */ ((CompanyType2) => {
  CompanyType2["KSA_PRIVATE"] = "KSA_PRIVATE";
  CompanyType2["PIF_ESTABLISHED"] = "PIF_ESTABLISHED";
  CompanyType2["PIF_PROJECT"] = "PIF_PROJECT";
  CompanyType2["INTERNATIONAL"] = "INTERNATIONAL";
  CompanyType2["ANONYMOUS"] = "ANONYMOUS";
  return CompanyType2;
})(CompanyType || {});
var isValidCurrentPage = (page) => {
  return Object.values(CurrentPage).includes(page);
};
var isValidCompanyType = (type) => {
  return Object.values(CompanyType).includes(type);
};
var getCurrentPageValues = () => {
  return Object.values(CurrentPage);
};
var getCompanyTypeValues = () => {
  return Object.values(CompanyType);
};
var BUSINESS_AI_BUTTON_IMG_SRC = "/assets/images/businessAiButton.svg";
var BUTTON_TEST_ID = "business-ai-button";
var HEADER_TITLE = "SAMA AI";
var HEADER_DESCRIPTION = "Powered by Tasama";
var DELAYS = {
  SCROLL_AFTER_MESSAGES: 100,
  // ensure DOM paint before scroll
  SCROLL_RETRY_SHORT: 50,
  // first retry scroll pass
  SCROLL_RETRY_LONG: 150,
  // second retry scroll pass
  MODAL_CLOSE_ANIMATION: 250,
  // modal slide-out animation
  RENDERING_INDICATOR: 500,
  // hide rendering indicator after receiving a message
  TIMESTAMP_THRESHOLD: 3e5
  // 5 minutes — show timestamp divider between messages
};
var MAX_CHARACTERS = 2e3;
var WARNING_LIMIT = 1500;

// src/components/organisms/businessAdvisorAi/components/modalContent/AdvisorMessageInput.tsx
import { Fragment as Fragment15, jsx as jsx110, jsxs as jsxs82 } from "react/jsx-runtime";
var AdvisorMessageInput = ({
  textAreaValue,
  onTextAreaChange,
  onSend,
  bottomIconName,
  loading,
  disabled = false,
  testID
}) => {
  const { t } = useTranslation51("common");
  const currentLength = textAreaValue.length;
  const isAtLimit = currentLength >= WARNING_LIMIT;
  const textareaRef = useRef19(null);
  const handleSend = () => {
    onSend();
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !disabled) {
      e.preventDefault();
      onSend();
    }
  };
  useEffect19(() => {
    const updateBorderRadius = () => {
      const textarea2 = textareaRef.current;
      if (!textarea2) return;
      const height = textarea2.offsetHeight;
      textarea2.style.setProperty("--textarea-height", `${height}px`);
    };
    updateBorderRadius();
    const textarea = textareaRef.current;
    if (textarea && window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(updateBorderRadius);
      resizeObserver.observe(textarea);
      return () => {
        resizeObserver.disconnect();
      };
    } else {
      const timeoutId = setTimeout(updateBorderRadius, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [textAreaValue]);
  return /* @__PURE__ */ jsxs82(Fragment15, { children: [
    /* @__PURE__ */ jsx110(
      "div",
      {
        className: "mt-auto transition-colors [border-radius:min(var(--radius-4xl),calc(var(--radius-4xl)+(var(--textarea-height,var(--spacing-6xl))-var(--spacing-6xl))*-0.1))]",
        "data-testid": testID || "advisor-message-input",
        children: /* @__PURE__ */ jsxs82("div", { className: "relative", children: [
          /* @__PURE__ */ jsx110(AISparkleIcon, { className: "absolute ml-4 bottom-3 w-6 h-6 z-10 pointer-events-none" }),
          /* @__PURE__ */ jsx110(
            Textarea,
            {
              ref: textareaRef,
              value: textAreaValue,
              onChange: (e) => !disabled && onTextAreaChange(e.target.value),
              onKeyDown: handleKeyDown,
              disabled,
              testID: "advisor-modal-textarea",
              uiSize: "xs",
              placeholder: t("businessAdvisor.inputPlaceholder"),
              autoResize: true,
              hasCustomBorderRadius: true,
              autoResizeOptions: {
                minHeight: 48,
                // xs size
                maxHeight: 360,
                // Updated to 360px as requested
                heightOffset: 0
                // Remove extra offset to ensure exact 48px start
              },
              className: "w-full py-3 ps-11 pe-14 focus:outline-none cursor-pointer hover:bg-gray-300 focus:bg-gray-37 focus:ring-0 focus:border-0 resize-none !border-0 bg-gray-37 overflow-hidden [border-radius:min(var(--radius-4xl),calc(var(--radius-4xl)+(var(--textarea-height,var(--spacing-6xl))-var(--spacing-6xl))*-0.1))] placeholder:font-light",
              "aria-label": t("businessAdvisor.inputAriaLabel")
            }
          ),
          /* @__PURE__ */ jsx110(
            Button,
            {
              onClick: handleSend,
              disabled,
              testID: "advisor-close-btn",
              loading,
              className: "absolute flex items-center justify-center p-0 bg-transparent w-[30px] h-[30px] end-3 top-1/2 -translate-y-1/2 bg-brand-green-500 hover:bg-brand-green-700 border-none hover:border-none",
              "aria-label": t("businessAdvisor.sendAriaLabel"),
              children: /* @__PURE__ */ jsx110(Icon, { name: bottomIconName, size: 16, className: "text-white" })
            }
          )
        ] })
      }
    ),
    isAtLimit && /* @__PURE__ */ jsxs82("div", { className: "flex items-center justify-start mt-lg px-1", children: [
      /* @__PURE__ */ jsx110("div", { children: /* @__PURE__ */ jsx110(
        Icon,
        {
          name: "CircleAlert",
          size: 16,
          className: "text-red-500 me-xs",
          testID: "character-limit-alert-icon"
        }
      ) }),
      /* @__PURE__ */ jsx110(
        Text,
        {
          size: "xs",
          color: "secondary",
          className: "text-red-500",
          testID: "character-limit-text",
          children: t("businessAdvisor.characterLimit", {
            max: MAX_CHARACTERS,
            current: currentLength
          })
        }
      )
    ] })
  ] });
};
AdvisorMessageInput.displayName = "AdvisorMessageInput";
var AdvisorMessageInput_default = AdvisorMessageInput;

// src/components/organisms/businessAdvisorAi/components/modalContent/AdvisorModal.tsx
import { jsx as jsx111, jsxs as jsxs83 } from "react/jsx-runtime";
var AdvisorModal = ({
  title,
  description,
  textAreaValue,
  onTextAreaChange,
  onClose,
  onSend,
  bottomIconName,
  testID,
  loading,
  hasApiError,
  className,
  isExpanded = false,
  onToggleExpand,
  scrollContainerRef,
  children,
  isClosing: externalIsClosing = false,
  messages,
  onResetConversation,
  isLoading = false,
  isAuthenticated = false
}) => {
  const { t } = useTranslation52("common");
  const [internalIsClosing, setInternalIsClosing] = useState25(false);
  const [isAnimating] = useState25(true);
  const isClosing = externalIsClosing || internalIsClosing;
  const handleCloseWithAnimation = useCallback17(() => {
    setInternalIsClosing(true);
    setTimeout(() => {
      onClose();
    }, DELAYS.MODAL_CLOSE_ANIMATION);
  }, [onClose]);
  const handleSend = () => {
    if (textAreaValue?.trim()) {
      onSend();
    }
  };
  return /* @__PURE__ */ jsxs83(
    Modal,
    {
      isOpen: true,
      onClose: handleCloseWithAnimation,
      testID: testID || "advisor-modal-root",
      overlayClassName: "items-end justify-end z-business-advisor-modal",
      className: cn(
        "rounded-4xl shadow-2xl",
        "flex flex-col h-ba-modal-height-default min-h-[350px] max-h-[calc(100dvh-160px)]",
        "mb-[85px] me-0",
        isExpanded ? "w-ba-modal-width-expanded max-w-ba-modal-width-expanded" : "w-ba-modal-width-collapsed max-w-ba-modal-width-collapsed",
        "transition-all duration-200 ease-in-out",
        isAnimating && !isClosing ? "modal-slide-in" : "",
        isClosing ? "modal-slide-out" : "",
        className
      ),
      children: [
        /* @__PURE__ */ jsx111("div", { className: "shrink-0 h-[102px]", children: /* @__PURE__ */ jsx111(
          AdvisorHeader,
          {
            title,
            description,
            isExpanded,
            onToggleExpand,
            onClose: handleCloseWithAnimation
          }
        ) }),
        /* @__PURE__ */ jsxs83(
          "div",
          {
            ref: scrollContainerRef,
            className: cn(
              "flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent",
              isExpanded ? "px-2xl" : "px-3xl"
            ),
            style: { minHeight: 0, overflowAnchor: "none" },
            children: [
              messages.length > 0 && /* @__PURE__ */ jsx111("div", { className: "sticky top-0 z-10 bg-white", children: /* @__PURE__ */ jsx111("div", { className: "flex justify-center", children: /* @__PURE__ */ jsx111(
                Button,
                {
                  className: "hover:bg-white h-auto min-h-0 p-0",
                  testID: "reset-conversation-button",
                  children: /* @__PURE__ */ jsxs83(
                    "div",
                    {
                      onClick: onResetConversation,
                      className: "flex items-center justify-center px-2 py-1 text-xs text-fg-tertiary hover:text-fg-primary hover:bg-white transition-colors cursor-pointer bg-transparent border-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded",
                      "data-testid": "reset-conversation-button-wrapper",
                      children: [
                        /* @__PURE__ */ jsx111(
                          Icon,
                          {
                            name: "RotateCcw",
                            size: 12,
                            className: "text-current me-1",
                            testID: "reset-conversation-icon"
                          }
                        ),
                        /* @__PURE__ */ jsx111(
                          Text,
                          {
                            size: "xs",
                            weight: "light",
                            color: "primary",
                            as: "span",
                            testID: "reset-conversation-text",
                            children: t("businessAdvisor.cta.restartConversation")
                          }
                        )
                      ]
                    }
                  )
                }
              ) }) }),
              children
            ]
          }
        ),
        !hasApiError && /* @__PURE__ */ jsx111(
          "div",
          {
            className: cn("shrink-0 px-3xl pt-3xl", !isAuthenticated && "pb-3xl"),
            children: /* @__PURE__ */ jsx111(
              AdvisorMessageInput,
              {
                textAreaValue,
                onTextAreaChange,
                onSend: handleSend,
                bottomIconName,
                loading,
                disabled: isLoading,
                testID: "advisor-message-input"
              }
            )
          }
        ),
        isAuthenticated && /* @__PURE__ */ jsx111("div", { className: "shrink-0 flex justify-center px-3xl pt-xs pb-3xl", children: /* @__PURE__ */ jsx111(
          Text,
          {
            size: "2xs",
            weight: "light",
            className: "text-fg-quaternary text-center",
            testID: "advisor-disclaimer-text",
            children: t("businessAdvisor.disclaimer")
          }
        ) })
      ]
    }
  );
};
AdvisorModal.displayName = "AdvisorModal";
var AdvisorModal_default = AdvisorModal;

// src/components/organisms/businessAdvisorAi/components/modalContent/AdvisorModalContent.tsx
import { useTranslation as useTranslation56 } from "@tasama/i18n";

// src/components/organisms/businessAdvisorAi/components/modalContent/AdvisorModalHomeContent.tsx
import { useTranslation as useTranslation53 } from "@tasama/i18n";
import { jsx as jsx112, jsxs as jsxs84 } from "react/jsx-runtime";
var AdvisorModalHomeContent = ({
  subtitle: _subtitle,
  suggestions,
  dynamicCta = [],
  testID,
  className,
  isExpanded = false,
  hasMessages,
  onSuggestionClick,
  onCtaClick,
  isAuthenticated = false,
  isInitializing = false,
  isFetchingMessages = false
}) => {
  const { t } = useTranslation53("common");
  return /* @__PURE__ */ jsxs84(
    "div",
    {
      className: cn(
        "flex flex-col gap-xl h-full",
        !hasMessages && "justify-center",
        className
      ),
      "data-testid": testID || "advisor-modal-home-content-wrapper",
      children: [
        /* @__PURE__ */ jsxs84(
          "div",
          {
            className: "flex flex-col items-center text-center gap-md mb-xl",
            "data-testid": `${testID}-welcome`,
            children: [
              /* @__PURE__ */ jsxs84("h2", { className: "text-center", children: [
                /* @__PURE__ */ jsx112("span", { className: "text-fg-primary font-tasama text-3xl font-light leading-3xl", children: t("businessAdvisor.greetingPrefix") }),
                /* @__PURE__ */ jsx112("span", { className: "text-brand-blue-500 font-tasama text-3xl font-medium leading-3xl", children: t("businessAdvisor.greetingHighlight") })
              ] }),
              /* @__PURE__ */ jsx112(
                "p",
                {
                  className: cn(
                    "text-center text-fg-primary font-tasama text-sm font-light leading-md",
                    isExpanded ? "w-full" : "max-w-[400px]"
                  ),
                  children: t("businessAdvisor.defaultSubtitle")
                }
              )
            ]
          }
        ),
        isInitializing || isFetchingMessages ? /* @__PURE__ */ jsx112("div", { className: "flex justify-center items-center py-xl", children: /* @__PURE__ */ jsx112(
          Loader2,
          {
            size: "md",
            label: t("businessAdvisor.initialising"),
            showLabel: true,
            testID: `${testID}-loader`
          }
        ) }) : /* @__PURE__ */ jsx112(
          "div",
          {
            className: cn("flex gap-xl"),
            "data-testid": `${testID}-suggestions-wrapper`,
            children: /* @__PURE__ */ jsx112(
              Suggestions,
              {
                suggestions: dynamicCta.length > 0 ? [] : suggestions,
                dynamicCta,
                testID: "advisor-suggestions",
                onSuggestionClick,
                onCtaClick,
                isAuthenticated,
                variant: "home"
              }
            )
          }
        )
      ]
    }
  );
};
AdvisorModalHomeContent.displayName = "AdvisorModalHomeContent";
var AdvisorModalHomeContent_default = AdvisorModalHomeContent;

// src/components/organisms/businessAdvisorAi/components/modalContent/AdvisorModalMessageContent.tsx
import { useTranslation as useTranslation55 } from "@tasama/i18n";

// src/components/organisms/businessAdvisorAi/components/modalContent/MessageItem.tsx
import { useTranslation as useTranslation54 } from "@tasama/i18n";

// src/components/organisms/businessAdvisorAi/components/Messages/MessageSent.tsx
import { Fragment as Fragment16, jsx as jsx113 } from "react/jsx-runtime";
var MessageSent = ({
  text,
  testID,
  className
}) => /* @__PURE__ */ jsx113(Fragment16, { children: /* @__PURE__ */ jsx113(
  "div",
  {
    className: cn(
      "flex justify-end w-full message-sent-animation",
      className
    ),
    "data-testid": testID || "message-sent-wrapper",
    children: /* @__PURE__ */ jsx113(
      "div",
      {
        className: cn(
          "max-w-[80%] px-lg py-md",
          "rounded-lg",
          "bg-brand-green-500"
        ),
        children: /* @__PURE__ */ jsx113(
          Text,
          {
            size: "sm",
            weight: "light",
            className: "text-start text-black break-words whitespace-pre-wrap overflow-wrap-anywhere",
            children: text
          }
        )
      }
    )
  }
) });
MessageSent.displayName = "MessageSent";
var MessageSent_default = MessageSent;

// src/components/organisms/businessAdvisorAi/utils.ts
import React44 from "react";
var formatBoldText = (text) => {
  return text.split(/(\*\*.*?\*\*)/g).map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return React44.createElement(
        "strong",
        { key: index, className: "font-semibold" },
        part.slice(2, -2)
      );
    }
    return part;
  });
};
var getCurrentTimestamp = (msg) => {
  if (msg.timestamp) return new Date(msg.timestamp);
  if (msg.sent_at) return new Date(msg.sent_at * 1e3);
  return /* @__PURE__ */ new Date();
};
var formatTimestamp = (message, locale) => {
  let timestamp;
  if (message.timestamp) {
    timestamp = message.timestamp;
  } else if (message.sent_at) {
    timestamp = new Date(message.sent_at * 1e3);
  } else {
    timestamp = /* @__PURE__ */ new Date();
  }
  const resolvedLocale = locale === "ar" ? "ar-u-nu-latn" : locale || [];
  return new Date(timestamp).toLocaleTimeString(resolvedLocale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};
var shouldShowTimestamp = (messages, currentIndex) => {
  if (currentIndex === 0) return true;
  const currentMessage = messages[currentIndex];
  const previousMessage = messages[currentIndex - 1];
  if (!currentMessage || !previousMessage) return false;
  const timeDifference = new Date(getCurrentTimestamp(currentMessage)).getTime() - new Date(getCurrentTimestamp(previousMessage)).getTime();
  return timeDifference >= DELAYS.TIMESTAMP_THRESHOLD;
};
var formatMessageTime = (timestamp) => {
  try {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "";
  }
};

// src/components/organisms/businessAdvisorAi/components/Messages/MessageReceived.tsx
import { jsx as jsx114 } from "react/jsx-runtime";
var MessageReceived = ({
  text,
  testID,
  className
}) => /* @__PURE__ */ jsx114(
  "div",
  {
    className: cn(
      "flex justify-start w-full whitespace-pre-line message-received-animation",
      className
    ),
    "data-testid": testID || "message-received-wrapper",
    children: /* @__PURE__ */ jsx114("div", { className: "w-full", children: /* @__PURE__ */ jsx114("div", { className: cn("pe-lg pt-md"), children: /* @__PURE__ */ jsx114(Text, { size: "sm", weight: "light", color: "primary", className: "text-start", children: formatBoldText(text) }) }) })
  }
);
MessageReceived.displayName = "MessageReceived";
var MessageReceived_default = MessageReceived;

// src/components/organisms/businessAdvisorAi/components/modalContent/MessageItem.tsx
import { jsx as jsx115, jsxs as jsxs85 } from "react/jsx-runtime";
var MessageItem = ({
  message,
  index,
  messages,
  nextActionTexts,
  dynamicCta,
  isCollapsed,
  hasSendMessageError,
  onSuggestionClick,
  onCtaClick,
  renderLinks,
  isAuthenticated
}) => {
  const { t, i18n: i18n2 } = useTranslation54("common");
  return /* @__PURE__ */ jsxs85("div", { className: "w-full", "data-testid": `message-item-${message.id}`, children: [
    shouldShowTimestamp(messages, index) && /* @__PURE__ */ jsx115(
      "div",
      {
        className: "text-center my-6 text-quaternary font-body text-md font-light leading-sm",
        "data-testid": `message-timestamp-${message.id}`,
        children: formatTimestamp(message, i18n2.language)
      }
    ),
    message.role === "user" && /* @__PURE__ */ jsx115(MessageSent, { text: message.message }),
    message.role === "assistant" && /* @__PURE__ */ jsxs85(
      "div",
      {
        className: "w-full flex gap-md",
        "data-testid": `message-assistant-${message.id}`,
        children: [
          /* @__PURE__ */ jsx115(
            "div",
            {
              className: `${isCollapsed ? "w-[10%]" : "w-[5%]"} flex shrink-0 justify-center items-end`,
              children: /* @__PURE__ */ jsx115(
                "img",
                {
                  src: "/assets/images/businessAdvisorIcon.png",
                  alt: t("businessAdvisor.imageAlt"),
                  className: "w-9 h-9 shrink-0"
                }
              )
            }
          ),
          /* @__PURE__ */ jsxs85(
            "div",
            {
              className: `${isCollapsed ? "w-[90%]" : "w-[80%]"} flex flex-col`,
              children: [
                /* @__PURE__ */ jsx115(MessageReceived, { text: message.message }),
                message.links?.length > 0 && renderLinks?.(message.links),
                dynamicCta.length > 0 && /* @__PURE__ */ jsx115("div", { className: "mt-lg", children: /* @__PURE__ */ jsx115(
                  Suggestions,
                  {
                    suggestions: [],
                    dynamicCta,
                    testID: `message-suggestions-${message.id}`,
                    onSuggestionClick,
                    onCtaClick,
                    isAuthenticated
                  }
                ) }),
                index === messages.length - 1 && nextActionTexts.length > 0 && dynamicCta.length === 0 && /* @__PURE__ */ jsx115("div", { className: "mt-lg", children: /* @__PURE__ */ jsx115(
                  Suggestions,
                  {
                    suggestions: nextActionTexts,
                    dynamicCta: [],
                    testID: `message-suggestions-${message.id}`,
                    onSuggestionClick,
                    onCtaClick,
                    isAuthenticated
                  }
                ) })
              ]
            }
          )
        ]
      }
    ),
    hasSendMessageError && index === messages.length - 1 && message.role === "user" && /* @__PURE__ */ jsxs85(
      "div",
      {
        className: "w-full flex gap-md mt-md",
        "data-testid": `message-error-${message.id}`,
        children: [
          /* @__PURE__ */ jsx115(
            "div",
            {
              className: `${isCollapsed ? "w-[10%]" : "w-[5%]"} flex shrink-0 justify-center items-end`,
              children: /* @__PURE__ */ jsx115(
                "img",
                {
                  src: "/assets/images/businessAdvisorIcon.png",
                  alt: t("businessAdvisor.imageAlt"),
                  className: "w-9 h-9 shrink-0"
                }
              )
            }
          ),
          /* @__PURE__ */ jsx115(
            "div",
            {
              className: `${isCollapsed ? "w-[90%]" : "w-[80%]"} flex flex-col`,
              children: /* @__PURE__ */ jsx115(MessageReceived, { text: t("errors.unableToComplete") })
            }
          )
        ]
      }
    )
  ] });
};
var MessageItem_default = MessageItem;

// src/components/organisms/businessAdvisorAi/components/modalContent/AdvisorModalMessageContent.tsx
import { jsx as jsx116, jsxs as jsxs86 } from "react/jsx-runtime";
var AdvisorModalMessageContent = ({
  messages = [],
  nextActions = [],
  dynamicCta = [],
  mapCta,
  testID,
  className,
  isLoading = false,
  isLoadingMore = false,
  onSuggestionClick,
  onCtaClick,
  renderLinks,
  isAuthenticated = false,
  isCollapsed = false,
  hasSendMessageError = false
}) => {
  const { t } = useTranslation55("common");
  const nextActionTexts = nextActions.map((action) => action.text);
  return /* @__PURE__ */ jsxs86(
    "div",
    {
      className: cn("flex flex-col gap-md", className),
      style: { overflowAnchor: "none" },
      "data-testid": testID || "advisor-modal-message-content-wrapper",
      children: [
        /* @__PURE__ */ jsx116("div", { "data-top-sentinel": true, style: { height: 1 } }),
        isLoadingMore && /* @__PURE__ */ jsxs86(
          "div",
          {
            className: "w-full flex justify-center py-2",
            "data-testid": `${testID}-load-more-loader`,
            children: [
              /* @__PURE__ */ jsx116("span", { className: "w-2.5 h-2.5 rounded-full bg-fg-brand-primary animate-bounce [animation-delay:0ms]" }),
              /* @__PURE__ */ jsx116("span", { className: "w-2.5 h-2.5 rounded-full bg-fg-brand-primary opacity-50 animate-bounce [animation-delay:150ms] mx-1.5" }),
              /* @__PURE__ */ jsx116("span", { className: "w-2.5 h-2.5 rounded-full bg-fg-brand-primary opacity-25 animate-bounce [animation-delay:300ms]" })
            ]
          }
        ),
        messages.map((message, index) => {
          const messageCta = message.cta?.length && mapCta ? mapCta(message.cta, message.cta_metadata) : [];
          return /* @__PURE__ */ jsx116(
            MessageItem_default,
            {
              message,
              index,
              messages,
              nextActionTexts,
              dynamicCta: messageCta.length > 0 ? messageCta : dynamicCta,
              isCollapsed,
              hasSendMessageError,
              onSuggestionClick,
              onCtaClick,
              renderLinks,
              isAuthenticated
            },
            message.id
          );
        }),
        isLoading && /* @__PURE__ */ jsxs86("div", { className: "w-full flex gap-md", children: [
          /* @__PURE__ */ jsx116(
            "div",
            {
              className: `${isCollapsed ? "w-[10%]" : "w-[5%]"} flex shrink-0 justify-center items-end`,
              children: /* @__PURE__ */ jsx116(
                "img",
                {
                  src: "/assets/images/businessAdvisorIcon.png",
                  alt: t("businessAdvisor.imageAlt"),
                  className: "w-9 h-9 shrink-0 mb-1"
                }
              )
            }
          ),
          /* @__PURE__ */ jsxs86(
            "div",
            {
              className: "flex items-center gap-1.5 py-3",
              "data-testid": `${testID}-loader`,
              children: [
                /* @__PURE__ */ jsx116("span", { className: "w-2.5 h-2.5 rounded-full bg-fg-brand-primary animate-bounce [animation-delay:0ms]" }),
                /* @__PURE__ */ jsx116("span", { className: "w-2.5 h-2.5 rounded-full bg-fg-brand-primary opacity-50 animate-bounce [animation-delay:150ms]" }),
                /* @__PURE__ */ jsx116("span", { className: "w-2.5 h-2.5 rounded-full bg-fg-brand-primary opacity-25 animate-bounce [animation-delay:300ms]" })
              ]
            }
          )
        ] })
      ]
    }
  );
};
AdvisorModalMessageContent.displayName = "AdvisorModalMessageContent";
var AdvisorModalMessageContent_default = AdvisorModalMessageContent;

// src/components/organisms/businessAdvisorAi/components/modalContent/AdvisorModalContent.tsx
import { jsx as jsx117 } from "react/jsx-runtime";
var AdvisorModalContent = ({
  testID,
  className,
  isExpanded = false,
  isConversationMode,
  isResetting,
  isInitializingConversation,
  isInitializing = false,
  dynamicSuggestions,
  dynamicCta,
  hasApiError,
  hasSendMessageError = false,
  isSending,
  isFetchingMessages,
  isLoadingMoreMessages = false,
  messages,
  nextActions,
  conversationSessionId,
  subtitle,
  onSuggestionClick,
  onCtaClick,
  renderLinks,
  isAuthenticated = false,
  mapCta
}) => {
  const { t } = useTranslation56("common");
  return /* @__PURE__ */ jsx117(
    "div",
    {
      className: cn("flex flex-col gap-4 h-full", className),
      "data-testid": testID || "advisor-modal-content-wrapper",
      children: hasApiError && !hasSendMessageError ? /* @__PURE__ */ jsx117("div", { className: "flex-1 flex items-center justify-center", children: /* @__PURE__ */ jsx117(
        Messages_default,
        {
          type: "error",
          title: t("businessAdvisor.apiError"),
          description: t("businessAdvisor.apiErrorDescription"),
          testID: "advisor-api-error-message"
        }
      ) }) : !isConversationMode ? /* @__PURE__ */ jsx117("div", { className: "flex-1 flex items-center justify-center", children: /* @__PURE__ */ jsx117(
        AdvisorModalHomeContent,
        {
          subtitle,
          hasMessages: messages.length > 0,
          suggestions: dynamicSuggestions,
          dynamicCta,
          isExpanded,
          onSuggestionClick,
          ...onCtaClick && { onCtaClick },
          isAuthenticated,
          testID: "advisor-home-content",
          isInitializing: isInitializing || isResetting || isInitializingConversation,
          isFetchingMessages: isFetchingMessages && messages.length === 0,
          className: "w-full"
        }
      ) }) : /* @__PURE__ */ jsx117(
        AdvisorModalMessageContent,
        {
          messages,
          nextActions,
          dynamicCta,
          conversationSessionId: conversationSessionId ?? null,
          testID: "advisor-message-content",
          isLoading: isSending,
          isLoadingMore: isLoadingMoreMessages,
          onSuggestionClick,
          ...onCtaClick && { onCtaClick },
          ...renderLinks && { renderLinks },
          ...mapCta && { mapCta },
          isAuthenticated,
          isCollapsed: !isExpanded,
          hasSendMessageError
        }
      )
    }
  );
};
AdvisorModalContent.displayName = "AdvisorModalContent";
var AdvisorModalContent_default = AdvisorModalContent;

// src/components/organisms/businessAdvisorAi/components/BusinessAdvisorButton.tsx
import { useState as useState26, useRef as useRef20 } from "react";
import { jsx as jsx118, jsxs as jsxs87 } from "react/jsx-runtime";
var BusinessAdvisorButton = ({
  content,
  children,
  className,
  testID
}) => {
  const [isVisible, setIsVisible] = useState26(false);
  const tooltipRef = useRef20(null);
  const triggerRef = useRef20(null);
  const handleMouseEnter = () => {
    setIsVisible(true);
  };
  const handleMouseLeave = () => {
    setIsVisible(false);
  };
  return /* @__PURE__ */ jsxs87(
    "div",
    {
      ref: triggerRef,
      className: "relative inline-block",
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      "data-testid": testID,
      children: [
        children,
        isVisible && /* @__PURE__ */ jsx118(
          "div",
          {
            ref: tooltipRef,
            className: cn(
              "absolute pointer-events-none",
              "inline-flex items-center gap-xs",
              "px-lg py-sm",
              "rounded-full",
              "border border-brand-tint-100",
              "bg-white",
              "shadow-lg",
              "text-sm font-light leading-5",
              "text-fg-primary",
              "whitespace-nowrap",
              "animate-in fade-in-0 zoom-in-95 duration-200",
              "end-full top-1/2 transform -translate-y-1/2 me-2",
              className
            ),
            role: "tooltip",
            "aria-hidden": !isVisible,
            children: content
          }
        )
      ]
    }
  );
};
BusinessAdvisorButton.displayName = "BusinessAdvisorButton";
var BusinessAdvisorButton_default = BusinessAdvisorButton;

// src/components/organisms/charts/DonutChart/DonutChart.tsx
import * as React46 from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { useTranslation as useTranslation57 } from "@tasama/i18n";

// src/components/organisms/charts/ChartTooltip.tsx
import { jsx as jsx119, jsxs as jsxs88 } from "react/jsx-runtime";
var ChartTooltip = ({
  active,
  payload,
  variant = "category",
  unit
}) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const categoryLabel = entry?.payload?.fullLabel ?? entry?.payload?.label;
  const displayLabel = variant === "series" ? entry?.name ?? categoryLabel : categoryLabel ?? entry?.name;
  const displayValue = variant === "stackedTotal" ? payload.reduce(
    (sum, e) => sum + (typeof e.value === "number" ? e.value : 0),
    0
  ) : entry?.payload?.value ?? entry?.value;
  return /* @__PURE__ */ jsxs88(
    "div",
    {
      className: "flex flex-col gap-1 rounded-lg bg-background border border-border px-4 py-3",
      style: { boxShadow: `0 4px 12px var(--color-shadow-lg)` },
      children: [
        displayLabel != null && /* @__PURE__ */ jsx119("p", { className: "text-xs text-fg-tertiary leading-normal", children: displayLabel }),
        displayValue != null && /* @__PURE__ */ jsxs88("p", { className: "text-sm font-semibold text-fg-primary leading-normal", children: [
          displayValue,
          unit
        ] })
      ]
    }
  );
};

// src/components/organisms/charts/DonutChart/DonutChart.tsx
import { jsx as jsx120, jsxs as jsxs89 } from "react/jsx-runtime";
var DonutChart = ({
  data,
  total,
  totalLabel,
  showLegend = true,
  layout = "horizontal",
  size = 220,
  className,
  testID
}) => {
  const isVertical = layout === "vertical";
  const { t } = useTranslation57("common");
  const dir = useDir();
  const isRtl = dir === "rtl";
  const startAngle = isRtl ? -90 : 90;
  const endAngle = isRtl ? 270 : -270;
  const [activeSector, setActiveSector] = React46.useState(
    null
  );
  const handleSectorEnter = (entry) => {
    const sector = entry;
    setActiveSector({
      label: sector.label,
      value: sector.value,
      cx: sector.cx,
      cy: sector.cy,
      outerRadius: sector.outerRadius,
      midAngle: sector.midAngle
    });
  };
  const handleSectorLeave = () => setActiveSector(null);
  if (data.length === 0) {
    return /* @__PURE__ */ jsx120(
      "div",
      {
        "data-testid": testID,
        className: cn(
          "flex items-center justify-center text-sm text-fg-tertiary",
          className
        ),
        style: { height: size },
        children: t("charts.noData")
      }
    );
  }
  return /* @__PURE__ */ jsxs89(
    "div",
    {
      "data-testid": testID,
      className: cn(
        // `gap-24` is a fixed 96px (`--spacing-9xl`) gap between the ring and
        // the legend on wide cards, per Figma — not `justify-between`, which
        // would push the legend all the way to the card's far edge instead
        // of the roomier-but-still-left-leaning spacing the design calls for.
        "flex items-center gap-24",
        isVertical && "flex-col gap-6",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs89("div", { className: "relative shrink-0", style: { width: size, height: size }, children: [
          /* @__PURE__ */ jsx120(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsx120(PieChart, { children: /* @__PURE__ */ jsx120(
            Pie,
            {
              data,
              dataKey: "value",
              nameKey: "label",
              innerRadius: "70%",
              outerRadius: "100%",
              paddingAngle: 4,
              cornerRadius: 8,
              startAngle,
              endAngle,
              stroke: "none",
              isAnimationActive: false,
              onMouseEnter: handleSectorEnter,
              onMouseLeave: handleSectorLeave,
              children: data.map((entry) => /* @__PURE__ */ jsx120(Cell, { fill: entry.color }, entry.label))
            }
          ) }) }),
          /* @__PURE__ */ jsxs89("div", { className: "pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center", children: [
            /* @__PURE__ */ jsx120("span", { className: "text-2xl font-semibold text-fg-primary leading-tight", children: total }),
            /* @__PURE__ */ jsx120("span", { className: "text-xs text-fg-tertiary", children: totalLabel })
          ] }),
          activeSector != null && (() => {
            const RADIAN = Math.PI / 180;
            const offset = 8;
            const cos = Math.cos(-RADIAN * activeSector.midAngle);
            const sin = Math.sin(-RADIAN * activeSector.midAngle);
            const x = activeSector.cx + (activeSector.outerRadius + offset) * cos;
            const y = activeSector.cy + (activeSector.outerRadius + offset) * sin;
            return /* @__PURE__ */ jsx120(
              "div",
              {
                className: "pointer-events-none absolute z-10",
                style: {
                  left: x,
                  top: y,
                  transform: `translate(${cos >= 0 ? "0" : "-100%"}, ${sin >= 0 ? "0" : "-100%"})`
                },
                children: /* @__PURE__ */ jsx120(
                  ChartTooltip,
                  {
                    active: true,
                    unit: "%",
                    payload: [
                      {
                        payload: {
                          label: activeSector.label,
                          value: activeSector.value
                        }
                      }
                    ]
                  }
                )
              }
            );
          })()
        ] }),
        showLegend && (() => {
          const legendItem = (point) => /* @__PURE__ */ jsxs89("li", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx120(
              "span",
              {
                "aria-hidden": "true",
                className: "h-2.5 w-2.5 shrink-0 rounded-full",
                style: { backgroundColor: point.color }
              }
            ),
            /* @__PURE__ */ jsxs89("div", { className: "flex flex-col", children: [
              /* @__PURE__ */ jsx120("span", { className: "text-xs text-fg-tertiary", children: point.label }),
              /* @__PURE__ */ jsxs89("span", { className: "text-sm font-semibold text-fg-primary", children: [
                point.value,
                "%"
              ] })
            ] })
          ] }, point.label);
          if (isVertical) {
            const half = Math.ceil(data.length / 2);
            const columns = [data.slice(0, half), data.slice(half)];
            return /* @__PURE__ */ jsx120("div", { className: "flex w-full gap-x-6", children: columns.map((column) => /* @__PURE__ */ jsx120(
              "ul",
              {
                className: "flex flex-1 flex-col gap-6",
                children: column.map(legendItem)
              },
              column[0]?.label ?? "empty"
            )) });
          }
          return /* @__PURE__ */ jsx120("ul", { className: "flex flex-col gap-6", children: data.map(legendItem) });
        })()
      ]
    }
  );
};

// src/components/organisms/charts/BarChart/BarChart.tsx
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  ResponsiveContainer as ResponsiveContainer2,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { useTranslation as useTranslation58 } from "@tasama/i18n";
import { Fragment as Fragment17, jsx as jsx121, jsxs as jsxs90 } from "react/jsx-runtime";
var X_AXIS_LABEL_LINE_CHARS = 12;
var wrapLabel = (label) => {
  const words = label.split(" ");
  let line1 = "";
  let wordIndex = 0;
  for (; wordIndex < words.length; wordIndex++) {
    const word = words[wordIndex] ?? "";
    const candidate = line1 ? `${line1} ${word}` : word;
    if (line1 && candidate.length > X_AXIS_LABEL_LINE_CHARS) break;
    line1 = candidate;
  }
  const rest = words.slice(wordIndex).join(" ");
  if (!rest) return [line1];
  const line2 = rest.length > X_AXIS_LABEL_LINE_CHARS ? `${rest.slice(0, X_AXIS_LABEL_LINE_CHARS - 3).trimEnd()}...` : rest;
  return [line1, line2];
};
var XAxisTick = ({
  x = 0,
  y = 0,
  payload = { value: "" },
  angled = true,
  rtl = false
}) => {
  if (!angled) {
    return /* @__PURE__ */ jsx121(
      "text",
      {
        x,
        y,
        dy: 12,
        textAnchor: "middle",
        className: "recharts-cartesian-axis-tick-value",
        fill: "var(--fg-tertiary)",
        fontSize: 12,
        children: payload.value
      }
    );
  }
  const lines = wrapLabel(payload.value);
  return /* @__PURE__ */ jsx121(
    "text",
    {
      x,
      y,
      dy: 8,
      textAnchor: "start",
      transform: `rotate(${rtl ? -45 : 45} ${x} ${y})`,
      className: "recharts-cartesian-axis-tick-value",
      fill: "var(--fg-tertiary)",
      fontSize: 7,
      children: lines.length === 1 ? lines[0] : lines.map((line, index) => /* @__PURE__ */ jsx121("tspan", { x, dy: index === 0 ? 0 : 9, children: line }, line))
    }
  );
};
var BarChart2 = ({
  data,
  series,
  stacked = false,
  yAxisTicks,
  color = "var(--color-brand-green-500)",
  seriesLabel,
  angledLabels = true,
  showLegend = false,
  mobileLetterLegend = false,
  height = 280,
  className,
  testID
}) => {
  const { t } = useTranslation58("common");
  const dir = useDir();
  const isRtl = dir === "rtl";
  const isDesktop = useBreakpoint("lg");
  const useLetterLegend = mobileLetterLegend && !isDesktop;
  const chartData = useLetterLegend ? data.map((point, index) => ({
    ...point,
    label: String.fromCharCode(65 + index),
    // The X-axis tick and letter-legend need the short letter as
    // `label`, but the tooltip should still surface the real category
    // name — carry it through separately rather than losing it.
    fullLabel: point.label
  })) : data;
  const seriesConfig = series ?? [
    { key: "value", color, ...seriesLabel ? { label: seriesLabel } : {} }
  ];
  const tooltipVariant = stacked ? "stackedTotal" : series || seriesLabel ? "series" : "category";
  if (data.length === 0) {
    return /* @__PURE__ */ jsx121(
      "div",
      {
        "data-testid": testID,
        className: cn(
          "flex items-center justify-center text-sm text-fg-tertiary",
          className
        ),
        style: { height },
        children: t("charts.noData")
      }
    );
  }
  return /* @__PURE__ */ jsxs90("div", { "data-testid": testID, className, children: [
    /* @__PURE__ */ jsx121("div", { style: { width: "100%", height }, children: /* @__PURE__ */ jsx121(ResponsiveContainer2, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs90(
      RechartsBarChart,
      {
        data: chartData,
        margin: { top: 8, right: 4, left: 0, bottom: angledLabels ? 8 : 0 },
        children: [
          /* @__PURE__ */ jsx121(
            CartesianGrid,
            {
              vertical: false,
              stroke: "var(--border)",
              strokeDasharray: "0"
            }
          ),
          /* @__PURE__ */ jsx121(
            XAxis,
            {
              dataKey: "label",
              reversed: isRtl,
              orientation: "bottom",
              axisLine: false,
              tickLine: false,
              interval: 0,
              height: angledLabels ? 64 : 32,
              tick: /* @__PURE__ */ jsx121(XAxisTick, { angled: angledLabels, rtl: isRtl })
            }
          ),
          /* @__PURE__ */ jsx121(
            YAxis,
            {
              orientation: isRtl ? "right" : "left",
              allowDecimals: false,
              ...yAxisTicks ? { ticks: yAxisTicks } : {},
              axisLine: false,
              tickLine: false,
              tick: { fill: "var(--fg-tertiary)", fontSize: 12 },
              width: 28
            }
          ),
          /* @__PURE__ */ jsx121(
            Tooltip,
            {
              content: /* @__PURE__ */ jsx121(ChartTooltip, { variant: tooltipVariant }),
              cursor: { fill: "var(--bg-secondary)" }
            }
          ),
          seriesConfig.map((s, index) => /* @__PURE__ */ jsx121(
            Bar,
            {
              dataKey: s.key,
              fill: s.color,
              ...s.label ? { name: s.label } : {},
              ...stacked ? { stackId: "stack" } : {},
              radius: index === seriesConfig.length - 1 ? [8, 8, 0, 0] : 0,
              maxBarSize: 32,
              isAnimationActive: false
            },
            s.key
          ))
        ]
      }
    ) }) }),
    useLetterLegend ? /* @__PURE__ */ jsxs90(Fragment17, { children: [
      /* @__PURE__ */ jsx121(
        "ul",
        {
          className: "mt-6 grid grid-cols-2 gap-x-6 gap-y-2 text-[7px] text-fg-tertiary",
          "data-testid": testID ? `${testID}-letter-legend` : void 0,
          children: data.map((point, index) => /* @__PURE__ */ jsxs90("li", { children: [
            String.fromCharCode(65 + index),
            ") ",
            point.label
          ] }, point.label))
        }
      ),
      series && /* @__PURE__ */ jsx121(
        "ul",
        {
          className: "mt-4 flex flex-wrap items-center justify-center gap-4",
          "data-testid": testID ? `${testID}-legend` : void 0,
          children: series.map((s) => /* @__PURE__ */ jsxs90("li", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx121(
              "span",
              {
                "aria-hidden": "true",
                className: "h-2 w-2 shrink-0 rounded-full",
                style: { backgroundColor: s.color }
              }
            ),
            /* @__PURE__ */ jsx121("span", { className: "text-[7px] text-fg-tertiary", children: s.label })
          ] }, s.key))
        }
      )
    ] }) : showLegend && series && /* @__PURE__ */ jsx121(
      "ul",
      {
        className: "mt-4 flex flex-wrap items-center justify-center gap-4",
        "data-testid": testID ? `${testID}-legend` : void 0,
        children: series.map((s) => /* @__PURE__ */ jsxs90("li", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx121(
            "span",
            {
              "aria-hidden": "true",
              className: "h-2 w-2 shrink-0 rounded-full",
              style: { backgroundColor: s.color }
            }
          ),
          /* @__PURE__ */ jsx121("span", { className: "text-xs text-fg-tertiary", children: s.label })
        ] }, s.key))
      }
    )
  ] });
};

// src/components/organisms/charts/AreaChart/AreaChart.tsx
import * as React47 from "react";
import {
  Area,
  AreaChart as RechartsAreaChart,
  CartesianGrid as CartesianGrid2,
  ResponsiveContainer as ResponsiveContainer3,
  Tooltip as Tooltip2,
  XAxis as XAxis2,
  YAxis as YAxis2
} from "recharts";
import { useTranslation as useTranslation59 } from "@tasama/i18n";
import { jsx as jsx122, jsxs as jsxs91 } from "react/jsx-runtime";
var AreaChartTooltip = ({
  active,
  payload,
  unit
}) => {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value;
  if (value == null) return null;
  return /* @__PURE__ */ jsx122(
    "div",
    {
      className: "rounded-lg bg-bg-secondary p-2",
      style: {
        boxShadow: "0 2px 4px -2px var(--color-shadow-md), 0 4px 6px -1px var(--color-shadow-md)"
      },
      children: /* @__PURE__ */ jsxs91("span", { className: "text-sm text-fg-primary", children: [
        value,
        unit
      ] })
    }
  );
};
var AreaChart = ({
  data,
  color = "var(--color-brand-green-500)",
  yAxisTicks,
  unit,
  height = 280,
  className,
  testID
}) => {
  const { t } = useTranslation59("common");
  const dir = useDir();
  const isRtl = dir === "rtl";
  const gradientId = React47.useId();
  if (data.length === 0) {
    return /* @__PURE__ */ jsx122(
      "div",
      {
        "data-testid": testID,
        className: cn(
          "flex items-center justify-center text-sm text-fg-tertiary",
          className
        ),
        style: { height },
        children: t("charts.noData")
      }
    );
  }
  const chartData = data.length === 1 ? [
    { label: "", value: data[0].value },
    data[0],
    { label: "", value: data[0].value }
  ] : data;
  return /* @__PURE__ */ jsx122(
    "div",
    {
      "data-testid": testID,
      className,
      style: { width: "100%", height },
      children: /* @__PURE__ */ jsx122(ResponsiveContainer3, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs91(
        RechartsAreaChart,
        {
          data: chartData,
          margin: { top: 8, right: 4, left: 0, bottom: 0 },
          children: [
            /* @__PURE__ */ jsx122("defs", { children: /* @__PURE__ */ jsxs91("linearGradient", { id: gradientId, x1: "0", y1: "0", x2: "0", y2: "1", children: [
              /* @__PURE__ */ jsx122("stop", { offset: "0%", stopColor: color, stopOpacity: 0.25 }),
              /* @__PURE__ */ jsx122("stop", { offset: "100%", stopColor: color, stopOpacity: 0 })
            ] }) }),
            /* @__PURE__ */ jsx122(
              CartesianGrid2,
              {
                vertical: false,
                stroke: "var(--border)",
                strokeDasharray: "0"
              }
            ),
            /* @__PURE__ */ jsx122(
              XAxis2,
              {
                dataKey: "label",
                reversed: isRtl,
                orientation: "bottom",
                axisLine: false,
                tickLine: false,
                tick: { fill: "var(--fg-tertiary)", fontSize: 12 }
              }
            ),
            /* @__PURE__ */ jsx122(
              YAxis2,
              {
                orientation: isRtl ? "right" : "left",
                allowDecimals: false,
                ...yAxisTicks ? { ticks: yAxisTicks } : {},
                axisLine: false,
                tickLine: false,
                tick: { fill: "var(--fg-tertiary)", fontSize: 12 },
                width: 28
              }
            ),
            /* @__PURE__ */ jsx122(
              Tooltip2,
              {
                content: /* @__PURE__ */ jsx122(AreaChartTooltip, { ...unit ? { unit } : {} }),
                cursor: { stroke: "var(--border)", strokeDasharray: "4 4" }
              }
            ),
            /* @__PURE__ */ jsx122(
              Area,
              {
                type: "monotone",
                dataKey: "value",
                stroke: color,
                strokeWidth: 2,
                fill: `url(#${gradientId})`,
                dot: false,
                activeDot: {
                  r: 4.5,
                  fill: "var(--background)",
                  stroke: color,
                  strokeWidth: 3
                },
                isAnimationActive: false
              }
            )
          ]
        }
      ) })
    }
  );
};

// src/components/organisms/charts/chart-colors.ts
var SEQUENTIAL_BLUE_STOPS_DARK_TO_LIGHT = [
  "950",
  "800",
  "700",
  "600",
  "500",
  "300",
  "200",
  "100",
  "50"
];
function getSequentialChartColors(count, stops = SEQUENTIAL_BLUE_STOPS_DARK_TO_LIGHT) {
  if (count <= 0) return [];
  if (count === 1) return [`var(--color-brand-blue-${stops[0]})`];
  return Array.from({ length: count }, (_, index) => {
    const stopIndex = Math.round(index / (count - 1) * (stops.length - 1));
    return `var(--color-brand-blue-${stops[stopIndex]})`;
  });
}

// src/components/templates/AccountPage.tsx
import { useState as useState28 } from "react";
import { useTranslation as useTranslation60 } from "@tasama/i18n";
import { jsx as jsx123 } from "react/jsx-runtime";
var defaultPersonalInfo = {
  firstName: "Ahmed",
  lastName: "Alsaif",
  prefix: "mr",
  jobTitle: "Director of operations",
  email: "Ahmed@netflix.com",
  countryCode: "+966",
  mobilePhone: "05577889921"
};
var AccountPage = ({
  initialPersonalInfo = defaultPersonalInfo,
  onPersonalInfoChange,
  className,
  testID = "account-page"
}) => {
  const { t } = useTranslation60("common");
  const [personalInfo, setPersonalInfo] = useState28(initialPersonalInfo);
  const [isEditingPersonalInfo, setIsEditingPersonalInfo] = useState28(false);
  const handlePersonalInfoChange = (data) => {
    setPersonalInfo(data);
    onPersonalInfoChange?.(data);
  };
  const tabs = [
    {
      id: "personal-info",
      label: t("accountPage.tabs.personalInformation"),
      content: /* @__PURE__ */ jsx123(
        PersonalInformationForm,
        {
          data: personalInfo,
          onChange: handlePersonalInfoChange,
          isEditing: isEditingPersonalInfo,
          onEditModeChange: setIsEditingPersonalInfo,
          testID: `${testID}-personal-info-form`
        }
      )
    },
    {
      id: "security",
      label: t("accountPage.tabs.security"),
      content: /* @__PURE__ */ jsx123("div", { className: "p-6 text-center text-gray-500", children: t("accountPage.status.securityComingSoon") })
    },
    {
      id: "notifications",
      label: t("accountPage.tabs.notifications"),
      content: /* @__PURE__ */ jsx123("div", { className: "p-6 text-center text-gray-500", children: t("accountPage.status.notificationsComingSoon") })
    },
    {
      id: "billing",
      label: t("accountPage.tabs.billing"),
      content: /* @__PURE__ */ jsx123("div", { className: "p-6 text-center text-gray-500", children: t("accountPage.status.billingComingSoon") })
    }
  ];
  return /* @__PURE__ */ jsx123(Container, { maxWidth: "7xl", className: cn("py-8", className), testID, children: /* @__PURE__ */ jsx123(
    TabsContainer,
    {
      tabs,
      defaultActiveTab: "personal-info",
      testID: `${testID}-tabs`,
      className: "w-full",
      contentClassName: "pt-8"
    }
  ) });
};
AccountPage.displayName = "AccountPage";

// src/components/templates/Layout.tsx
import { useState as useState29, useCallback as useCallback18, useMemo as useMemo12, Suspense as Suspense2 } from "react";
import { useTranslation as useTranslation61 } from "@tasama/i18n";
import { Outlet, useLocation as useLocation2, useNavigate as useNavigate2 } from "react-router-dom";
import { Fragment as Fragment18, jsx as jsx124, jsxs as jsxs92 } from "react/jsx-runtime";
var Layout = ({
  variant = "onboarding",
  navigation,
  stepper,
  content,
  actions,
  error,
  modal,
  layout,
  sidebarContent,
  customSidebarComponent,
  testID = "layout",
  children
}) => {
  const location = useLocation2();
  const navigate = useNavigate2();
  const { t, i18n: i18n2 } = useTranslation61("common");
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isDesktop = useIsDesktop();
  const [modalState, setModalState] = useState29({
    isOpen: false,
    title: "",
    content: ""
  });
  const navigationConfig = useMemo12(() => {
    const isArabic = i18n2.language === "ar";
    const arabicLink = navigation?.showLanguageSwitcher !== false ? [
      {
        label: isArabic ? "ENGLISH" : "\u0627\u0644\u0639\u0631\u0628\u064A\u0629",
        onClick: () => {
          void i18n2.changeLanguage(isArabic ? "en" : "ar");
        },
        className: "text-fg-brand-primary text-xs font-medium leading-[1.43]"
      }
    ] : [];
    if (navigation?.custom) {
      return {
        ...navigation.custom,
        links: [...arabicLink, ...navigation.custom.links ?? []]
      };
    }
    const defaultConfigs = {
      onboarding: {
        links: [
          ...arabicLink,
          {
            // "LOGIN" is short enough (5 chars) to fit on every viewport,
            // so we keep the label visible on mobile too — unlike the
            // portal "BACK TO OUR WEBSITE" link below, which drops to an
            // icon-only pill on mobile because it can't fit otherwise.
            label: t("actions.login"),
            variant: "button",
            iconName: "ArrowRight",
            onClick: () => window.location.href = navigation?.loginUrl,
            testID: "nav-login-link"
          }
        ]
      },
      portal: {
        links: [
          ...arabicLink,
          {
            // Tablet + desktop show the full "BACK TO OUR WEBSITE" label.
            // Mobile (< md) drops the label entirely via mobileIconOnly,
            // leaving just the arrow icon — see NavMenu.
            label: t("actions.backToWebsite"),
            mobileLabel: "BACK",
            variant: "button",
            iconName: "ArrowLeft",
            iconPosition: "leading",
            onClick: () => navigation?.onBackToWebsite?.() || /* @__PURE__ */ (() => {
            })(),
            testID: "nav-back-link"
          }
        ]
      },
      accountActivation: {
        links: [
          ...arabicLink,
          {
            label: t("actions.backToWebsite"),
            mobileIconOnly: true,
            variant: "button",
            iconName: "ArrowLeft",
            iconPosition: "leading",
            onClick: () => navigation?.onBackToWebsite?.() || /* @__PURE__ */ (() => {
            })(),
            testID: "nav-back-link"
          }
        ]
      },
      nda: {
        links: [
          ...arabicLink,
          {
            variant: "button",
            label: t("actions.close"),
            mobileIconOnly: true,
            onClick: () => window.location.href = navigation?.loginUrl,
            testID: "nav-login-link"
          }
        ]
      },
      "authenticated-portal": {
        links: [
          ...arabicLink,
          {
            label: t("actions.close"),
            mobileIconOnly: true,
            onClick: () => {
              if (navigation?.onClose) {
                navigation.onClose();
              } else {
                window.location.href = navigation?.closeUrl || "/my-account";
              }
            },
            testID: "nav-close-button",
            variant: "button"
          }
        ]
      }
    };
    return defaultConfigs[variant] || defaultConfigs.onboarding;
  }, [variant, navigation, i18n2.language, t]);
  const stepperConfig = useMemo12(() => {
    const currentStepIndex = stepper?.currentStepIndex !== void 0 ? stepper.currentStepIndex : stepper?.routes ? stepper.routes.indexOf(location.pathname) : 0;
    let stepperVariant;
    if (variant === "onboarding" || variant === "authenticated-portal") {
      stepperVariant = "stepper";
    } else if (variant === "portal") {
      stepperVariant = "login";
    } else {
      stepperVariant = variant;
    }
    return {
      variant: stepperVariant,
      currentStep: currentStepIndex,
      isLastStep: stepper?.routes ? currentStepIndex === stepper.routes.length - 1 : false,
      currentStepIndex
    };
  }, [variant, stepper?.routes, stepper?.currentStepIndex, location.pathname]);
  const handleNavigate = useCallback18(
    (path) => {
      navigate(path);
    },
    [navigate]
  );
  const handleStepNavigation = useCallback18(
    (direction) => {
      if (!stepper?.routes) return;
      const { currentStepIndex } = stepperConfig;
      const targetIndex = direction === "next" ? currentStepIndex + 1 : currentStepIndex - 1;
      const targetRoute = stepper.routes[targetIndex];
      if (targetRoute) {
        handleNavigate(targetRoute);
      }
    },
    [stepper?.routes, stepperConfig, handleNavigate]
  );
  const handleModalClose = useCallback18(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  }, []);
  const handleSetIsModalOpen = useCallback18(
    (isOpen) => {
      if (isOpen) {
        setModalState({
          isOpen: true,
          title: modal?.title || t("layout.information"),
          content: modal?.content || ""
        });
      } else {
        setModalState((prev) => ({ ...prev, isOpen: false }));
      }
    },
    [modal?.title, modal?.content, t]
  );
  const handleSetModalContent = useCallback18(
    (content2) => {
      setModalState((prev) => ({
        ...prev,
        content: content2,
        title: modal?.title || t("layout.information")
      }));
    },
    [modal?.title, t]
  );
  const actionHandlers = useMemo12(
    () => ({
      onContinue: () => {
        if (actions?.onContinue) {
          actions.onContinue();
        } else {
          handleStepNavigation("next");
        }
      },
      onBack: () => {
        if (actions?.onBack) {
          actions.onBack();
        } else {
          handleStepNavigation("previous");
        }
      },
      onDone: actions?.onDone || (() => {
      }),
      onStepClick: stepper?.onStepClick || (() => {
      }),
      onStartOnboarding: stepper?.onStartOnboarding || (() => {
      })
    }),
    [actions, handleStepNavigation, stepper]
  );
  const isCurrentStepValid = useMemo12(() => {
    if (!stepper?.validation) return true;
    if (stepper.currentStepIndex !== void 0 && stepper.steps) {
      const currentStep = stepper.steps[stepper.currentStepIndex];
      if (currentStep) {
        const currentRoute = currentStep.id;
        return stepper.validation[currentRoute] ?? true;
      }
      return true;
    }
    return stepper.validation[location.pathname] ?? true;
  }, [
    stepper?.validation,
    stepper?.currentStepIndex,
    stepper?.steps,
    location.pathname
  ]);
  const layoutConfig = useMemo12(
    () => ({
      maxWidth: layout?.maxWidth || "max-w-[1600px]",
      padding: layout?.padding || "px-[var(--spacing-7xl)] pt-[var(--spacing-7xl)]",
      gap: layout?.gap || "gap-[var(--spacing-section-margin)]",
      contentHeight: layout?.contentHeight,
      showSidebar: layout?.showSidebar ?? true,
      showBottomNavigation: layout?.showBottomNavigation ?? (variant === "onboarding" || variant === "authenticated-portal"),
      // Responsive classes based on media query hooks
      responsiveClasses: isMobile ? "w-full" : isTablet ? "w-full" : layout?.maxWidth || "min-w-container-max max-w-container-3xl",
      // Dynamic padding based on device type.
      // Top padding doubles as the gap between the branded nav header (which
      // sits above this padded container) and the sidebar/content cards.
      // Desktop: pt-3xl = 24px to match Figma. Tablet inherits 24px from
      // pt-responsive-sm; mobile keeps 16px from pt-responsive-xs.
      responsivePadding: isMobile ? "px-responsive-xs pt-responsive-xs pb-95" : isTablet ? "px-responsive-sm pt-responsive-sm pb-95" : layout?.padding || "px-7xl pt-3xl",
      // Dynamic container padding
      containerPadding: isMobile ? "p-responsive-xs" : isTablet ? "p-responsive-sm" : "p-4xl",
      // Dynamic border radius
      containerRadius: isMobile ? "rounded-xl" : isTablet ? "rounded-2xl" : "rounded-3xl"
    }),
    [isMobile, isTablet, isDesktop, layout, variant]
  );
  const showDoneButton = actions?.showDoneButton ?? false;
  const navigationButtons = {
    showBack: stepperConfig.currentStepIndex > 0 && !showDoneButton,
    showContinue: !showDoneButton,
    continueDisabled: actions?.isContinueDisabled ?? !isCurrentStepValid
  };
  return /* @__PURE__ */ jsxs92(PageBackground, { children: [
    /* @__PURE__ */ jsx124(
      OnboardingNavHeader,
      {
        className: "w-full",
        testID: `${testID}-nav-header`,
        navigation: navigationConfig,
        variant: "branded"
      }
    ),
    /* @__PURE__ */ jsxs92(
      "div",
      {
        className: `lg:max-h-screen flex flex-col pt-0 mx-auto ${layoutConfig.responsiveClasses} ${layoutConfig.responsivePadding} ${layoutConfig.gap}`,
        children: [
          /* @__PURE__ */ jsxs92(
            "div",
            {
              className: `flex gap-6 flex-1 ${layoutConfig.responsiveClasses} lg:pb-62 ${isMobile || isTablet ? "flex-col" : "flex-row"}`,
              children: [
                layoutConfig.showSidebar && /* @__PURE__ */ jsx124(
                  "aside",
                  {
                    className: `flex-shrink-0 ${isDesktop ? "w-auto min-w-sidebar-width-desktop" : "w-full"}`,
                    children: customSidebarComponent ? customSidebarComponent.component : /* @__PURE__ */ jsxs92(Fragment18, { children: [
                      isDesktop && /* @__PURE__ */ jsx124(
                        OnboardingStepper,
                        {
                          variant: stepperConfig.variant,
                          currentStep: stepperConfig.currentStep,
                          sidebarContent,
                          steps: stepper?.steps,
                          onStartOnboarding: actionHandlers.onStartOnboarding,
                          onStepClick: actionHandlers.onStepClick,
                          lang: i18n2.resolvedLanguage ?? i18n2.language,
                          testID: `${testID}-stepper-${stepperConfig.currentStepIndex}`
                        }
                      ),
                      (isMobile || isTablet) && /* @__PURE__ */ jsx124(
                        OnboardingStepperMobile,
                        {
                          variant: stepperConfig.variant === "login" ? "login" : "compact",
                          currentStep: stepperConfig.currentStep,
                          sidebarContent,
                          steps: stepper?.steps,
                          onStartOnboarding: actionHandlers.onStartOnboarding,
                          onStepClick: actionHandlers.onStepClick,
                          testID: `${testID}-stepper-mobile-${stepperConfig.currentStepIndex}`,
                          showStepperDetails: false
                        }
                      )
                    ] })
                  }
                ),
                /* @__PURE__ */ jsxs92(
                  Container,
                  {
                    className: `
                bg-alpha-white-90 backdrop-blur-sm ${layoutConfig.containerRadius}
                overflow-y-auto w-full flex flex-col flex-1
                ${// Portal variant: zero internal padding so LoginForm owns
                    // the visible padding directly (no double-stacking).
                    variant === "portal" ? "p-0" : layoutConfig.containerPadding}
                ${// Portal variant height:
                    //  - mobile/tablet: h-auto (card hugs content, no extra
                    //    white below the form on small screens).
                    //  - lg+ desktop:  h-full + min-h-sidebar-md so the white
                    //    card matches the welcome banner height; bottoms align.
                    variant === "portal" ? "h-auto lg:h-full xl:min-h-sidebar-md" : isMobile || isTablet ? "min-h-container-mobile h-full" : "h-full min-h-sidebar-md"} `,
                    children: [
                      /* @__PURE__ */ jsx124(
                        "div",
                        {
                          className: variant === "portal" ? `flex-1 flex xl:items-center justify-center p-4xl xl:p-0` : `flex-1 flex flex-col p-0`,
                          children: /* @__PURE__ */ jsx124(
                            Suspense2,
                            {
                              fallback: /* @__PURE__ */ jsx124(Loader2, { size: "lg", testID: `${testID}-content-loader` }),
                              children: children ? children : /* @__PURE__ */ jsx124(
                                Outlet,
                                {
                                  context: {
                                    setIsModalOpen: handleSetIsModalOpen,
                                    setModalContent: handleSetModalContent,
                                    ...content?.outletContext
                                  }
                                }
                              )
                            }
                          )
                        }
                      ),
                      layoutConfig.showBottomNavigation && /* @__PURE__ */ jsx124("div", { className: "pt-[var(--spacing-2xl)]", children: navigationButtons.showContinue ? /* @__PURE__ */ jsx124(
                        NavigationButtonBar,
                        {
                          backButton: {
                            text: actions?.backText?.toUpperCase() || t("actions.previous").toUpperCase(),
                            variant: "tertiary",
                            onClick: actionHandlers.onBack,
                            testID: `${testID}-nav-back-button`,
                            show: navigationButtons.showBack && !actions?.isBackDisabled,
                            loading: actions?.isBackLoading,
                            disabled: actions?.isBackDisabled
                          },
                          skipButton: {
                            text: actions?.skipText?.toUpperCase() || t("actions.skipForNow").toUpperCase(),
                            variant: "tertiary",
                            ...actions?.onSkip ? { onClick: actions.onSkip } : {},
                            testID: `${testID}-nav-skip-button`,
                            show: actions?.isSkipVisible ?? false
                          },
                          continueButton: {
                            text: actions?.continueText || t("actions.next"),
                            variant: "secondary",
                            disabled: navigationButtons.continueDisabled,
                            onClick: actionHandlers.onContinue,
                            testID: `${testID}-nav-continue-button`,
                            show: true,
                            loading: actions?.isContinueLoading
                          },
                          size: "md",
                          testID: `${testID}-navigation`
                        }
                      ) : /* @__PURE__ */ jsx124("div", { className: "flex justify-end w-full", children: /* @__PURE__ */ jsx124(
                        Button,
                        {
                          variant: "secondary",
                          size: "md",
                          onClick: actionHandlers.onDone,
                          disabled: actions?.isDoneDisabled,
                          loading: actions?.isDoneLoading,
                          testID: `${testID}-done-button`,
                          className: "w-120 h-11",
                          label: actions?.doneText || t("actions.done"),
                          iconName: "CircleCheck",
                          iconPosition: "trailing"
                        }
                      ) }) })
                    ]
                  }
                )
              ]
            }
          ),
          error?.show && error.data && /* @__PURE__ */ jsx124(
            AlertsAndNotifications_default,
            {
              variant: error.data.type === "unauthorized" ? "warning" : "error",
              title: error.data.type === "unauthorized" ? t("errors.sessionExpiredTitle") : t("errors.errorTitle"),
              description: error.data.message,
              onClose: error.data.onClear,
              testID: `${testID}-error-alert`,
              className: "fixed top-8 z-page-loader inset-x-3 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:px-4"
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs92(
      Modal,
      {
        size: isMobile ? "sm" : isTablet ? "md" : "lg",
        isOpen: modal?.isOpen ?? modalState.isOpen,
        onClose: modal?.onClose ?? handleModalClose,
        testID: `${testID}-modal`,
        className: modal?.className ?? "",
        children: [
          /* @__PURE__ */ jsx124(
            Modal.Header,
            {
              type: modal?.headerType ?? "left",
              title: modal?.title ?? modalState.title,
              icon: modal?.icon ?? /* @__PURE__ */ jsx124(
                InfoIconWithBackground_default,
                {
                  size: isMobile ? "medium" : "large",
                  testID: `${testID}-modal-icon`
                }
              ),
              testID: `${testID}-modal-header`
            }
          ),
          (modal?.content ?? modalState.content) && /* @__PURE__ */ jsx124(Modal.Content, { testID: `${testID}-modal-content`, children: /* @__PURE__ */ jsx124(Text, { size: "sm", weight: "normal", className: "text-fg-secondary", children: modal?.content ?? modalState.content }) })
        ]
      }
    )
  ] });
};

// src/components/pages/AppInfoPage.tsx
import { useTranslation as useTranslation62 } from "@tasama/i18n";
import { jsx as jsx125, jsxs as jsxs93 } from "react/jsx-runtime";
var AppInfoPage = ({
  rows,
  onBackClick,
  backLabel
}) => {
  const { t } = useTranslation62("common");
  const resolvedBackLabel = backLabel ?? t("appInfo.backToHome");
  const navigationConfig = { links: [] };
  return /* @__PURE__ */ jsx125(PageBackground, { testID: "app-info-page", children: /* @__PURE__ */ jsxs93("div", { className: "flex flex-col min-h-full", children: [
    /* @__PURE__ */ jsx125(
      OnboardingNavHeader,
      {
        className: "w-full",
        testID: "app-info-nav-header",
        navigation: navigationConfig,
        variant: "branded"
      }
    ),
    /* @__PURE__ */ jsx125("div", { className: "flex-1 flex flex-col mt-6 px-responsive-xs md:px-responsive-sm lg:px-7xl pb-responsive-xs md:pb-responsive-sm lg:pb-7xl", children: /* @__PURE__ */ jsx125("div", { className: "flex-1 flex flex-col w-full mx-auto max-w-container-3xl bg-background rounded-3xl p-6 md:p-12 lg:p-16", children: /* @__PURE__ */ jsxs93("div", { className: "w-full mx-auto max-w-[42rem]", children: [
      /* @__PURE__ */ jsxs93("div", { className: "flex items-center justify-between mb-6", children: [
        /* @__PURE__ */ jsx125("h1", { className: "text-2xl font-semibold text-fg-primary", children: t("appInfo.title") }),
        /* @__PURE__ */ jsx125(
          Button,
          {
            variant: "quaternary",
            size: "sm",
            onClick: onBackClick,
            label: resolvedBackLabel,
            testID: "app-info-back-button"
          }
        )
      ] }),
      /* @__PURE__ */ jsx125("div", { className: "space-y-4", children: rows.map(({ label, value }) => /* @__PURE__ */ jsxs93(
        "div",
        {
          className: "flex items-center justify-between p-4 bg-bg-secondary rounded-md",
          children: [
            /* @__PURE__ */ jsx125("span", { className: "text-fg-secondary", children: label }),
            /* @__PURE__ */ jsx125("span", { className: "font-medium text-fg-primary", children: value })
          ]
        },
        label
      )) })
    ] }) }) })
  ] }) });
};

// src/components/pages/ErrorPage.tsx
import { useTranslation as useTranslation63 } from "@tasama/i18n";
import { jsx as jsx126, jsxs as jsxs94 } from "react/jsx-runtime";
var ErrorPage = ({
  variant = "error",
  onHomeClick,
  onReloadClick,
  className,
  testID
}) => {
  const { t } = useTranslation63("common");
  const isNotFound = variant === "not-found";
  const title = isNotFound ? t("errorPage.notFoundTitle") : t("errorPage.errorTitle");
  const description = isNotFound ? t("errorPage.notFoundDescription") : t("errorPage.errorDescription");
  const handleHomeClick = () => {
    if (onHomeClick) {
      onHomeClick();
    } else {
      window.location.href = "/";
    }
  };
  const handleReloadClick = () => {
    if (onReloadClick) {
      onReloadClick();
    } else {
      window.location.reload();
    }
  };
  const navigationConfig = {
    links: []
  };
  return /* @__PURE__ */ jsx126(PageBackground, { testID: `${testID}-page`, children: /* @__PURE__ */ jsxs94("div", { className: "flex flex-col min-h-full", children: [
    /* @__PURE__ */ jsx126(
      OnboardingNavHeader,
      {
        className: "w-full",
        testID: `${testID}-nav-header`,
        navigation: navigationConfig,
        variant: "branded"
      }
    ),
    /* @__PURE__ */ jsx126("div", { className: "flex-1 flex flex-col mt-6 px-responsive-xs md:px-responsive-sm lg:px-7xl pb-responsive-xs md:pb-responsive-sm lg:pb-7xl", children: /* @__PURE__ */ jsxs94(
      "div",
      {
        className: cn(
          "flex flex-1 flex-col items-center justify-center text-center",
          // Wide card matching the desktop Figma spec (capped at the
          // project's `--spacing-container-3xl` = 1600px max width).
          // `mx-auto` keeps it centered when the viewport exceeds
          // the cap on ultrawide displays.
          "w-full mx-auto max-w-container-3xl",
          // Responsive internal padding so the centered content has
          // breathing room from the card edges on every breakpoint.
          "p-6 md:p-12 lg:p-16",
          "bg-background rounded-3xl",
          className
        ),
        role: "alert",
        "aria-live": "polite",
        "data-testid": testID,
        children: [
          /* @__PURE__ */ jsx126("div", { className: "mb-6", "data-testid": `${testID}-icon-wrapper`, children: /* @__PURE__ */ jsx126(
            Icon,
            {
              name: "CircleAlert",
              size: 120,
              strokeWidth: 3,
              absoluteStrokeWidth: true,
              testID: `${testID}-error-icon`,
              className: "text-fg-error-primary opacity-90"
            }
          ) }),
          /* @__PURE__ */ jsx126(
            Heading,
            {
              level: 1,
              size: "xl",
              weight: "normal",
              className: "mb-4 text-foreground lg:text-3xl",
              testID: `${testID}-title`,
              children: title
            }
          ),
          /* @__PURE__ */ jsx126(
            Text,
            {
              size: "base",
              color: "muted",
              leading: "relaxed",
              className: "mb-8 max-w-[480px] lg:text-lg",
              testID: `${testID}-description`,
              children: description
            }
          ),
          isNotFound ? /* @__PURE__ */ jsx126(
            Button,
            {
              variant: "secondary",
              size: "md",
              onClick: handleHomeClick,
              iconPosition: "trailing",
              iconName: "RotateCw",
              testID: `${testID}-home-button`,
              label: t("errorPage.backToHome")
            }
          ) : /* @__PURE__ */ jsxs94(
            "div",
            {
              className: "flex flex-col sm:flex-row gap-3 w-full sm:w-auto justify-center",
              "data-testid": `${testID}-actions`,
              children: [
                /* @__PURE__ */ jsx126(
                  Button,
                  {
                    variant: "primary",
                    size: "md",
                    onClick: handleHomeClick,
                    iconPosition: "leading",
                    iconName: "Home",
                    testID: `${testID}-home-button`,
                    label: t("errorPage.goHome")
                  }
                ),
                /* @__PURE__ */ jsx126(
                  Button,
                  {
                    variant: "secondary",
                    size: "md",
                    onClick: handleReloadClick,
                    iconPosition: "leading",
                    iconName: "RotateCcw",
                    testID: `${testID}-secondary-button`,
                    label: t("errorPage.reloadPage")
                  }
                )
              ]
            }
          )
        ]
      }
    ) })
  ] }) });
};
ErrorPage.displayName = "ErrorPage";

// src/theme/types.ts
function isTokenCategory(key) {
  return [
    "colors",
    "spacing",
    "typography",
    "radius",
    "shadows",
    "animations"
  ].includes(key);
}

// src/theme/utils.ts
function applyTokenOverrides(overrides) {
  const root = document.documentElement;
  Object.entries(overrides).forEach(([, tokens]) => {
    if (tokens) {
      Object.entries(tokens).forEach(([tokenName, value]) => {
        const cssVarName = tokenName.startsWith("--") ? tokenName : `--${tokenName}`;
        root.style.setProperty(cssVarName, value);
      });
    }
  });
}
function removeTokenOverrides(overrides) {
  const root = document.documentElement;
  Object.entries(overrides).forEach(([, tokens]) => {
    if (tokens) {
      Object.keys(tokens).forEach((tokenName) => {
        const cssVarName = tokenName.startsWith("--") ? tokenName : `--${tokenName}`;
        root.style.removeProperty(cssVarName);
      });
    }
  });
}
function getCurrentTokenValue(tokenName) {
  const cssVarName = tokenName.startsWith("--") ? tokenName : `--${tokenName}`;
  return getComputedStyle(document.documentElement).getPropertyValue(cssVarName).trim() || null;
}
function batchUpdateTokens(updates) {
  applyTokenOverrides(updates);
}
function convertFigmaTokens(figmaTokens) {
  const overrides = {};
  figmaTokens.forEach((token) => {
    const category = getCategoryFromTokenType(token.type);
    if (category) {
      if (!overrides[category]) {
        overrides[category] = {};
      }
      overrides[category][token.name] = token.value;
    }
  });
  return overrides;
}
function getCategoryFromTokenType(type) {
  const typeMap = {
    color: "colors",
    spacing: "spacing",
    typography: "typography",
    borderRadius: "radius",
    boxShadow: "shadows",
    duration: "animations",
    easing: "animations"
  };
  return typeMap[type] || null;
}
function validateTokenOverrides(overrides) {
  if (typeof overrides !== "object" || overrides === null) {
    return false;
  }
  const validCategories = [
    "colors",
    "spacing",
    "typography",
    "radius",
    "shadows",
    "animations"
  ];
  return Object.keys(overrides).every((key) => {
    if (!validCategories.includes(key) && typeof overrides[key] !== "object") {
      return false;
    }
    if (overrides[key] && typeof overrides[key] === "object") {
      return Object.values(overrides[key]).every(
        (value) => typeof value === "string"
      );
    }
    return true;
  });
}
function mergeTokenOverrides(...overrides) {
  const merged = {};
  overrides.forEach((override) => {
    Object.entries(override).forEach(([category, tokens]) => {
      if (tokens) {
        if (!merged[category]) {
          merged[category] = {};
        }
        Object.assign(merged[category], tokens);
      }
    });
  });
  return merged;
}
function cloneTokenOverrides(overrides) {
  const cloned = {};
  Object.entries(overrides).forEach(([category, tokens]) => {
    if (tokens) {
      cloned[category] = { ...tokens };
    }
  });
  return cloned;
}
function areTokenOverridesEqual(a, b) {
  const keysA = Object.keys(a).sort();
  const keysB = Object.keys(b).sort();
  if (keysA.length !== keysB.length || !keysA.every((key, index) => key === keysB[index])) {
    return false;
  }
  return keysA.every((category) => {
    const tokensA = a[category] || {};
    const tokensB = b[category] || {};
    const tokenKeysA = Object.keys(tokensA).sort();
    const tokenKeysB = Object.keys(tokensB).sort();
    if (tokenKeysA.length !== tokenKeysB.length || !tokenKeysA.every((key, index) => key === tokenKeysB[index])) {
      return false;
    }
    return tokenKeysA.every((token) => tokensA[token] === tokensB[token]);
  });
}
function getAvailableTokens(category) {
  const root = document.documentElement;
  const styles = getComputedStyle(root);
  const tokens = [];
  for (let i = 0; i < styles.length; i++) {
    const property = styles[i];
    if (property && typeof property === "string" && property.startsWith("--")) {
      if (category === "colors" && property.includes("color")) {
        tokens.push(property);
      } else if (category === "spacing" && property.includes("spacing")) {
        tokens.push(property);
      } else if (category === "typography" && (property.includes("font") || property.includes("line") || property.includes("letter"))) {
        tokens.push(property);
      } else if (category === "radius" && property.includes("radius")) {
        tokens.push(property);
      } else if (category === "shadows" && property.includes("shadow")) {
        tokens.push(property);
      } else if (category === "animations" && (property.includes("duration") || property.includes("ease") || property.includes("transition"))) {
        tokens.push(property);
      }
    }
  }
  return tokens.sort();
}
function exportTokensToJSON(overrides) {
  return JSON.stringify(overrides, null, 2);
}
function importTokensFromJSON(jsonString) {
  try {
    const parsed = JSON.parse(jsonString);
    if (validateTokenOverrides(parsed)) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}
export {
  MAX_CHARACTERS as ADVISOR_MAX_CHARACTERS,
  WARNING_LIMIT as ADVISOR_WARNING_LIMIT,
  AISparkleIcon,
  Accordion,
  AccordionContent,
  AccordionGroup,
  AccordionItem,
  AccordionTrigger,
  AccountPage,
  AdvisorHeader_default as AdvisorHeader,
  AdvisorMessageInput_default as AdvisorMessageInput,
  AdvisorModal_default as AdvisorModal,
  AdvisorModalContent_default as AdvisorModalContent,
  AdvisorModalHomeContent_default as AdvisorModalHomeContent,
  AdvisorModalMessageContent_default as AdvisorModalMessageContent,
  AlertsAndNotifications_default as AlertsAndNotifications,
  AppInfoPage,
  AreaChart,
  BAContextualButton,
  BUSINESS_AI_BUTTON_IMG_SRC,
  BUTTON_TEST_ID,
  BackToTop,
  Badge,
  Banner,
  BarChart2 as BarChart,
  Breadcrumbs,
  BusinessActivityTaskCard,
  BusinessAdvisorButton_default as BusinessAdvisorButton,
  Button,
  Card,
  CheckCircleIcon,
  CheckIcon2 as CheckIcon,
  Checkbox,
  Chip,
  CompanyLogoUpload,
  CompanyType,
  Container,
  CountryFlag,
  CurrentPage,
  DELAYS,
  DateInput,
  DateRangePicker,
  DateTimeInput,
  DocumentDownload,
  DocumentUploadSection,
  DonutChart,
  Dropdown,
  DynamicRowsTable,
  EmptyScreen,
  ErrorBoundary,
  ErrorPage,
  FeaturedIcon,
  FileIcon_default as FileIcon,
  FileUploadBase,
  FileUploadItem,
  FilterDrawer,
  FilterTriggerButton,
  Flex,
  FloatingMenu,
  Grid,
  HEADER_DESCRIPTION,
  HEADER_TITLE,
  Header,
  HeaderWithBoundary,
  Heading,
  Hero,
  Icon,
  InfoIconCircularWave_default as InfoIconCircularWave,
  InfoIconWithBackground_default as InfoIconWithBackground,
  Input,
  InputWithLabel,
  Label,
  LanguageSwitcher,
  Layout,
  Loader2 as Loader,
  MAX_CHARACTERS,
  MessageReceived_default as MessageReceived,
  MessageSent_default as MessageSent,
  Messages_default as Messages,
  MilestoneStepper,
  MobileInputField,
  Modal,
  ModalActions,
  ModalContent,
  ModalFooter,
  ModalHeader,
  MultiSelectDropdown,
  MyTaskCard,
  NavigationButtonBar,
  NotificationItem,
  OnboardingHeader,
  OnboardingNavHeader,
  OnboardingStepper,
  OnboardingStepperMobile,
  TasamaLogo2 as PIFLogo,
  PageBackground,
  PageLoader,
  Pagination,
  PersonalInformationForm,
  ProfileLogo,
  ProgressBar,
  RadioButton,
  RadioGroup,
  RefreshPage,
  SARCurrencyIcon_default as SARCurrencyIcon,
  SearchInput,
  SearchInputSelection,
  Section,
  ServiceBadge,
  ServiceCard,
  ServiceCardL2,
  ServiceCategoryCard,
  Services,
  SidePanel,
  SidebarItem,
  SidebarItems,
  Skeleton,
  SomeThingWentWrongModal_default as SomeThingWentWrongModal,
  Spinner,
  StatusBadge,
  StatusCounter,
  Stepper,
  Suggestions_default as Suggestions,
  TabBar,
  Table,
  TabsContainer,
  TasamaLogo,
  TasamaTextLogo,
  TasamaTextLogoWithTagline,
  TaskCard,
  Tasks,
  TermsAndConditionsContent,
  Text,
  Textarea,
  TextareaWithCharacterCount,
  TextareaWithLabel,
  TimeInput,
  WARNING_LIMIT,
  applyTokenOverrides,
  areTokenOverridesEqual,
  batchUpdateTokens,
  brandButtonVariants,
  cloneTokenOverrides,
  cn,
  convertFigmaTokens,
  exportTokensToJSON,
  formatMessageTime,
  formatTimestamp,
  getAvailableTokens,
  getCompanyTypeValues,
  getCurrentPageValues,
  getCurrentTimestamp,
  getCurrentTokenValue,
  getSequentialChartColors,
  getStatusBadgeText,
  importTokensFromJSON,
  isTokenCategory,
  isValidCompanyType,
  isValidCurrentPage,
  mergeTokenOverrides,
  removeTokenOverrides,
  shouldShowTimestamp,
  useAutoResizeTextarea,
  useBackToTop,
  useBreakpoint,
  useDefaultDateRangePresets,
  useDir,
  useInfiniteScrollWithQuery,
  useIsDesktop,
  useIsMobile,
  useIsMobileOverlayOpen,
  useIsTablet,
  useMediaQuery,
  useMobileOverlayStore,
  usePageLoader,
  usePageLoaderStore,
  usePopoverPosition,
  useRefreshPageStore,
  useSomeThingWentWrongModalStore,
  validateTokenOverrides
};
//# sourceMappingURL=index.js.map